
/* ==== js/core-globals.js ==== */
// Core global state — previously lived in app.v2.js (now merged here).
// Loaded FIRST inside core.min.js so every later module sees these.
var currentUser = null;
var currentPage = '';
var seenOrderIds = new Set();
var trackedOrderStates = new Map();
var paymentRemindedOrderIds = new Map();
window.__appV2Loaded = true;

// Performance: pause decorative work while the tab is in the background.
// Infinite CSS animations + polling timers are the main cause of the high
// CPU/RAM usage on phones and laptops when the console is left open.
(function () {
  function sync() {
    try { document.documentElement.classList.toggle('tab-hidden', document.hidden); } catch (e) {}
  }
  document.addEventListener('visibilitychange', sync);
  sync();
})();

/* ==== js/api.js ==== */
// Android kills the app process whenever it needs RAM, and a localStorage
// write made shortly before that kill can be lost before the WebView flushes
// it — the merchant then opens the app to a login screen while the server
// still holds an active session for that token. The native vault
// (AndroidAuthBridge) keeps the same token in SharedPreferences, which is
// committed by the activity manager. Hydrate before anything reads the token,
// so every reader — including code that goes straight to localStorage — sees
// the restored value.
try {
  if (!localStorage.getItem('token') && window.AndroidAuth && AndroidAuth.getToken) {
    const saved = AndroidAuth.getToken();
    if (saved) localStorage.setItem('token', saved);
  }
} catch (e) { /* not the APK */ }

const API = {
  baseUrl: '',
  token: null,

  getToken() { return localStorage.getItem('token'); },
  setToken(t) {
    localStorage.setItem('token', t);
    try { if (window.AndroidAuth && AndroidAuth.saveToken) AndroidAuth.saveToken(t); } catch (e) {}
    this.token = t;
  },
  clearToken() {
    localStorage.removeItem('token');
    try { if (window.AndroidAuth && AndroidAuth.clearToken) AndroidAuth.clearToken(); } catch (e) {}
    this.token = null;
  },

  getHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    const t = this.getToken();
    if (t) headers['Authorization'] = 'Bearer ' + t;
    return headers;
  },

  getHeadersWith(extra) {
    const headers = this.getHeaders();
    const x = extra || {};
    Object.keys(x).forEach(function(k) { headers[k] = x[k]; });
    return headers;
  },

  async request(method, path, body, timeoutMs, extraHeaders) {
    const opts = { method, headers: this.getHeadersWith(extraHeaders) };
    if (body) opts.body = JSON.stringify(body);
    const timeout = timeoutMs || 30000;
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      opts.signal = controller.signal;
      const res = await fetch(this.baseUrl + path, opts);
      clearTimeout(timeoutId);
      const rid = res.headers.get('X-Request-Id');
      if (rid) window.__lastRequestId = rid;
      const newToken = res.headers.get('X-New-Token');
      if (newToken) this.setToken(newToken);
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('text/html')) {
        throw new Error('Server returned a page instead of JSON. The server may be restarting or unavailable.');
      }
      const data = await res.json();
      if (!res.ok && !data.code) throw new Error('The server could not complete the request (HTTP ' + res.status + '). Check your connection or try again shortly.');
      if (data.code && data.code !== '0' && data.code !== 0) {
        // Clear the login token ONLY for our own auth layer's exact messages.
        // Substring checks ('expired', etc.) caused false logouts when upstream
        // business errors happened to contain those words.
        const isSessionExpired = data.msg && data.msg.includes('Session expired');
        if ((res.status === 401 || res.status === 403) &&
            (data.msg === 'Authentication required' || data.msg === 'Invalid or expired token' || isSessionExpired)) {
          this.clearToken();
          if (isSessionExpired) {
            setTimeout(function() {
              if (typeof showToast === 'function') showToast('Logged out — you logged in from another device (max 5 devices)', 'info', 5000);
              if (window.location.hash !== '#login') window.location.hash = '#login';
            }, 100);
          }
        }
        const e = new Error(data.msg || 'API error');
        // Structured errors carry { title, reason, solution } — msg already
        // contains all three as one readable line.
        if (data.error && data.error.title) e.detail = data.error;
        throw e;
      }
      return data;
    } catch (err) {
      if (err.name === 'AbortError' || err.message.includes('aborted')) {
        throw new Error('Request timed out after ' + Math.round(timeout / 1000) + 's. The server may be busy, try again.');
      }
      const isSessionExpiredCatch = err.message && err.message.includes('Session expired');
      if (err.message === 'Authentication required' || err.message === 'Invalid or expired token' || isSessionExpiredCatch) {
        this.clearToken();
      }
      throw err;
    }
  },

  get(path, timeoutMs) { return this.request('GET', path, null, timeoutMs); },
  post(path, body, timeoutMs) { return this.request('POST', path, body, timeoutMs); },
  postWithHeaders(path, body, headers) { return this.request('POST', path, body, null, headers); },
  put(path, body) { return this.request('PUT', path, body); },
  delete(path, body) { return this.request('DELETE', path, body); },

  // Auth
  login(email, password, phone, deviceToken, verificationMethod, turnstileToken, captchaToken, captchaAnswer, username) { return this.post('/api/auth/login', { email, password, phone, deviceToken, verificationMethod, captchaToken, captchaAnswer, username }); },
  register(name, email, password, username, phone, captchaToken, captchaAnswer) { return this.post('/api/auth/register', { name, email, password, username: username || name, phone, captchaToken, captchaAnswer }); },
  getOkxInfo() { return this.get('/api/auth/okx-info'); },
  linkPhone(email, phone, password) { return this.post('/api/auth/link-phone', { email, phone, password }); },
  linkEmail(email, phone, password) { return this.post('/api/auth/link-email', { email, phone, password }); },
  googleLogin() { return Promise.reject(new Error('Google login not available')); },
  trustDevice() { return this.post('/api/auth/trust-device', {}); },
  forgetDevice(deviceToken) { return this.post('/api/auth/forget-device', { deviceToken }); },
  verifyOtp(tempToken, otp, rememberDevice) { return this.post('/api/auth/verify-otp', { tempToken, otp, rememberDevice }); },
  resendOtp(tempToken, method) { return this.post('/api/auth/resend-otp', { tempToken, method }); },
  del(path) { return this.delete(path); },
  getDeviceTokens() { return this.get('/api/auth/device-tokens'); },
  getProfile() { return this.get('/api/auth/profile'); },
  getAltContacts() { return this.get('/api/auth/contacts'); },
  addAltContact(contact, type) { return this.post('/api/auth/contacts', { contact, type }); },
  verifyAltContact(tempToken, otp) { return this.post('/api/auth/contacts/verify', { tempToken, otp }); },
  deleteAltContact(id) { return this.delete('/api/auth/contacts/' + id); },

  // P2P
  getAds(filters) {
    const q = new URLSearchParams(filters || {}).toString();
    return this.get('/api/p2p/ads' + (q ? '?' + q : ''));
  },
  getMyAds() { return this.get('/api/p2p/ads/mine'); },
  createAd(data) { return this.post('/api/p2p/ads', data); },
  updateAd(id, data) { return this.put('/api/p2p/ads/' + id, data); },
  placeOrder(adId, side, price, fiatAmount) { return this.post('/api/p2p/orders', { adId, side, price, fiatAmount }); },
  getOrders() { return this.get('/api/p2p/orders'); },
  getOrder(id) { return this.get('/api/p2p/orders/' + encodeURIComponent(id)); },
  markOrderPaid(orderId, payload) { return this.post('/api/okx/p2p/orders/' + orderId + '/mark-paid', payload); },
  releaseOrder(id) { return this.post('/api/p2p/orders/' + id + '/release', {}); },
  cancelOrder(id) { return this.post('/api/p2p/orders/' + id + '/cancel', {}); },
  getDashboard() { return this.get('/api/p2p/dashboard'); },

  // P2P File Upload
  uploadP2PFile(file, fileName) { return this.post('/api/okx/p2p/file/upload', { file, fileName }); },

  // P2P Ad Share
  getAdShareInfo(adId) { return this.get('/api/p2p/ads/' + adId + '/share'); },

  // P2P Merchant
  getMerchantBasicInfo() { return this.get('/api/p2p/merchant/info'); },
  getMerchantShareInfo(shareCode) { return this.get('/api/p2p/merchant/share/' + shareCode); },
  getMerchantShareProfile() { return this.get('/api/p2p/merchant/share-profile'); },
  getMerchantProfile(userId) { return this.get('/api/p2p/merchant/profile?userId=' + userId); },
  getMerchantAds(userId) { return this.get('/api/p2p/merchant/ads/' + userId); },
  getMerchantFollowings(pageIndex, pageLength) { return this.get('/api/p2p/merchant/followings?pageIndex=' + (pageIndex || 1) + '&pageLength=' + (pageLength || 20)); },
  followMerchant(userId, shouldFollow) { return this.post('/api/p2p/merchant/follow', { userId, shouldFollow }); },

  // P2P Ticker
  getP2PSwapPrice(cryptoCurrency, fiatCurrency, usdtAmount) { return this.get('/api/p2p/ticker/swap?cryptoCurrency=' + cryptoCurrency + '&fiatCurrency=' + fiatCurrency + '&usdtAmount=' + usdtAmount); },
  getP2PAllCryptoPrices(fiatCurrency) { return this.get('/api/p2p/ticker/all?fiatCurrency=' + fiatCurrency); },
  getP2PPlatformTicker(price) { return this.get('/api/p2p/ticker/platform?price=' + price); },
  getP2PEstimatedPrice(cryptoCurrency, fiatCurrency, side, depositName, paymentMethod) {
    let q = 'cryptoCurrency=' + cryptoCurrency + '&fiatCurrency=' + fiatCurrency;
    if (side) q += '&side=' + side;
    if (depositName) q += '&depositName=' + encodeURIComponent(depositName);
    if (paymentMethod) q += '&paymentMethod=' + paymentMethod;
    return this.get('/api/p2p/ticker/estimated-price?' + q);
  },

  // P2P Currency
  getP2PFiatCurrencies() { return this.get('/api/p2p/currencies/fiat'); },
  getP2PCurrencyPairs(fiatCurrency) { return this.get('/api/p2p/currencies/pairs?fiatCurrency=' + fiatCurrency); },

  // P2P Counterparty / Trade
  getCounterpartyInfo(orderId) { return this.get('/api/p2p/counterparty/' + orderId); },
  getCounterpartyBatch(orderIds) { return this.post('/api/p2p/counterparty/batch', { orderIds }); },
  getTradeSummary(userId) { return this.get('/api/p2p/trade-summary/' + userId); },
  getUserIdByUid(uid) { return this.get('/api/p2p/user/id?uid=' + uid); },
  getP2POrderCancelReasons(orderId) { return this.get('/api/p2p/orders/' + orderId + '/cancel-reasons'); },

  // P2P Ad Config
  getP2PAdConfig(side, cryptoCurrency, fiatCurrency) { return this.get('/api/okx/p2p/ad-config?side=' + side + '&cryptoCurrency=' + cryptoCurrency + '&fiatCurrency=' + fiatCurrency); },

  // P2P Online Status
  setP2POnlineStatus(status) { return this.post('/api/okx/p2p/online-status', { status }); },

  // P2P Blacklist
  getP2PBlacklist(currencySymbol) { return this.get('/api/p2p/user/blacklist?currencySymbol=' + (currencySymbol || 'USDT')); },
  addP2PBlacklistUser(userId, reasons, otherReason, orderId) { return this.post('/api/p2p/user/blacklist', { userId, reasons, otherReason, orderId }); },
  removeP2PBlacklistUser(userId) { return this.delete('/api/p2p/user/blacklist', { userId }); },

  // OKX Trading
  getUsdtKesRate() { return this.get('/api/okx/usdt-kes'); },
  getP2PPrice(ccy, fiat) { return this.get('/api/p2p/ads?side=buy&ccy='+(ccy||'USDT')+'&fiat='+(fiat||'KES')); },
  getP2PAds(side, ccy, fiat) { return this.get('/api/p2p/ads' + (side ? '?side='+side+'&ccy='+(ccy||'USDT')+'&fiat='+(fiat||'KES') : '')); },
  getTickers(params) {
    const q = new URLSearchParams(params || {}).toString();
    return this.get('/api/okx/tickers' + (q ? '?' + q : ''));
  },
  getOrderBook(instId, sz) { return this.get('/api/okx/orderbook?instId=' + instId + (sz ? '&sz=' + sz : '')); },
  getCandles(instId, bar, limit) {
    let q = 'instId=' + instId;
    if (bar) q += '&bar=' + bar;
    if (limit) q += '&limit=' + limit;
    return this.get('/api/okx/candles?' + q);
  },
  getBalance() { return this.get('/api/okx/balance'); },
  getPositions(instType) { return this.get('/api/okx/positions' + (instType ? '?instType=' + instType : '')); },
  placeTradeOrder(data) { return this.post('/api/okx/trade/order', data); },
  cancelTradeOrder(instId, ordId) { return this.post('/api/okx/trade/cancel', { instId, ordId }); },
  getTradeHistory(params) {
    const q = new URLSearchParams(params || {}).toString();
    return this.get('/api/okx/trade/history' + (q ? '?' + q : ''));
  },
  getFundingBalance(ccy) { return this.get('/api/okx/funding/balance' + (ccy ? '?ccy=' + ccy : '')); },
  getInstruments(instType, uly) {
    let q = 'instType=' + instType;
    if (uly) q += '&uly=' + uly;
    return this.get('/api/okx/instruments?' + q);
  },

  // Admin
  getAdminStats() { return this.get('/api/admin/stats'); },
  getAdminUsers() { return this.get('/api/admin/users'); },


  // System notifications (regular users see only theirs)
  getSystemNotifications(limit) { return this.get('/api/admin/notifications?mine=1' + (limit ? '&limit=' + limit : '')); },
  // Admin: get all system notifications
  getAllSystemNotifications(limit) { return this.get('/api/admin/notifications' + (limit ? '?limit=' + limit : '')); },
  markNotificationRead(id) { return this.post('/api/admin/notifications/mark-read', { id }); },
  markAllNotificationsRead() { return this.post('/api/admin/notifications/mark-all-read', {}); },
  clearOldNotifications() { return this.post('/api/admin/notifications/clear-old', {}); },
  sendHeartbeat() { return this.post('/api/admin/send-heartbeat', {}); },

  // Export
  async exportOrdersCsv() { const r = await this.post('/api/export/orders/token', {}); window.open('/api/export/orders/csv?dl=' + r.data.token + '&t=' + Date.now(), '_blank'); },
  async exportOrdersJson() { const r = await this.post('/api/export/orders/token', {}); window.open('/api/export/orders/json?dl=' + r.data.token + '&t=' + Date.now(), '_blank'); },

  // P2P Chat
  getChatMessages(orderId) { return this.get('/api/okx/p2p/chat-history/' + encodeURIComponent(orderId)); },
  // `kind` declares which lifecycle message this is (welcome / thankyou /
  // cancelled / reminder / manual). Omitted for genuine human-typed chat.
  sendChatMessage(orderId, message, kind) {
    var body = { content: message, contentType: '101' };
    if (kind) body.kind = kind;
    return this.post('/api/okx/p2p/chat/' + encodeURIComponent(orderId), body);
  },
  getOkxChatHistory(orderId) { return this.get('/api/okx/p2p/chat-history/' + encodeURIComponent(orderId)); },
  sendOkxChatMessage(orderId, message) { return this.post('/api/okx/p2p/chat/' + encodeURIComponent(orderId), { content: message, contentType: '101' }); },

  // 2FA
  setup2fa() { return this.post('/api/auth/2fa/setup', {}); },
  get2faStatus() { return this.get('/api/auth/2fa/status'); },
  get2faQR() { return this.get('/api/auth/2fa/qr'); },
  verify2fa(token) { return this.post('/api/auth/2fa/verify', { token }); },
  disable2fa(password, token) { return this.post('/api/auth/2fa/disable', { password, token }); },

  // Sessions
  getSessions() { return this.get('/api/auth/sessions'); },
  revokeSession(sessionId) { return this.post('/api/auth/sessions/revoke', { sessionId }); },
  revokeAllSessions(password) { return this.post('/api/auth/sessions/revoke-all', { password }); },

  // Support
  supportChatbot(message) { return this.post('/api/support/chatbot', { message }); },
  createSupportTicket(message) { return this.post('/api/support/ticket', { message }); },
  getMyTickets() { return this.get('/api/support/my-tickets'); },
  getTicket(ticketId) { return this.get('/api/support/ticket/' + ticketId); },
  sendTicketMessage(ticketId, message) { return this.post('/api/support/ticket/' + ticketId + '/message', { message }); },
  getAdminTickets() { return this.get('/api/support/admin/tickets'); },
  adminReplyTicket(ticketId, message) { return this.post('/api/support/admin/ticket/' + ticketId + '/message', { message }); },
  adminCloseTicket(ticketId) { return this.post('/api/support/admin/ticket/' + ticketId + '/close', {}); },

  // Passkey
  passkeyRegisterBegin() { return this.post('/api/auth/passkey/register/begin', {}); },
  passkeyRegisterComplete(response) { return this.post('/api/auth/passkey/register/complete', response); },
  passkeyRename(id, name) { return this.put('/api/auth/passkey/' + id + '/rename', { name }); },
  passkeyLoginBegin() { return this.post('/api/auth/passkey/login/begin', {}); },
  passkeyLoginComplete(response) { return this.post('/api/auth/passkey/login/complete', response); },

  // Standalone Passkey (APK-only)
  passkeyStandaloneRegisterBegin() { return this.post('/api/auth/passkey/standalone/register-begin', {}); },
  passkeyStandaloneRegisterComplete(data) { return this.post('/api/auth/passkey/standalone/register-complete', data); },
  passkeyStandaloneLoginBegin(data) { return this.post('/api/auth/passkey/standalone/login-begin', typeof data === 'string' ? { email: data } : data); },
  passkeyStandaloneLoginComplete(data) { return this.post('/api/auth/passkey/standalone/login-complete', data); },
  passkeyStandaloneDelete() { return this.delete('/api/auth/passkey/standalone'); },

  // Suggestions
  submitSuggestion(title, message) { return this.post('/api/suggestions', { title, message }); },
  getAdminSuggestions() { return this.get('/api/suggestions/admin/list'); },
  updateSuggestionStatus(id, status) { return this.post('/api/suggestions/admin/' + id + '/status', { status }); },
  passkeyList() { return this.get('/api/auth/passkey/list'); },
  passkeyDelete(id) { return this.delete('/api/auth/passkey/' + id); },

  // Price Alerts
  getPriceAlerts() { return this.get('/api/p2p/price-alerts'); },
  createPriceAlert(data) { return this.post('/api/p2p/price-alerts', data); },
  deletePriceAlert(id) { return this.delete('/api/p2p/price-alerts/' + id); },

  // Webhooks
  getWebhooks() { return this.get('/api/p2p/webhooks'); },
  createWebhook(data) { return this.post('/api/p2p/webhooks', data); },
  updateWebhook(id, data) { return this.put('/api/p2p/webhooks/' + id, data); },
  deleteWebhook(id) { return this.delete('/api/p2p/webhooks/' + id); },

  // Insights
  getTradingInsights(days) { return this.get('/api/p2p/insights?days=' + (days || 30)); },

  // Telegram
  getTelegramStatus() { return this.get('/api/telegram/status'); },
  generateTelegramLink() { return this.post('/api/telegram/generate-link', {}); },
  unlinkTelegram() { return this.post('/api/telegram/unlink', {}); },

  // Audit Log
  getAuditLog(limit, offset, userId) {
    var q = '/api/admin/audit-log?limit=' + (limit || 100) + '&offset=' + (offset || 0);
    if (userId) q += '&userId=' + encodeURIComponent(userId);
    return this.get(q);
  },

  // Bank SMS-confirmed payment (badge only — never auto-releases).
  // Preferred path: when a device secret exists, post natively to /api/apk/sms
  // (same server-side match logic, but works even when the WebView's session
  // JWT died, which is the entire point). Falls back to the JWT route.
  smsPayment(payload) {
    var self = this;
    var secret = (window.__apkDeviceSecret && window.__apkDeviceSecret()) || '';
    if (secret) {
      return this.postWithHeaders('/api/apk/sms', payload, { 'x-device-secret': secret });
    }
    return this.post('/api/bank/sms-payment', payload);
  },

  // Server-side ref dedupe + match audit log + sell auto-release settings
  smsRefCheck(payload) { return this.post('/api/bank/sms-ref/check', payload); },
  // Buy-side proof delivery. Matching is done server-side (single source of truth).
  smsProof(payload) { return this.post('/api/okx/p2p/sms-proof', payload); },
  smsRefClaim(payload) { return this.post('/api/bank/sms-ref/claim', payload); },
  smsRefRelease(payload) { return this.post('/api/bank/sms-ref/release', payload); },
  smsRefPosted(payload) { return this.post('/api/bank/sms-ref/posted', payload); },
  smsMatchLog(payload) { return this.post('/api/bank/sms-match/log', payload); },
  getSmsMatchLog(limit) { return this.get('/api/bank/sms-match/log?limit=' + (limit || 50)); },
  getSmsAutoRelease() { return this.get('/api/bank/sms-auto-release'); },
  setSmsAutoRelease(payload) { return this.post('/api/bank/sms-auto-release', payload); }
};

// Called by the APK's notification listener when it detects an I&M Bank
// OUTGOING transfer SMS (buy side — we paid a seller): Local Funds Transfer /
// Bank to Airtel Money Transfer / M-PESA, containing "Transaction Ref ID".
// The raw SMS text is buffered here and drained by sms-match.js, which matches
// it to the correct BUY order and posts the transaction ref to that order's
// chat automatically. No manual pasting involved.
window.__apkBuySms = function (json) {
  var payload = (typeof json === 'string') ? null : json;
  if (!payload && typeof json === 'string') {
    try { payload = JSON.parse(json); } catch (e) { payload = null; }
  }
  if (!payload) return;
  var text = String(payload.text || '');
  if (text.trim().length < 20) return;
  var q = window.__apkBuySmsQueue = window.__apkBuySmsQueue || [];
  // Avoid queueing the same message twice (the listener also dedupes).
  if (q.indexOf(text) !== -1) return;
  q.push(text);
  if (typeof window.__drainApkBuySmsQueue === 'function') window.__drainApkBuySmsQueue();
};

// Called by the APK's notification listener (OtpNotificationListenerService)
// when it parses an I&M Bank credit SMS: window.__apkBankPayment(json).
// Posts the payment to the server, which matches it against a pending sell
// order (payer name + amount) and marks it "Payment received (SMS)". This
// never triggers auto-release — releasing stays manual.
window.__apkBankPayment = function (json) {
  // Native bridge may fire before init.js (loaded last) defines showToast —
  // queue toasts fired early instead of crashing with ReferenceError.
  var _toast = function (msg, type, dur) {
    if (typeof showToast === 'function') { showToast(msg, type, dur); return; }
    (window.__pendingToasts = window.__pendingToasts || []).push([msg, type, dur]);
  };
  var payload = (typeof json === 'string') ? null : json;
  if (!payload && typeof json === 'string') {
    try { payload = JSON.parse(json); } catch (e) { payload = null; }
  }
  if (!payload) return;
  var amount = parseFloat(String(payload.amount == null ? '' : payload.amount).replace(/,/g, ''));
  if (!amount || amount <= 0) return;
  var _smsLog = function (s) { try { if (typeof AndroidOtp !== 'undefined' && AndroidOtp.reportSmsPost) AndroidOtp.reportSmsPost(String(s).slice(0, 180)); } catch (e) {} };
  API.smsPayment({
    ref: payload.ref || '',
    amount: amount,
    payer: payload.payer || '',
    account: payload.account || '',
    at: payload.at || ''
  }).then(function (r) {
    if (r && r.data && r.data.matched) {
      _smsLog('OK matched order=' + (r.data.orderId || '?') + ' by=' + (r.data.byName ? 'name+amount' : 'amount'));
      _toast('KES ' + amount.toLocaleString() + ' payment matched to order', 'success', 5000);
    } else {
      _smsLog('OK no-match (' + ((r && r.msg) || 'no pending sell order') + ')');
      _toast('Payment seen — no pending sell order matches KES ' + amount.toLocaleString(), 'info', 5000);
    }
  }).catch(function (err) {
    _smsLog('FAIL ' + (err.message || 'error'));
    _toast('Payment check failed: ' + (err.message || 'error'), 'error', 5000);
  });
};

function detectDeviceName() {
  var ua = navigator.userAgent || '';
  var ual = ua.toLowerCase();
  if (ual.indexOf('android') !== -1) return 'Android';
  if (ual.indexOf('iphone') !== -1 || ual.indexOf('ipad') !== -1 || ual.indexOf('ios') !== -1) return 'iOS';
  if (ual.indexOf('windows') !== -1) return 'Windows';
  if (ual.indexOf('mac') !== -1) return 'macOS';
  if (ual.indexOf('linux') !== -1) return 'Linux';
  return 'Unknown';
}
// Universal safe date formatter — accepts epoch-ms, epoch-seconds, ISO strings,
// or Date objects; always returns readable text ('—' for anything unusable)
// instead of "Invalid Date".
window.fmtDate = function(v, style) {
  try {
    if (v === null || v === undefined || v === '') return '—';
    var d;
    if (v instanceof Date) { d = v; }
    else {
      var s = String(v).trim();
      if (/^\d+$/.test(s)) {
        var n = Number(s);
        d = new Date(n < 1e12 ? n * 1000 : n);
      } else {
        d = new Date(s);
      }
    }
    if (!d || isNaN(d.getTime())) return '—';
    if (style === 'date') return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    if (style === 'time') return d.toLocaleTimeString();
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (e) { return '—'; }
};

// ===== Global unhandled-rejection surface =====
// Most calls are fire-and-forget (SSE handlers, background refreshes, button
// handlers that don't await). If one of those rejects, the user previously saw
// nothing at all - the action just silently didn't happen. Surface it.
// Deduped by message so a tight retry loop can't spam, and recorded on window
// for support/diagnostics.
(function () {
  if (window.__unhandledRejectionHooked) return;
  window.__unhandledRejectionHooked = true;
  window.__lastUnhandledRejection = null;
  var seen = {};
  window.addEventListener('unhandledrejection', function (ev) {
    var reason = ev && ev.reason;
if (!reason) return;
    var msg = (reason && reason.message) ? String(reason.message) : String(reason);
    if (!msg || msg === '[object Object]') return;
    var now = Date.now();
    var key = msg.slice(0, 160);
    if (seen[key] && (now - seen[key]) < 15000) return;
    seen[key] = now;
    window.__lastUnhandledRejection = { message: msg, at: now };
    try {
      if (typeof showToast === 'function') showToast(msg, 'error', 6000);
    } catch (e) {}
  });
})();

/* ==== js/events.js ==== */
// data-on-click handler system
// Usage: <button data-on-click="handlerName" data-arg-foo="val">Click</button>
// Or:    <button data-on-click="expressionUsingEvent">Click</button>
//
// v2 — no eval/new Function: expressions are parsed and evaluated safely,
// removing the need for 'unsafe-eval' in Content-Security-Policy.

var _handlers = {};

function registerHandler(name, fn) {
  _handlers[name] = fn;
}

// ── Safe expression evaluator (replaces new Function) ────────────────
// Handles the patterns actually used in data-on-click / data-on-submit:
//   funcName('arg')          simple function calls
//   a.b.c('arg')             chained method calls
//   a ? b : c                ternary
//   a === b / !== / == / !=  equality
//   a || b                   logical OR
//   a && b                   logical AND
//   a + b                    concatenation
//   !a                       negation
//   {} object literals       (for showConfirm callbacks etc.)
//   [array]                  array literals
//   ; separated statements   multiple statements
//   return expr              explicit return
//   var x = expr             variable declarations

function _safeEval(expr, scope) {
  if (!expr || !expr.trim()) return undefined;
  var s = scope || {};
  s.__ref = null;
  var tokens = _tokenize(expr);
  var pos = { v: 0 };
  var result;
  while (pos.v < tokens.length) {
    result = _parseStmt(tokens, pos, s);
    // consume optional semicolon between statements
    if (pos.v < tokens.length && tokens[pos.v].v === ';') pos.v++;
  }
  return result;
}

function _tokenize(expr) {
  var t = [], i = 0, len = expr.length;
  while (i < len) {
    if (/\s/.test(expr[i])) { i++; continue; }
    // strings
    if (expr[i] === "'" || expr[i] === '"' || expr[i] === '`') {
      var q = expr[i], j = i + 1, val = '';
      while (j < len && expr[j] !== q) {
        if (expr[j] === '\\') { val += expr[j + 1] || ''; j += 2; continue; }
        val += expr[j]; j++;
      }
      t.push({ t: 'str', v: val }); i = j + 1; continue;
    }
    // numbers
    if (/[0-9]/.test(expr[i]) || (expr[i] === '.' && i + 1 < len && /[0-9]/.test(expr[i + 1]))) {
      var num = '';
      while (i < len && /[0-9.]/.test(expr[i])) { num += expr[i]; i++; }
      t.push({ t: 'num', v: parseFloat(num) }); continue;
    }
    // identifiers / keywords
    if (/[a-zA-Z_$]/.test(expr[i])) {
      var id = '';
      while (i < len && /[a-zA-Z0-9_$]/.test(expr[i])) { id += expr[i]; i++; }
      t.push({ t: 'id', v: id }); continue;
    }
    // multi-char operators
    var two = expr.slice(i, i + 2);
    if (two === '==' || two === '!=' || two === '&&' || two === '||' || two === '=>' || two === '??') {
      t.push({ t: 'op', v: two }); i += 2; continue;
    }
    if (two === '===') { t.push({ t: 'op', v: '===' }); i += 3; continue; }
    if (two === '!==') { t.push({ t: 'op', v: '!==' }); i += 3; continue; }
    // single-char tokens
    var ch = expr[i];
    if ('{}[],;:?!()'.indexOf(ch) >= 0) { t.push({ t: ch, v: ch }); i++; continue; }
    if ('+-*/%<>=!&|^~.'.indexOf(ch) >= 0) { t.push({ t: 'op', v: ch }); i++; continue; }
    // skip unknown chars
    i++;
  }
  return t;
}

function _parseStmt(tokens, pos, scope) {
  // keyword: return
  if (pos.v < tokens.length && tokens[pos.v].t === 'id' && tokens[pos.v].v === 'return') {
    pos.v++;
    if (pos.v < tokens.length && tokens[pos.v].v !== ';') {
      return _parseExpr(tokens, pos, scope);
    }
    return undefined;
  }
  // keyword: var/let/const
  if (pos.v < tokens.length && tokens[pos.v].t === 'id' && (tokens[pos.v].v === 'var' || tokens[pos.v].v === 'let' || tokens[pos.v].v === 'const')) {
    pos.v++;
    if (pos.v < tokens.length && tokens[pos.v].t === 'id') {
      var name = tokens[pos.v].v;
      pos.v++;
      if (pos.v < tokens.length && tokens[pos.v].v === '=') {
        pos.v++;
        scope[name] = _parseExpr(tokens, pos, scope);
      } else {
        scope[name] = undefined;
      }
      return scope[name];
    }
  }
  return _parseExpr(tokens, pos, scope);
}

function _parseExpr(tokens, pos, scope) {
  return _parseAssignment(tokens, pos, scope);
}

function _parseAssignment(tokens, pos, scope) {
  var left = _parseTernary(tokens, pos, scope);
  if (pos.v < tokens.length && tokens[pos.v].t === 'op' && tokens[pos.v].v === '=' && scope.__ref) {
    var ref = scope.__ref;
    scope.__ref = null;
    pos.v++;
    var right = _parseAssignment(tokens, pos, scope);
    ref.obj[ref.prop] = right;
    return right;
  }
  scope.__ref = null;
  return left;
}

function _parseTernary(tokens, pos, scope) {
  var left = _parseOr(tokens, pos, scope);
  if (pos.v < tokens.length && tokens[pos.v].t === '?') {
    pos.v++;
    var mid = _parseExpr(tokens, pos, scope);
    if (pos.v < tokens.length && tokens[pos.v].t === ':') pos.v++;
    var right = _parseExpr(tokens, pos, scope);
    return left ? mid : right;
  }
  return left;
}

function _parseOr(tokens, pos, scope) {
  var left = _parseAnd(tokens, pos, scope);
  while (pos.v < tokens.length && tokens[pos.v].t === 'op' && tokens[pos.v].v === '||') {
    pos.v++;
    var right = _parseAnd(tokens, pos, scope);
    left = left || right;
  }
  return left;
}

function _parseAnd(tokens, pos, scope) {
  var left = _parseEquality(tokens, pos, scope);
  while (pos.v < tokens.length && tokens[pos.v].t === 'op' && tokens[pos.v].v === '&&') {
    pos.v++;
    var right = _parseEquality(tokens, pos, scope);
    left = left && right;
  }
  return left;
}

function _parseEquality(tokens, pos, scope) {
  var left = _parseComparison(tokens, pos, scope);
  while (pos.v < tokens.length && tokens[pos.v].t === 'op' &&
    (tokens[pos.v].v === '==' || tokens[pos.v].v === '!=' ||
     tokens[pos.v].v === '===' || tokens[pos.v].v === '!==')) {
    var op = tokens[pos.v].v; pos.v++;
    var right = _parseComparison(tokens, pos, scope);
    if (op === '==') left = left == right;
    else if (op === '!=') left = left != right;
    else if (op === '===') left = left === right;
    else left = left !== right;
  }
  return left;
}

function _parseComparison(tokens, pos, scope) {
  var left = _parseAddSub(tokens, pos, scope);
  while (pos.v < tokens.length && tokens[pos.v].t === 'op' &&
    (tokens[pos.v].v === '<' || tokens[pos.v].v === '>' ||
     tokens[pos.v].v === '<=' || tokens[pos.v].v === '>=')) {
    var op = tokens[pos.v].v; pos.v++;
    var right = _parseAddSub(tokens, pos, scope);
    if (op === '<') left = left < right;
    else if (op === '>') left = left > right;
    else if (op === '<=') left = left <= right;
    else left = left >= right;
  }
  return left;
}

function _parseAddSub(tokens, pos, scope) {
  var left = _parseMulDiv(tokens, pos, scope);
  while (pos.v < tokens.length && tokens[pos.v].t === 'op' &&
    (tokens[pos.v].v === '+' || tokens[pos.v].v === '-')) {
    var op = tokens[pos.v].v; pos.v++;
    var right = _parseMulDiv(tokens, pos, scope);
    left = op === '+' ? left + right : left - right;
  }
  return left;
}

function _parseMulDiv(tokens, pos, scope) {
  var left = _parseUnary(tokens, pos, scope);
  while (pos.v < tokens.length && tokens[pos.v].t === 'op' &&
    (tokens[pos.v].v === '*' || tokens[pos.v].v === '/' || tokens[pos.v].v === '%')) {
    var op = tokens[pos.v].v; pos.v++;
    var right = _parseUnary(tokens, pos, scope);
    if (op === '*') left = left * right;
    else if (op === '/') left = left / right;
    else left = left % right;
  }
  return left;
}

function _parseUnary(tokens, pos, scope) {
  if (pos.v < tokens.length && tokens[pos.v].t === 'op' && tokens[pos.v].v === '!') {
    pos.v++;
    return !_parseUnary(tokens, pos, scope);
  }
  if (pos.v < tokens.length && tokens[pos.v].t === 'op' && tokens[pos.v].v === '-') {
    pos.v++;
    return -_parseUnary(tokens, pos, scope);
  }
  return _parsePostfix(tokens, pos, scope);
}

function _parsePostfix(tokens, pos, scope) {
  var val = _parsePrimary(tokens, pos, scope);
  while (pos.v < tokens.length && tokens[pos.v].t === '[') {
    pos.v++;
    var idx = _parseExpr(tokens, pos, scope);
    if (pos.v < tokens.length && tokens[pos.v].t === ']') pos.v++;
    // Track assignment reference only if next is '=' — otherwise clear it
    if (pos.v < tokens.length && tokens[pos.v].t === 'op' && tokens[pos.v].v === '=') {
      scope.__ref = { obj: val, prop: idx };
    }
    val = val != null ? val[idx] : undefined;
  }
  return val;
}

function _parsePrimary(tokens, pos, scope) {
  if (pos.v >= tokens.length) return undefined;
  var tok = tokens[pos.v];

  // grouping: (expr)
  if (tok.t === '(') {
    pos.v++;
    var inner = _parseExpr(tokens, pos, scope);
    if (pos.v < tokens.length && tokens[pos.v].t === ')') pos.v++;
    // check if this is a function call: (expr)(args)
    if (pos.v < tokens.length && tokens[pos.v].t === '(') {
      return _callValue(inner, tokens, pos, scope, null);
    }
    return inner;
  }

  // object literal: { key: val, ... }
  if (tok.t === '{') {
    pos.v++;
    var obj = {};
    while (pos.v < tokens.length && tokens[pos.v].t !== '}') {
      if (tokens[pos.v].t === ',') { pos.v++; continue; }
      var key = tokens[pos.v].v || '';
      pos.v++;
      if (pos.v < tokens.length && tokens[pos.v].v === ':') pos.v++;
      obj[key] = _parseExpr(tokens, pos, scope);
    }
    if (pos.v < tokens.length && tokens[pos.v].t === '}') pos.v++;
    return obj;
  }

  // array literal: [expr, expr]
  if (tok.t === '[') {
    pos.v++;
    var arr = [];
    while (pos.v < tokens.length && tokens[pos.v].t !== ']') {
      if (tokens[pos.v].t === ',') { pos.v++; continue; }
      arr.push(_parseExpr(tokens, pos, scope));
    }
    if (pos.v < tokens.length && tokens[pos.v].t === ']') pos.v++;
    return arr;
  }

  // string
  if (tok.t === 'str') { pos.v++; return tok.v; }
  // number
  if (tok.t === 'num') { pos.v++; return tok.v; }

  // identifier: variable or function call or property access chain
  if (tok.t === 'id') {
    pos.v++;
    var val = _resolveVar(tok.v, scope);
    return _chainValue(val, tokens, pos, scope, null);
  }

  // skip unknown
  pos.v++;
  return undefined;
}

function _resolveVar(name, scope) {
  if (name === 'undefined') return undefined;
  if (name === 'null') return null;
  if (name === 'true') return true;
  if (name === 'false') return false;
  if (name === 'NaN') return NaN;
  if (name === 'Infinity') return Infinity;
  if (name in scope) return scope[name];
  if (typeof window !== 'undefined' && name in window) return window[name];
  return undefined;
}

function _chainValue(val, tokens, pos, scope, thisVal) {
  while (pos.v < tokens.length) {
    // .property
    if (tokens[pos.v].t === 'op' && tokens[pos.v].v === '.') {
      pos.v++;
      if (pos.v >= tokens.length) break;
      var prop = tokens[pos.v].v;
      pos.v++;
      thisVal = val;
      // Track assignment reference: a.b = ... → ref = { obj: a, prop: 'b' }
      if (pos.v < tokens.length && tokens[pos.v].t === 'op' && tokens[pos.v].v === '=') {
        scope.__ref = { obj: val, prop: prop };
      }
      val = val != null ? val[prop] : undefined;
      continue;
    }
    // (args) — function call
    if (tokens[pos.v].t === '(') {
      val = _callValue(val, tokens, pos, scope, thisVal);
      thisVal = null;
      continue;
    }
    break;
  }
  return val;
}

function _callValue(fn, tokens, pos, scope, thisVal) {
  if (pos.v >= tokens.length || tokens[pos.v].t !== '(') return fn;
  pos.v++; // skip (
  var args = [];
  while (pos.v < tokens.length && tokens[pos.v].t !== ')') {
    if (tokens[pos.v].t === ',') { pos.v++; continue; }
    args.push(_parseExpr(tokens, pos, scope));
  }
  if (pos.v < tokens.length && tokens[pos.v].t === ')') pos.v++;
  if (typeof fn !== 'function') return undefined;
  var ctx = thisVal || (typeof window !== 'undefined' ? window : {});
  return fn.apply(ctx, args);
}
// ── End safe expression evaluator ────────────────────────────────────

function _closestAttr(target, attr) {
  if (!target) return null;
  var el = target.nodeType === 1 ? target : (target.parentElement || null);
  while (el) {
    if (el.getAttribute && el.getAttribute(attr)) return el;
    el = el.parentElement;
  }
  return null;
}

function _runBoundHandler(el, expr, attr, e) {
  if (!el || !expr) return;
  if (attr === 'data-on-click') {
    if (el.hasAttribute('data-prevent-default')) e.preventDefault();
    if (el.hasAttribute('data-stop-propagation')) e.stopPropagation();
  }
  var fn = _handlers[expr];
  if (typeof fn === 'function') return fn.call(el, e);
  if (typeof window[expr] === 'function') return window[expr].call(el, e);
  try {
    return _safeEval(expr, { event: e, this: el, e: e });
  } catch (err) {
    console.error(attr + ' error:', expr, err);
  }
}

// ── Delegated binding ──────────────────────────────────────────────
// A modal/table re-render used to allocate one closure per [data-on-click]
// element (hundreds per snapshot cycle on the Orders page) plus a
// MutationObserver to re-run binding on every tbody replace. Event
// delegation needs exactly ONE document listener for clicks and changes,
// so re-renders are free and even a re-executed bundle cannot stack
// handlers (the flag below guards the single registration).
var _boundWithDelegation = false;
function ensureDelegatedHandlers() {
  if (_boundWithDelegation) return true;
  _boundWithDelegation = true;
  document.addEventListener('click', function(e) {
    var el = _closestAttr(e.target, 'data-on-click');
    if (el) _runBoundHandler(el, el.getAttribute('data-on-click'), 'data-on-click', e);
  });
  document.addEventListener('change', function(e) {
    var el = _closestAttr(e.target, 'data-on-change');
    if (el) _runBoundHandler(el, el.getAttribute('data-on-change'), 'data-on-change', e);
  });
  return true;
}
ensureDelegatedHandlers();

function bindFormHandlers(root) {
  if (!root) root = document;
  var forms = root.querySelectorAll('[data-on-submit]');
  forms.forEach(function(el) {
    if (el._submitBound) return;
    el._submitBound = true;
    var expr = el.getAttribute('data-on-submit');
    el.addEventListener('submit', function(e) {
      e.preventDefault();
      try {
        var fn = _handlers[expr];
        if (typeof fn === 'function') return fn.call(this, e);
        if (typeof window[expr] === 'function') return window[expr].call(this, e);
        return _safeEval(expr, { event: e, this: this, e: e });
      } catch (err) {
        console.error('data-on-submit error:', expr, err);
      }
    });
  });
}

function bindClickHandlers(root) {
  // Events are delegated document-wide; kept as a no-op callable API for
  // existing callers (nav, orders render, MutationObserver).
  ensureDelegatedHandlers();
}

function bindChangeHandlers(root) {
  ensureDelegatedHandlers();
}

if (typeof MutationObserver !== 'undefined') {
  // Clicks/changes are delegated document-wide; only dynamically-inserted
  // <form data-on-submit> still need direct binding (submit events don't bubble
  // in older WebViews), and forms are rare so this never churns per-render.
  (function() {
    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(m) {
        m.addedNodes.forEach(function(n) {
          if (n.nodeType === 1) bindFormHandlers(n);
        });
      });
    });
    function startObserving() {
      if (document.body) {
        var target = document.getElementById('page-content') || document.body;
        observer.observe(target, { childList: true, subtree: true });
        bindFormHandlers();
      }
    }
    if (document.body) {
      startObserving();
    } else {
      document.addEventListener('DOMContentLoaded', function() {
        startObserving();
        bindFormHandlers();
      });
    }
  })();
}

window.safeHtml = function(str) {
  return String(str).replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
};

/* ==== js/modules/icons.js ==== */
// MONEYFESTLTD — SVG icon system (Phase 1).
// Single source for stroke icons. Replaces emoji in navigation chrome so the
// app reads as a product, not a chat. Icons inherit currentColor.
(function() {
  var W = '<svg class="app-ic" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">';
  var E = '</svg>';
  var P = {
    home:    '<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>',
    list:    '<line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>',
    megaphone:'<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>',
    bank:    '<path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 21v-6h6v6"/>',
    chart:   '<line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>',
    chat:    '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
    shield:  '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>',
    bot:     '<rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/>',
    coins:   '<circle cx="8" cy="8" r="6"/><path d="M18.09 10.37A6 6 0 1 1 10.34 18"/><path d="M7 6h1v4"/><path d="m16.71 13.88.7.71-2.82 2.82"/>',
    users:   '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
    star:    '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26"/>',
    play:    '<circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/>',
    gear:    '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
    target:  '<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>',
    user:    '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
    qr:      '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 14h3v3h-3z"/><path d="M21 14v3"/><path d="M14 21h3"/><path d="M21 21h.01"/>',
    bell:    '<path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>',
    logout:  '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>',
    alert:   '<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
    globe:   '<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
    bag:     '<path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>',
    clip:    '<path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>',
    grid:    '<rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>',
    moon:    '<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>'
  };

  function svgFor(name) {
    return P[name] ? W + P[name] + E : '';
  }

  // emoji -> icon name (navigation chrome only)
  var MAP = {
    '\uD83C\uDFE0': 'home',          // 🏠
    '\uD83D\uDCCB': 'list',          // 📋
    '\uD83D\uDCE2': 'megaphone',     // 📢
    '\uD83C\uDFE6': 'bank',          // 🏦
    '\uD83D\uDCCA': 'chart',         // 📊
    '\uD83D\uDCAC': 'chat',          // 💬
    '\uD83D\uDEE1\uFE0F': 'shield',  // 🛡️
    '\uD83D\uDEE1': 'shield',
    '\uD83E\uDD16': 'bot',           // 🤖
    '\uD83D\uDCB0': 'coins',         // 💰
    '\uD83D\uDC65': 'users',         // 👥
    '\u2B50': 'star',                // ⭐
    '\uD83C\uDFAE': 'play',          // 🎮
    '\u2699\uFE0F': 'gear',          // ⚙️
    '\u2699': 'gear',
    '\uD83C\uDFAF': 'target',        // 🎯
    '\uD83D\uDC64': 'user',          // 👤
    '\uD83D\uDCF1': 'qr',            // 📱
    '\uD83D\uDE9A': 'logout',        // 🚪
    '\u26A0\uFE0F': 'alert',         // ⚠️
    '\uD83C\uDF10': 'globe',         // 🌐
    '\uD83D\uDED2': 'bag',           // 🛒
    '\uD83D\uDCCE': 'clip',          // 📎
    '\uD83D\uDFE9': 'grid',          // 🟩
    '\uD83D\uDFE8': 'grid',          // 🟨
    '\uD83C\uDF1F': 'star'           // 🌟
  };

  function enhance(root) {
    root = root || document;
    var nodes = root.querySelectorAll('.side-icon,.tab-icon,.pm-ic,#mobile-tabbar a>span:first-child');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.dataset.icDone) continue;
      var t = (el.textContent || '').trim();
      var name = MAP[t];
      if (name) {
        el.dataset.icDone = '1';
        el.textContent = '';
        el.insertAdjacentHTML('beforeend', svgFor(name));
      }
    }
  }

  window.AppIcons = { svg: svgFor, map: MAP, enhance: enhance };

  // Auto-enhance dynamically injected content (debounced)
  var _t = null;
  var _pendingNodes = [];
  var obs = new MutationObserver(function(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        if (added[j].nodeType === 1) _pendingNodes.push(added[j]);
      }
    }
    clearTimeout(_t);
    _t = setTimeout(function() {
      if (document.hidden || _pendingNodes.length === 0) return;
      try {
        var nodes = _pendingNodes.splice(0);
        for (var k = 0; k < nodes.length; k++) {
          if (nodes[k].isConnected) enhance(nodes[k]);
        }
      } catch (e) {}
    }, 120);
  });
  function start() {
    if (!document.body) return setTimeout(start, 50);
    enhance(document);
    obs.observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
})();

/* ==== js/modules/passkey-bridge.js ==== */
window._apkPasskeyPending = null;

function isApk() {
  return navigator.userAgent.indexOf('MONEYFESTLTD-APK') !== -1;
}

function hasStandalone() {
  try { return isApk() && window.AndroidStandalonePasskey && typeof AndroidStandalonePasskey.listUserIds === 'function'; } catch(e) { return false; }
}

function hasNativeWebAuthn() {
  // Native WebAuthn (navigator.credentials) is DISABLED in the APK: the
  // WebView delegation to Google Credential Manager crashes the renderer on
  // some Android 16 WebView builds. The APK only opts in by setting
  // window.__apkUseNativeWebAuthn = true — no build does, so the app always
  // uses the exception-safe standalone Keystore bridge instead.
  return isApk() && window.__apkUseNativeWebAuthn === true;
}

// Google Password Manager passkeys via the native Credential Manager Java
// bridge (window.AndroidPasskey). Unlike navigator.credentials inside WebView,
// this never touches the crashing renderer path — our own Java code talks to
// Credential Manager directly. Credentials created here SYNC across every
// device signed into the user's Google account.
function hasCredMan() {
  try {
    return isApk() && window.AndroidPasskey &&
      typeof window.AndroidPasskey.create === 'function' &&
      typeof window.AndroidPasskey.get === 'function';
  } catch (e) { return false; }
}

// optionsJson must be the server's WebAuthn options AS-IS (base64url strings,
// NOT ArrayBuffers — that's what Credential Manager's FIDO2 JSON expects).
function credManCreate(optionsJson) {
  return new Promise(function(resolve, reject) {
    window._apkPasskeyPending = { resolve: resolve, reject: reject, type: 'credman-create' };
    try {
      window.AndroidPasskey.create(String(optionsJson));
    } catch (e) {
      window._apkPasskeyPending = null;
      reject(new Error('Failed to start passkey creation: ' + e.message));
    }
  });
}

function credManGet(optionsJson) {
  return new Promise(function(resolve, reject) {
    window._apkPasskeyPending = { resolve: resolve, reject: reject, type: 'credman-get' };
    try {
      window.AndroidPasskey.get(String(optionsJson));
    } catch (e) {
      window._apkPasskeyPending = null;
      reject(new Error('Failed to start passkey authentication: ' + e.message));
    }
  });
}

function apkPasskeyAvailable() {
  return hasNativeWebAuthn() || hasStandalone() || (isApk() && window.AndroidPasskey && (typeof AndroidPasskey.create === 'function' || typeof AndroidPasskey.register === 'function'));
}

function standaloneListUserIds() {
  try {
    if (!hasStandalone()) return [];
    return JSON.parse(AndroidStandalonePasskey.listUserIds() || '[]');
  } catch(e) { return []; }
}

function standaloneRegister(userId, userName, challenge, tempToken) {
  return new Promise(function(resolve, reject) {
    window._apkPasskeyPending = { resolve: resolve, reject: reject, type: 'register' };
    try {
      AndroidStandalonePasskey.register(String(userId), String(userName), String(challenge), String(tempToken));
    } catch (e) {
      window._apkPasskeyPending = null;
      reject(new Error('Failed to start standalone registration: ' + e.message));
    }
  });
}

function standaloneLogin(userId, challenge, tempToken) {
  return new Promise(function(resolve, reject) {
    window._apkPasskeyPending = { resolve: resolve, reject: reject, type: 'login' };
    try {
      AndroidStandalonePasskey.authenticate(String(userId), String(challenge), String(tempToken));
    } catch (e) {
      window._apkPasskeyPending = null;
      reject(new Error('Failed to start standalone login: ' + e.message));
    }
  });
}

function standaloneDelete(userId) {
  return new Promise(function(resolve, reject) {
    window._apkPasskeyPending = { resolve: resolve, reject: reject, type: 'delete' };
    try {
      AndroidStandalonePasskey.delete(String(userId));
    } catch (e) {
      window._apkPasskeyPending = null;
      reject(new Error('Failed to delete standalone passkey: ' + e.message));
    }
  });
}

function apkPasskeyCancel() {
  window._apkPasskeyPending = null;
  try { if (window.AndroidStandalonePasskey && typeof AndroidStandalonePasskey.cancel === 'function') AndroidStandalonePasskey.cancel(); } catch {}
}

window._apkPasskeyCallback = function(jsonStr) {
  var pending = window._apkPasskeyPending;
  window._apkPasskeyPending = null;
  if (!pending) return;
  try {
    var data = JSON.parse(jsonStr);
    if (data.error) {
      pending.reject(new Error(data.error));
    } else {
      pending.resolve(data);
    }
  } catch (e) {
    pending.reject(new Error('Invalid bridge response'));
  }
};

window._apkGetPublicKeyCredential = function() {
  return !!window.PublicKeyCredential || isApk() || apkPasskeyAvailable();
};

window._apkListUserIds = standaloneListUserIds;
window._apkStandaloneRegister = standaloneRegister;
window._apkStandaloneLogin = standaloneLogin;
window._apkStandaloneDelete = standaloneDelete;
window._apkHasStandalone = hasStandalone;
window._apkHasNativeWebAuthn = hasNativeWebAuthn;
window._apkHasCredMan = hasCredMan;
window._apkCredManCreate = credManCreate;
window._apkCredManGet = credManGet;

/* ==== js/modules/ui.js ==== */
const esc = s => String(s).replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/[&<>"]/g, function(c) { if (c === '&') return '&amp;'; if (c === '<') return '&lt;'; if (c === '>') return '&gt;'; return '&quot;'; });

// TEMP DIAGNOSTIC: fire-and-forget client telemetry (where do order updates die?)
window._tele = function(e, extra) {
  try {
    if (!window.API || !API.post) return;
    var payload = { e: String(e).slice(0, 32), p: (typeof currentPage !== 'undefined' ? currentPage : ''), h: document.hidden ? 1 : 0, t: Date.now() };
    if (extra) for (var k in extra) payload[k] = extra[k];
    API.post('/api/okx/telemetry', payload).catch(function() {});
  } catch (_te) {}
};


// Atomic claim for thank-you notes — returns true only once per orderId
function _tyClaim(orderId) {
  if (!window._tySentOrders) window._tySentOrders = new Set();
  if (window._tySentOrders.has(orderId)) return false;
  window._tySentOrders.add(orderId);
  return true;
}

function base64urlToBuffer(s) { s = s.replace(/-/g, '+').replace(/_/g, '/'); while (s.length % 4) s += '='; return Uint8Array.from(atob(s), function(c) { return c.charCodeAt(0); }).buffer; }
function bufferToBase64url(b) { var s = btoa(String.fromCharCode.apply(null, new Uint8Array(b))); return s.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); }

// Reusable confirm modal
window._confirmCbId = 0;
window._confirmCallbacks = {};
window._confirmRun = function(id) {
  var fn = window._confirmCallbacks[id];
  delete window._confirmCallbacks[id];
  if (typeof fn === 'function') fn();
};
window.showConfirm = function(message, onConfirm, confirmText, cancelText) {
  var id = 'cb_' + (++window._confirmCbId);
  window._confirmCallbacks[id] = onConfirm;
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  const safeMsg = esc(message);
  overlay.innerHTML = `
    <div class="modal-content" style="max-width:400px;text-align:center;">
      <p style="color:var(--text-primary);font-size:15px;margin-bottom:24px;line-height:1.5;">${safeMsg}</p>
      <div style="display:flex;gap:10px;justify-content:center;">
        <button class="btn btn-primary" data-on-click="this.closest('.modal-overlay').remove(); _confirmRun('${id}')">${esc(confirmText || 'Confirm')}</button>
        <button class="btn btn-secondary" data-on-click="this.closest('.modal-overlay').remove()">${esc(cancelText || 'Cancel')}</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
};

// Shared page countdown timer
window.startPageCountdown = function(intervalSec) {
  if (window._pageCdTimer) clearInterval(window._pageCdTimer);
  let sec = intervalSec || 10;
  const el = document.getElementById('page-countdown');
  if (el) el.textContent = '· ' + sec + 's';
  window._pageCdTimer = setInterval(() => {
    if (document.hidden) return;
    sec -= 2;
    const e = document.getElementById('page-countdown');
    if (e) e.textContent = sec > 0 ? '· ' + sec + 's' : '· refreshing...';
    if (sec <= 0) sec = intervalSec || 10;
  }, 2000);
};
function requestNativeNotification() {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'granted') return;
  if (Notification.permission !== 'denied') Notification.requestPermission();
}
let pagePollTimer = null;
let ordersErrorTimer = null;
let globalPollTimer = null;
let pendingPollTimer = null;

function orderState(o) { return (o.state || o.completionStatus || o.orderStatus || o.status || '').toLowerCase(); }

// True only for GENUINELY completed orders. OKX's completionStatus 'completed' also
// covers cancelled orders (spec §2.3), so orderState() alone misclassifies them —
// and cancelled orders carry a growing durationAfterCompletionInSeconds which would
// skew release-time averages upward forever. Trust orderStatus as the discriminator.
function isCompletedOrder(o) {
  var os = String(o.orderStatus || '').toLowerCase();
  if (os === 'canceled' || os === 'cancelled') return false;
  if (os === 'completed') return true;
  if (o.orderStatus && os !== '') return false;
  return orderState(o) === 'completed';
}

// Dark/Light Theme Management
function initTheme() {
  const saved = localStorage.getItem('theme');
  if (saved) {
    applyTheme(saved);
  } else {
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    applyTheme(mq.matches ? 'light' : 'dark');
    mq.addEventListener('change', function(e) {
      if (!localStorage.getItem('theme')) applyTheme(e.matches ? 'light' : 'dark');
    });
  }
}

function applyTheme(theme) {
  const body = document.body;
  if (theme === 'light') {
    body.classList.add('light-mode');
    localStorage.setItem('theme', 'light');
    updateThemeToggleButton('☀️');
  } else {
    body.classList.remove('light-mode');
    localStorage.setItem('theme', 'dark');
    updateThemeToggleButton('🌙');
  }
}

function toggleTheme() {
  const current = localStorage.getItem('theme') || 'dark';
  const next = current === 'dark' ? 'light' : 'dark';
  applyTheme(next);
  if (currentUser) {
    API.put('/api/auth/profile', { theme: next }).catch(err => console.error('Failed to save theme:', err));
  }
}

function updateThemeToggleButton(icon) {
  const btn = document.getElementById('theme-toggle');
  if (btn) btn.textContent = icon;
}

// Competitive Alert Monitoring
let competitiveAlertCooldown = new Map();
setInterval(function() {
  var cutoff = Date.now() - 600000;
  competitiveAlertCooldown.forEach(function(ts, key) {
    if (ts < cutoff) competitiveAlertCooldown.delete(key);
  });
  if (competitiveAlertCooldown.size > 200) competitiveAlertCooldown.clear();
}, 300000);

function checkCompetitiveAlerts(marketplaceAds) {
  if (!currentUser?.competitiveAlertEnabled) return;
  if (!marketplaceAds || marketplaceAds.length === 0) return;
  
  try {
    // Find user's sell ads  
    const myName = currentUser.name || currentUser.email;
    const mySellAds = marketplaceAds.filter(a => a.merchant === myName && (a.side === 'sell' || a.side === 'SELL'));
    if (mySellAds.length === 0) return;
    
    const mySellPrice = Math.max(...mySellAds.map(a => parseFloat(a.price) || 0));
    if (mySellPrice <= 0) return;
    
    const limitThreshold = currentUser.competitiveAlertLimitThreshold || 100;
    const priceThreshold = currentUser.competitiveAlertPriceThreshold || 0;
    const now = Date.now();
    const cooldownMs = 5 * 60 * 1000; // 5 minute cooldown per alert
    
    // Check for competing SELL ads (Takers) that beat ours
    const competitorSells = marketplaceAds.filter(a => (a.side === 'sell' || a.side === 'SELL') && a.merchant !== myName);
    
    for (const ad of competitorSells) {
      const price = parseFloat(ad.price) || 0;
      
      // Check price condition: only if price is higher than mine OR higher than price threshold
      let priceConditionMet = false;
      if (priceThreshold > 0) {
        priceConditionMet = price >= priceThreshold;
      } else {
        priceConditionMet = price > mySellPrice;
      }
      if (!priceConditionMet) continue;
      
      // Parse minimum limit from limits string (e.g., "KES 100 - KES 53,627.25")
      const limits = ad.limits || '';
      const limitMatch = limits.match(/KES\s*([\d,]+)\s*-/) || limits.match(/(\d+)\s*-/);
      if (!limitMatch) continue;
      
      const minLimit = parseInt(limitMatch[1].replace(/,/g, '')) || 0;
      if (minLimit < limitThreshold) continue; // Skip if minimum limit below our threshold
      
      // This ad meets our criteria - check cooldown and alert
      const alertKey = (ad.id || ad.adId || ad.merchant + ':' + price + ':' + minLimit);
      const lastAlert = competitiveAlertCooldown.get(alertKey) || 0;
      
      if (now - lastAlert < cooldownMs) continue; // Still in cooldown
      
      // ALERT!
      competitiveAlertCooldown.set(alertKey, now);
      playPaymentReminder();
      showToast(`🚨 Competitive Alert: ${ad.merchant} at ${price} KES (limit ≥${minLimit})`, 'info', 8000);
    }
  } catch (err) {
    console.error('Competitive alert check error:', err);
  }
}
function copyText(t) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(t).then(function() { showToast('Copied', 'success'); }).catch(function() { _fallbackCopy(t); });
  } else {
    _fallbackCopy(t);
  }
}
function _fallbackCopy(t) {
  var ta = document.createElement('textarea');
  ta.value = t;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  ta.style.left = '-999px';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
    showToast('Copied', 'success');
  } catch (e) {
    showToast('Copy failed - select manually', 'error');
  }
  document.body.removeChild(ta);
}

function retryError(container, renderFn, err) {
  container.innerHTML = `<div class="card" style="text-align:center;padding:40px;"><div class="spinner" style="margin:0 auto 16px;"></div><p style="color:var(--accent-orange);">Reconnecting...</p><p style="font-size:11px;color:var(--text-quaternary);margin-top:8px;">${esc(err.message)}</p></div>`;
  if (pagePollTimer) clearInterval(pagePollTimer);
  startPagePoll(container, renderFn, 30000);
}

function getPaymentType(method) {
  if (!method) return { short: '-', color: '#55556a' };
  const m = (typeof method === 'string' ? method : (method.type || method.bankCode || method.paymentDescription || '')).toUpperCase();
  if (m.includes('M-PESA') || m.includes('SAFARICOM')) return { short: 'Saf', color: '#00c853' };
  if (m.includes('AIRTEL')) return { short: 'Air', color: '#ff9800' };
  return { short: 'Bank', color: '#448aff' };
}

function formatKES(n) {
  const num = parseFloat(n) || 0;
  return 'KES ' + num.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function formatKESint(n) {
  const num = parseInt(n) || 0;
  return 'KES ' + num.toLocaleString('en-KE');
}
function formatFiat(amount, ccy) {
  const num = parseFloat(amount) || 0;
  const c = (ccy || 'KES').toUpperCase();
  return c + ' ' + num.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function tradedBadge(o) {
  var cp = o.counterpartyDetail || {};
  var key = cp.nickName || cp.realName || cp.userNo || '';
  if (key && window._counterpartyCount && (window._counterpartyCount[key] || 0) > 1) return '<span style="display:inline-block;margin-left:4px;padding:1px 6px;border-radius:4px;background:rgba(0,210,104,0.12);color:var(--accent-green);font-size:9px;font-weight:600;vertical-align:middle;">TRADED</span>';
  return '';
}
function smsReceivedBadge(o) {
  if (!o || !o.smsReceived) return '';
  return '<span style="display:inline-block;margin-left:4px;padding:1px 6px;border-radius:4px;background:rgba(0,200,83,0.15);border:1px solid rgba(0,200,83,0.35);color:var(--accent-green);font-size:9px;font-weight:700;vertical-align:middle;white-space:nowrap;" title="Bank SMS confirmed this payment arrived">💳 Payment received (SMS)</span>';
}
function clientCell(o, cp) {
  return '<td style="font-size:11px;"><div>' + cp.name + tradedBadge(o) + '</div><div style="display:flex;align-items:center;gap:4px;"><span style="color:var(--text-quaternary);font-size:10px;">' + cp.phone + '</span><button data-on-click="copyText(\'' + esc(cp.phone) + '\')" style="background:none;border:none;cursor:pointer;padding:1px 4px;font-size:9px;color:var(--text-tertiary);border:1px solid #33334a;border-radius:3px;" title="Copy">Copy</button></div></td>';
}
function _firstChatPay(o) {
  if (!o || !Array.isArray(o.chatPayments)) return null;
  for (var i = 0; i < o.chatPayments.length; i++) {
    var cp = o.chatPayments[i];
    if (!cp) continue;
    var num = String(cp.phoneNumber || cp.accountNumber || '');
    if (num) return cp;
  }
  return null;
}
function getCounterpartyInfo(o) {
  const cp = o.counterpartyDetail || {};
  const pm = cp.takerPaymentMethod || o.takerPaymentMethod || {};
  // Show BOTH names — real name for records, nickname for fast recognition.
  // Fall back to root-level fields (preserved in SSE slim payload)
  var realN = String(cp.realName || o.realName || '').trim();
  var nick = String(cp.nickName || o.nickName || '').trim();
  // Treat "-" (OKX placeholder for missing name) as empty
  if (realN === '-') realN = '';
  if (nick === '-') nick = '';
  var name;
  if (realN && nick && realN.toLowerCase() !== nick.toLowerCase()) name = realN + ' · ' + nick;
  else name = realN || nick || '';
  // OKX omits counterpartyDetail.takerPaymentMethod on most orders; the real pay
  // number then lives only in chatPayments. Fall back so the phone column isn't '-'.
  var _cp0 = _firstChatPay(o) || {};
  const phone = pm.accountNo || _cp0.accountNumber || _cp0.phoneNumber || '-';
  const type = getPaymentType(pm);
  return { name: name, nickName: nick, realName: realN, phone, type: type.short, color: type.color };
}
function renderPaymentInfo(o) {
  const pm = (o.counterpartyDetail || {}).takerPaymentMethod || o.takerPaymentMethod || {};
  const bank = esc(pm.bankName || '');
  const acct = esc(pm.accountNo || pm.accountNumber || '');
  const type = getPaymentType(pm);
  let parts = [];
  if (bank) parts.push(`<div style="color:#aaa;font-size:11px;">${bank}</div>`);
  if (acct) parts.push(`<div style="display:flex;align-items:center;gap:4px;font-size:11px;"><span style="color:var(--text-primary);font-family:monospace;">${acct}</span><button data-on-click="copyText('${acct}')" style="background:none;border:none;cursor:pointer;padding:2px 6px;font-size:10px;color:var(--text-tertiary);border:1px solid #33334a;border-radius:4px;" title="Copy">Copy</button></div>`);
  // Pay numbers read from the order chat (real pay number when the registered
  // account was 00.../empty). This is the ONLY source when OKX omits
  // counterpartyDetail.takerPaymentMethod, so it must render even when the
  // primary bank/account is empty — hence no early return before this loop.
  if (o && Array.isArray(o.chatPayments)) {
    o.chatPayments.forEach(function(cp) {
      if (!cp) return;
      const cpHint = esc(cp.bankHint || 'Chat');
      const cpNum = esc(String(cp.phoneNumber || cp.accountNumber || ''));
      if (!cpNum) return;
      parts.push(`<div style="display:flex;align-items:center;gap:4px;font-size:11px;margin-top:2px;"><span style="color:var(--accent-blue);font-family:monospace;">${cpNum}</span><span style="font-size:9px;color:var(--text-quaternary);">${cpHint}</span><button data-on-click="copyText('${cpNum}')" style="background:none;border:none;cursor:pointer;padding:2px 6px;font-size:10px;color:var(--text-tertiary);border:1px solid #33334a;border-radius:4px;" title="Copy">Copy</button></div>`);
    });
  }
  if (!parts.length) return `<span style="font-size:10px;padding:2px 6px;border-radius:4px;background:${type.color}22;color:${type.color};border:1px solid ${type.color}44;">${type.short}</span>`;
  parts.push(`<span style="font-size:10px;padding:1px 4px;border-radius:3px;background:${type.color}22;color:${type.color};">${type.short}</span>`);
  return parts.join('');
}

// SSE Real-Time Event Stream
let sseClient = null;
let sseReconnectTimer = null;
let sseReconnectCount = 0;
// Single-flight guard: connectSSE is async, and multiple callers (heartbeat,
// visibilitychange, native EventSource retry, onerror timer) can invoke it at
// once. Without this, a server outage triggers a reconnect storm (hundreds of
// concurrent one-shot ticket POSTs) that wedges the WebView connection pool
// and freezes the whole app UI until a full restart. Only ONE connect attempt
// may be in flight; later requests coalesce onto that attempt.
let sseConnectInFlight = false;

async function connectSSE() {
  // Never open SSE in the background — the server's per-user push loop runs
  // every 5s while the connection is open, which burns CPU/network on mobile.
  // The visibilitychange handler reconnects on return to foreground.
  if (document.hidden) return;
  if (sseConnectInFlight) return; // another attempt already running — don't pile on
  if (sseClient) { sseClient.close(); sseClient = null; }
  sseConnectInFlight = true;
  const token = API.getToken();
  if (!token) { sseConnectInFlight = false; return; }

  // Orders are delivered via SSE 'orders' events — the server pushes this user's
  // orders every 5s while the SSE connection is open.

  try {
    // One-shot SSE ticket: authenticate without putting the JWT in a ?token=
    // query string (URLs land in proxy logs and browser history). The server
    // burns the ticket on first use (30s TTL, pinned to this client IP).
    const ticketRes = await API.post('/api/sse/ticket', {}).catch(() => null);
    const ticket = ticketRes && ticketRes.data && ticketRes.data.ticket;
    if (!ticket) {
      sseConnectInFlight = false;
      sseReconnectTimer = setTimeout(connectSSE, 5000);
      return;
    }
    sseClient = new EventSource('/api/events?ticket=' + encodeURIComponent(ticket) + '&t=' + Date.now());
    sseClient.addEventListener('open', () => {
      sseConnectInFlight = false;
    });
    
    // Server keepalive ping (25s) — marks the stream alive so the watchdog
    // doesn't force-reconnect a healthy but quiet connection.
    sseClient.addEventListener('ping', () => {
      window._lastSseEventTime = Date.now();
    });

    sseClient.addEventListener('connected', () => {
      if (sseReconnectTimer) { clearTimeout(sseReconnectTimer); sseReconnectTimer = null; }
      sseReconnectCount = 0;
    });
    
    
    sseClient.addEventListener('notification', (e) => {
      try {
        const data = JSON.parse(e.data);
        playNotification();
        sendNativeNotification(data.title || 'Notification', data.message || '');
        addNotifToBell(data);
      } catch {}
    });

    sseClient.addEventListener('appeal', (e) => {
      try {
        const data = JSON.parse(e.data);
        playNotification();
        var preview = data.message ? (data.message.slice(0, 80) + (data.message.length > 80 ? '...' : '')) : '';
        sendNativeNotification('⚠️ New Appeal', (data.userName || 'User') + ': ' + preview);
      } catch {}
    });

    sseClient.addEventListener('chat_message', (e) => {
      try {
        const data = JSON.parse(e.data);
        const event = new CustomEvent('sse_chat_message', { detail: data });
        document.dispatchEvent(event);
        // Show native notification for incoming messages from counterparties (not self-sent)
        if (!data.sentBySelf) {
          playNotification();
          const preview = data.text ? data.text.slice(0, 80) : '';
          const title = '💬 New message from ' + (data.counterpartyName || 'Client');
          sendNativeNotification(title, preview);
        }
      } catch {}
    });

    sseClient.addEventListener('support_reply', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (typeof handleSupportReply === 'function') handleSupportReply(data.message);
        if (currentPage !== 'generalsupport') {
          playNotification();
          showToast('📩 Admin replied to your support ticket', 'info', 8000);
        }
      } catch {}
    });
    
    sseClient.addEventListener('support_closed', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (typeof handleSupportClosed === 'function') handleSupportClosed(data);
        if (currentPage !== 'generalsupport') {
          showToast('Your support ticket was closed', 'info');
        }
      } catch {}
    });

    sseClient.addEventListener('support_ticket', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (currentUser?.role === 'admin' && typeof handleNewTicket === 'function') handleNewTicket(data);
        if (currentPage !== 'generalsupport') {
          playNotification();
          showToast('New support ticket from ' + esc(data.userName || 'a user'), 'info');
        }
      } catch {}
    });
    
    sseClient.addEventListener('support_ticket_message', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (currentUser?.role === 'admin' && typeof handleTicketMessage === 'function') handleTicketMessage(data);
        if (currentUser?.role === 'admin' && currentPage !== 'admin') {
          playNotification();
          showToast('New message in ticket from ' + esc(data.userName || 'user'), 'info');
        }
      } catch {}
    });
    
    sseClient.addEventListener('support_call_ringing', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (currentUser?.role === 'admin' && typeof handleIncomingCall === 'function') {
          handleIncomingCall(data);
        }
      } catch (err) { console.error('[SSE] support_call_ringing error:', err); }
    });

    sseClient.addEventListener('support_call_answered', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (typeof clientCallAnswered === 'function') clientCallAnswered(data);
      } catch {}
    });

    sseClient.addEventListener('support_call_accepted', (e) => {
      try {
        if (typeof clearIncomingCallUI === 'function') clearIncomingCallUI();
      } catch {}
    });

    sseClient.addEventListener('support_call_signal', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (typeof clientCallSignal === 'function') clientCallSignal(data);
        if (typeof adminCallSignal === 'function') adminCallSignal(data);
      } catch {}
    });

    sseClient.addEventListener('support_call_ended', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (typeof clientCallEnded === 'function') clientCallEnded(data);
        if (typeof adminCallEnded === 'function') adminCallEnded(data);
      } catch {}
    });

    sseClient.addEventListener('support_call_timeout', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (typeof _showCallTimeoutUI === 'function') _showCallTimeoutUI(false);
      } catch {}
    });

    sseClient.addEventListener('support_queue_connected', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (typeof handleQueueAvailable === 'function') handleQueueAvailable(data);
      } catch {}
    });

    sseClient.addEventListener('support_queue_position', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data && data.message) showToast(data.message, 'info', 6000);
      } catch {}
    });

    sseClient.addEventListener('ads_update', (e) => {
      try {
        document.dispatchEvent(new CustomEvent('sse_ads_update', { detail: JSON.parse(e.data) }));
      } catch {}
    });

    sseClient.addEventListener('alert', (e) => {
      try {
        const data = JSON.parse(e.data);
        playNotification();
        // The server sends ONE 'alert' per event (it used to also send a
        // duplicate 'notification', which doubled every buzz). Keep the bell in
        // sync here so dropping that duplicate loses nothing.
        addNotifToBell(data);
        sendNativeNotification(data.title || 'Alert', data.message || '');
      } catch {}
    });

    sseClient.addEventListener('orders', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (!data || !data.orders) {
          // Heartbeat event (orders unchanged): keep the connection heartbeat fresh but
          // don't touch the snapshot — saves parsing ~127KB every few seconds on mobile.
          if (data && data.changed === false) window._lastSseEventTime = Date.now();
          return;
        }
        // Optimistic shield: never let an OKX-lagged snapshot flip an order the
        // user JUST marked paid back to unpaid (first 3 min after the tap).
        try {
          var __now = Date.now();
          window._optimisticPaidAt = window._optimisticPaidAt || {};
          if (data && Array.isArray(data.orders)) {
            for (var _gi = 0; _gi < data.orders.length; _gi++) {
              var _go = data.orders[_gi];
              var _gt = window._optimisticPaidAt[String(_go.orderId)];
              if (_gt && (__now - _gt) < 180000 && String(_go.paymentStatus || '').toLowerCase() === 'unpaid') _go.paymentStatus = 'paid';
            }
          }
        } catch (_ge) {}
        // Optimistic shield for RELEASE: never let an OKX-lagged snapshot flip
        // an order the user JUST released back to pending/paid (first 3 min).
        try {
          var __now2 = Date.now();
          window._optimisticReleasedAt = window._optimisticReleasedAt || {};
          if (data && Array.isArray(data.orders)) {
            for (var _gi2 = 0; _gi2 < data.orders.length; _gi2++) {
              var _go2 = data.orders[_gi2];
              var _gr = window._optimisticReleasedAt[String(_go2.orderId)];
              var st2 = String(_go2.orderStatus || _go2.state || _go2.completionStatus || _go2.status || '').toLowerCase();
              if (_gr && (__now2 - _gr) < 180000 && st2 !== 'completed' && st2 !== 'canceled' && st2 !== 'cancelled') {
                _go2.orderStatus = 'completed';
                _go2.paymentStatus = 'paid';
              }
            }
          }
        } catch (_gre) {}
        // Anti-flash guards: ignore transient-empty snapshots and unchanged data
        try {
          var __prev = window._sseOrdersCache;
          if (data && Array.isArray(data.orders)) {
            if (data.orders.length === 0 && __prev && Array.isArray(__prev.orders) && __prev.orders.length > 0) {
              window._lastSseEventTime = Date.now(); return;
            }
            // Prefer the server's real per-order digest (covers mid-list changes);
            // fall back to a cheap signature if a payload arrives without one.
            var __sig = data.sig || (data.orders.length + ':' + ((data.orders[0]||{}).orderId||'') + ':' + ((data.orders[data.orders.length-1]||{}).orderId||'') + ':' + ((data.orders[0]||{}).updatedTimestamp||''));
            if (__sig === window._lastOrderSig) { window._lastSseEventTime = Date.now(); _tele('sse_dup', { n: data.orders.length }); return; }
            window._lastOrderSig = __sig;
          }
        } catch (_gf) {}
        window._sseOrdersCache = data;
        if (window._persistOrdersSnapshot) window._persistOrdersSnapshot();
        window._lastSseTimestamp = Date.now();
        window._lastSseEventTime = Date.now();
        window._lastSseOrderTime = Date.now();
        // ── Detect new completions/cancellations in full snapshot ─────────────
        if (window._prevOrdersSnapshot && Array.isArray(window._prevOrdersSnapshot.orders) && Array.isArray(data.orders)) {
          var prevMap = {};
          window._prevOrdersSnapshot.orders.forEach(function(o) { prevMap[o.orderId] = o; });
          data.orders.forEach(function(o) {
            // `st` AND `prevSt` must come from the SAME rule (window._tradeState)
            // or the transition is invisible. On a BUY order orderStatus already
            // reads 'completed' at mark-paid, so a strict `st` paired with a loose
            // `prevSt` meant the notice never fired at all once the order was paid.
            var _ts = window._tradeState || function (x) { return ''; };
            var st = _ts(o);
            var prev = prevMap[o.orderId];
            var prevSt = prev ? _ts(prev) : '';
            var completedStates = ['completed', 'finished', 'released'];
            var cancelledStates = ['cancelled', 'canceled'];
            var isNewCompleted = completedStates.includes(st) && !completedStates.includes(prevSt);
            var isNewCancelled = cancelledStates.includes(st) && !cancelledStates.includes(prevSt);
            if (isNewCompleted) {
              console.log('[SSE snapshot] New completion:', o.orderId, 'status:', st, 'prev:', prevSt, 'side:', o.side);
              if (_tyClaim(o.orderId) && typeof _autoSendThankYou === 'function') {
                setTimeout(function() { _autoSendThankYou(o.orderId); }, 200);
              }
            } else if (isNewCancelled) {
              console.log('[SSE snapshot] New cancellation:', o.orderId, 'status:', st, 'prev:', prevSt);
              if (_tyClaim(o.orderId) && typeof _autoSendCancelledNote === 'function') {
                setTimeout(function() { _autoSendCancelledNote(o.orderId, o.side || 'sell'); }, 200);
              }
            }
          });
        }
        window._prevOrdersSnapshot = data;
        if (typeof window._sweepForCompletedOrders === 'function') window._sweepForCompletedOrders();
        _tele('sse_recv', { n: data.orders.length, s: String(data.sig || data.timestamp || '').slice(0, 12) });
        if (typeof handleSSEOrderUpdate === 'function' && data && data.orders) handleSSEOrderUpdate(data);
        document.dispatchEvent(new CustomEvent('order-update', { detail: data }));
      } catch (err) { console.warn('[SSE] orders parse error:', err); }
    });

    // Server broadcasts 'order-update' after mark-paid/release/cancel — fetch fresh orders + auto-send thank-you
    sseClient.addEventListener('order-update', (e) => {
      var data = null;
      try {
        data = JSON.parse(e.data);
        if (data && data.orderId) {
          window._lastOrderAction = Date.now();
          console.log('[SSE order-update]', data);
          // OPTIMISTIC UPDATE: immediately mark paid/completed locally so order
          // disappears from pending even if server cache is stale.
          if (data.action === 'release' || data.action === 'mark-paid') {
            // If server sent the updated order, use it; otherwise patch locally
            var order = data.order;
            if (!order && window._ordersMap && window._ordersMap[data.orderId]) {
              order = window._ordersMap[data.orderId];
            }
            if (order) {
              order.paymentStatus = 'paid';
              // mark-paid: keep orderStatus as-is (pending); release: set completed
              if (data.action === 'release') {
                order.orderStatus = 'completed';
              }
            }
            // Patch local maps
            if (window._ordersMap && window._ordersMap[data.orderId]) {
              window._ordersMap[data.orderId].paymentStatus = 'paid';
              if (data.action === 'release') {
                window._ordersMap[data.orderId].orderStatus = 'completed';
              }
            }
            if (window._sseOrdersCache && Array.isArray(window._sseOrdersCache.orders)) {
              for (var i = 0; i < window._sseOrdersCache.orders.length; i++) {
                if (String(window._sseOrdersCache.orders[i].orderId) === String(data.orderId)) {
                  window._sseOrdersCache.orders[i].paymentStatus = 'paid';
                  if (data.action === 'release') {
                    window._sseOrdersCache.orders[i].orderStatus = 'completed';
                  }
                  break;
                }
              }
            }
            if (data.action === 'release') {
              // Mark optimistically released so SSE shield protects it for 3 min
              window._optimisticReleasedAt = window._optimisticReleasedAt || {}; window._optimisticReleasedAt[String(data.orderId)] = Date.now();
            }
            // Immediate render so the row moves to Completed right away instead of
            // waiting on the next SSE snapshot — this is what stops the release
            // button from being hittable again after the trade is already done.
            if (typeof renderOrders === 'function') {
              var container = document.getElementById('page-content');
              if (container && container._ordersRendered && window.currentPage === 'orders') {
                renderOrders(container, window._sseOrdersCache, true);
              }
            }
            // Auto-send thank-you ONLY when the trade actually completes.
            // This also fired on 'mark-paid', which is mid-trade: the buyer has
            // paid but the order is not released or completed yet. That was the
            // source of thank-you notes landing on pending trades, and it was
            // worse than cosmetic — _tyClaim is one-shot per order, so claiming
            // it on mark-paid SUPPRESSED the real completion triggers (the
            // release action below and the SSE snapshot completion check), and
            // the note was delivered early and never again when it completed.
            if (data.action === 'release' && typeof _autoSendThankYou === 'function') {
              if (_tyClaim(data.orderId)) { setTimeout(() => _autoSendThankYou(data.orderId), 200); }
            }
          } else if (data.action === 'cancel' && typeof _autoSendCancelledNote === 'function') {
            // We don't know side here; fetch it via the order or default to sell
            if (_tyClaim(data.orderId)) { setTimeout(() => {
              var o = window._ordersMap && window._ordersMap[data.orderId];
              _autoSendCancelledNote(data.orderId, o ? o.side : 'sell');
            }, 200); }
          }
        }
      } catch (err) { console.warn('[SSE] order-update parse error:', err); }
      // If server sent the updated order, we already patched it; skip refresh to avoid flicker
      if (!data) return;
      if (!data.order) _refreshOrdersFromCache();
      else if (window.currentPage === 'orders' && typeof renderOrders === 'function') {
        // Re-render with patched data immediately
        var container = document.getElementById('page-content');
        if (container && container._ordersRendered) renderOrders(container, window._sseOrdersCache, true);
      }
    });

    // Bank SMS payment matched — stamp the badge locally for an INSTANT render,
    // then refresh as backup so the full order data catches up.
    sseClient.addEventListener('sms-payment', (e) => {
      try {
        var d = JSON.parse(e.data || '{}');
        if (d && d.orderId && window._sseOrdersCache && Array.isArray(window._sseOrdersCache.orders)) {
          var hit = false;
          for (var si = 0; si < window._sseOrdersCache.orders.length; si++) {
            if (String(window._sseOrdersCache.orders[si].orderId) === String(d.orderId)) {
              window._sseOrdersCache.orders[si].smsReceived = true;
              hit = true;
              break;
            }
          }
          if (hit && typeof handleSSEOrderUpdate === 'function') handleSSEOrderUpdate(window._sseOrdersCache);
        }
      } catch (err) {}
      _refreshOrdersFromCache();
    });

    // On visibility change or focus, immediately fetch fresh orders if we're on orders page
    if (!window._sseVisibilityBound) {
      window._sseVisibilityBound = true;
      document.addEventListener('visibilitychange', function() {
        if (!document.hidden && typeof currentPage !== 'undefined' && currentPage === 'orders') {
          _refreshOrdersFromCache();
        }
      });
      window.addEventListener('focus', function() {
        if (typeof currentPage !== 'undefined' && currentPage === 'orders') {
          _refreshOrdersFromCache();
        }
      });
    }

    sseClient.onerror = (e) => {
      console.warn('[SSE] connection error', e?.message || '');
      if (sseClient) { sseClient.close(); sseClient = null; }
      sseConnectInFlight = false;
      sseReconnectCount++;
      if (sseReconnectCount > 10) { console.warn('[SSE] max reconnects reached, giving up'); return; }
      var delay = Math.min(5000 * Math.pow(1.5, sseReconnectCount - 1), 60000);
      sseReconnectTimer = setTimeout(connectSSE, delay);
    };
  } catch (e) {
    sseConnectInFlight = false;
    sseReconnectTimer = setTimeout(connectSSE, 10000);
  }
  _startSseHeartbeat();
}

// Immediately fetch orders from server — hits the warm per-user cache (refreshed by
// the SSE push every ~10s) so this returns fast instead of a cold multi-second OKX call.
// Pass ?_t= to force a live OKX round-trip when absolute freshness is required.
function _refreshOrdersFromCache() {
  if (document.hidden) return;
  if (window.__ordersRefreshBusy) { window.__ordersRefreshQueued = true; return; }
  window.__ordersRefreshBusy = true;
  // No refresh/_t/force param: routes/okx.js serves the warm per-user cache (kept
  // fresh by the SSE push) instead of a live OKX round-trip. force=1 here was a bug —
  // every order-update/reconnect did a cold OKX call and could stall on OKX 504s.
  var url = '/api/okx/p2p/order-list?pageSize=50&pageIndex=1';
  API.get(url).then(function(res) {
    if (res && Array.isArray(res.data)) {
      _tele('poll_recv', { n: res.data.length, th: res._throttled ? 1 : 0 });
      // `_throttled` responses carry the server's FRESHEST known data (warm
      // cache or snapshot during an OKX cooldown). Render them like any other
      // snapshot — the empty/richer guards below still protect against
      // regressions, and skipping used to freeze the UI for the whole
      // 60-120s cooldown (the "orders take 2 minutes" bug).
      var data = { orders: res.data };
      // Anti-flash: never replace a populated snapshot with a transient-empty
      // response (warm-cache miss while OKX is slow/errory). Same guard the SSE
      // orders listener has; this path bypasses that listener, so address it here.
      var _prevCache = window._sseOrdersCache;
      if (data.orders.length === 0 && _prevCache && Array.isArray(_prevCache.orders) && _prevCache.orders.length > 0) {
        window._lastSseTimestamp = Date.now();
        return;
      }
      // Extra guard: if we already have a newer/richer snapshot (more orders),
      // don't downgrade to an older cache. This handles the race where SSE
      // delivered a fresh order but the warm cache hasn't been updated yet.
      if (_prevCache && Array.isArray(_prevCache.orders) && _prevCache.orders.length > data.orders.length + 2) {
        window._lastSseTimestamp = Date.now();
        return;
      }
      // Merge-by-orderId: the SSE push broadcasts the newest-50 sorted list, while
      // /order-list serves the warm cache (raw OKX ordering) that can briefly have
      // different membership. Wholesale replacement made orders flicker — appear
      // via SSE, vanish when a stale cache refresh replaced the snapshot. Union
      // keeps any order either source knows about — BUT only if it is still young
      // and active: a completed/cancelled order that left the newest-50 is a real
      // deletion (it would reappear as a "ghost" forever otherwise), and a very
      // old order simply aged out of the snapshot. Preserving only recent active
      // orders expresses the flicker guard without inventing resurrections.
      try {
        if (_prevCache && Array.isArray(_prevCache.orders)) {
          var _byId = {};
          data.orders.forEach(function(o) { if (o && o.orderId) _byId[o.orderId] = o; });
          var _merged = data.orders.slice();
          var _nowMs = Date.now();
          _prevCache.orders.forEach(function(o) {
            if (!o || !o.orderId || _byId[o.orderId]) return;
            var _st = (o.state || o.completionStatus || o.orderStatus || o.status || '').toLowerCase();
            var _ts = parseInt(o.createdTimestamp) || 0;
            // Drop completed/cancelled orders older than 10 minutes (ghost guard)
            if ((_st === 'completed' || _st === 'finished' || _st === 'released' || _st === 'cancelled' || _st === 'canceled') && _ts < _nowMs - 600000) return;
            // Drop ANY order older than 30 minutes to prevent unbounded accumulation
            if (_ts < _nowMs - 1800000) return;
            _merged.push(o);
          });
          if (_merged.length > data.orders.length) {
            _merged.sort(function(a, b) { return (parseInt(b.createdTimestamp) || 0) - (parseInt(a.createdTimestamp) || 0); });
            if (_merged.length > 150) _merged = _merged.slice(0, 150);
            data.orders = _merged;
          }
        }
      } catch (_mergeE) {}
      window._lastSseTimestamp = Date.now();
      window._sseOrdersCache = data;
      if (window._persistOrdersSnapshot) window._persistOrdersSnapshot();
      if (typeof handleSSEOrderUpdate === 'function') handleSSEOrderUpdate(data);
    }
  }).catch(function() {}).then(function() {
    window.__ordersRefreshBusy = false;
    if (window.__ordersRefreshQueued) {
      window.__ordersRefreshQueued = false;
      setTimeout(_refreshOrdersFromCache, 0);
    }
  });
}

// Orders are delivered via SSE only (prefetcher broadcasts 'orders' event every ~20s)
  // After server actions (mark-paid/release/cancel), the 'order-update' SSE event triggers _refreshOrdersFromCache
  document.addEventListener('visibilitychange', function() {
    window._hidden = document.hidden;
    if (document.hidden) {
      if (sseClient) { sseClient.close(); sseClient = null; }
      // Cleanup orders timers when page hidden (saves CPU on mobile)
      if (window._ordersTimerTick) { clearInterval(window._ordersTimerTick); window._ordersTimerTick = null; }
    } else {
      if (!sseClient && API.getToken()) {
        if (sseReconnectTimer) clearTimeout(sseReconnectTimer);
        sseReconnectCount = 0;
        connectSSE();
      }
    }
  });

// SSE heartbeat: if no REAL SSE event received for 40s (or the client object is
// gone entirely), force reconnect and fetch fresh orders. Checked every 5s so a
// silently-dead SSE connection (common on mobile networks and Android WebViews)
// heals within ~45s. _lastSseEventTime is updated ONLY by actual SSE events
// ('orders') and the server's 25s keepalive 'ping' — a 15s threshold made the
// watchdog kill HEALTHY connections during quiet periods (no order changes),
// which is why the app sat on "Reconnecting..." forever. 40s tolerates one
// missed ping while still catching a truly dead stream quickly.
var _sseHeartbeatTimer = null;
function _startSseHeartbeat() {
  if (_sseHeartbeatTimer) clearInterval(_sseHeartbeatTimer);
  _sseHeartbeatTimer = setInterval(function() {
    // Skip entirely in background: SSE is intentionally closed while hidden
    // (see visibilitychange), and reconnecting would restart the server's 5s
    // push loop — a constant CPU/network drain on mobile. The visibilitychange
    // handler reconnects on return to foreground.
    if (document.hidden || !API.getToken()) return;
    var dead = !sseClient || !window._lastSseEventTime || (Date.now() - window._lastSseEventTime > 40000);
    if (!dead) return;
    // If a reconnect is already scheduled (onerror backoff), let IT fire — calling
    // connectSSE here too would reset the backoff counter and restart the storm.
    if (sseReconnectTimer || sseConnectInFlight) return;
    console.warn('[sse] heartbeat: SSE dead, reconnecting');
    if (sseClient) { sseClient.close(); sseClient = null; }
    sseReconnectCount = 0;
    connectSSE();
    _refreshOrdersFromCache();
  }, 5000);
}

function disconnectSSE() {
  if (sseClient) { sseClient.close(); sseClient = null; }
  if (sseReconnectTimer) { clearTimeout(sseReconnectTimer); sseReconnectTimer = null; }
  sseReconnectCount = 0;
}

// ── APK push (FCM): token registration + incoming payload handling ──
// The native side stores the FCM token in SharedPreferences; this picks it up
// and registers it with the server. Idempotent (upsert) so it can run on every
// authenticated page load. No-ops entirely when the APK isn't FCM-enabled.
function _registerPushToken() {
  try {
    if (typeof AndroidPush === 'undefined' || !AndroidPush.hasPush()) return;
    var t = AndroidPush.getToken();
    if (!t || !API.getToken()) return;
    API.post('/api/apk/register-token', { token: t, platform: 'android' }).catch(function() {});
  } catch (e) {}
}

// ── APK device secret (native SMS direct-post) ──
// The APK wraps the server in a WebView that can die while its native SMS
// listener keeps running. To post parsed bank SMS to the server WITHOUT the
// WebView JS bridge being alive, the native side needs a server-issued device
// secret. This mints one on every authenticated load (idempotent server-side:
// same device id → same secret, rotated only on explicit revoke) and caches it
// in localStorage plus hands it to the native bridge so the listener can POST
// natively. Harmless no-op on the desktop browser.
function _apkDeviceId() {
  try {
    var k = 'apk_device_id';
    var id = localStorage.getItem(k);
    if (!id) { id = 'dev-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10); localStorage.setItem(k, id); }
    return id;
  } catch (e) { return 'dev-' + Date.now().toString(36); }
}

function _registerDeviceSecret() {
  try {
    if (!API.getToken()) return;
    var id = _apkDeviceId();
    API.post('/api/apk/register-device', { device: id }).then(function(res) {
      try {
        var secret = res && res.data && res.data.secret;
        if (!secret) return;
        localStorage.setItem('apk_device_secret', secret);
        if (typeof AndroidOtp !== 'undefined' && AndroidOtp.storeDeviceSecret) {
          AndroidOtp.storeDeviceSecret(secret);
        }
      } catch (e) {}
    }).catch(function() {});
  } catch (e) {}
}

// Keep a fallback of the secret in localStorage so the page itself can also
// post natively (fetch + x-device-secret) even outside the APK WebView.
window.__apkDeviceSecret = function() {
  try {
    var s = localStorage.getItem('apk_device_secret');
    return s || (typeof AndroidOtp !== 'undefined' && AndroidOtp.getDeviceSecret ? AndroidOtp.getDeviceSecret() : '') || '';
  } catch (e) { return ''; }
};

window.__onPushData = function(data) {
  try {
    if (!data) return;
    // Token refreshed by Firebase — re-register.
    if (data.type === 'token-refresh' && data.token) {
      if (API.getToken()) API.post('/api/apk/register-token', { token: data.token, platform: 'android' }).catch(function() {});
      return;
    }
    var title = data.title || 'MONEYFESTLTD';
    var body = data.body || '';
    // Record that this order was just announced via FCM so the SSE snapshot's
    // compareOrderChanges (nav.js) does not announce the same event a second time
    // (double buzz). The SSE push loop and FCM push fire back-to-back for the same
    // new-order / buyer-paid event — this dedupes them client-side.
    if (data.orderId) {
      window._fcmAnnouncedAt = window._fcmAnnouncedAt || {};
      window._fcmAnnouncedAt[String(data.orderId)] = Date.now();
      if (Object.keys(window._fcmAnnouncedAt).length > 300) window._fcmAnnouncedAt = { [String(data.orderId)]: Date.now() };
    }
    // Dedupe near-identical FCM sends (e.g. the mark-paid route + the SSE
    // paid-detector both fire "buyer paid" for the same order) — a repeat push
    // arrives with the same type+orderId within a few seconds.
    if (data.orderId) {
      window._fcmDedup = window._fcmDedup || {};
      var _fdk = String(data.type || 'push') + ':' + String(data.orderId);
      var _fdt = window._fcmDedup[_fdk];
      if (_fdt && (Date.now() - _fdt) < 4000) return;
      window._fcmDedup[_fdk] = Date.now();
      if (Object.keys(window._fcmDedup).length > 400) window._fcmDedup = { [_fdk]: Date.now() };
    }
    // FCM-carried order snapshot: merge into the render cache and re-render
    // immediately — the screen updates the instant the push arrives, no SSE or
    // OKX roundtrip needed (covers frozen-WebView resume where the SSE
    // broadcast was lost).
    if (data.order) {
      try {
        var po = typeof data.order === 'string' ? JSON.parse(data.order) : data.order;
        if (po && po.orderId && window._sseOrdersCache && Array.isArray(window._sseOrdersCache.orders)) {
          // The FCM order is a lossy trim (no chatPayments, no takerPaymentMethod).
          // Merge it OVER any richer copy SSE/REST already delivered instead of
          // overwriting — otherwise the push's empty counterpartyDetail clobbers
          // the name/pay number and the row renders blank (app-only symptom).
          var _prev = null;
          for (var _pi = 0; _pi < window._sseOrdersCache.orders.length; _pi++) {
            if (String(window._sseOrdersCache.orders[_pi].orderId) === String(po.orderId)) { _prev = window._sseOrdersCache.orders[_pi]; break; }
          }
          var _prevCd = (_prev && _prev.counterpartyDetail) || {};
          var _havePushPay = !!(po.accountNo || po.bankName || po.paymentMethod);
          po.counterpartyDetail = {
            nickName: po.nickName || _prevCd.nickName || '',
            realName: po.realName || _prevCd.realName || '',
            takerPaymentMethod: _havePushPay
              ? { accountNo: po.accountNo || '', bankName: po.bankName || '', paymentMethod: po.paymentMethod || '' }
              : (_prevCd.takerPaymentMethod || { accountNo: po.accountNo || '', bankName: po.bankName || '', paymentMethod: po.paymentMethod || '' })
          };
          if ((!po.chatPayments || !po.chatPayments.length) && _prev && _prev.chatPayments) po.chatPayments = _prev.chatPayments;
          var arr = window._sseOrdersCache.orders.filter(function(x) { return String(x.orderId) !== String(po.orderId); });
          arr.unshift(po);
          arr.sort(function(a, b) { return (parseInt(b.createdTimestamp) || 0) - (parseInt(a.createdTimestamp) || 0); });
          var mergedData = { orders: arr };
          window._sseOrdersCache = mergedData;
          if (window._persistOrdersSnapshot) window._persistOrdersSnapshot();
          if (typeof handleSSEOrderUpdate === 'function') handleSSEOrderUpdate(mergedData);
        }
      } catch (e) {}
    }
    if (typeof playNotification === 'function') { try { playNotification(); } catch (e) {} }
    // In the APK the FCM service posts the system notification itself — local
    // duplicates via the legacy bridge or web Notification API are skipped.
    // Desktop browsers still get a native notification.
    var _inApk = false;
    try { _inApk = typeof isApk === 'function' && isApk(); } catch (e) {}
    if (!_inApk && typeof sendNativeNotification === 'function') { try { sendNativeNotification(title, body); } catch (e) {} }
    if (!_inApk && typeof AndroidNotification !== 'undefined' && AndroidNotification.show) {
      try { AndroidNotification.show(title, body); } catch (e) {}
    }
    // Payment-required pushes demand immediate action — jump to the order.
    if ((data.type === 'manual-payment' || data.type === 'payment-required') && data.orderId) {
      window.__handlePushNav(String(data.orderId));
    }
  } catch (e) {}
};

// Deep-link target for notification taps: switches to the Orders tab and
// opens the order detail for the pushed order. Queues until login completes.
window.__handlePushNav = function(orderId) {
  try {
    if (!orderId) return;
    if (!window.currentUser || !API.getToken()) {
      window.__pendingPushNav = String(orderId);
      return;
    }
    if (typeof navigate === 'function') {
      try { navigate('orders'); } catch (e) {}
    }
    setTimeout(function() {
      try { if (typeof showOrderDetail === 'function') showOrderDetail(String(orderId)); } catch (e) {}
    }, 700);
  } catch (e) {}
};

let _audioCtx = null;
var _audioCtxTimer = null;
function _closeAudioCtx() {
  if (_audioCtx) { _audioCtx.close().catch(function(){}); _audioCtx = null; }
}
async function _getAudioCtx() {
  if (!_audioCtx) { _audioCtx = new (window.AudioContext || window.webkitAudioContext)(); }
  if (_audioCtx.state === 'suspended') { await _audioCtx.resume(); }
  if (_audioCtxTimer) clearTimeout(_audioCtxTimer);
  _audioCtxTimer = setTimeout(_closeAudioCtx, 30000);
  return _audioCtx;
}

async function playNotification() {
  if (localStorage.getItem('notificationSoundEnabled') === 'false') return;
  if (window._notifySuppressed) return;
  if (localStorage.getItem('nativeNotifications') === 'true') {
    sendNativeNotification('MONEYFESTLTD P2P', 'New notification', { vibrate: [100, 50, 100] });
    return;
  }
  if (window.AndroidNotification && window.AndroidNotification.isSupported()) {
    try { AndroidNotification.playRingtone(); } catch {}
    return;
  }
  const custom = localStorage.getItem('notificationSoundFile');
  const vol = Math.min(parseFloat(localStorage.getItem('notificationVolume') || '0.5') * 2.5, 1);
  if (custom && custom.startsWith('data:audio/')) {
    try { const a = new Audio(custom); a.volume = vol; a.play().catch(() => {}); } catch {}
    return;
  }
  const preset = localStorage.getItem('notificationSoundPreset') || 'classic';
  try {
    const ctx = await _getAudioCtx();
    const now = ctx.currentTime;
    const n = (freq, start, dur, type = 'sine', gv = vol) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = type; o.frequency.value = freq;
      g.gain.setValueAtTime(gv, now + start);
      g.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
      o.connect(g); g.connect(ctx.destination); o.start(now + start); o.stop(now + start + dur);
    };
    const p = (freq, start, dur, gv) => n(freq, start, dur, 'sine', gv);
    if (preset === 'classic') {
      p(523, 0, 0.08, vol); p(659, 0.1, 0.08, vol);
      p(784, 0.2, 0.08, vol); p(1047, 0.3, 0.25, vol);
      p(784, 0.55, 0.12, vol); p(1047, 0.7, 0.35, vol);
    }
    else if (preset === 'marimba') {
      [523, 587, 659, 784, 880, 1047].forEach((f, i) => p(f, i * 0.14, 0.1, vol));
      p(1175, 0.84, 0.2, vol); p(1047, 1.0, 0.3, vol);
    }
    else if (preset === 'xylophone') {
      [392, 440, 494, 523, 587, 659, 784, 880].forEach((f, i) => p(f, i * 0.1, 0.08, vol));
    }
    else if (preset === 'alarm') {
      for (let i = 0; i < 8; i++) n(i % 2 ? 880 : 660, i * 0.2, 0.13, 'square', vol);
    }
    else if (preset === 'ascending') {
      [262, 330, 392, 523, 659, 784].forEach((f, i) => p(f, i * 0.15, 0.12, vol));
      p(1047, 0.9, 0.4, vol);
    }
    else if (preset === 'descending') {
      [1047, 784, 659, 523, 392, 330, 262].forEach((f, i) => p(f, i * 0.14, 0.12, vol));
    }
    else if (preset === 'sms') {
      p(880, 0, 0.06, vol); p(1175, 0.08, 0.06, vol);
      p(880, 0.16, 0.06, vol); p(1175, 0.24, 0.08, vol);
    }
    else if (preset === 'digital') {
      [660, 880, 660, 880, 1047].forEach((f, i) => n(f, i * 0.1, 0.06, 'square', vol));
      p(1319, 0.5, 0.25, vol);
    }
    else if (preset === 'phonering') {
      for (let i = 0; i < 3; i++) { n(440, i * 0.6, 0.25, 'sine', vol); n(480, i * 0.6 + 0.25, 0.25, 'sine', vol); }
    }
    else if (preset === 'chime') {
      p(523, 0, 0.5, vol); p(659, 0.15, 0.4, vol); p(784, 0.3, 0.35, vol);
      p(1047, 0.5, 0.6, vol); p(784, 0.65, 0.4, vol);
    }
    else if (preset === 'wakeup') {
      [262, 294, 330, 349, 392, 440, 494, 523].forEach((f, i) => p(f, i * 0.18, i === 7 ? 0.5 : 0.12, vol));
    }
    else if (preset === 'crystal') {
      p(1319, 0, 0.2, vol); p(1760, 0.1, 0.18, vol); p(2093, 0.2, 0.15, vol);
      p(2637, 0.3, 0.3, vol); p(2093, 0.5, 0.4, vol);
    }
    else if (preset === 'echoring') {
      for (let i = 0; i < 4; i++) p(784, i * 0.35, 0.12, vol * (1.0 - i * 0.15));
    }
    else if (preset === 'vibrato') {
      const o = ctx.createOscillator(); const g = ctx.createGain(); const l = ctx.createOscillator();
      l.frequency.value = 8; l.type = 'sine'; const lg = ctx.createGain(); lg.gain.value = 120;
      l.connect(lg); lg.connect(o.frequency); o.type = 'sine'; o.frequency.value = 660;
      g.gain.setValueAtTime(vol, now); g.gain.setValueAtTime(vol, now + 0.8);
      g.gain.exponentialRampToValueAtTime(0.001, now + 1.0);
      o.connect(g); g.connect(ctx.destination); o.start(now); o.stop(now + 1.0); l.start(now); l.stop(now + 1.0);
    }
    else if (preset === 'siren') {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = 'sawtooth';
      o.frequency.setValueAtTime(300, now); o.frequency.linearRampToValueAtTime(900, now + 0.3);
      o.frequency.linearRampToValueAtTime(300, now + 0.6); o.frequency.linearRampToValueAtTime(900, now + 0.9);
      o.frequency.setValueAtTime(300, now + 1.0);
      g.gain.setValueAtTime(vol, now); g.gain.setValueAtTime(vol, now + 0.9);
      g.gain.exponentialRampToValueAtTime(0.001, now + 1.1);
      o.connect(g); g.connect(ctx.destination); o.start(now); o.stop(now + 1.2);
    }
    else if (preset === 'jingle') {
      [523, 659, 784, 1047, 784, 1047, 1175].forEach((f, i) => p(f, i * 0.12, i > 4 ? 0.25 : 0.08, vol));
    }
  } catch (e) { console.warn('playNotification error:', e.message); }
}

// ===== Notification Bell =====
window._notifCount = 0;

window.addNotifToBell = function(data) {
  window._notifCount++;
  updateNotifBadge();
  const list = document.getElementById('notif-list');
  if (!list) return;
  const el = document.createElement('div');
  el.style.cssText = 'padding:8px 10px;border-bottom:1px solid #1a1a2e;font-size:12px;cursor:pointer;';
  el.innerHTML = '<div style="font-weight:600;color:var(--text-primary);font-size:11px;">' + esc(data.title || 'Notification') + '</div>' +
    '<div style="color:var(--text-tertiary);font-size:11px;margin-top:2px;">' + esc(data.message || '') + '</div>' +
    '<div style="color:var(--text-quaternary);font-size:9px;margin-top:4px;">' + new Date().toLocaleTimeString() + '</div>';
  el.onclick = function() { clearNotifBadge(); toggleNotifPanel(); };
  list.insertBefore(el, list.firstChild);
  // Keep max 50
  while (list.children.length > 50) list.removeChild(list.lastChild);
  // Show bell
  const bell = document.getElementById('notif-bell');
  if (bell) bell.style.display = '';
};

// --- Clear all notifications ---
window.clearAllNotifications = function() {
  const list = document.getElementById('notif-list');
  if (list) list.innerHTML = '';
  window._notifCount = 0;
  updateNotifBadge();
  // Optionally call server to clear history
  try { API.post('/api/system/notifications/clear', {}).catch(() => {}); } catch {}
  // Refresh panel if open
  if (list && list.children.length === 0) {
    list.innerHTML = '<div style="text-align:center;padding:20px;color:var(--text-quaternary);font-size:12px;">No notifications</div>';
  }
};

window.updateNotifBadge = function() {
  const badge = document.getElementById('notif-count');
  if (!badge) return;
  if (window._notifCount > 0) {
    badge.textContent = window._notifCount > 99 ? '99+' : window._notifCount;
    badge.classList.remove('hidden');
  } else {
    badge.classList.add('hidden');
  }
};

window.clearNotifBadge = function() {
  window._notifCount = 0;
  updateNotifBadge();
};

window.toggleNotifPanel = function() {
  const panel = document.getElementById('notif-panel');
  if (!panel) return;
  const hidden = panel.classList.contains('hidden');
  // Close all other panels/dropdowns first
  document.querySelectorAll('.modal-overlay, #notif-panel').forEach(el => {
    if (el !== panel) el.classList.add('hidden');
  });
  panel.classList.toggle('hidden');
  if (!hidden) return;
  // Load notifications from API if list is empty
  const list = document.getElementById('notif-list');
  if (list && list.children.length === 0) {
    list.innerHTML = '<div style="text-align:center;padding:20px;color:var(--text-quaternary);font-size:12px;">Loading...</div>';
    fetchNotifHistory();
  }
};

// Close notification panel on click outside
document.addEventListener('click', function(e) {
  const panel = document.getElementById('notif-panel');
  const bell = document.getElementById('notif-bell');
  if (panel && !panel.classList.contains('hidden') && bell && !bell.contains(e.target) && !panel.contains(e.target)) {
    panel.classList.add('hidden');
  }
});

function restoreHorzScrolls(container, saved) {
  if (!saved || !saved.length) return;
  var els = container.querySelectorAll('.table-container');
  for (var i = 0; i < els.length && i < saved.length; i++) els[i].scrollLeft = saved[i];
}
async function fetchNotifHistory() {
  const list = document.getElementById('notif-list');
  if (!list) return;
  try {
    const res = await API.getSystemNotifications(20);
    const items = res.data || [];
    list.innerHTML = '';
    if (items.length === 0) {
      list.innerHTML = '<div style="text-align:center;padding:20px;color:var(--text-quaternary);font-size:12px;">No notifications yet</div>';
      return;
    }
    items.forEach(function(n) {
      const el = document.createElement('div');
      el.style.cssText = 'padding:8px 10px;border-bottom:1px solid #1a1a2e;font-size:12px;cursor:pointer;';
      el.innerHTML = '<div style="font-weight:600;color:var(--text-primary);font-size:11px;">' + esc(n.title || '') + '</div>' +
        '<div style="color:var(--text-tertiary);font-size:11px;margin-top:2px;">' + esc(n.message || '') + '</div>' +
        '<div style="color:var(--text-quaternary);font-size:9px;margin-top:4px;">' + fmtDate(n.createdAt || n.created_at) + '</div>';
      el.onclick = function() { clearNotifBadge(); };
      list.appendChild(el);
    });
  } catch (err) {
    list.innerHTML = '<div style="text-align:center;padding:20px;color:var(--accent-red);font-size:12px;">Failed to load</div>';
  }
}


// OKX P2P order paymentStatus values: 'unpaid' (not paid), 'paid' (buyer marked
// paid), 'confirmed' (payment confirmed, awaiting release). 'confirmed' must
// render as paid everywhere — treating it as unpaid makes orders look like the
// payment reverted moments after marking it paid.
if (!window.orderIsPaid) {
  window.orderIsPaid = function(o) {
    var p = String((o && o.paymentStatus) || '').toLowerCase();
    return p === 'paid' || p === 'confirmed';
  };
}

// ===== Appeal guards =====
// OKX signals an active dispute/appeal via MULTIPLE fields (different API
// versions populate different ones):
//   - state/completionStatus  = 'appealing...'
//   - disputeStatus           = '1' (in dispute) or '4' (appeal freeze)
//   - frozenType              = 'appealFreeze'
// A release on such an order pays the counterparty while funds are disputed —
// a money-losing mistake. Every release button/action must pass this check,
// and the server refuses the release too (see okxService.releaseP2PCrypto).
function _appealSignals(o) {
  var s = String((o && (o.state || o.completionStatus || o.orderStatus)) || '').toLowerCase();
  var ds = String((o && (o.disputeStatus !== undefined ? o.disputeStatus : '')) || '').toLowerCase();
  var ft = String((o && (o.frozenType !== undefined ? o.frozenType : '')) || '').toLowerCase();
  return s.indexOf('appealing') !== -1 || ds === '1' || ds === '4' || ft.indexOf('appeal') !== -1;
}
window._appealedOrderIds = window._appealedOrderIds || {};
window.isAppealedOrder = function(o) {
  if (!o) return false;
  if (_appealSignals(o)) return true;
  if (window._appealedOrderIds[o.orderId]) {
    var age = Date.now() - (window._appealedOrderIds[o.orderId] || 0);
    return age < 30 * 60 * 1000;
  }
  return false;
};
window.__markOrderAppealed = function(orderId) {
  if (!orderId) return;
  window._appealedOrderIds[orderId] = Date.now();
};
window.__syncAppealedFlags = function(orders) {
  (orders || []).forEach(function(o) {
    if (!o || !o.orderId) return;
    if (_appealSignals(o)) window._appealedOrderIds[o.orderId] = Date.now();
    else delete window._appealedOrderIds[o.orderId];
  });
};
window.__uiLoaded = true;

// Optimistic mark-paid: flip the row to 'paid' INSTANTLY on tap so the user
// isn't staring at 'unpaid' while OKX processes the call (5-20s). The server
// response confirms or reverts.
window.optimisticMarkPaid = function(orderId) {
  try {
    window._optimisticPaidAt = window._optimisticPaidAt || {}; window._optimisticPaidAt[String(orderId)] = Date.now();
    var patched = false;
    if (window._ordersMap && window._ordersMap[orderId]) { window._ordersMap[orderId].paymentStatus = 'paid'; patched = true; }
    if (window._sseOrdersCache && Array.isArray(window._sseOrdersCache.orders)) {
      var arr = window._sseOrdersCache.orders;
      for (var i = 0; i < arr.length; i++) {
        if (String(arr[i].orderId) === String(orderId)) { arr[i].paymentStatus = 'paid'; patched = true; break; }
      }
    }
    // IMMEDIATE render — bypass SSE debounce (which compares against already-patched cache)
    if (patched && typeof renderOrders === 'function') {
      var container = document.getElementById('page-content');
      if (container && container._ordersRendered && window.currentPage === 'orders') {
        renderOrders(container, window._sseOrdersCache, true);
      }
    }
    return patched;
  } catch (e) { return false; }
};
window.revertOptimisticPaid = function(orderId) {
  try {
    if (window._optimisticPaidAt) delete window._optimisticPaidAt[String(orderId)];
    if (window._ordersMap && window._ordersMap[orderId]) window._ordersMap[orderId].paymentStatus = 'unpaid';
    if (window._sseOrdersCache && Array.isArray(window._sseOrdersCache.orders)) {
      var arr = window._sseOrdersCache.orders;
      for (var j = 0; j < arr.length; j++) {
        if (String(arr[j].orderId) === String(orderId)) { arr[j].paymentStatus = 'unpaid'; break; }
      }
    }
    // IMMEDIATE render
    if (typeof renderOrders === 'function') {
      var container = document.getElementById('page-content');
      if (container && container._ordersRendered && window.currentPage === 'orders') {
        renderOrders(container, window._sseOrdersCache, true);
      }
    }
  } catch (e) {}
};

// Optimistic release — same instant-feedback pattern as mark-paid.
window.optimisticRelease = function(orderId) {
  try {
    function set(o, k, v) { o[k] = v; }
    if (window._ordersMap && window._ordersMap[orderId]) { set(window._ordersMap[orderId], 'orderStatus', 'completed'); set(window._ordersMap[orderId], 'paymentStatus', 'paid'); }
    if (window._sseOrdersCache && Array.isArray(window._sseOrdersCache.orders)) {
      var arr = window._sseOrdersCache.orders;
      for (var i = 0; i < arr.length; i++) {
        if (String(arr[i].orderId) === String(orderId)) { arr[i].orderStatus = 'completed'; arr[i].paymentStatus = 'paid'; break; }
      }
    }
    // Mark optimistically released so SSE shield protects it for 3 min
    window._optimisticReleasedAt = window._optimisticReleasedAt || {}; window._optimisticReleasedAt[String(orderId)] = Date.now();
    // IMMEDIATE render — bypass SSE debounce (which compares against already-patched cache)
    if (typeof renderOrders === 'function') {
      var container = document.getElementById('page-content');
      if (container && container._ordersRendered && window.currentPage === 'orders') {
        renderOrders(container, window._sseOrdersCache, true);
      }
    }
  } catch (e) {}
};
window.revertOptimisticRelease = function(orderId) {
  try {
    if (window._optimisticReleasedAt) delete window._optimisticReleasedAt[String(orderId)];
    if (window._ordersMap && window._ordersMap[orderId]) { window._ordersMap[orderId].orderStatus = 'new'; window._ordersMap[orderId].paymentStatus = 'paid'; }
    if (window._sseOrdersCache && Array.isArray(window._sseOrdersCache.orders)) {
      var arr = window._sseOrdersCache.orders;
      for (var j = 0; j < arr.length; j++) {
        if (String(arr[j].orderId) === String(orderId)) { arr[j].orderStatus = 'new'; arr[j].paymentStatus = 'paid'; break; }
      }
    }
    // IMMEDIATE render
    if (typeof renderOrders === 'function') {
      var container = document.getElementById('page-content');
      if (container && container._ordersRendered && window.currentPage === 'orders') {
        renderOrders(container, window._sseOrdersCache, true);
      }
    }
  } catch (e) {}
};

// ── Status chip system (makeover track 4, phase 1: helper + styles live) ──
function statusChip(o) {
  var st = String(o.orderStatus || o.state || '').toLowerCase();
  var pay = String(o.paymentStatus || '').toLowerCase();
  var dispute = String(o.disputeStatus || '');
  if (dispute && dispute !== '0') return '<span class="chip chip-red">⚖️ Dispute</span>';
  if (st === 'completed') return '<span class="chip chip-green">✓ Completed</span>';
  if (st === 'canceled' || st === 'cancelled') return '<span class="chip chip-gray">Cancelled</span>';
  if (pay === 'paid' || pay === 'confirmed') {
    if (o.side === 'sell') return '<span class="chip chip-blue">🔵 Release pending</span>';
    return '<span class="chip chip-blue">Paid — awaiting release</span>';
  }
  if (st === 'new' || st === 'pending') return '<span class="chip chip-amber">● Awaiting payment</span>';
  return '';
}

// Status-chip applier: converts exact status text in rendered order rows to chips.
window.__chipify = function(root) {
  try {
    var M = { 'unpaid':'chip-amber|● Unpaid', 'paid':'chip-blue|● Paid', 'completed':'chip-green|✓ Completed', 'cancelled':'chip-gray|Cancelled', 'canceled':'chip-gray|Cancelled', 'disputed':'chip-red|⚖ Disputed' };
    var els = root.querySelectorAll('td, span');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.children.length || el.classList.contains('chip')) continue;
      var k = el.textContent.trim().toLowerCase();
      var v = M[k];
      if (v) { var p2 = v.split('|'); el.innerHTML = '<span class="chip ' + p2[0] + '">' + p2[1] + '</span>'; }
    }
  } catch (e) {}
};

// ── OKX IP-whitelist compliance notice (single source; used by Settings→API
// and the onboarding wizard — do NOT duplicate this markup elsewhere) ──
window.OKX_SERVER_IP = null;
window.okxIpNoticeHtml = async function() {
  if (!window.OKX_SERVER_IP) {
    try {
      var r = await API.get('/api/config/public');
      window.OKX_SERVER_IP = (r && r.okxServerIp) || '103.6.168.203';
    } catch (e) { window.OKX_SERVER_IP = '103.6.168.203'; }
  }
  var ip = window.OKX_SERVER_IP;
  return '<div style="background:rgba(255,179,0,0.08);border:1px solid rgba(255,179,0,0.35);border-radius:8px;padding:10px 14px;margin-bottom:12px;font-size:12px;line-height:1.7;">' +
    '<strong style="color:#ffb300;">🛡️ Required before your keys will work:</strong><br>' +
    'In your OKX API settings, set the <strong>IP whitelist</strong> to:' +
    '<div style="display:flex;align-items:center;gap:8px;margin-top:6px;">' +
    '<code style="background:var(--bg-primary);border:1px solid var(--border-primary);border-radius:6px;padding:6px 12px;font-size:13px;color:var(--text-primary);user-select:all;">' + esc(ip) + '</code>' +
    '<button type="button" class="btn btn-sm btn-outline" data-on-click="copyText(\'' + ip + '\')">Copy</button>' +
    '</div>' +
    '<span style="color:var(--text-tertiary);">Keys without this whitelist are rejected by OKX and can get the account suspended.</span>' +
    '</div>';
};

// ─── Profile + Hub chip dropdowns ────────────────────
window.toggleProfileMenu = function(ev) {
  if (ev && ev.stopPropagation) ev.stopPropagation();
  var menu = document.getElementById('profile-menu');
  if (!menu) return;
  var opening = menu.classList.contains('hidden');
  document.getElementById('hub-menu')?.classList.add('hidden');
  menu.classList.toggle('hidden', !opening);
  updateProfileMenuTheme();
};

window.toggleHubMenu = function(ev) {
  if (ev && ev.stopPropagation) ev.stopPropagation();
  var menu = document.getElementById('hub-menu');
  if (!menu) return;
  var opening = menu.classList.contains('hidden');
  document.getElementById('profile-menu')?.classList.add('hidden');
  menu.classList.toggle('hidden', !opening);
};

document.addEventListener('click', function(e) {
  [['profile-menu', 'user-chip'], ['hub-menu', 'hub-chip']].forEach(function(pair) {
    var menu = document.getElementById(pair[0]);
    if (!menu || menu.classList.contains('hidden')) return;
    var chip = document.getElementById(pair[1]);
    if (chip && chip.contains(e.target)) return;
    if (menu.contains(e.target)) return;
    menu.classList.add('hidden');
  });
// Start SSE on page load (not just on visibilitychange) so the orders
// stream is active immediately after login, not only when the tab
// regains focus. The connectSSE() function already guards against
// running in background and duplicate calls.
document.addEventListener('DOMContentLoaded', function() {
  if (API.getToken()) connectSSE();
});
});

window.updateProfileMenuTheme = function() {
  var light = document.body.classList.contains('light-mode');
  var icon = document.getElementById('pmThemeIcon');
  var label = document.getElementById('pmThemeLabel');
  if (icon) icon.textContent = light ? '☀️' : '🌙';
  if (label) label.textContent = light ? 'Light theme' : 'Dark theme';
};

/* ==== js/modules/nav.js ==== */
function toggleSidebar() {
  const sidebar = document.getElementById('sidebar');
  const overlay = document.getElementById('sidebar-overlay');
  const toggle = document.querySelector('.nav-toggle');
  if (!sidebar) return;
  const open = !sidebar.classList.contains('open');
  sidebar.classList.toggle('open', open);
  if (overlay) overlay.classList.toggle('open', open);
  if (toggle) toggle.classList.toggle('active', open);
  document.body.style.overflow = open ? 'hidden' : '';
}

// Native posts are deduped by title+body and PERSISTED: every SSE-driven event
// (new order, payment, chat message, payment-required alert) used to re-post the
// same text on every cycle/reconnect, and in 3.4.1 each post got a NEW
// notification id — so the tray stacked the same alerts up to ~49 and buzzed on
// each one. Distinct events always carry a different orderId/body, so only the
// repeats are dropped. TTL 30 min.
var _NATIVE_NOTIF_TTL = 30 * 60 * 1000;
function sendNativeNotification(title, body, options) {
  if (localStorage.getItem('nativeNotifications') === 'false') return;
  options = options || {};
  try {
    var nk = 'natNotif:' + String(title) + '|' + String(body);
    var last = parseInt(localStorage.getItem(nk) || '0', 10);
    if (last && Date.now() - last < _NATIVE_NOTIF_TTL) return;
    localStorage.setItem(nk, String(Date.now()));
    // Bound the key store: drop entries older than the TTL once it grows.
    if (localStorage.length > 400) {
      var cut = Date.now() - _NATIVE_NOTIF_TTL;
      var old = [];
      for (var ni = 0; ni < localStorage.length; ni++) {
        var k = localStorage.key(ni);
        if (k && k.indexOf('natNotif:') === 0) {
          var t = parseInt(localStorage.getItem(k) || '0', 10);
          if (!t || t < cut) old.push(k);
        }
      }
      for (var oi = 0; oi < old.length; oi++) localStorage.removeItem(old[oi]);
    }
  } catch (e) {}
  if (window.AndroidNotification && window.AndroidNotification.isSupported()) {
    try {
      if (options.ringtone) AndroidNotification.playRingtone();
      if (options.vibrate) AndroidNotification.vibrate(JSON.stringify(options.vibrate));
      AndroidNotification.show(title, body);
    } catch {}
    return;
  }
  if (!('Notification' in window)) return;
  if (Notification.permission === 'granted') {
    try { new Notification(title, { body, silent: !options.ringtone }); } catch {}
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission().then(p => {
      if (p === 'granted') try { new Notification(title, { body }); } catch {};
    }).catch(() => {});
  }
}
function showAuth() {
  document.getElementById('auth-container').classList.remove('hidden');
  document.getElementById('page-content').classList.add('hidden');
  document.getElementById('navbar').classList.add('hidden');
  document.getElementById('page-content').innerHTML = '';
  ['register-form', 'forgot-form', 'identify-form', 'reset-form', 'otp-form'].forEach(id => document.getElementById(id).classList.add('hidden'));
  document.getElementById('login-form').classList.remove('hidden');
  API.clearToken();
  closeSidebar();
  setTimeout(function () { if (typeof loadCaptcha === 'function') loadCaptcha('login'); }, 200);
}

function showApp() {
  document.getElementById('auth-container').classList.add('hidden');
  document.getElementById('page-content').classList.remove('hidden');
  document.getElementById('navbar').classList.remove('hidden');
  closeSidebar();
}

function closeSidebar() {
  const sidebar = document.getElementById('sidebar');
  const overlay = document.getElementById('sidebar-overlay');
  const toggle = document.querySelector('.nav-toggle');
  if (sidebar) sidebar.classList.remove('open');
  if (overlay) overlay.classList.remove('open');
  if (toggle) toggle.classList.remove('active');
  document.body.style.overflow = '';
}

function showAuthTab(tab) {
  ['login-form', 'register-form', 'forgot-form', 'identify-form', 'reset-form', 'otp-form'].forEach(id => document.getElementById(id).classList.add('hidden'));
  const el = document.getElementById(tab + '-form');
  if (el) el.classList.remove('hidden');
  if (tab === 'login' || tab === 'register' || tab === 'forgot') {
    setTimeout(function () { if (typeof loadCaptcha === 'function') loadCaptcha(tab); }, 50);
  }
}

function togglePassword(id, el) {
  const inp = document.getElementById(id);
  if (inp.type === 'password') { inp.type = 'text'; el.textContent = '🙈'; }
  else { inp.type = 'password'; el.textContent = '👁'; }
}

function _ns(id) { return (currentUser ? currentUser.id + ':' : 'anon:') + id; }

function compareOrderChanges(orders) {
  const nowActive = new Map();
  const uid = currentUser ? currentUser.id : 'anon';
  orders.filter(o => o.orderId).forEach(o => {
    nowActive.set(_ns(o.orderId), { state: orderState(o), paymentStatus: o.paymentStatus, _rawId: o.orderId });
  });
  if (nowActive.size > 0 && trackedOrderStates.size > 0) {
    // In the APK, order system-notifications come from the server via FCM —
    // posting locally too would duplicate them. Desktop/web keeps native ones.
    var _navInApk = false;
    try { _navInApk = typeof isApk === 'function' && isApk(); } catch (e) {}
    nowActive.forEach((s, id) => {
      const rawId = s._rawId;
      const short = rawId.slice(0,12);
      // FCM already announced this exact order event within 30s — local
      // sound/toast/native would double-buzz. (Server FCM + SSE broadcast fire
      // back-to-back for the same new-order / buyer-paid event.)
      var _fcmA = window._fcmAnnouncedAt && window._fcmAnnouncedAt[String(rawId)];
      var _fcmRecent = !!_fcmA && (Date.now() - _fcmA) < 30000;
      // Hard per-order cooldown. OKX's list and by-id endpoints disagree about an
      // order's state (the release watcher was seeing "paid" from one and
      // "checks-not-met" from the other on the same order), so a snapshot can
      // flip-flop between two values. `s.state !== p.state` then stays true on
      // every single push cycle and the same alert re-fires forever. Whatever the
      // cause, no order may announce itself more than once per window.
      var _lc = window._lastLocalNotifyAt || (window._lastLocalNotifyAt = {});
      var _lAt = _lc[String(rawId)];
      var _tooSoon = !!_lAt && (Date.now() - _lAt) < 90000;
      function _claimLocalNotify() { _lc[String(rawId)] = Date.now(); }
      if (!seenOrderIds.has(id)) {
        seenOrderIds.add(id);
        if (s.state !== 'completed' && s.state !== 'canceled' && s.state !== 'cancelled') {
          if (!_fcmRecent && !_tooSoon) {
            playNotification();
            _claimLocalNotify();
            if (!_navInApk) sendNativeNotification('New P2P Order', 'Order ' + short + ' placed');
            window._highlightOrderId = rawId;
          }
        }
      } else {
        const p = trackedOrderStates.get(id);
        if (p) {
          if (!_fcmRecent && !_tooSoon) {
            if (orderIsPaid(s) && !orderIsPaid(p)) {
              playNotification();
              _claimLocalNotify();
              if (!_navInApk) sendNativeNotification('Payment Received', 'Order ' + short + ' paid by buyer');
            }
            if (s.state === 'completed' && p.state !== 'completed') {
              playNotification();
              _claimLocalNotify();
              if (!_navInApk) sendNativeNotification('Crypto Released', 'Order ' + short + ' completed');
            }
            if (s.state !== p.state) {
              playNotification();
              _claimLocalNotify();
              if (!_navInApk) sendNativeNotification('Order Status Changed', 'Order ' + short + ': → ' + s.state);
            }
          }
        }
      }
    });
  }
  orders.filter(o => o.orderId).forEach(o => seenOrderIds.add(_ns(o.orderId)));
  // Cap seenOrderIds to prevent memory leak
  if (seenOrderIds.size > 5000) {
    var arr = Array.from(seenOrderIds).slice(-2500);
    seenOrderIds = new Set(arr);
  }
  trackedOrderStates = nowActive;
}

// Reminder dedupe is persisted in localStorage: the APK reloads the WebView on
// every resume, and this map used to start empty each time. With 48 stuck
// unpaid orders that posted 48 notifications (twice each) on EVERY load — the
// merchant saw "the same 49 notifications over and over".
const _REMIND_TTL_MS = 12 * 60 * 60 * 1000;
function _reminderKey(nsk) { return 'payRemind:' + nsk; }
function _reminderDone(nsk) {
  if (paymentRemindedOrderIds.has(nsk)) return true;
  try {
    const t = parseInt(localStorage.getItem(_reminderKey(nsk)) || '0', 10);
    if (t && Date.now() - t < _REMIND_TTL_MS) { paymentRemindedOrderIds.set(nsk, t); return true; }
  } catch (e) {}
  return false;
}
function _markReminderDone(nsk) {
  paymentRemindedOrderIds.set(nsk, Date.now());
  try { localStorage.setItem(_reminderKey(nsk), String(Date.now())); } catch (e) {}
}

function checkPaymentReminders(orders) {
  const now = Date.now();
  const dueBuy = [], dueSell = [];
  orders.filter(o => o.orderId).forEach(o => {
    const created = parseInt(o.createdTimestamp || '0');
    if (created <= 0) return;
    const nsk = _ns(o.orderId);
    if (o.side === 'buy' && o.paymentStatus === 'unpaid' && now - created > 900000 && !_reminderDone(nsk)) {
      _markReminderDone(nsk);
      dueBuy.push(o);
      return;
    }
    if (o.side === 'sell' && orderIsPaid(o) && now - created > 900000 && !_reminderDone(nsk)) {
      _markReminderDone(nsk);
      dueSell.push(o);
    }
  });
  const fired = dueBuy.concat(dueSell);
  if (!fired.length) return;
  window._highlightOrderId = fired[0].orderId;
  const parts = [];
  if (dueBuy.length) parts.push(dueBuy.length + ' to pay');
  if (dueSell.length) parts.push(dueSell.length + ' to release');
  const title = '⏰ ' + fired.length + ' order' + (fired.length > 1 ? 's' : '') + ' need attention';
  const body = parts.join(', ') + ' — ' + fired.slice(0, 3).map(o => o.orderId.slice(0, 8)).join(', ') +
    (fired.length > 3 ? ' +' + (fired.length - 3) + ' more' : '');
  requestNativeNotification();
  // ONE aggregated notification per sweep. Posting one per order (plus another
  // from playPaymentReminder) is what buried the tray under ~50 duplicates.
  sendNativeNotification(title, body, { ringtone: true, vibrate: [200, 100, 200, 100, 300] });
  if (!(window.AndroidNotification && window.AndroidNotification.isSupported())) playPaymentReminder();
  window._notifySuppressed = true; setTimeout(() => { window._notifySuppressed = false; }, 10000);
  // Cap paymentRemindedOrderIds to prevent memory leak
  if (paymentRemindedOrderIds.size > 3000) {
    var arr = Array.from(paymentRemindedOrderIds).slice(-1500);
    paymentRemindedOrderIds = new Map(arr);
  }
}

async function playPaymentReminder() {
  // Sound only — the caller posts one aggregated native notification, so
  // popping another one here double-notified on every sweep.
  const list = window._ringtones || [];
  const custom = list.filter(r => !r.isPreset);
  const vol = parseFloat(localStorage.getItem('paymentReminderVolume') || '0.7');
  const boost = 2.5;
  const gv = Math.min(vol * boost, 1);
  if (custom.length > 0) {
    const entry = custom[Math.floor(Math.random() * custom.length)];
    try {
      const res = await API.get('/api/ringtones/' + entry.id + '/data');
      const d = res.data;
      if (d && d.data && d.data.startsWith('data:audio/')) {
        const a = new Audio(d.data);
        a.volume = gv;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.play().catch(function(e) { if (a.parentNode) a.parentNode.removeChild(a); });
        return;
      }
    } catch (e) { /* fall through to preset */ }
  }
  const preset = localStorage.getItem('notificationSoundPreset') || 'classic';
  try {
    const ctx = await _getAudioCtx();
    const now = ctx.currentTime;
    const n = (freq, start, dur, type = 'sine') => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = type; o.frequency.value = freq;
      g.gain.setValueAtTime(gv, now + start);
      g.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
      o.connect(g); g.connect(ctx.destination); o.start(now + start); o.stop(now + start + dur);
    };
    const p = (freq, start, dur) => n(freq, start, dur, 'sine');
    if (preset === 'classic') {
      p(523, 0, 0.08); p(659, 0.1, 0.08);
      p(784, 0.2, 0.08); p(1047, 0.3, 0.25);
      p(784, 0.55, 0.12); p(1047, 0.7, 0.35);
    } else if (preset === 'marimba') {
      [523, 587, 659, 784, 880, 1047].forEach((f, i) => p(f, i * 0.14, 0.1));
      p(1175, 0.84, 0.2); p(1047, 1.0, 0.3);
    } else if (preset === 'xylophone') {
      [392, 440, 494, 523, 587, 659, 784, 880].forEach((f, i) => p(f, i * 0.1, 0.08));
    } else if (preset === 'alarm') {
      for (let i = 0; i < 8; i++) n(i % 2 ? 880 : 660, i * 0.2, 0.13, 'square');
    } else if (preset === 'ascending') {
      [262, 330, 392, 523, 659, 784].forEach((f, i) => p(f, i * 0.15, 0.12));
      p(1047, 0.9, 0.4);
    } else if (preset === 'descending') {
      [1047, 784, 659, 523, 392, 330, 262].forEach((f, i) => p(f, i * 0.14, 0.12));
    } else if (preset === 'sms') {
      p(880, 0, 0.06); p(1175, 0.08, 0.06);
      p(880, 0.16, 0.06); p(1175, 0.24, 0.08);
    } else if (preset === 'digital') {
      [660, 880, 660, 880, 1047].forEach((f, i) => n(f, i * 0.1, 0.06, 'square'));
      p(1319, 0.5, 0.25);
    } else {
      p(440, 0, 0.15); p(554, 0.18, 0.15);
      p(659, 0.36, 0.15); p(880, 0.54, 0.35);
    }
  } catch (e) { /* silent fallback */ }
}

var _orderUpdateListenerAttached = false;
async function startGlobalPoll() {
  if (globalPollTimer) clearInterval(globalPollTimer);
  if (pendingPollTimer) clearInterval(pendingPollTimer);
  await loadRingtones();
  // SSE-driven — guard against duplicate listeners on re-login
  if (!_orderUpdateListenerAttached) {
    _orderUpdateListenerAttached = true;
    document.addEventListener('order-update', function(e) {
      if (!currentUser || document.hidden) return;
      var data = e.detail;
      if (data && Array.isArray(data.orders)) {
        var pending = data.orders.filter(function(o) { var s = (o.state || o.completionStatus || o.orderStatus || o.status || '').toLowerCase(); return s !== 'completed' && s !== 'canceled' && s !== 'cancelled'; });
        if (currentPage === 'orders' || currentPage === 'dashboard') {
          checkPaymentReminders(pending);
          compareOrderChanges(data.orders);
        }
        var completed = data.orders.filter(function(o) { var s = (o.state || o.completionStatus || o.orderStatus || o.status || '').toLowerCase(); return s === 'completed'; });
        if (completed.length > 0) API.post('/api/bank/auto-sync', { orders: completed }).catch(function() {});
      }
    });
  }
}

function startPagePoll(container, fn, ms) {
  if (pagePollTimer) clearInterval(pagePollTimer);
  pagePollTimer = setInterval(() => {
    if (!currentUser || !document.body.contains(container) || document.hidden) return;
    // Re-entrancy guard: if the previous run is still in flight (slow network),
    // skip this tick instead of stacking another full render + HTTP flood.
    if (fn._running) return;
    fn._running = true;
    var p;
    try { p = fn(); } catch (e) { fn._running = false; return; }
    if (p && typeof p.then === 'function') {
      p.then(function() { fn._running = false; }, function() { fn._running = false; });
    } else {
      fn._running = false;
    }
  }, ms);
}

function triggerPageRefresh() {
  // Re-render current page immediately after an action
  const container = document.getElementById('page-content');
  if (!container) return;
  if (currentPage === 'dashboard') renderDashboard(container);
  else if (currentPage === 'orders') { renderOrders(container); if (typeof fetchOrderStats === 'function') fetchOrderStats(); }
  else return;
  // Also reset the page countdown timer
  const cd = document.getElementById('page-countdown');
  if (cd) cd.textContent = '';
}

// ===== OKX API Routes =====
var routeMap = {
  '/': 'general-dashboard', '/login': 'login', '/dashboard': 'dashboard', '/home': 'dashboard', '/general': 'general-dashboard',
  '/okx/dashboard': 'dashboard', '/okx/home': 'dashboard',
  '/price-explorer': 'price-explorer', '/okx/price-explorer': 'price-explorer',
  '/ads': 'ads', '/ads/mine': 'ads', '/okx/ads': 'ads', '/okx/ads/mine': 'ads',
  '/orders': 'orders', '/pending': 'orders', '/unreleased': 'orders',
  '/okx/orders': 'orders', '/okx/pending': 'orders', '/okx/unreleased': 'orders',
  '/messages': 'messages', '/okx/messages': 'messages',
  '/bank': 'bank', '/bank/transactions': 'bank-transactions', '/bank/balance': 'bank-balance',
  '/okx/bank': 'bank', '/okx/bank/transactions': 'bank-transactions', '/okx/bank/balance': 'bank-balance',
  '/generalsupport': 'generalsupport', '/disputes': 'generalsupport', '/blacklist': 'generalsupport', '/support': 'generalsupport',
  '/settings': 'settings', '/settings/profile': 'settings-profile', '/settings/api': 'settings-api',
  '/profile': 'profile',
  '/settings/payment-reminder': 'settings-payment-reminder', '/settings/notifications': 'settings-notifications',
  '/settings/advanced': 'settings-advanced', '/settings/account': 'settings-account',
  '/settings/api-config': 'settings-api', '/settings/passkey': 'settings-passkey', '/settings/about': 'settings-about',
  '/settings/price-alerts': 'settings-price-alerts', '/settings/webhooks': 'settings-webhooks',
  '/pricing': 'pricing', '/reviews': 'reviews', '/funzone': 'reviews',
  '/admin': 'admin', '/admin/suggestions': 'admin-suggestions',
  '/support-admin': 'support-admin', '/automation': 'automation', '/okx/automation': 'automation',
  '/merchant': 'merchant', '/merchant/:userId': 'merchant-profile',
  '/okx/merchant': 'merchant', '/okx/merchant/:userId': 'merchant-profile',
  '/insights': 'insights', '/okx/insights': 'insights',
  '/market': 'market', '/okx/market': 'market', '/contacts': 'contacts', '/okx/contacts': 'contacts', '/qr-scan': 'qr-scan', '/binance': 'binance-hub', '/okx/binance': 'binance-hub',
  // ===== Bybit Routes =====
  '/bybit/dashboard': 'bybit-dashboard', '/bybit/ads': 'bybit-ads',
  '/bybit/orders': 'bybit-orders', '/bybit/trailing': 'bybit-trailing', '/bybit/settings': 'bybit-settings',
  '/bybit/balances': 'bybit-balances', '/bybit/account': 'bybit-account', '/bybit/order-detail': 'bybit-order-detail',
  // ===== Binance Routes =====
  '/binance/dashboard': 'binance-dashboard', '/binance/ads': 'binance-ads',
  '/binance/my-ads': 'binance-my-ads', '/binance/trailing': 'binance-trailing',
  '/binance/pay': 'binance-pay', '/binance/deposits': 'binance-deposits',
  '/binance/fiat-deposits': 'binance-fiat-deposits', '/binance/money-flow': 'binance-money-flow',
  '/binance/outgoing': 'binance-outgoing', '/binance/orders': 'binance-orders', '/binance/settings': 'binance-settings',
  '/binance/balances': 'binance-balances'
};
var reverseRouteMap = {
  // ===== OKX API =====
  login: '/login', dashboard: '/okx/dashboard', home: '/okx/dashboard', 'general-dashboard': '/general',
  'price-explorer': '/okx/price-explorer',
  ads: '/okx/ads', orders: '/okx/orders', pending: '/okx/orders', unreleased: '/okx/orders',
  messages: '/okx/messages', bank: '/okx/bank',
  'bank-transactions': '/okx/bank/transactions', 'bank-balance': '/okx/bank/balance',
  generalsupport: '/generalsupport',
  settings: '/settings', 'settings-profile': '/settings/profile', 'settings-api': '/settings/api',
  profile: '/profile',
  'settings-payment-reminder': '/settings/payment-reminder', 'settings-notifications': '/settings/notifications',
  'settings-advanced': '/settings/advanced', 'settings-account': '/settings/account',
  'settings-api': '/settings/api-config', 'settings-passkey': '/settings/passkey',
  'settings-about': '/settings/about',
  'settings-price-alerts': '/settings/price-alerts', 'settings-webhooks': '/settings/webhooks',
  pricing: '/pricing', reviews: '/reviews',
  'support-admin': '/support-admin', admin: '/admin', 'admin-suggestions': '/admin/suggestions',
  automation: '/okx/automation', merchant: '/okx/merchant', 'merchant-profile': '/okx/merchant',
  insights: '/okx/insights',
  // ===== Bybit =====
  'bybit-dashboard': '/bybit/dashboard', 'bybit-ads': '/bybit/ads',
  'bybit-orders': '/bybit/orders', 'bybit-trailing': '/bybit/trailing', 'bybit-settings': '/bybit/settings',
  'bybit-balances': '/bybit/balances', 'bybit-account': '/bybit/account', 'bybit-order-detail': '/bybit/order-detail',
  // ===== Binance =====
  'binance-dashboard': '/binance/dashboard', 'binance-ads': '/binance/ads',
  'binance-my-ads': '/binance/my-ads', 'binance-trailing': '/binance/trailing',
  'binance-pay': '/binance/pay', 'binance-deposits': '/binance/deposits',
  'binance-fiat-deposits': '/binance/fiat-deposits', 'binance-money-flow': '/binance/money-flow',
  'binance-outgoing': '/binance/outgoing', 'binance-orders': '/binance/orders', 'binance-settings': '/binance/settings',
  'binance-balances': '/binance/balances'
};

function getPageFromPath(path) {
  try {
    const cleanPath = path.startsWith('/') ? path : '/' + path;
    if (cleanPath.indexOf('/qr/') === 0) return 'qr-approve';
    return routeMap[cleanPath] || 'general-dashboard';
  } catch (e) { return 'general-dashboard'; }
}

function getPathFromPage(page) {
  try {
    return reverseRouteMap[page] || '/general';
  } catch (e) { return '/general'; }
}

function updateUrl(page, query, replace = false) {
  const path = getPathFromPage(page);
  const full = query ? path + query : path;
  if (replace) {
    history.replaceState({ page }, '', full);
  } else {
    history.pushState({ page }, '', full);
  }
}

// Login page render function
async function renderLogin(content) {
  // Hide navbar, show auth container
  document.getElementById('navbar').classList.add('hidden');
  document.getElementById('auth-container').classList.remove('hidden');
  content.innerHTML = ''; // Clear page content since auth is separate
  
  ['register-form', 'forgot-form', 'identify-form', 'reset-form', 'otp-form'].forEach(id => document.getElementById(id).classList.add('hidden'));
  document.getElementById('login-form').classList.remove('hidden');
  API.clearToken();
  setTimeout(function () { if (typeof loadCaptcha === 'function') loadCaptcha('login'); }, 200);
}

// Handle browser back/forward — disabled by default to prevent accidental navigation loops
window.addEventListener('popstate', (event) => {
  if (window._popstateDisabled) return; // APK/browser toggle to fully disable back-navigation
  if (window._popstateBusy) { history.forward(); return; } // re-entrant guard
  if (Date.now() - (window._lastNavTime || 0) < 800) return; // debounce
  if (window._navRetryTimer) return; // don't popstate during error recovery
  window._popstateBusy = true;
  try {
    let targetPage = event.state && event.state.page ? event.state.page : getPageFromPath(window.location.pathname);
    if (!targetPage || targetPage === currentPage || targetPage === 'login') return;
    navigate(targetPage, true);
  } finally { window._popstateBusy = false; }
});

window._popstateDisabled = true; // permanently disable browser back/forward navigation to prevent accidental page shifts
window._merchantUserId = null;
window.navigateToMerchant = function(userId) {
  window._merchantUserId = userId;
  navigate('merchant-profile');
};

function loadScript(url) {
  return new Promise(function(resolve, reject) {
    var s = document.createElement('script');
    s.src = url;
    s.onload = resolve;
    s.onerror = function() { reject(new Error('Failed to load ' + url)); };
    document.head.appendChild(s);
  });
}

async function loadSettingsIfNeeded(content, tab) {
  if (typeof renderSettings !== 'function') {
    try {
      await loadScript('/js/modules/settings.js?v=20260716b&_=' + Date.now());
    } catch(e) {
      content.innerHTML = '<div class="card" style="text-align:center;padding:60px 20px;"><div style="font-size:48px;margin-bottom:16px;">⚠️</div><h3>Settings failed to load</h3><p style="color:var(--text-tertiary);">' + esc(e.message) + '</p><button class="btn btn-primary" style="margin-top:16px;" onclick="location.reload()">Reload Page</button></div>';
      return;
    }
  }
  await renderSettings(content, tab);
}

// Lazy module loader: heavy modules (admin panel, insights) load on demand so
// regular users don't download ~95KB of admin-only JS at boot.
window.MODULE_VERSIONS = { exchangeDashboards: '20260823a', 'okx/insights': '20260727a', 'bybit/bybit-pages': '20260823a', 'binance/binance-pages': '20260823a', 'binance/binance-trailing': '20260823a', admin: '20260823a', 'admin-support': '20260822d', 'admin-suggestions': '20260822d' };
window._loadedModules = {};
window.ensureModules = function(names) {
  return Promise.all(names.map(function(n) {
    if (!window._loadedModules[n]) {
      window._loadedModules[n] = new Promise(function(resolve, reject) {
        var s = document.createElement('script');
        s.src = '/js/modules/' + n + '.js?v=' + (window.MODULE_VERSIONS[n] || '1');
        s.onload = function() { resolve(); };
        s.onerror = function() { delete window._loadedModules[n]; reject(new Error('Failed to load module: ' + n)); };
        document.head.appendChild(s);
      });
    }
    return window._loadedModules[n];
  }));
};

async function navigate(page, paramsOrSkip, skipHistory) {
  var query = '';
  if (typeof page === 'string') {
    var qi = page.indexOf('?');
    if (qi !== -1) { query = page.slice(qi); page = page.slice(0, qi); }
  }
  var params = {};
  if (typeof paramsOrSkip === 'boolean') { skipHistory = paramsOrSkip; }
  else { params = paramsOrSkip || {}; skipHistory = skipHistory || false; }
  // Assign unique ID to each navigate call to prevent stale retries
  if (window._navId === undefined) window._navId = 0;
  var currentNavId = ++window._navId;
  // Cancel any pending retry from a previous failed navigate
  if (window._navRetryTimer) { clearTimeout(window._navRetryTimer); window._navRetryTimer = null; }
  if (window._navRetryCount === undefined) window._navRetryCount = 0;
  if (page === window._navRetryPage) { window._navRetryCount++; } else { window._navRetryCount = 0; }
  window._navRetryPage = page;
  currentPage = page;
  window._lastNavTime = Date.now();
  window._ordersPageOpen = (page === 'orders' || page === 'pending' || page === 'unreleased');
  var aiBubble = document.getElementById('ai-chat-bubble');
  if (aiBubble) aiBubble.style.display = window._ordersPageOpen ? 'none' : '';
  if (pagePollTimer) { clearInterval(pagePollTimer); pagePollTimer = null; }
  if (ordersErrorTimer) { clearTimeout(ordersErrorTimer); ordersErrorTimer = null; }
  if (page !== 'orders' && page !== 'pending' && page !== 'unreleased') { delete window.__cachedOrders; }
  // Clear leaking page-level timers on navigation away
  if (window._chatPollTimer) { clearInterval(window._chatPollTimer); window._chatPollTimer = null; }
  if (window._trailingLogTimer) { clearInterval(window._trailingLogTimer); window._trailingLogTimer = null; }
  if (window._bybitTrailingLogTimer) { clearInterval(window._bybitTrailingLogTimer); window._bybitTrailingLogTimer = null; }
  if (window._binanceTrailingLogTimer) { clearInterval(window._binanceTrailingLogTimer); window._binanceTrailingLogTimer = null; }
  if (window._autoAdLogTimer) { clearInterval(window._autoAdLogTimer); window._autoAdLogTimer = null; }
  if (window._moneyViewTimer) { clearInterval(window._moneyViewTimer); window._moneyViewTimer = null; }
  if (window._adminCallTimer) { clearInterval(window._adminCallTimer); window._adminCallTimer = null; }
  if (window._callRingingTimer) { clearInterval(window._callRingingTimer); window._callRingingTimer = null; }
  var _ringingOverlay = document.getElementById('call-ringing-overlay');
  if (_ringingOverlay) _ringingOverlay.remove();
  if (window._callTimerInterval) { clearInterval(window._callTimerInterval); window._callTimerInterval = null; }
  if (window._otpCooldownTimer) { clearInterval(window._otpCooldownTimer); window._otpCooldownTimer = null; }
  if (window._ordersTimerTick) { clearInterval(window._ordersTimerTick); window._ordersTimerTick = null; }
  if (window._automationPollTimer) { clearInterval(window._automationPollTimer); window._automationPollTimer = null; }
  
  if (!skipHistory) updateUrl(page, query);
  closeSidebar();
  const notifPanel = document.getElementById('notif-panel');
  if (notifPanel) notifPanel.classList.add('hidden');
  
  const content = document.getElementById('page-content');
  content.innerHTML = '<div class="page-loading"><div class="spinner"></div><span>Loading...</span></div>';
  content.classList.remove('page-enter');
  requestAnimationFrame(function() { content.classList.add('page-enter'); });
  var navLinks = document.querySelectorAll('.nav-links a, #sidebar .sidebar-links a');
  navLinks.forEach(function(a) {
    const attr = a.getAttribute('data-on-click') || a.getAttribute('onclick') || '';
    if (attr.includes("'" + page + "'") || attr.includes('"' + page + '"')) a.classList.add('active');
    else a.classList.remove('active');
  });
  try {
    switch (page) {
      // ===== Login =====
      case 'login': await renderLogin(content); break;

      // QR device-login approval (phone scans /qr/<code>)
      case 'qr-approve': {
        const m = (location.pathname || '').match(/^\/qr\/([A-Za-z0-9_-]+)/);
        await window.renderQrApprove(content, m ? m[1] : '');
        break;
      }

      // ===== General =====
      case 'general-dashboard': await ensureModules(['exchangeDashboards']); content.innerHTML = '<div id="general-page-content"></div>'; await renderGeneralDashboard(content); break;

      // ===== OKX API =====
      case 'home':
      case 'dashboard': await renderDashboard(content); break;
      case 'price-explorer': await renderPriceExplorer(content); break;
      case 'ads': await renderAds(content); break;
      case 'orders':
      case 'pending':
      case 'unreleased':
        await renderOrders(content); if (typeof fetchOrderStats === 'function') fetchOrderStats();
        // Independent SSE fallback: refresh the orders page every 5s regardless of
        // SSE health. SSE pushes re-render on every change anyway, so this only
        // shows on flaky connections where SSE dies (mobile/WebView) — bounding
        // stale orders to ~5s instead of the old 30s (which is exactly the
        // ">30 seconds for the order to appear" symptom). It polls the WARM
        // per-user cache via _refreshOrdersFromCache (scheduleRender re-paints
        // the open orders page) — NOT renderOrders(content, null, true), which
        // forced a cold refresh=1 OKX round-trip on every tick.
        if (typeof startPagePoll === 'function' && typeof _refreshOrdersFromCache === 'function' && !pagePollTimer) {
          startPagePoll(content, _refreshOrdersFromCache, 5000);
        }
        break;
      case 'disputes':
      case 'generalsupport': await renderGeneralSupport(content); break;
      case 'reviews': await renderReviews(content); break;
      case 'messages': await renderChat(content); break;
      case 'bank':
      case 'bank-transactions': _bankTab = 'transactions'; await renderBank(content); break;
      case 'bank-balance': _bankTab = 'balance'; await renderBank(content); break;
      case 'settings': await loadSettingsIfNeeded(content); break;
      case 'settings-profile': await renderProfilePage(content); break;
      case 'profile': await renderProfilePage(content); break;
      case 'settings-payment-reminder': await loadSettingsIfNeeded(content, 'payment-reminder'); break;
      case 'settings-notifications': await loadSettingsIfNeeded(content, 'notifications'); break;
      case 'settings-advanced': await loadSettingsIfNeeded(content, 'advanced'); break;
      case 'settings-account': await loadSettingsIfNeeded(content, 'account'); break;
      case 'settings-api': await loadSettingsIfNeeded(content, 'api'); break;
      case 'settings-passkey': await loadSettingsIfNeeded(content, 'passkey'); break;
      case 'settings-price-alerts': await loadSettingsIfNeeded(content, 'price-alerts'); break;
      case 'settings-webhooks': await loadSettingsIfNeeded(content, 'webhooks'); break;
      case 'settings-about': await loadSettingsIfNeeded(content, 'about'); break;
      case 'admin': await ensureModules(['admin']); await renderAdmin(content); break;
      case 'support-admin': await ensureModules(['admin-support']); await renderAdminSupport(content); break;
      case 'admin-suggestions': await ensureModules(['admin-suggestions']); await renderAdminSuggestions(content); break;
      case 'pricing': await renderPricing(content); break;
      case 'insights': await ensureModules(['okx/insights']); await renderInsights(content); break;
      case 'market': await renderMarket(content); break;
      case 'contacts': await renderContacts(content); break;
      case 'qr-scan': await window.renderQrScanner(content); break;
      case 'binance-hub': await renderBinanceHub(content); break;
      case 'automation': await renderAutomation(content); break;
      case 'merchant':
      case 'merchant-profile': await renderMerchantProfile(content, params); break;

      // ===== Bybit =====
      case 'bybit-dashboard': await ensureModules(['bybit/bybit-pages']); content.innerHTML = '<div id="bybit-page-content"></div>'; await renderBybitDashboard(content); break;
      case 'bybit-ads': await ensureModules(['bybit/bybit-pages']); content.innerHTML = '<div id="bybit-page-content"></div>'; await renderBybitAds(content); break;
      case 'bybit-orders': await ensureModules(['bybit/bybit-pages']); content.innerHTML = '<div id="bybit-page-content"></div>'; await renderBybitOrders(content); break;
      case 'bybit-trailing': await ensureModules(['bybit/bybit-pages']); content.innerHTML = '<div id="bybit-page-content"></div>'; await renderBybitTrailing(content); break;
      case 'bybit-balances': await ensureModules(['bybit/bybit-pages']); content.innerHTML = '<div id="bybit-page-content"></div>'; await renderBybitBalances(content); break;
      case 'bybit-account': await ensureModules(['bybit/bybit-pages']); content.innerHTML = '<div id="bybit-page-content"></div>'; await renderBybitAccount(content); break;
      case 'bybit-order-detail': await ensureModules(['bybit/bybit-pages']); content.innerHTML = '<div id="bybit-page-content"></div>'; await renderBybitOrderDetail(content); break;

      // ===== Binance =====
      case 'binance-dashboard': await ensureModules(['binance/binance-pages','binance/binance-trailing']); content.innerHTML = '<div id="binance-page-content"></div>'; await renderBinanceDashboard(content); break;
      case 'binance-ads': await ensureModules(['binance/binance-pages','binance/binance-trailing']); content.innerHTML = '<div id="binance-page-content"></div>'; await renderBinanceAds(content); break;
      case 'binance-my-ads': await ensureModules(['binance/binance-pages','binance/binance-trailing']); content.innerHTML = '<div id="binance-page-content"></div>'; await renderBinanceMyAds(content); break;
      case 'binance-trailing': await ensureModules(['binance/binance-pages','binance/binance-trailing']); content.innerHTML = '<div id="binance-page-content"></div>'; await renderBinanceTrailing(content); break;
      case 'binance-pay': await ensureModules(['binance/binance-pages','binance/binance-trailing']); content.innerHTML = '<div id="binance-page-content"></div>'; await renderBinancePayTransactions(content); break;
      case 'binance-deposits': await ensureModules(['binance/binance-pages','binance/binance-trailing']); content.innerHTML = '<div id="binance-page-content"></div>'; await renderBinanceDeposits(content); break;
      case 'binance-fiat-deposits': await ensureModules(['binance/binance-pages','binance/binance-trailing']); content.innerHTML = '<div id="binance-page-content"></div>'; await renderBinanceFiatDeposits(content); break;
      case 'binance-money-flow': await ensureModules(['binance/binance-pages','binance/binance-trailing']); content.innerHTML = '<div id="binance-page-content"></div>'; await renderBinanceMoneyFlow(content); break;
      case 'binance-outgoing': await ensureModules(['binance/binance-pages','binance/binance-trailing']); content.innerHTML = '<div id="binance-page-content"></div>'; await renderBinanceOutgoingMoneyFlow(content); break;
      case 'binance-orders': await ensureModules(['binance/binance-pages','binance/binance-trailing']); content.innerHTML = '<div id="binance-page-content"></div>'; await renderBinanceOrders(content); break;
      case 'binance-balances': await ensureModules(['binance/binance-pages','binance/binance-trailing']); content.innerHTML = '<div id="binance-page-content"></div>'; await renderBinanceBalances(content); break;
      case 'binance-settings':
      case 'bybit-settings': await loadSettingsIfNeeded(content, 'api'); break;

      default: content.innerHTML = '<h2>Page not found</h2>';
    }
  } catch (err) {
    content.innerHTML = `<div class="card" style="text-align:center;padding:40px;"><div style="font-size:48px;margin-bottom:16px;">⚠️</div><h3>Page error</h3><p style="color:var(--text-tertiary);font-size:13px;">${esc(err.name)}: ${esc(err.message)}</p><button class="btn btn-primary" style="margin-top:16px;" onclick="location.reload()">Reload Page</button></div>`;
  }
  if (typeof bindClickHandlers === 'function') setTimeout(function() { bindClickHandlers(); }, 100);
}

/* ==== js/modules/auth-ui.js ==== */
function _setOtpMsg(msg) {
  var el = document.getElementById('otp-message');
  if (!el) return;
  var m = msg.match(/\b(\d{6})\b/);
  if (m) {
    var safe = msg.replace(m[1], '<span style="cursor:pointer;color:#00c853;font-weight:700;font-size:18px;letter-spacing:4px;" onclick="navigator.clipboard.writeText(\'' + m[1].replace(/['"<>]/g, '') + '\').then(function(){showToast(\'OTP copied\')},function(){})">' + m[1].replace(/['"<>]/g, '') + '</span>');
    el.innerHTML = safe;
  } else {
    el.textContent = msg;
  }
}
var _forgotEmail;
function _showApp() {
  document.getElementById('auth-container').classList.add('hidden');
  document.getElementById('page-content').classList.remove('hidden');
  document.getElementById('navbar').classList.remove('hidden');
}
function _startGlobalPoll() {
  if (typeof startGlobalPoll === 'function') startGlobalPoll();
}
function _navigate(page) {
  if (typeof navigate === 'function') { try { navigate(page).catch(function(e) { console.error('navigate error:', e); }); } catch (e) { console.error('navigate error:', e); } }
  else {
    currentPage = page;
    var el = document.getElementById('page-content');
    if (el) el.innerHTML = '<p style="text-align:center;color:#888;padding:40px;">Loading...</p>';
  }
}
function _updateUserUI() {
  if (typeof updateUserUI === 'function') updateUserUI();
}
// ── Persistent OTP/2FA temp tokens ──
// These live only in JS memory otherwise, so ANY app reload mid-login (FCM tap,
// WebView restart, force-close) wiped them and showed "Session expired". Keep
// them in localStorage with a short TTL matching the server's temp-token life.
function _savePendingToken(tok) {
  try { localStorage.setItem('_ptt', JSON.stringify({ t: tok, exp: Date.now() + 600000 })); } catch (e) {}
  window._pendingTempToken = tok;
}
function _loadPendingToken() {
  if (window._pendingTempToken) return window._pendingTempToken;
  try {
    var raw = localStorage.getItem('_ptt');
    if (!raw) return null;
    var o = JSON.parse(raw);
    if (!o || !o.t || !o.exp || o.exp < Date.now()) { localStorage.removeItem('_ptt'); return null; }
    window._pendingTempToken = o.t;
    return o.t;
  } catch (e) { return null; }
}
function _clearPendingToken() {
  window._pendingTempToken = null;
  try { localStorage.removeItem('_ptt'); } catch (e) {}
}
function _save2FAToken(tok) {
  try { localStorage.setItem('_tfa', JSON.stringify({ t: tok, exp: Date.now() + 600000 })); } catch (e) {}
  window._tempToken2FA = tok;
}
function _load2FAToken() {
  if (window._tempToken2FA) return window._tempToken2FA;
  try {
    var raw = localStorage.getItem('_tfa');
    if (!raw) return null;
    var o = JSON.parse(raw);
    if (!o || !o.t || !o.exp || o.exp < Date.now()) { localStorage.removeItem('_tfa'); return null; }
    window._tempToken2FA = o.t;
    return o.t;
  } catch (e) { return null; }
}
function _clear2FAToken() {
  window._tempToken2FA = null;
  try { localStorage.removeItem('_tfa'); } catch (e) {}
}

function completeAuth(res) {
  const d = res.data;
  API.setToken(d.token);
  currentUser = d.user;
  _clearPendingToken();
  _clear2FAToken();
  if (d.deviceToken) localStorage.setItem('deviceToken', d.deviceToken);
  applyTheme(localStorage.getItem('theme') || currentUser.theme || 'dark');
  _updateUserUI();
  _showApp();
  if (currentUser.role === 'admin') { document.getElementById('globalAdminLink')?.classList.remove('hidden'); document.getElementById('globalTicketsLink')?.classList.remove('hidden'); document.getElementById('globalSuggestLink')?.classList.remove('hidden'); ['hubAdminLink', 'hubTicketsLink', 'hubSuggestLink', 'hubAdminDivider'].forEach(function(id) { document.getElementById(id)?.classList.remove('hidden'); }); }
  showToast('Welcome, ' + currentUser.name, 'success');
  setTimeout(function() {
    _navigate('general-dashboard');
    _startGlobalPoll();
    if (window.__pendingPushNav) {
      var pendingOrder = window.__pendingPushNav;
      window.__pendingPushNav = null;
      setTimeout(function() { try { window.__handlePushNav && window.__handlePushNav(pendingOrder); } catch (e) {} }, 600);
    }
    if ('Notification' in window && Notification.permission === 'default') {
      setTimeout(() => Notification.requestPermission().catch(() => {}), 1000);
    }
    if ('Notification' in window && localStorage.getItem('nativeNotifications') !== 'false' && Notification.permission === 'default') {
      showToast('Enable desktop notifications in Settings > Payment Reminder', 'info', 8000);
    }
    promptSecuritySetup();
  }, 300);
}

async function promptSecuritySetup() {
  if (localStorage.getItem('securityPromptSkipped')) return;
  localStorage.removeItem('passkeyPromptSkipped');
  try {
    var r = await API.get('/api/auth/security-status');
    if (r.code !== 0) return;
    var status = r.data;
    if (status.hasBoth) return;
    var passkeySupported = !!(isApk() || window.PublicKeyCredential || apkPasskeyAvailable());
    var needsPasskey = !status.hasPasskey && passkeySupported;
    var needs2FA = !status.has2FA;
    if (!needsPasskey && !needs2FA) return;
    var overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    var html = '<div class="modal-content" style="max-width:420px;text-align:center;">';
    html += '<h3>🔐 Secure Your Account</h3>';
    html += '<p style="color:var(--text-tertiary);margin:16px 0;">Add extra security to your account. You can set up both or skip for now.</p>';
    if (needsPasskey) {
      html += '<div style="background:rgba(68,138,255,0.08);border:1px solid rgba(68,138,255,0.2);border-radius:12px;padding:14px;margin:8px 0;text-align:left;">';
      html += '<div style="font-size:15px;font-weight:600;color:var(--text-primary);">🔑 Passkey Login</div>';
      html += '<div style="font-size:12px;color:var(--text-tertiary);margin:4px 0 10px;">Fingerprint, Face ID, or PIN — faster login</div>';
      html += '<button class="btn btn-primary btn-sm" data-on-click="setupPasskeyFromPrompt();document.querySelector(\'.security-2fa-section\').style.display=\'block\'">Set Up Passkey</button></div>';
    }
    if (needs2FA) {
      html += '<div class="security-2fa-section" style="background:rgba(0,200,83,0.08);border:1px solid rgba(0,200,83,0.2);border-radius:12px;padding:14px;margin:8px 0;text-align:left;' + (needsPasskey ? 'display:none;' : '') + '">';
      html += '<div style="font-size:15px;font-weight:600;color:var(--text-primary);">🔐 Two-Factor Auth (2FA)</div>';
      html += '<div style="font-size:12px;color:var(--text-tertiary);margin:4px 0 10px;">Google Authenticator, Authy, etc.</div>';
      html += '<button class="btn btn-primary btn-sm" data-on-click="setup2FAFromPrompt()">Set Up 2FA</button></div>';
    }
    html += '<div style="margin-top:12px;"><button class="btn btn-secondary btn-sm" data-on-click="skipSecurityPrompt()">Skip</button></div>';
    html += '</div>';
    overlay.innerHTML = html;
    document.body.appendChild(overlay);
  } catch {}
}

window.setup2FAFromPrompt = function() {
  var overlay = document.querySelector('.modal-overlay');
  if (overlay) overlay.remove();
  navigate('settings');
  setTimeout(function() {
    var passkeyTab = document.querySelector('[data-tab="passkey"]');
    if (passkeyTab) passkeyTab.click();
    setTimeout(function() {
      var enableBtn = document.querySelector('[data-on-click="setup2FA()"]');
      if (enableBtn) enableBtn.click();
    }, 300);
  }, 500);
};

window.skipSecurityPrompt = function() {
  localStorage.setItem('securityPromptSkipped', '1');
  var overlay = document.querySelector('.modal-overlay');
  if (overlay) overlay.remove();
};

window.setupPasskeyFromPrompt = async function() {
  var overlay = document.querySelector('.modal-overlay');
  if (overlay) overlay.remove();
  if (!window._apkGetPublicKeyCredential()) { showToast('Passkey not supported on this browser', 'error'); return; }
  try {
    var deviceName = typeof detectDeviceName === 'function' ? detectDeviceName() : 'Unknown';
    if (isApk() && window._apkHasCredMan && window._apkHasCredMan()) {
      // Google Password Manager passkey via the native Credential Manager
      // bridge — synced across every device signed into this Google account.
      var cmBegin = await API.passkeyRegisterBegin();
      if (!cmBegin || cmBegin.code !== 0) { showToast(cmBegin?.msg || 'Failed to start passkey registration', 'error'); return; }
      var credJson = await _apkCredManCreate(JSON.stringify(cmBegin.data));
      var regResp = typeof credJson === 'string' ? JSON.parse(credJson) : credJson;
      if (regResp.error) throw new Error(regResp.error);
      regResp.deviceName = deviceName + ' · Google synced';
      var cmComplete = await API.passkeyRegisterComplete(regResp);
      if (cmComplete.code === 0) showToast('✅ Passkey saved to Google — works on all your signed-in devices!', 'success');
      else showToast(cmComplete.msg || 'Passkey registration failed', 'error');
      return;
    }
    if (isApk() && !(window._apkHasNativeWebAuthn && window._apkHasNativeWebAuthn())) {
      if (typeof _apkStandaloneRegister !== 'function') {
        showToast('Standalone passkey not available', 'error');
        return;
      }
      var beginRes = await API.passkeyStandaloneRegisterBegin();
      if (beginRes.code !== 0) { showToast(beginRes.msg || 'Failed', 'error'); return; }
      var apkData = await _apkStandaloneRegister(String(currentUser.id), String(currentUser.email || ''), beginRes.data.challenge, beginRes.data.tempToken);
      var completeRes = await API.passkeyStandaloneRegisterComplete({ tempToken: apkData.tempToken, credentialId: apkData.credentialId, publicKeySpki: apkData.publicKeySpki, clientSignature: apkData.clientSignature });
      if (completeRes.code === 0) showToast('✅ Passkey registered!', 'success');
      else showToast('Passkey registration failed', 'error');
      return;
    }
    var beginRes = await API.passkeyRegisterBegin();
    var opts = beginRes.data;
    opts.challenge = base64urlToBuffer(opts.challenge);
    opts.user.id = base64urlToBuffer(opts.user.id);
    opts.excludeCredentials = (opts.excludeCredentials || []).map(function(c) { c.id = base64urlToBuffer(c.id); return c; });
    var cred = await navigator.credentials.create({ publicKey: opts });
    var resp = {
      id: cred.id, rawId: bufferToBase64url(cred.rawId), type: cred.type, deviceName: deviceName,
      response: {
        clientDataJSON: bufferToBase64url(cred.response.clientDataJSON),
        attestationObject: bufferToBase64url(cred.response.attestationObject),
        transports: cred.response.getTransports ? cred.response.getTransports() : []
      }
    };
    var completeRes = await API.passkeyRegisterComplete(resp);
    if (completeRes.code === 0) showToast('✅ Passkey registered! Next time use fingerprint/Face ID to login.', 'success');
    else showToast('Passkey registration failed', 'error');
  } catch (err) { showToast(err.message || 'Passkey registration failed', 'error'); }
};

window.skipPasskeyPrompt = function() {
  localStorage.setItem('passkeyPromptSkipped', '1');
  var overlay = document.querySelector('.modal-overlay');
  if (overlay) overlay.remove();
};

var _captchaCache = {};
async function loadCaptcha(formId) {
  try {
    if (_captchaCache[formId] && Date.now() - _captchaCache[formId].ts < 60000) {
      var oldEl = document.getElementById('captcha-widget-' + formId);
      if (oldEl && oldEl.querySelector('input')) return;
    }
    var r = await fetch('/api/auth/captcha');
    var d = await r.json();
    var el = document.getElementById('captcha-widget-' + formId);
    var tokenEl = document.getElementById('captcha-token-' + formId);
    if (el) {
      el.innerHTML = '<span style="color:var(--text-tertiary);">What is ' + d.question + '? </span><input type="number" id="captcha-answer-' + formId + '" style="width:70px;padding:6px;border-radius:6px;border:1px solid var(--border-primary);background:var(--bg-primary);color:var(--text-primary);font-size:14px;text-align:center;" required>';
    }
    if (tokenEl) tokenEl.value = d.token;
    _captchaCache[formId] = { ts: Date.now() };
  } catch {}
}
function _showAuthTab(tab) {
    var ids = ['login-form','register-form','forgot-form','identify-form','reset-form','otp-form'];
  ids.forEach(function(i){var f=document.getElementById(i);if(f)f.classList.add('hidden')});
  var el = document.getElementById(tab + '-form');
  if (el) el.classList.remove('hidden');
  if (tab === 'otp') window._applyPendingApkOtp();
  if (tab === 'login' || tab === 'register' || tab === 'forgot') loadCaptcha(tab);
  if (tab === 'login') {
    var pkBtn = document.getElementById('passkey-login-btn');
    if (pkBtn) {
      if (window.PublicKeyCredential || isApk()) {
        pkBtn.style.display = '';
        pkBtn.textContent = '🔑 Sign in with Passkey';
      } else {
        pkBtn.style.display = 'none';
      }
    }
  }
}
async function handleLogin(e) {
  e.preventDefault();
  try {
    const deviceToken = localStorage.getItem('deviceToken') || undefined;
    const username = document.getElementById('login-username').value.trim();
    const email = document.getElementById('login-email').value;
    const phone = document.getElementById('login-phone').value;
    const code = document.getElementById('login-phone-code').value;
    const ph = phone ? code + phone.replace(/[^0-9]/g, '') : '';
    const captchaToken = document.getElementById('captcha-token-login')?.value || '';
    const captchaAnswer = document.getElementById('captcha-answer-login')?.value || '';
    const res = await API.login(email || '', document.getElementById('login-password').value, ph, deviceToken, undefined, undefined, captchaToken, captchaAnswer, username);
    if (res.data.step === 'complete') {
      completeAuth(res);
    } else if (res.data.step === 'verify-2fa') {
      _save2FAToken(res.data.tempToken);
      var msgEl = document.getElementById('tfa-message');
      if (msgEl) msgEl.textContent = res.data.message || 'Enter the 6-digit code from your authenticator app';
      document.getElementById('tfa-code').value = '';
      _showAuthTab('tfa');
    } else if (res.data.step === 'verify-otp') {
      _savePendingToken(res.data.tempToken);
      window._otpMethods = res.data.methods || [];
      window._otpRetryCount = 0;
      window._otpFailureCount = 0;
      window._otpMethod = '';
      document.getElementById('otp-message').textContent = res.data.message || 'Enter the OTP sent to your contact';
      _showAuthTab('otp');
      startOtpCooldown();
      updateOtpFallbackLink();
      if (res.data.smsFailed) { window._otpFailureCount = 1; showToast('SMS failed. Try another method below.', 'error'); }
    } else if (res.data.step === 'choose-method') {
      window._loginPassword = document.getElementById('login-password').value;
      showChooseMethodModal(res.data.user, res.data.methods);
    } else if (res.data.step === 'link-email-to-phone') {
      showLinkEmailModal(res.data.user, email, code);
    }
  } catch (err) { showToast(err.message, 'error'); _captchaCache['login'] = null; loadCaptcha('login'); }
}

async function handleRegister(e) {
  e.preventDefault();
  try {
    const email = document.getElementById('reg-email').value;
    const phoneInput = document.getElementById('reg-phone').value;
    const code = document.getElementById('reg-phone-code').value;
    const ph = phoneInput ? code + phoneInput.replace(/[^0-9]/g, '') : '';
    if (!email && !phoneInput) { showToast('Enter email or phone number', 'error'); return; }
    const password = document.getElementById('reg-password').value;
    if (password.length < 8) { showToast('Password must be at least 8 characters with uppercase, lowercase, number & special character', 'error'); return; }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d])/.test(password)) { showToast('Password must include uppercase, lowercase, number & special character', 'error'); return; }
    const captchaToken = document.getElementById('captcha-token-register')?.value || '';
    const captchaAnswer = document.getElementById('captcha-answer-register')?.value || '';
    const res = await API.register(
      document.getElementById('reg-name').value,
      email || '',
      password,
      document.getElementById('reg-username').value,
      ph,
      captchaToken,
      captchaAnswer
    );
    if (res.data.step === 'verify-otp') {
      _savePendingToken(res.data.tempToken);
      window._otpMethods = res.data.methods || [];
      window._otpRetryCount = 0;
      window._otpFailureCount = 0;
      window._otpMethod = '';
      document.getElementById('otp-message').textContent = res.data.message || 'Enter the OTP sent to your contact';
      _showAuthTab('otp');
      startOtpCooldown();
      updateOtpFallbackLink();
      if (res.data.smsFailed) { window._otpFailureCount = 1; showToast('SMS failed. Try another method below.', 'error'); }
    } else if (res.data.step === 'link-account') {
      showLinkAccountModal(res.data.user, phoneInput, code);
    } else if (res.data.step === 'link-email-to-phone') {
      showLinkEmailModal(res.data.user, email, code);
    }
  } catch (err) { showToast(err.message, 'error'); _captchaCache['register'] = null; loadCaptcha('register'); }
}

function autoSubmitOtp(input) {
  if (input.value.length === 6) {
    var form = input.closest('form');
    if (form) form.requestSubmit();
  }
}

async function handleVerifyOtp(e) {
  e.preventDefault();
  try {
    const code = document.getElementById('otp-code').value.trim();
    if (code.length < 6) { showToast('Enter the 6-digit code', 'error'); return; }
    if (!_loadPendingToken()) { showToast('Session expired. Please register or login again.', 'error'); _showAuthTab('login'); return; }
    const rm = document.getElementById('otp-remember').checked;
    const r = await API.verifyOtp(_loadPendingToken(), code, rm);
    if (r.data.step === 'complete') completeAuth(r);
  } catch (err) {
    if (err.message.includes('expired') || err.message.includes('invalid session')) { showToast('OTP session expired. Please register again.', 'error'); _clearPendingToken(); _showAuthTab('register'); }
    else showToast(err.message, 'error');
  }
}

async function handleVerify2FA(e) {
  e.preventDefault();
  try {
    const code = document.getElementById('tfa-code').value.trim();
    if (code.length < 6) { showToast('Enter the 6-digit code from your authenticator app', 'error'); return; }
    if (!_load2FAToken()) { showToast('Session expired. Please login again.', 'error'); _showAuthTab('login'); return; }
    const rm = document.getElementById('tfa-remember').checked;
    const r = await API.post('/api/auth/verify-2fa', { tempToken: _load2FAToken(), totp: code, rememberDevice: rm });
    if (r.data.step === 'complete') completeAuth(r);
  } catch (err) {
    if (err.message.includes('expired') || err.message.includes('invalid session')) { showToast('2FA session expired. Please login again.', 'error'); _clear2FAToken(); _showAuthTab('login'); }
    else showToast(err.message, 'error');
  }
}

window._otpRetryCount = 0;
window._otpFailureCount = 0;
window._otpCooldownTimer = null;
function getOtpCooldown() {
  var base = 30 + (window._otpRetryCount * 30);
  if (window._otpFailureCount >= 3) base += 60;
  return base;
}
function startOtpCooldown() {
  var el = document.getElementById('otp-resend-link');
  if (!el) return;
  var total = getOtpCooldown();
  var remaining = total;
  el.style.pointerEvents = 'none';
  el.style.opacity = '0.5';
  if (window._otpCooldownTimer) clearInterval(window._otpCooldownTimer);
  window._otpCooldownTimer = setInterval(function () {
    remaining--;
    if (remaining <= 0) {
      clearInterval(window._otpCooldownTimer);
      window._otpCooldownTimer = null;
      el.textContent = 'Resend code';
      el.style.pointerEvents = '';
      el.style.opacity = '';
    } else {
      el.textContent = 'Resend in ' + remaining + 's';
    }
  }, 1000);
  el.textContent = 'Resend in ' + remaining + 's';
}
async function handleResendOtp(e, method) {
  if (e) e.preventDefault();
  var cooldownEl = document.getElementById('otp-resend-link');
  var isSameMethod = !method || method === window._otpMethod;
  if (isSameMethod && cooldownEl && cooldownEl.style.pointerEvents === 'none') return;
  method = method || window._otpMethod || '';
  document.getElementById('otp-message').textContent = 'Sending code...';
  try {
    var r = await API.resendOtp(_loadPendingToken(), method);
    _savePendingToken(r.data.tempToken);
    if (r.data.method) window._otpMethod = r.data.method;
    var altMethods = r.data.methods && r.data.methods.length ? r.data.methods : window._otpMethods;
    if (altMethods && altMethods.length) window._otpMethods = altMethods;
    document.getElementById('otp-message').textContent = r.data.message || 'New code sent';
    if (r.data.smsFailed) {
      window._otpFailureCount++;
      showToast('SMS failed. Try another method below.', 'error');
    } else {
      window._otpRetryCount++;
      window._otpFailureCount = 0;
    }
    startOtpCooldown();
    updateOtpFallbackLink();
  } catch (err) { showToast(err.message, 'error'); }
}

function updateOtpFallbackLink() {
  const el = document.getElementById('otp-fallback');
  const methods = window._otpMethods || [];
  const current = window._otpMethod || '';
  const altMethods = methods.filter(m => m !== current);
  if (altMethods.length > 0 && el) {
    const links = altMethods.map(m => {
      const label = m === 'email' ? '📧 Email' : m === 'sms' ? '📱 SMS' : '💬 WhatsApp';
      const safe = esc(m);
      return '<a href="#" data-on-click="handleResendOtp(event,\'' + safe + '\')" style="color:#448aff;">' + label + '</a>';
    });
    el.innerHTML = 'Code not received? Try ' + links.join(' · ');
    el.style.display = 'block';
  } else if (el) {
    el.style.display = 'none';
  }
}

function showChooseMethodModal(user, methods) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  const icons = { email: '📧', sms: '📱', whatsapp: '💬' };
  const labels = { email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp' };
  const methodBtns = methods.map(m => `<button class="btn btn-primary" style="width:100%;margin:8px 0;padding:14px;font-size:16px;" data-on-click="chooseMethod('${esc(m)}',this)">${icons[m] || '📧'} ${labels[m] || esc(m)} OTP</button>`).join('');
  overlay.innerHTML = `
    <div class="modal-content" style="max-width:400px;">
      <h3>Choose Verification Method</h3>
      <p style="color:var(--text-tertiary);margin:16px 0;">${user.name}, choose how to verify:</p>
      ${methodBtns}
      <button class="btn btn-secondary" style="width:100%;margin-top:16px;" data-on-click="this.closest('.modal-overlay').remove()">Cancel</button>
    </div>`;
  document.body.appendChild(overlay);
  window._chooseMethodUser = user;
  if (typeof bindClickHandlers === 'function') bindClickHandlers(overlay);
}
function chooseMethod(method, btn) {
  if (window._choosingMethod) return;
  window._choosingMethod = true;
  if (btn) {
    var overlay = btn.closest('.modal-overlay');
    if (overlay) {
      var buttons = overlay.querySelectorAll('button');
      buttons.forEach(function(b) { b.disabled = true; });
      btn.textContent = '⏳ Sending code...';
    }
  }
  var user = window._chooseMethodUser;
  var pw = window._loginPassword || '';
  API.login(user.email || '', pw, '', undefined, method).then(function (res) {
    window._choosingMethod = false;
    document.querySelector('.modal-overlay')?.remove();
    if (res.data.step === 'verify-otp') {
      _savePendingToken(res.data.tempToken);
      window._otpMethod = method;
      window._otpMethods = res.data.methods || [];
      window._otpRetryCount = 0;
      window._otpFailureCount = 0;
      document.getElementById('otp-message').textContent = res.data.message || 'Enter the OTP sent to your contact';
      _showAuthTab('otp');
      if (res.data.smsFailed) {
        window._otpFailureCount = 1;
        showToast('SMS failed. Try another method below.', 'error');
      }
      startOtpCooldown();
      updateOtpFallbackLink();
    } else if (res.data.step === 'complete') {
      completeAuth(res);
    }
  }).catch(function (e) {
    window._choosingMethod = false;
    if (btn) {
      var overlay2 = btn.closest('.modal-overlay');
      if (overlay2) {
        overlay2.querySelectorAll('button').forEach(function(b) { b.disabled = false; });
        btn.textContent = (btn.dataset.label || '');
      }
    }
    showToast(e.message, 'error');
  });
}

function showLinkAccountModal(user, phone, code) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal-content" style="max-width:400px;">
      <h3>Link Phone to Existing Account</h3>
      <p style="color:var(--text-tertiary);margin:16px 0;">${user.name} (${user.email}) already has an account.</p>
      <p style="color:var(--text-tertiary);margin-bottom:20px;">Link this phone number to it?</p>
      <button class="btn btn-primary" style="width:100%;margin:8px 0;padding:14px;font-size:16px;" data-on-click="confirmLinkAccount('${esc(user.email)}','${esc(phone)}','${esc(code)}')">✅ Yes, Link Phone</button>
      <button class="btn btn-secondary" style="width:100%;margin:8px 0;padding:14px;font-size:16px;" data-on-click="createNewAccount()">🆕 No, Create New Account</button>
      <button class="btn btn-secondary" style="width:100%;margin-top:16px;" data-on-click="this.closest('.modal-overlay').remove()">Cancel</button>
    </div>`;
  document.body.appendChild(overlay);
  window._linkAccountData = { email: user.email, phone, code };
}
function confirmLinkAccount(email, phone, code) {
  document.querySelector('.modal-overlay')?.remove();
  const data = window._linkAccountData;
  const password = prompt('Enter your password to confirm linking:');
  if (!password) return;
  API.post('/api/auth/link-phone', { email, phone: data.code + data.phone.replace(/[^0-9]/g, ''), password }).then(res => {
    if (res.data.step === 'verify-otp') {
      _savePendingToken(res.data.tempToken);
      window._otpMethods = [];
      document.getElementById('otp-message').textContent = res.data.message || 'Enter the OTP sent to your contact';
      // dev OTP removed
      _showAuthTab('otp');
      updateOtpFallbackLink();
    }
  }).catch(e => showToast(e.message, 'error'));
}
function createNewAccount() {
  document.querySelector('.modal-overlay')?.remove();
  showToast('Proceeding with new account registration...', 'info');
}

function showLinkEmailModal(user, email, code) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal-content" style="max-width:400px;">
      <h3>Link Email to Existing Account</h3>
      <p style="color:var(--text-tertiary);margin:16px 0;">${user.name} (${user.phone}) already has an account.</p>
      <p style="color:var(--text-tertiary);margin-bottom:20px;">Link this email (${email}) to it?</p>
      <button class="btn btn-primary" style="width:100%;margin:8px 0;padding:14px;font-size:16px;" data-on-click="confirmLinkEmail('${esc(user.email)}','${esc(email)}','${esc(code)}')">✅ Yes, Link Email</button>
      <button class="btn btn-secondary" style="width:100%;margin:8px 0;padding:14px;font-size:16px;" data-on-click="createNewAccount()">🆕 No, Create New Account</button>
      <button class="btn btn-secondary" style="width:100%;margin-top:16px;" data-on-click="this.closest('.modal-overlay').remove()">Cancel</button>
    </div>`;
  document.body.appendChild(overlay);
  window._linkEmailData = { email: user.email, phone: user.phone, code };
}
function confirmLinkEmail(email, phone, code) {
  document.querySelector('.modal-overlay')?.remove();
  const data = window._linkEmailData;
  const password = prompt('Enter your password to confirm linking:');
  if (!password) return;
  API.linkEmail(data.email, data.code + data.phone.replace(/[^0-9]/g, ''), password).then(res => {
    if (res.data.step === 'verify-otp') {
      _savePendingToken(res.data.tempToken);
      window._otpMethods = [];
      document.getElementById('otp-message').textContent = res.data.message || 'Enter the OTP sent to your contact';
      // dev OTP removed
      _showAuthTab('otp');
      updateOtpFallbackLink();
    }
  }).catch(e => showToast(e.message, 'error'));
}

async function handleForgotPassword(e) {
  e.preventDefault();
  try {
    const email = document.getElementById('forgot-email').value;
    const captchaToken = document.getElementById('captcha-token-forgot')?.value || '';
    const captchaAnswer = document.getElementById('captcha-answer-forgot')?.value || '';
    const res = await API.post('/api/auth/forgot-password', { email, captchaToken, captchaAnswer });
    _forgotEmail = email;
    showToast(res.data.message || 'Reset code sent if email exists', 'success');
    _showAuthTab('reset');
  } catch (err) { showToast(err.message, 'error'); _captchaCache['forgot'] = null; loadCaptcha('forgot'); }
}
async function handleIdentifyAccount(e) {
  e.preventDefault();
  try {
    const body = {};
    const u = document.getElementById('identify-username').value;
    const n = document.getElementById('identify-realname').value;
    const p = document.getElementById('identify-oldpw').value;
    if (u) body.username = u;
    if (n) body.realName = n;
    if (p) body.oldPasswordHint = p;
    if (!u && !n && !p) { showToast('Provide at least one field', 'error'); return; }
    const res = await API.post('/api/auth/identify-account', body);
    if (res.data.found) {
      _forgotEmail = res.data.maskedEmail;
      showToast(res.data.message || 'Account found — reset code sent', 'success');
      _showAuthTab('reset');
    } else {
      showToast(res.data.message || 'No account found', 'error');
    }
  } catch (err) { showToast(err.message, 'error'); }
}
async function handleResetPassword(e) {
  e.preventDefault();
  try {
    const code = document.getElementById('reset-code').value.trim();
    const np = document.getElementById('reset-new-pw').value;
    const cp = document.getElementById('reset-conf-pw').value;
    if (np !== cp) { showToast('Passwords do not match', 'error'); return; }
    if (np.length < 8) { showToast('Password must be at least 8 characters with uppercase, lowercase, number & special character', 'error'); return; }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d])/.test(np)) { showToast('Password must include uppercase, lowercase, number & special character', 'error'); return; }
    const res = await API.post('/api/auth/reset-password', { resetToken: code, newPassword: np });
    showToast(res.data.message || 'Password reset successful', 'success');
    _showAuthTab('login');
    document.getElementById('login-email').value = _forgotEmail || '';
  } catch (err) { showToast(err.message, 'error'); }
}

async function handlePasskeyLogin() {
  try {
    if (!isApk() && !window.PublicKeyCredential) {
      showToast('🔑 Passkey not available — your browser or app does not support passkeys. Use email/password or OTP instead.', 'error', 6000);
      return;
    }
    if (isApk() && window._apkHasCredMan && window._apkHasCredMan()) {
      // Google Password Manager passkey via the native Credential Manager
      // bridge — the synced credential shows up on every signed-in device.
      var cmLoginBegin = await API.passkeyLoginBegin();
      if (!cmLoginBegin || cmLoginBegin.code !== 0) { showToast(cmLoginBegin?.msg || 'Failed to start passkey login', 'error'); return; }
      // Empty allowCredentials means "discoverable credentials only" — drop it
      // so Credential Manager offers everything saved for this RP.
      var loginOpts = cmLoginBegin.data;
      if (loginOpts && Array.isArray(loginOpts.allowCredentials) && loginOpts.allowCredentials.length === 0) delete loginOpts.allowCredentials;
      var authJson = await _apkCredManGet(JSON.stringify(loginOpts));
      var authResp = typeof authJson === 'string' ? JSON.parse(authJson) : authJson;
      if (authResp.error) throw new Error(authResp.error);
      var cmComplete = await API.passkeyLoginComplete(authResp);
      if (cmComplete && cmComplete.code === 0) { completeAuth(cmComplete); return; }
      showToast(cmComplete?.msg || 'Passkey login failed', 'error');
      return;
    }
    if (isApk() && !(window._apkHasNativeWebAuthn && window._apkHasNativeWebAuthn())) {
      // Use standalone passkey (Android Keystore + BiometricPrompt) — no Credential Manager needed
      var deviceIds = typeof _apkListUserIds === 'function' ? _apkListUserIds() : [];
      for (var i = 0; i < deviceIds.length; i++) {
        // A stale device key (deleted account, old registration) must NOT
        // abort the loop — api.js throws on non-zero responses, so each
        // attempt is try/caught and we fall through to the next key.
        try {
          var _sb = await API.passkeyStandaloneLoginBegin({ userId: deviceIds[i] });
          if (!_sb || _sb.code !== 0) continue;
          var _apkData = await standaloneLogin(_sb.data.userId, _sb.data.challenge, _sb.data.tempToken);
          var _complete = await API.passkeyStandaloneLoginComplete({ tempToken: _apkData.tempToken, clientSignature: _apkData.clientSignature });
          if (_complete && _complete.code === 0) { completeAuth(_complete); return; }
        } catch (e) {
          // try the next device passkey
        }
      }
      // No usable device passkey found — offer to register or login with email
      if (typeof _apkStandaloneRegister === 'function') {
        if (confirm('No passkey found on this device. Do you want to register one? (Cancel to try email login)')) {
          navigate('settings-passkey');
          return;
        }
      }
      showToast('No passkey found. Register one in Settings.', 'info', 5000);
      return;
    }
    const beginRes = await API.passkeyLoginBegin();
    if (!beginRes || beginRes.code !== 0 || !beginRes.data) {
      showToast(beginRes?.msg || 'Failed to start passkey login', 'error');
      return;
    }
    const opts = beginRes.data;
    opts.challenge = base64urlToBuffer(opts.challenge);
    opts.allowCredentials = (opts.allowCredentials || []).map(function(c) {
      c.id = base64urlToBuffer(c.id);
      return c;
    });
    if (opts.userVerification === 'required') opts.userVerification = 'preferred';
    const cred = await navigator.credentials.get({ publicKey: opts });
    const response = {
      id: cred.id, rawId: bufferToBase64url(cred.rawId),
      type: cred.type,
      response: {
        authenticatorData: bufferToBase64url(cred.response.authenticatorData),
        clientDataJSON: bufferToBase64url(cred.response.clientDataJSON),
        signature: bufferToBase64url(cred.response.signature),
        userHandle: cred.response.userHandle ? bufferToBase64url(cred.response.userHandle) : null
      }
    };
    const completeRes = await API.passkeyLoginComplete(response);
    if (completeRes.code === 0) completeAuth(completeRes);
  } catch (err) { showToast(err.message || 'Passkey login failed', 'error'); }
}
window.openChromeForPasskey = function() {
  closeModal();
  showToast('🔑 Use the app\'s built-in passkey feature — no browser needed', 'info', 4000);
};

// OTP autofill — the Android NotificationListenerService pushes 6-digit codes
// from email/WhatsApp notifications here. Fills whichever OTP input is visible
// and submits automatically so the user never has to type the code.
function _elementVisible(el) {
  if (!el) return false;
  if (el.offsetParent !== null) return true;
  var display = getComputedStyle(el).display;
  return display !== 'none' && display !== 'hidden';
}
window._applyPendingApkOtp = function() {
  if (!window._pendingApkOtp) return;
  var p = window._pendingApkOtp;
  window._pendingApkOtp = null;
  // Stashed codes older than 2 minutes are stale (notification replay /
  // service reconnect) — drop them instead of failing a login attempt.
  if (!p || !p.code || (Date.now() - (p.at || 0)) > 120000) return;
  window.__apkOtpCallback(p.code);
};
window.__apkOtpCallback = function(code) {
  try {
    if (!code || !/^\d{6}$/.test(String(code))) return;
    var c = String(code);
    // Never auto-submit the same code twice — old notifications replaying
    // through the listener must not eat login attempts with wrong codes.
    window._apkOtpTried = window._apkOtpTried || {};
    if (window._apkOtpTried[c]) return;
    var targets = ['otp-code', 'otp-code-input', 'alt-otp-input'];
    for (var i = 0; i < targets.length; i++) {
      var el = document.getElementById(targets[i]);
      if (!el) continue;
      var form = el.closest('form');
      var formVisible = form && _elementVisible(form);
      var modal = el.closest('.modal-overlay');
      var modalVisible = modal && _elementVisible(modal);
      if (formVisible || modalVisible) {
        window._apkOtpTried[c] = Date.now();
        el.value = c;
        // #otp-code has an inline oninput that auto-submits on 6 digits —
        // don't double-submit. Other targets need an explicit submit.
        if (targets[i] !== 'otp-code') {
          el.dispatchEvent(new Event('input', { bubbles: true }));
          setTimeout(function() {
            try {
              if (form && typeof form.requestSubmit === 'function') {
                form.requestSubmit();
              } else if (modal) {
                var btn = modal.querySelector('[data-on-click*="confirm"]');
                if (btn && typeof btn.click === 'function') btn.click();
              }
            } catch (e) {}
          }, 50);
        } else {
          el.dispatchEvent(new Event('input', { bubbles: true }));
        }
        showToast('OTP auto-filled from notification', 'success', 2500);
        return;
      }
    }
    // No visible OTP field yet — stash with timestamp; _showAuthTab applies later
    window._pendingApkOtp = { code: c, at: Date.now() };
  } catch (e) {}
};

function logout() {
  if (typeof disconnectSSE === 'function') disconnectSSE();
  if (globalPollTimer) { clearInterval(globalPollTimer); globalPollTimer = null; }
  if (pendingPollTimer) { clearInterval(pendingPollTimer); pendingPollTimer = null; }
  if (pagePollTimer) { clearInterval(pagePollTimer); pagePollTimer = null; }
  if (ordersErrorTimer) { clearTimeout(ordersErrorTimer); ordersErrorTimer = null; }
  seenOrderIds = new Set(); trackedOrderStates = new Map(); paymentRemindedOrderIds = new Map(); window._highlightOrderId = null;
  const dt = localStorage.getItem('deviceToken');
  if (dt && currentUser) API.forgetDevice(dt).catch(() => {});
  localStorage.removeItem('deviceToken');
  currentUser = null;
  document.getElementById('globalAdminLink')?.classList.add('hidden'); document.getElementById('globalTicketsLink')?.classList.add('hidden'); document.getElementById('globalSuggestLink')?.classList.add('hidden'); ['hubAdminLink', 'hubTicketsLink', 'hubSuggestLink', 'hubAdminDivider'].forEach(function(id) { document.getElementById(id)?.classList.add('hidden'); });
  showAuth();
  history.pushState({ page: 'login' }, '', '/login');
  showToast('Logged out', 'info');
}

// ─── QR device login ─────────────────────────────────

// PC side: generates a QR, polls until the phone approves, then completes login.
window.showQrLogin = async function() {
  try {
    var res = await API.post('/api/auth/qr/start', {});
    var d = res.data || {};
    if (!d.qrDataUrl) throw new Error('Could not start QR session');
    showModal(
      '<h3>📱 Login via QR</h3>' +
      '<div style="text-align:center;">' +
      '<img src="' + d.qrDataUrl + '" alt="QR" style="width:260px;height:260px;border-radius:12px;background:#fff;padding:8px;">' +
      '<p style="color:#8888a0;font-size:12px;margin:10px 0 4px;">Scan with your phone → approve in the app.</p>' +
      '<p id="qr-status" style="color:#6c63ff;font-size:13px;font-weight:600;margin:0 0 12px;">Waiting for scan…</p>' +
      '<button class="btn btn-secondary" data-on-click="closeModal()" style="padding:8px 20px;">Cancel</button>' +
      '</div>'
    );
    var deadline = Date.now() + (d.expiresInSeconds || 120) * 1000;
    var poll = setInterval(async function() {
      var box = document.getElementById('qr-status');
      if (!box) { clearInterval(poll); return; }
      if (Date.now() > deadline) { clearInterval(poll); box.textContent = 'Expired — tap Login via QR again.'; return; }
      try {
        var pr = await fetch('/api/auth/qr/poll/' + encodeURIComponent(d.code)).then(function(r) { return r.json(); });
        var st = pr && pr.data && pr.data.status;
        if (st === 'approved') {
          clearInterval(poll);
          document.querySelector('.modal-overlay')?.remove();
          completeAuth(pr);
        } else if (st === 'expired') {
          clearInterval(poll);
          box.textContent = 'Expired — tap Login via QR again.';
        }
      } catch (e) {}
    }, 2000);
  } catch (err) {
    showToast(err.message, 'error');
  }
};

// Phone side: /qr/<code> opened after scanning — requires authenticated device.
window.renderQrApprove = async function(container, code) {
  if (!code) { container.innerHTML = '<div class="card"><p style="color:#f44336;">Invalid QR link.</p></div>'; return; }
  if (!API.getToken()) {
    container.innerHTML = '<div class="card" style="max-width:420px;margin:40px auto;text-align:center;padding:32px;">' +
      '<div style="font-size:48px;margin-bottom:8px;">🔐</div>' +
      '<h3>Login required</h3>' +
      '<p style="color:var(--text-tertiary);font-size:13px;">Log in on this device first, then re-scan the QR to approve your computer.</p>' +
      '<button class="btn btn-primary" data-on-click="navigate(\'login\')" style="margin-top:12px;">Go to Login</button></div>';
    return;
  }
  container.innerHTML = '<div class="card" style="max-width:420px;margin:40px auto;text-align:center;padding:32px;">' +
    '<div style="font-size:48px;margin-bottom:8px;">🖥️</div>' +
    '<h3>Approve computer login?</h3>' +
    '<p style="color:var(--text-tertiary);font-size:13px;margin:8px 0 20px;">A sign-in was requested from another device with this code:<br><code style="user-select:all;font-size:11px;">' + esc(code.slice(0, 14)) + '…</code></p>' +
    '<div style="display:flex;gap:10px;justify-content:center;">' +
    '<button class="btn btn-primary" data-on-click="approveQrLogin(\'' + esc(code) + '\')">✅ Approve</button>' +
    '<button class="btn btn-secondary" data-on-click="navigate(\'general-dashboard\')">❌ Deny</button>' +
    '</div><div id="qr-approve-result" style="margin-top:16px;"></div></div>';
};

window.approveQrLogin = async function(code) {
  var out = document.getElementById('qr-approve-result');
  try {
    await API.post('/api/auth/qr/approve', { code: code });
    if (out) out.innerHTML = '<p style="color:var(--accent-green);font-weight:600;">✅ Approved — your computer is logged in.</p>';
    showToast('Login approved', 'success');
  } catch (err) {
    if (out) out.innerHTML = '<p style="color:var(--accent-red);">' + esc(err.message) + '</p>';
  }
};

// ─── QR scanner page (/qr-scan) — in-app camera scan → approve PC login ──

window.renderQrScanner = async function(container) {
  if (!API.getToken()) {
    container.innerHTML = '<div class="card" style="max-width:420px;margin:40px auto;text-align:center;padding:32px;"><h3>Login required</h3><p style="color:var(--text-tertiary);">Log in first to approve QR logins.</p></div>';
    return;
  }
  container.innerHTML = '<div class="page-header"><div><h2>📱 Scan QR Login</h2><div class="subtitle">Scan the code shown on your computer to log it in</div></div></div>' +
    '<div class="card" style="max-width:480px;margin:0 auto;text-align:center;padding:20px;">' +
    '<div id="qrs-video-wrap" style="position:relative;border-radius:12px;overflow:hidden;background:#000;aspect-ratio:1;max-width:340px;margin:0 auto;">' +
    '<video id="qrs-video" muted autoplay playsinline style="width:100%;height:100%;object-fit:cover;"></video></div>' +
    '<p id="qrs-status" style="color:var(--text-tertiary);font-size:13px;margin:12px 0 0;">Starting camera…</p>' +
    '<div id="qrs-result" style="margin-top:14px;"></div></div>';

  var video = document.getElementById('qrs-video');
  var statusEl = document.getElementById('qrs-status');
  var resultEl = document.getElementById('qrs-result');
  var stream = null, rafId = null, canvas = document.createElement('canvas'), done = false;

  function stop() { if (stream) stream.getTracks().forEach(function(t) { t.stop(); }); if (rafId) cancelAnimationFrame(rafId); }
  window.addEventListener('popstate', stop, { once: true });

  async function approve(code) {
    done = true; stop();
    try {
      await API.post('/api/auth/qr/approve', { code: code });
      resultEl.innerHTML = '<p style="color:var(--accent-green);font-weight:600;font-size:15px;">✅ Approved — your computer is logged in.</p>';
      showToast('Login approved', 'success');
    } catch (err) {
      resultEl.innerHTML = '<p style="color:var(--accent-red);">' + esc(err.message) + '</p>' +
        '<button class="btn btn-primary btn-sm" onclick="navigate(\'qr-scan\')">Scan Again</button>';
    }
  }

  var _qrFrameCount = 0;
  function tick() {
    if (done) return;
    _qrFrameCount++;
    if (_qrFrameCount % 3 !== 0) { rafId = requestAnimationFrame(tick); return; }
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth; canvas.height = video.videoHeight;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      var img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      var code = window.jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
      if (code && code.data) {
        var m = String(code.data).match(/\/qr\/([A-Za-z0-9_-]+)/);
        var val = m ? m[1] : String(code.data).trim();
        if (/^[A-Za-z0-9_-]{20,}$/.test(val)) { approve(val); return; }
      }
    }
    rafId = requestAnimationFrame(tick);
  }

  function start() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      statusEl.textContent = 'Camera not supported on this device.';
      return;
    }
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } }).then(function(s) {
      stream = s; video.srcObject = s; video.setAttribute('playsinline', true); video.play().catch(function() {});
      statusEl.textContent = 'Point the camera at the QR on your computer…';
      if (!window.jsQR) {
        // jsQR not loaded yet — retry loop waits for library
        var waitLib = setInterval(function() { if (window.jsQR) { clearInterval(waitLib); tick(); } }, 200);
      } else { tick(); }
    }).catch(function(err) {
      var name = String(err && err.name || '');
      if (name === 'NotAllowedError' && window.AndroidScan && window.AndroidScan.requestCamera) {
        statusEl.textContent = 'Grant camera permission to continue…';
        window.__cameraReady = function() { start(); };
        window.__cameraDenied = function() { statusEl.textContent = 'Camera permission denied. Enable it in Android Settings → Apps → MONEYFESTLTD → Permissions.'; };
        window.AndroidScan.requestCamera();
      } else {
        statusEl.textContent = 'Camera error: ' + (err.message || name || 'unknown') + (name === 'NotFoundError' ? ' (no camera found)' : '');
      }
    });
  }

  if (!window.jsQR) {
    var s = document.createElement('script');
    s.src = '/vendor/jsQR.js';
    s.onload = start;
    s.onerror = function() { statusEl.textContent = 'Failed to load scanner library.'; };
    document.head.appendChild(s);
  } else { start(); }
};

/* ==== js/modules/bank.js ==== */
function bankTimeLeft(o) {
  if (orderState(o) !== 'new' && orderState(o) !== 'pending') return '';
  if (orderIsPaid(o)) return '<span style="color:var(--accent-blue);font-size:10px;">Awaiting release</span>';
  const elapsed = (Date.now() - parseInt(o.createdTimestamp || '0')) / 1000;
  const total = parseInt(o.expiryTimeInSeconds || '0');
  const remaining = total - elapsed;
  if (remaining <= 0) return '<span style="color:var(--accent-red);font-size:10px;">Expired</span>';
  const h = Math.floor(remaining / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = Math.floor(remaining % 60);
  return '<span style="color:var(--accent-orange);font-size:12px;font-weight:700;">' + (h > 0 ? h + 'h ' : '') + m + 'm ' + s + 's</span>';
}

let _bankTab = 'transactions';
function switchBankTab(tab) {
  _bankTab = tab;
  const el = document.getElementById('page-content');
  if (el) renderBank(el);
  // Update URL
  var path = '/bank/' + tab;
  if (typeof updateUrl === 'function') {
    updateUrl('bank-' + tab);
  } else {
    history.pushState({ page: 'bank-' + tab }, '', path);
  }
}
async function renderBank(container, sseData, silent) {
  if (currentUser && currentUser.role !== 'admin' && !currentUser.hasApiKeys) {
    container.innerHTML = `<div class="page-header"><div><h2>Payments</h2></div></div>` + noApiKeysDashboard();
    return;
  }
  if (currentUser && currentUser.approved === 0 && currentUser.hasApiKeys) {
    container.innerHTML = `<div class="page-header"><div><h2>Payments</h2></div></div><div class="card" style="border-color:rgba(255,145,0,0.3);margin-top:16px;text-align:center;padding:40px 20px;"><div style="font-size:48px;margin-bottom:16px;">🛂</div><h3 style="color:#ff9100;">Account Pending Approval</h3><p style="color:var(--text-tertiary);font-size:14px;max-width:400px;margin:12px auto;">Your API keys have been submitted for admin review.</p><p style="color:var(--text-quaternary);font-size:12px;">Contact admin: +254723472206</p></div>`;
    return;
  }
  const _lu = new Date().toLocaleTimeString();
  // SMS listener status: green when the APK has Notification access (the bank
  // SMS listener can run), red with a grant button otherwise. Browser = hidden.
  let _smsStatusHtml = '';
  if (typeof AndroidOtp !== 'undefined' && AndroidOtp.isSupported) {
    let _ok = false;
    try { _ok = AndroidOtp.isSupported() === true; } catch (e) {}
    _smsStatusHtml = _ok
      ? `<div style="margin-top:10px;padding:8px 14px;border-radius:8px;background:rgba(0,200,83,0.12);border:1px solid rgba(0,200,83,0.35);color:var(--accent-green);font-size:12px;display:flex;align-items:center;gap:10px;flex-wrap:wrap;">✅ SMS listener active — bank payment SMS will be matched to your orders <button class="btn btn-sm btn-outline" data-on-click="toggleSmsDebug()" style="font-size:10px;">Debug</button></div><div id="sms-debug-panel" style="display:none;"></div>`
      : `<div style="margin-top:10px;padding:8px 14px;border-radius:8px;background:rgba(255,82,82,0.12);border:1px solid rgba(255,82,82,0.4);color:#ff5252;font-size:12px;display:flex;align-items:center;gap:10px;flex-wrap:wrap;">⛔ SMS listener OFF — payment SMS cannot be detected <button class="btn btn-sm btn-outline" data-on-click="grantSmsListenerAccess()" style="font-size:11px;">Grant access</button></div>`;
  }
  let html = `
    <div class="page-header"><div><h2>Payments</h2><div class="subtitle">Bank · Balance · API Config · <span id="page-last-updated" style="color:var(--text-quaternary);font-size:11px;">Updated ${_lu}</span> <span id="page-countdown" style="color:var(--text-tertiary);font-size:11px;"></span></div></div><button class="btn btn-sm btn-outline" data-on-click="refreshBankPage()" style="font-size:11px;">⟳ Refresh</button></div>
    ${_smsStatusHtml}
    <div class="settings-tabs">
      <button class="settings-tab ${_bankTab === 'transactions' ? 'active' : ''}" data-banktab="transactions" data-on-click="switchBankTab('transactions')">📋 Transactions</button>
      <button class="settings-tab ${_bankTab === 'balance' ? 'active' : ''}" data-banktab="balance" data-on-click="switchBankTab('balance')">💰 Balance</button>
    </div>`;

  if (_bankTab === 'balance') {
    container.innerHTML = html;
    renderBankBalanceTab(container);
    startPageCountdown(5);
    startPagePoll(container, () => renderBank(container, null, true), 120000);
    return;
  }

  let buyOrders = [];
  let buyPaidOrders = [];
  let sellReleaseOrders = [];
  let sellUnpaidOrders = [];
  let balance = '0';
  let bankBalance = null;
  let okxBalData = {};
  let agentData = null;
  try {
    const [pendingRes, balRes, balEntryRes, agentRes] = await Promise.all([
      API.get('/api/okx/p2p/order-list?pageSize=100&pageIndex=1').catch(() => ({ data: [] })),
      API.get('/api/okx/p2p/user/balance?currencySymbol=USDT').catch(() => ({ data: [] })),
      API.get('/api/bank/balance').catch(() => ({ data: null })),
      API.get('/api/bank-agent/overview').catch(() => null)
    ]);
    const allOrders = pendingRes.data || [];
    window._ordersMap = window._ordersMap || {};
    allOrders.forEach(o => { if (o.orderId) window._ordersMap[o.orderId] = o; });
    // Filter to only active (non-completed, non-cancelled) orders for bank tab
    var _state = function(o) { return (o.state || o.completionStatus || o.orderStatus || o.status || '').toLowerCase(); };
    var activeOrders = allOrders.filter(function(o) { var s = _state(o); return s !== 'completed' && s !== 'canceled' && s !== 'cancelled'; });
    buyOrders = activeOrders.filter(o => o.side !== 'sell' && !orderIsPaid(o));
    sellReleaseOrders = activeOrders.filter(o => o.side === 'sell' && orderIsPaid(o));
    buyPaidOrders = activeOrders.filter(o => o.side !== 'sell' && orderIsPaid(o));
    sellUnpaidOrders = activeOrders.filter(o => o.side === 'sell' && !orderIsPaid(o));
    const ba = balRes.data?.[0] || {};
    balance = ba.availableAmount || '0';
    okxBalData = { available: parseFloat(balance), onHold: parseFloat(ba.onHoldAmount || '0') };
    bankBalance = balEntryRes.data;
    agentData = (agentRes && agentRes.devices) ? agentRes : null;
    window._agentData = agentData;
  } catch {}

  // Stats bar
  html += `
    <div data-section="bank-stats" style="display:flex;gap:12px;margin-bottom:16px;flex-wrap:wrap;">
      <div style="background:rgba(91,124,250,0.08);border:1px solid rgba(68,138,255,0.15);border-radius:10px;padding:12px 18px;">
        <div style="font-size:11px;color:var(--text-quaternary);">Buy Unpaid</div>
        <div data-stat="buy-unpaid" style="font-size:22px;font-weight:700;color:var(--accent-blue);">${buyOrders.length}</div>
      </div>
      <div style="background:rgba(91,124,250,0.15);border:1px solid rgba(68,138,255,0.25);border-radius:10px;padding:12px 18px;">
        <div style="font-size:11px;color:var(--text-quaternary);">Buy Paid / Awaiting</div>
        <div data-stat="buy-paid" style="font-size:22px;font-weight:700;color:var(--accent-blue);">${buyPaidOrders.length}</div>
      </div>
      <div style="background:rgba(255,145,0,0.08);border:1px solid rgba(255,145,0,0.15);border-radius:10px;padding:12px 18px;">
        <div style="font-size:11px;color:var(--text-quaternary);">Sell Unpaid</div>
        <div data-stat="sell-unpaid" style="font-size:22px;font-weight:700;color:var(--accent-amber);">${sellUnpaidOrders.length}</div>
      </div>
      <div style="background:rgba(0,210,104,0.08);border:1px solid rgba(0,200,83,0.15);border-radius:10px;padding:12px 18px;">
        <div style="font-size:11px;color:var(--text-quaternary);">Sell Paid / Release</div>
        <div data-stat="sell-release" style="font-size:22px;font-weight:700;color:var(--accent-green);">${sellReleaseOrders.length}</div>
      </div>
      <div style="background:rgba(0,210,104,0.08);border:1px solid rgba(0,200,83,0.15);border-radius:10px;padding:12px 18px;">
        <div style="font-size:11px;color:var(--text-quaternary);">P2P Balance</div>
        <div data-stat="p2p-balance" style="font-size:22px;font-weight:700;color:${currentUser && currentUser.role !== 'admin' && !currentUser.hasApiKeys ? '#55556a' : '#00c853'};">${currentUser && currentUser.role !== 'admin' && !currentUser.hasApiKeys ? '—' : parseFloat(balance).toFixed(2) + ' USDT'}</div>
      </div>
      <div style="background:rgba(156,39,176,0.08);border:1px solid rgba(156,39,176,0.15);border-radius:10px;padding:12px 18px;">
        <div style="font-size:11px;color:var(--text-quaternary);">Payment Agent</div>
        <div data-stat="agent" style="font-size:16px;font-weight:700;">${agentTileHtml(agentData)}</div>
      </div>
    </div>`;

  // Balance breakdown + Bank API entries in a 2-col grid
  html += `<div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px;">`;

  // Left: Balance breakdown
  const avail = okxBalData.available || 0;
  const onHold = okxBalData.onHold || 0;
  const totalBal = avail + onHold;
  const bb = bankBalance || { balance: 0 };
  const bankBal = parseInt(bb.balance || '0');
  html += `
    <div class="card" style="border-color:rgba(0,200,83,0.15);">
      <h3 style="margin:0 0 12px 0;">💰 Balance Breakdown</h3>
      <div style="display:flex;flex-direction:column;gap:8px;">
        <div style="display:flex;justify-content:space-between;padding:10px;background:var(--bg-primary);border-radius:8px;">
          <span style="color:var(--text-tertiary);font-size:12px;">P2P Available</span>
          <span style="color:var(--accent-green);font-weight:700;">${avail.toFixed(2)} USDT</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:10px;background:var(--bg-primary);border-radius:8px;">
          <span style="color:var(--text-tertiary);font-size:12px;">P2P On Hold</span>
          <span style="color:#ff9100;font-weight:700;">${onHold.toFixed(2)} USDT</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:10px;background:var(--bg-primary);border-radius:8px;border:1px solid rgba(0,200,83,0.2);">
          <span style="color:var(--text-primary);font-weight:600;">Total P2P Equity</span>
          <span style="color:#fff;font-weight:800;">${totalBal.toFixed(2)} USDT</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:10px;background:#0c1220;border-radius:8px;border:1px solid rgba(68,138,255,0.2);">
          <span style="color:var(--text-primary);font-weight:600;">🏦 Bank Balance (KES)<div style="font-size:9px;color:var(--text-quaternary);font-weight:400;">from PC agent · funds buy-ads</div></span>
          <span style="color:var(--accent-blue);font-weight:800;font-size:20px;">${formatKESint(bankBal)}</span>
        </div>
      </div>
      <div style="display:flex;gap:8px;margin-top:12px;">
        <button class="btn btn-sm btn-outline" data-on-click="refreshP2pBalance()" style="font-size:10px;">⟳ Refresh P2P</button>
        <button class="btn btn-sm btn-outline" data-on-click="setBankBalanceManually()" style="font-size:10px;">✏️ Set Bank Bal</button>
      </div>
    </div>`;

  // Right: payment agent + autopay
  html += `<div class="card" style="border-color:rgba(68,138,255,0.15);">
      <h3 style="margin:0 0 12px 0;">🤖 I&M Payment Agent</h3>
      <div id="agent-card-body">${agentCardBodyHtml(agentData)}</div>
      <div style="display:flex;gap:6px;flex-wrap:wrap;border-top:1px solid #1e1e2e;padding-top:12px;margin-top:12px;">
        <button class="btn btn-sm btn-outline" data-on-click="navigate('dashboard')" style="font-size:10px;">📊 Dashboard</button>
        <button class="btn btn-sm btn-outline" data-on-click="navigate('orders')" style="font-size:10px;">⏳ Orders</button>
      </div>
    </div>

    <!-- Autopay (single control — maps to the automation engine config) -->
    <div class="card" style="border-color:rgba(0,168,90,0.15);">
      <h3 style="margin:0 0 4px 0;">⚡ Autopay</h3>
      <div style="font-size:11px;color:var(--text-tertiary);margin-bottom:12px;">When ON, buy orders are paid automatically through your linked PC. Release &amp; messaging automations live in <a href="#automation" data-on-click="navigate('automation')" style="color:var(--accent-blue);">Automation</a>.</div>
      <div id="agent-autopay-body">${autopayCardBodyHtml(agentData)}</div>
    </div>

    <!-- Agent payment log (paid orders, both proofs) -->
    <div class="card" id="agent-payments-card" style="border-color:rgba(68,138,255,0.15);grid-column:span 2;">
      <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px;">
        <h3 style="margin:0;">💳 Agent Payment Log</h3>
        <button class="btn btn-sm btn-outline" data-on-click="refreshAgentPayments()" style="font-size:10px;">⟳ Refresh</button>
      </div>
      <div style="font-size:11px;color:var(--text-tertiary);margin:6px 0 10px;">Payments executed on your PC. Each payment is confirmed by a screenshot receipt <b>and</b> the bank SMS — both are stored as proof. Duplicates are blocked; failures never auto-retry.</div>
      <div id="agent-pay-body">${agentPaymentsHtml(agentData)}</div>
    </div>`;

  html += `</div>`;

  // Pending buy orders card
  html += `<div data-section="buy-unpaid" class="card">
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;flex-wrap:wrap;gap:8px;">
      <h3 style="margin:0;font-size:15px;">Buy Orders Awaiting Payment</h3>
      <span data-counter="buy-unpaid-count" style="font-size:12px;color:var(--text-quaternary);">${buyOrders.length} unpaid</span>
    </div>`;
  if (buyOrders.length === 0) {
    html += `<div style="text-align:center;padding:30px;background:var(--bg-primary);border-radius:10px;border:1px dashed #1e1e2e;">
      <div style="font-size:32px;margin-bottom:8px;opacity:0.4;">✅</div>
      <p style="color:var(--text-quaternary);font-size:13px;margin:0;">No buy orders awaiting payment</p>
    </div>`;
  } else {
    html += `<div style="display:flex;flex-direction:column;gap:10px;">`;
    buyOrders.slice(0, 10).forEach(o => {
      const short = (o.orderId || '').slice(0, 14);
      const fiat = parseFloat(o.fiatAmount || '0').toFixed(2);
      const crypto = parseFloat(o.cryptoAmount || '0').toFixed(4);
      const price = o.unitPrice || '-';
      const cp = o.counterpartyDetail?.takerPaymentMethod || o.takerPaymentMethod || {};
      const bankName = cp.bankName || cp.type || 'Bank Transfer';
      const accountNo = cp.accountNo || cp.accountNumber || '';
      const ref = o.orderId?.slice(-8) || '';
      const created = o.createdTimestamp ? new Date(parseInt(o.createdTimestamp)).toLocaleString() : '';
      const customerRef = ref;
      const tl = bankTimeLeft(o);
      html += `
        <div style="background:var(--bg-primary);border:1px solid var(--border-primary);border-radius:12px;padding:16px;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;flex-wrap:wrap;gap:8px;">
            <div style="display:flex;align-items:center;gap:8px;">
              <span style="font-family:monospace;font-size:11px;color:var(--text-quaternary);user-select:all;cursor:pointer;" title="Click to copy" data-on-click="copyText('${esc(o.orderId || '')}')">${short}</span>
              <span class="badge badge-buy" style="font-size:10px;">BUY</span>
              <span style="font-size:11px;background:rgba(255,122,0,0.1);padding:2px 8px;border-radius:4px;color:var(--accent-orange);">${tl}</span>
            </div>
            <div style="font-size:18px;font-weight:700;color:var(--accent-orange);">${formatKES(fiat)}</div>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-bottom:12px;font-size:12px;">
            <div><span style="color:var(--text-quaternary);">Crypto:</span> <span style="color:var(--text-primary);">${crypto} USDT</span></div>
            <div><span style="color:var(--text-quaternary);">Price:</span> <span style="color:var(--text-primary);">${price}/USDT</span></div>
            <div><span style="color:var(--text-quaternary);">Created:</span> <span style="color:var(--text-primary);">${created}</span></div>
          </div>
          <div style="background:rgba(91,124,250,0.06);border:1px solid rgba(68,138,255,0.1);border-radius:8px;padding:12px;margin-bottom:12px;">
            <div style="font-size:11px;color:#8888a0;font-weight:600;margin-bottom:6px;">Payment Details</div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:4px;font-size:12px;">
              <div><span style="color:var(--text-quaternary);">Bank:</span> <span style="color:var(--text-primary);">${bankName}</span></div>
              <div><span style="color:var(--text-quaternary);">Account:</span> <span style="color:var(--text-primary);font-family:monospace;">${accountNo || '—'}</span></div>
              <div><span style="color:var(--text-quaternary);">Amount:</span> <span style="color:var(--accent-orange);font-weight:700;">${formatKES(fiat)}</span></div>
              <div><span style="color:var(--text-quaternary);">Ref:</span> <span style="color:var(--text-primary);font-family:monospace;">${ref}</span></div>
            </div>
          </div>
          <div style="display:flex;gap:8px;flex-wrap:wrap;">
            <button class="btn btn-sm btn-primary" data-on-click="markBuyOrderPaid('${esc(o.orderId)}')" style="background:linear-gradient(135deg,var(--accent-green),var(--accent-green-bright));color:var(--text-on-accent);font-weight:600;">
              ✓ I've Paid — Release Crypto
            </button>
            <button class="btn btn-sm btn-outline" data-on-click="payNowOrder('${esc(o.orderId)}')" style="font-size:11px;border-color:var(--accent-blue);color:var(--accent-blue);">⚡ Pay now</button>
            <button class="btn btn-sm btn-outline" data-on-click="navigate('orders')" style="font-size:11px;">View Orders</button>
          </div>
        </div>`;
    });
    html += `</div>`;
  }
  html += `</div>`;

  // Buy paid orders awaiting seller release
  html += `<div data-section="buy-paid" class="card" style="margin-top:16px;border-color:rgba(68,138,255,0.2);">
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;flex-wrap:wrap;gap:8px;">
      <h3 style="margin:0;font-size:15px;color:var(--accent-blue);">Buy Orders Paid — Awaiting Release</h3>
      <span data-counter="buy-paid-count" style="font-size:12px;color:var(--text-quaternary);">${buyPaidOrders.length} paid</span>
    </div>`;
  if (buyPaidOrders.length === 0) {
    html += `<div style="text-align:center;padding:30px;background:var(--bg-primary);border-radius:10px;border:1px dashed #1e1e2e;">
      <div style="font-size:32px;margin-bottom:8px;opacity:0.4;">🔵</div>
      <p style="color:var(--text-quaternary);font-size:13px;margin:0;">No paid buy orders awaiting seller release</p>
    </div>`;
  } else {
    html += `<div style="display:flex;flex-direction:column;gap:10px;">`;
    buyPaidOrders.slice(0, 10).forEach(o => {
      const short = (o.orderId || '').slice(0, 14);
      const fiat = parseFloat(o.fiatAmount || '0').toFixed(2);
      const crypto = parseFloat(o.cryptoAmount || '0').toFixed(4);
      const cp = o.counterpartyDetail || {};
      const sellerName = cp.userName || cp.sellerName || cp.nickName || 'Seller';
      const paidTime = o.paymentTimestamp ? new Date(parseInt(o.paymentTimestamp)).toLocaleString() : '';
      html += `
        <div style="background:var(--bg-primary);border:1px solid rgba(68,138,255,0.15);border-radius:12px;padding:16px;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;flex-wrap:wrap;gap:8px;">
            <div style="display:flex;align-items:center;gap:8px;">
              <span style="font-family:monospace;font-size:11px;color:var(--text-quaternary);user-select:all;cursor:pointer;" title="Click to copy" data-on-click="copyText('${esc(o.orderId || '')}')">${short}</span>
              <span class="badge badge-buy" style="font-size:10px;">BUY</span>
              <span style="font-size:10px;padding:2px 6px;border-radius:4px;background:rgba(91,124,250,0.1);color:var(--accent-blue);">PAID</span>
              <span style="font-size:11px;color:var(--accent-blue);">Awaiting release</span>
            </div>
            <div style="font-size:18px;font-weight:700;color:var(--accent-blue);">${crypto} USDT</div>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:12px;font-size:12px;">
            <div><span style="color:var(--text-quaternary);">Amount:</span> <span style="color:var(--text-primary);">${formatKES(fiat)}</span></div>
            <div><span style="color:var(--text-quaternary);">Seller:</span> <span style="color:var(--text-primary);">${sellerName}</span></div>
            <div><span style="color:var(--text-quaternary);">Paid at:</span> <span style="color:var(--text-primary);">${paidTime}</span></div>
          </div>
          <div style="display:flex;gap:8px;flex-wrap:wrap;">
            <button class="btn btn-sm btn-outline" data-on-click="appealOrder('${esc(o.orderId)}')" style="font-size:11px;color:var(--accent-orange);border-color:#ff980044;font-weight:600;">⚠️ Appeal</button>
            <button class="btn btn-sm btn-outline" data-on-click="navigate('orders')" style="font-size:11px;">View Orders</button>
          </div>
        </div>`;
    });
    html += `</div>`;
  }
  html += `</div>`;

  // Sell unpaid orders awaiting buyer payment
  html += `<div data-section="sell-unpaid" class="card" style="margin-top:16px;border-color:var(--accent-amber-glow);">
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;flex-wrap:wrap;gap:8px;">
      <h3 style="margin:0;font-size:15px;color:var(--accent-orange);">Sell Orders — Waiting for Buyer Payment</h3>
      <span data-counter="sell-unpaid-count" style="font-size:12px;color:var(--text-quaternary);">${sellUnpaidOrders.length} unpaid</span>
    </div>`;
  if (sellUnpaidOrders.length === 0) {
    html += `<div style="text-align:center;padding:30px;background:var(--bg-primary);border-radius:10px;border:1px dashed #1e1e2e;">
      <div style="font-size:32px;margin-bottom:8px;opacity:0.4;">⏳</div>
      <p style="color:var(--text-quaternary);font-size:13px;margin:0;">No sell orders waiting for buyer payment</p>
    </div>`;
  } else {
    html += `<div style="display:flex;flex-direction:column;gap:10px;">`;
    sellUnpaidOrders.slice(0, 10).forEach(o => {
      const short = (o.orderId || '').slice(0, 14);
      const fiat = parseFloat(o.fiatAmount || '0').toFixed(2);
      const crypto = parseFloat(o.cryptoAmount || '0').toFixed(4);
      const price = o.unitPrice || '-';
      const cp = o.counterpartyDetail || {};
      const buyerName = cp.userName || cp.takerName || cp.nickName || 'Buyer';
      const created = o.createdTimestamp ? new Date(parseInt(o.createdTimestamp)).toLocaleString() : '';
      const tl = bankTimeLeft(o);
      html += `
        <div style="background:var(--bg-primary);border:1px solid rgba(255,152,0,0.15);border-radius:12px;padding:16px;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;flex-wrap:wrap;gap:8px;">
            <div style="display:flex;align-items:center;gap:8px;">
              <span style="font-family:monospace;font-size:11px;color:var(--text-quaternary);user-select:all;cursor:pointer;" title="Click to copy" data-on-click="copyText('${esc(o.orderId || '')}')">${short}</span>
              <span class="badge badge-sell" style="font-size:10px;">SELL</span>
              <span style="font-size:10px;padding:2px 6px;border-radius:4px;background:var(--accent-amber-glow);color:var(--accent-amber);">UNPAID</span>
              <span style="font-size:11px;background:rgba(255,122,0,0.1);padding:2px 8px;border-radius:4px;color:var(--accent-orange);">${tl}</span>
            </div>
            <div style="font-size:18px;font-weight:700;color:var(--accent-orange);">${crypto} USDT</div>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-bottom:12px;font-size:12px;">
            <div><span style="color:var(--text-quaternary);">Amount:</span> <span style="color:var(--text-primary);">${formatKES(fiat)}</span></div>
            <div><span style="color:var(--text-quaternary);">Price:</span> <span style="color:var(--text-primary);">${price}/USDT</span></div>
            <div><span style="color:var(--text-quaternary);">Buyer:</span> <span style="color:var(--text-primary);">${buyerName}</span></div>
            <div><span style="color:var(--text-quaternary);">Created:</span> <span style="color:var(--text-primary);">${created}</span></div>
          </div>
          <div style="display:flex;gap:8px;flex-wrap:wrap;">
            <button class="btn btn-sm btn-outline" data-on-click="navigate('orders')" style="font-size:11px;">View Orders</button>
          </div>
        </div>`;
    });
    html += `</div>`;
  }
  html += `</div>`;

  // Sell orders awaiting release section
  html += `<div data-section="sell-release" class="card" style="margin-top:16px;">
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;flex-wrap:wrap;gap:8px;">
      <h3 style="margin:0;font-size:15px;">Sell Orders Awaiting Release</h3>
      <span data-counter="sell-release-count" style="font-size:12px;color:var(--text-quaternary);">${sellReleaseOrders.length} paid</span>
    </div>`;
  if (sellReleaseOrders.length === 0) {
    html += `<div style="text-align:center;padding:30px;background:var(--bg-primary);border-radius:10px;border:1px dashed #1e1e2e;">
      <div style="font-size:32px;margin-bottom:8px;opacity:0.4;">🔒</div>
      <p style="color:var(--text-quaternary);font-size:13px;margin:0;">No sell orders awaiting release</p>
    </div>`;
  } else {
    html += `<div style="display:flex;flex-direction:column;gap:10px;">`;
    sellReleaseOrders.slice(0, 10).forEach(o => {
      const short = (o.orderId || '').slice(0, 14);
      const fiat = parseFloat(o.fiatAmount || '0').toFixed(2);
      const crypto = parseFloat(o.cryptoAmount || '0').toFixed(4);
      const price = o.unitPrice || '-';
      const cp = o.counterpartyDetail || {};
      const buyerName = cp.userName || cp.takerName || cp.nickName || 'Buyer';
      const created = o.createdTimestamp ? new Date(parseInt(o.createdTimestamp)).toLocaleString() : '';
      const paidTime = o.paymentTimestamp ? new Date(parseInt(o.paymentTimestamp)).toLocaleString() : created;
      const tl = bankTimeLeft(o);
      html += `
        <div style="background:var(--bg-primary);border:1px solid rgba(255,145,0,0.2);border-radius:12px;padding:16px;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;flex-wrap:wrap;gap:8px;">
            <div style="display:flex;align-items:center;gap:8px;">
              <span style="font-family:monospace;font-size:11px;color:var(--text-quaternary);user-select:all;cursor:pointer;" title="Click to copy" data-on-click="copyText('${esc(o.orderId || '')}')">${short}</span>
              <span class="badge badge-sell" style="font-size:10px;">SELL</span>
              <span style="font-size:10px;padding:2px 6px;border-radius:4px;background:rgba(0,210,104,0.1);color:var(--accent-green);">PAID</span>
              <span style="font-size:11px;background:rgba(255,122,0,0.1);padding:2px 8px;border-radius:4px;color:var(--accent-orange);">${tl}</span>
            </div>
            <div style="font-size:18px;font-weight:700;color:var(--accent-green);">${crypto} USDT</div>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-bottom:12px;font-size:12px;">
            <div><span style="color:var(--text-quaternary);">Amount:</span> <span style="color:var(--text-primary);">${formatKES(fiat)}</span></div>
            <div><span style="color:var(--text-quaternary);">Price:</span> <span style="color:var(--text-primary);">${price}/USDT</span></div>
            <div><span style="color:var(--text-quaternary);">Buyer:</span> <span style="color:var(--text-primary);">${buyerName}</span></div>
            <div><span style="color:var(--text-quaternary);">Paid:</span> <span style="color:var(--text-primary);">${paidTime}</span></div>
            <div style="grid-column:span 2;"><span style="color:var(--text-quaternary);">Created:</span> <span style="color:var(--text-primary);">${created}</span></div>
          </div>
          <div style="display:flex;gap:8px;flex-wrap:wrap;">
            ${window.isAppealedOrder && window.isAppealedOrder(o) ? `<span style="font-size:11px;padding:6px 12px;border-radius:6px;background:rgba(244,67,54,0.12);border:1px solid rgba(244,67,54,0.35);color:var(--accent-red);font-weight:700;white-space:nowrap;">⚖️ Under Appeal — Release Disabled</span>` : `<button class="btn btn-sm btn-primary" data-on-click="releaseSellOrder('${esc(o.orderId)}')" style="background:linear-gradient(135deg,#ff9100,#ffab00);color:var(--text-on-accent);font-weight:700;">
              🚀 Confirm & Release Crypto
            </button>`}
            <button class="btn btn-sm btn-outline" data-on-click="navigate('orders')" style="font-size:11px;">View Orders</button>
          </div>
        </div>`;
    });
    html += `</div>`;
  }
  html += `</div>`;

  if (silent && container._bankRendered) {
    var bankStructOk = !!container.querySelector('[data-section="buy-unpaid"]');
    bankStructOk = bankStructOk && !!container.querySelector('[data-section="buy-paid"]');
    bankStructOk = bankStructOk && !!container.querySelector('[data-section="sell-unpaid"]');
    bankStructOk = bankStructOk && !!container.querySelector('[data-section="sell-release"]');
    if (bankStructOk) {
      _incrementalBankUpdate(container, {
        buyOrders: buyOrders, buyPaidOrders: buyPaidOrders,
        sellUnpaidOrders: sellUnpaidOrders, sellReleaseOrders: sellReleaseOrders,
        balance: balance, agentData: agentData, _lu: _lu
      });
      _refreshAgentCards(agentData);
      startPageCountdown(10);
      startPagePoll(container, () => renderBank(container, null, true), 120000);
      return;
    }
  }
  const horzScrolls = Array.from(container.querySelectorAll('.table-container')).map(function(el) { return el.scrollLeft; });
  container.innerHTML = html;
  container._bankRendered = true;
  restoreHorzScrolls(container, horzScrolls);
  startPageCountdown(10);
  startPagePoll(container, () => renderBank(container, null, true), 120000);
}

async function renderBankBalanceTab(container) {
  try {
    const compRes = await API.get('/api/bank/balance/computation');
    const { balance, entries } = compRes.data || {};
    const bal = parseInt(balance || '0');
    var html = `
      <div class="card" style="border-color:rgba(0,200,83,0.15);margin-bottom:16px;">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <h3 style="margin:0;">🏦 Available Balance</h3>
          <div style="display:flex;align-items:center;gap:12px;">
            <span style="font-size:28px;font-weight:800;color:var(--accent-blue);">${formatKESint(bal)}</span>
            <button class="btn btn-sm btn-outline" data-on-click="setBankBalanceManually()" style="font-size:10px;">✏️ Edit</button>
          </div>
        </div>
        <div style="display:flex;gap:6px;margin-top:10px;">
          <button class="btn btn-sm btn-outline" data-on-click="refreshP2pBalance()" style="font-size:10px;">⟳ Refresh P2P</button>
          <button class="btn btn-sm btn-outline" data-on-click="confirmClearComputation()" style="font-size:10px;color:var(--accent-red);">🗑 Clear History</button>
        </div>
      </div>
      <div class="card" style="border-color:rgba(68,138,255,0.15);">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
          <h3 style="margin:0;">📊 Balance Computation</h3>
          <span style="font-size:12px;color:var(--text-quaternary);">${entries ? entries.length : 0} transactions</span>
        </div>`;
    if (!entries || entries.length === 0) {
      html += `<div style="text-align:center;padding:30px;background:var(--bg-primary);border-radius:10px;border:1px dashed #1e1e2e;">
        <div style="font-size:32px;margin-bottom:8px;opacity:0.4;">📊</div>
        <p style="color:var(--text-quaternary);font-size:13px;margin:0;">No transactions yet — balance will update as buy and sell orders are processed</p>
      </div>`;
    } else {
      html += `<div class="table-container"><table style="font-size:12px;"><thead><tr>
        <th>#</th><th>Order ID</th><th>Type</th><th>Amount (KES)</th><th>Effect</th><th>Date</th>
      </tr></thead><tbody>`;
      entries.forEach((e, i) => {
        const amt = parseFloat(e.fiat_amount || '0');
        html += `<tr>
          <td style="color:var(--text-quaternary);font-size:11px;">${i + 1}</td>
          <td style="font-family:monospace;font-size:11px;color:#8888a0;user-select:all;cursor:pointer;" data-on-click="copyText('${esc(e.order_id || '')}')">${(e.order_id || '').slice(0,14)}</td>
          // Badge classes are intentionally inverted: buy = PAY (money out, red), sell = RECEIVE (money in, green)
          <td><span class="badge badge-${e.side === 'buy' ? 'sell' : 'buy'}">${e.side === 'buy' ? 'PAY ↓' : 'RECEIVE ↑'}</span></td>
          <td style="font-weight:700;">${formatKES(amt.toFixed(2))}</td>
          <td style="color:${e.side === 'sell' ? '#00c853' : '#f44336'};">${e.side === 'sell' ? '+' : '−'}${formatKES(amt.toFixed(2))}</td>
          <td style="color:var(--text-quaternary);font-size:11px;">${e.processed_at ? new Date(e.processed_at).toLocaleString() : '—'}</td>
        </tr>`;
      });
      html += `</tbody></table></div>`;
      html += `<div style="margin-top:12px;padding:12px;background:var(--bg-primary);border-radius:8px;border:1px solid var(--border-primary);">
        <div style="display:flex;justify-content:space-between;font-size:13px;">
          <span style="color:var(--text-tertiary);">Running balance after all transactions:</span>
          <span style="font-weight:800;color:var(--accent-blue);font-size:18px;">${formatKESint(bal)}</span>
        </div>
      </div>`;
    }
    html += `</div>`;
    if (container) container.insertAdjacentHTML('beforeend', html);
  } catch (err) { showToast(err.message, 'error'); }
}

window.confirmClearComputation = function() {
  if (!confirm('Clear all balance computation history?')) return;
  API.del('/api/bank/balance/computation').then(() => {
    showToast('Computation history cleared', 'success');
    refreshBankPage();
  }).catch(err => showToast(err.message, 'error'));
};
function refreshBankPage() {
  const el = document.getElementById('page-content');
  if (el) { showToast('Refreshing bank data…', 'info'); loadAgentData(); renderBank(el); }
}

/* ===== I&M Payment Agent (PC integration app) ===== */

function _agentTimeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return s + 's ago';
  if (s < 3600) return Math.floor(s / 60) + 'm ago';
  if (s < 86400) return Math.floor(s / 3600) + 'h ago';
  return Math.floor(s / 86400) + 'd ago';
}

function agentTileHtml(d) {
  const devices = (d && d.devices) || [];
  if (!devices.length) return '<span style="color:var(--text-quaternary);">⚪ Not linked</span>';
  if (devices.some(x => x.online)) return '<span style="color:#00c853;">🟢 Online</span>';
  return '<span style="color:#ff9100;">🟡 Offline</span>';
}

function agentCardBodyHtml(d) {
  const devices = (d && d.devices) || [];
  let h = '';
  if (d && d.paymentInProgress) {
    h += `<div style="padding:8px 12px;background:rgba(255,152,0,0.12);border:1px solid rgba(255,152,0,0.35);border-radius:8px;margin-bottom:10px;font-size:12px;color:#ff9100;font-weight:600;">⏳ A payment is currently running on a linked PC — wait before sending another to avoid duplicates.</div>`;
  }
  if (!devices.length) {
    h += `<div style="padding:12px;background:var(--bg-primary);border-radius:8px;border:1px dashed #1e1e2e;margin-bottom:12px;">
      <div style="font-size:12px;color:var(--text-tertiary);">No PC linked yet. Install <b>MoneyFest I&amp;M Bank Integration</b> on your PC, then enter its integration code below.</div>
    </div>`;
  } else {
    devices.forEach(dev => {
      h += `<div style="display:flex;align-items:center;gap:8px;padding:10px;background:var(--bg-primary);border-radius:8px;border:1px solid var(--border-primary);margin-bottom:8px;flex-wrap:wrap;">
        <span style="width:8px;height:8px;border-radius:50%;background:${dev.online ? '#00c853' : '#ff9100'};display:inline-block;flex-shrink:0;"></span>
        <div style="flex:1;min-width:120px;">
          <div style="font-size:12px;font-weight:600;">${esc(dev.name || 'Your PC')}</div>
          <div style="font-size:10px;color:var(--text-quaternary);">${dev.online ? 'Online — ready for payments' : 'Offline — open the app on the PC'}${dev.version ? ' · v' + esc(dev.version) : ''}${dev.lastSeen ? ' · seen ' + _agentTimeAgo(dev.lastSeen) : ''}</div>
        </div>
        <button class="btn btn-sm btn-outline" data-on-click="unlinkAgentDevice('${esc(dev.deviceId)}')" style="font-size:10px;color:var(--accent-red);border-color:rgba(244,67,54,0.3);">Unlink</button>
      </div>`;
    });
  }
  const accts = (d && d.bankAccounts) || [];
  if (accts.length) {
    const buyAccount = String((d && d.buyAccount) || '').replace(/\s/g, '');
    h += `<div style="font-size:11px;color:var(--text-tertiary);margin:8px 0 4px;font-weight:600;">I&M accounts <span style="color:var(--text-quaternary);font-weight:400;">· fetched by your PC agent</span></div>`;
    accts.forEach(a => {
      const isBuy = buyAccount && String(a.account || '').replace(/\s/g, '') === buyAccount;
      h += `<div style="display:flex;justify-content:space-between;align-items:center;padding:6px 8px;background:var(--bg-primary);border-radius:6px;border:1px solid ${isBuy ? 'rgba(0,200,83,0.4)' : 'var(--border-primary)'};margin-bottom:4px;font-size:11px;">
        <div style="min-width:0;"><div style="font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${esc(a.name || 'Account')}${isBuy ? ' <span style="color:#00c853;font-size:9px;">BUY</span>' : ''}</div><div style="color:var(--text-quaternary);font-family:monospace;">${esc(a.account || '')}</div></div>
        <div style="font-weight:700;color:var(--accent-blue);white-space:nowrap;">KES ${formatKESint(a.balance || 0)}</div>
      </div>`;
    });
    const total = accts.reduce((sum, a) => sum + (Number(a.balance) || 0), 0);
    h += `<div style="display:flex;justify-content:space-between;padding:6px 8px;font-size:11px;font-weight:700;border-top:1px solid #1e1e2e;margin-top:4px;"><span>Total bank balance</span><span style="color:var(--accent-blue);">KES ${formatKESint(total)}</span></div>`;
  }
  h += `<div style="font-size:11px;color:var(--text-tertiary);margin-bottom:6px;">Link another PC — enter its integration code (shown in the PC app):</div>
    <div style="display:flex;gap:6px;">
      <input id="agent-link-code" placeholder="MF-XXXX-XXXX" maxlength="12" autocomplete="off" style="flex:1;padding:8px 10px;border-radius:8px;border:1px solid var(--border-primary);background:var(--bg-primary);color:var(--text-primary);font-size:12px;text-transform:uppercase;">
      <button class="btn btn-sm btn-primary" data-on-click="linkAgentDevice()">Link</button>
    </div>`;
  return h;
}

// Merchant presence (okx-tab) health. The tab writes /root/.okx-tab-alive.json
// every probe; when OKX expires the seeded session it flips to needs-login and
// ads silently leave the marketplace until a fresh capture is uploaded.
const PRESENCE_POLL_MS = 60000;
const PRESENCE_STALE_MS = 180000;

function presenceState(s) {
  if (!s) return { label: 'Presence status: checking…', color: 'var(--text-tertiary)', bad: false };
  const age = s.lastSeen ? (Date.now() - new Date(s.lastSeen).getTime()) : Infinity;
  if (s.state === 'authed' && age <= PRESENCE_STALE_MS) {
    return { label: 'Merchant online — presence active', color: 'var(--accent-green)', bad: false };
  }
  if (s.state === 'needs-login') {
    return { label: 'OKX session EXPIRED — capture a fresh session', color: '#ff5252', bad: true };
  }
  if (s.state === 'paused' || s.state === 'dormant') {
    return { label: 'Presence paused — your ads are offline by choice', color: 'var(--text-tertiary)', bad: false };
  }
  if (s.state === 'authed') {
    return { label: 'Presence heartbeat stale (>' + Math.round(PRESENCE_STALE_MS / 60000) + 'min) — tab may be stalled', color: '#ff9100', bad: true };
  }
  return { label: 'Presence NOT online (state: ' + s.state + ') — merchant shows offline', color: '#ff5252', bad: true };
}

function presenceRowHtml() {
  const p = presenceState(window.__okxTabStatus);
  return `<div id="presence-row" style="display:flex;align-items:center;justify-content:space-between;gap:8px;padding:8px 0;border-bottom:1px solid #1e1e2e;">
    <div style="font-size:11px;color:${p.color};font-weight:${p.bad ? '700' : '400'};">${p.label}</div>
    <button class="btn btn-sm" data-on-click="refreshOkxTabStatus(true)" style="flex-shrink:0;padding:4px 10px;font-size:11px;">Check</button>
  </div>`;
}

function patchPresenceRow() {
  const el = document.getElementById('presence-row');
  if (!el) return;
  const p = presenceState(window.__okxTabStatus);
  el.innerHTML = `<div style="font-size:11px;color:${p.color};font-weight:${p.bad ? '700' : '400'};">${p.label}</div>
    <button class="btn btn-sm" data-on-click="refreshOkxTabStatus(true)" style="flex-shrink:0;padding:4px 10px;font-size:11px;">Check</button>`;
}

async function refreshOkxTabStatus(manual) {
  try {
    const r = await API.get('/api/okx/tab/status');
    window.__okxTabStatus = (r && r.data) || null;
  } catch (e) {
    if (manual && typeof showToast === 'function') showToast('Could not read presence status', 'error', 3000);
  }
  patchPresenceRow();
}

if (typeof window !== 'undefined' && !window.__okxTabPoll) {
  window.__okxTabPoll = setInterval(function () { if (!document.hidden) refreshOkxTabStatus(false); }, PRESENCE_POLL_MS);
  refreshOkxTabStatus(false);
}

function autopayCardBodyHtml(d) {
  const ap = (d && d.autopay) || {};
  const autoPay = ap.autoPay !== false;
  const engineOn = ap.engineRunning !== false;
  const online = !!(d && d.devices && d.devices.some(x => x.online));
  const switchCss = 'position:relative;display:inline-block;width:44px;height:24px;flex-shrink:0;';
  const sliderCss = 'position:absolute;cursor:pointer;top:0;left:0;right:0;bottom:0;background:#2a2a3e;border-radius:24px;transition:0.3s;';
  let h = `${presenceRowHtml()}<label style="display:flex;align-items:center;justify-content:space-between;padding:8px 0;cursor:pointer;">
    <div>
      <div style="font-size:13px;color:var(--text-primary);font-weight:600;">Auto-pay buy orders</div>
      <div style="font-size:11px;color:${autoPay && engineOn && online ? 'var(--accent-green)' : 'var(--text-tertiary)'};">${!autoPay ? 'Off — pay orders manually' : (!engineOn ? 'Engine stopped — reconnecting' : (online ? 'Active — PC connected' : 'Waiting for your PC to come online'))}</div>
    </div>
    <label class="switch" style="${switchCss}">
      <input type="checkbox" ${autoPay ? 'checked' : ''} onchange="toggleAutoPay(this.checked)" style="opacity:0;width:0;height:0;">
      <span class="slider" style="${sliderCss}"></span>
    </label>
  </label>
  <label style="display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-top:1px solid #1e1e2e;cursor:pointer;">
    <div>
      <div style="font-size:13px;color:var(--text-primary);">Mobile money</div>
      <div style="font-size:11px;color:var(--text-tertiary);">M-Pesa &amp; Airtel (network auto-detected)</div>
    </div>
    <label class="switch" style="${switchCss}">
      <input type="checkbox" ${ap.payMobile !== false ? 'checked' : ''} onchange="toggleAgentRail('payMobile', this.checked)" style="opacity:0;width:0;height:0;">
      <span class="slider" style="${sliderCss}"></span>
    </label>
  </label>
  <label style="display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-top:1px solid #1e1e2e;cursor:pointer;">
    <div>
      <div style="font-size:13px;color:var(--text-primary);">Bank transfer</div>
      <div style="font-size:11px;color:var(--text-tertiary);">Local transfers to bank accounts</div>
    </div>
    <label class="switch" style="${switchCss}">
      <input type="checkbox" ${ap.payBank !== false ? 'checked' : ''} onchange="toggleAgentRail('payBank', this.checked)" style="opacity:0;width:0;height:0;">
      <span class="slider" style="${sliderCss}"></span>
    </label>
  </label>
  <div style="font-size:10px;color:var(--text-quaternary);padding-top:8px;border-top:1px solid #1e1e2e;">One payment per order — duplicates are blocked automatically. A failed or timed-out payment never auto-retries; you get a notification and can retry manually.</div>`;
  return h;
}

function agentPaymentsHtml(d) {
  const payments = (d && d.payments) || [];
  let h = `<div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px;align-items:center;">
    <select id="agent-pay-kind" style="padding:8px 10px;border-radius:8px;border:1px solid var(--border-primary);background:var(--bg-primary);color:var(--text-primary);font-size:12px;">
      <option value="mobile">Mobile (M-Pesa / Airtel)</option>
      <option value="bank">Bank transfer</option>
    </select>
    <input id="agent-pay-target" placeholder="Phone or account number" autocomplete="off" style="flex:1;min-width:140px;padding:8px 10px;border-radius:8px;border:1px solid var(--border-primary);background:var(--bg-primary);color:var(--text-primary);font-size:12px;">
    <input id="agent-pay-amount" type="number" min="1" step="1" placeholder="KES" style="width:90px;padding:8px 10px;border-radius:8px;border:1px solid var(--border-primary);background:var(--bg-primary);color:var(--text-primary);font-size:12px;">
    <button class="btn btn-sm btn-primary" data-on-click="sendAgentPayment()">Send</button>
    <span style="font-size:10px;color:var(--text-quaternary);">manual payment — runs on your PC</span>
  </div>`;
  if (!payments.length) {
    h += `<div style="text-align:center;padding:24px;background:var(--bg-primary);border-radius:10px;border:1px dashed #1e1e2e;">
      <div style="font-size:28px;margin-bottom:6px;opacity:0.4;">💳</div>
      <p style="color:var(--text-quaternary);font-size:13px;margin:0;">No payments yet — payments executed by the agent will appear here with their proofs.</p>
    </div>`;
    return h;
  }
  h += `<div class="table-container"><table style="font-size:12px;"><thead><tr>
    <th>Time</th><th>Type</th><th>To</th><th class="num">Amount</th><th>Status</th><th>Reference</th><th>Proofs</th>
  </tr></thead><tbody>`;
  payments.forEach(p => {
    const ok = p.status === 'success';
    const stColor = ok ? 'var(--accent-green)' : (p.status === 'failed' ? 'var(--accent-red)' : '#ff9100');
    const stLabel = ok ? '✓ success' : (p.status === 'timeout' ? '⏱ timeout' : '✗ ' + (p.status || 'failed'));
    let proofs = '';
    if (p.hasProof) proofs += `<button class="btn btn-sm btn-outline" data-on-click="openAgentProof('${esc(p.id)}')" style="font-size:10px;margin-right:4px;">📷 Receipt</button>`;
    if (p.hasSms) proofs += `<button class="btn btn-sm btn-outline" data-on-click="showSmsProof('${esc(p.id)}')" style="font-size:10px;">💬 SMS</button>`;
    h += `<tr>
      <td style="color:var(--text-quaternary);font-size:11px;">${p.createdAt ? new Date(p.createdAt).toLocaleString() : '—'}</td>
      <td><span class="tbadge" style="font-size:10px;padding:2px 8px;border-radius:4px;background:${p.kind === 'bank' ? 'rgba(68,138,255,0.12)' : 'rgba(0,200,83,0.12)'};color:${p.kind === 'bank' ? 'var(--accent-blue)' : 'var(--accent-green)'};">${p.kind === 'bank' ? 'BANK' : 'MOBILE'}</span></td>
      <td style="font-family:monospace;font-size:11px;">${esc(p.target || '')}</td>
      <td class="num" style="font-weight:700;">KES ${formatKESint(p.amount)}</td>
      <td><span style="color:${stColor};font-weight:600;font-size:11px;" title="${esc(p.error || '')}">${stLabel}</span>${p.error ? `<div style="font-size:10px;color:var(--text-quaternary);max-width:160px;overflow:hidden;text-overflow:ellipsis;" title="${esc(p.error)}">${esc(p.error.slice(0, 60))}</div>` : ''}</td>
      <td style="font-family:monospace;font-size:11px;color:#8888a0;user-select:all;cursor:pointer;" data-on-click="copyText('${esc(p.reference || '')}')">${esc((p.reference || '—').slice(-14))}${p.orderId ? `<div style="font-size:10px;color:var(--text-quaternary);">order ${esc(p.orderId.slice(0, 12))}</div>` : ''}</td>
      <td>${proofs || '<span style="color:var(--text-quaternary);font-size:11px;">—</span>'}</td>
    </tr>`;
  });
  h += `</tbody></table></div>`;
  return h;
}

function _refreshAgentCards(d) {
  if (!d || !d.devices) return;
  window._agentData = d;
  const ab = document.getElementById('agent-card-body');
  if (ab) ab.innerHTML = agentCardBodyHtml(d);
  const apb = document.getElementById('agent-autopay-body');
  if (apb) apb.innerHTML = autopayCardBodyHtml(d);
  const pb = document.getElementById('agent-pay-body');
  if (pb) pb.innerHTML = agentPaymentsHtml(d);
  const tile = document.querySelector('[data-stat="agent"]');
  if (tile) tile.innerHTML = agentTileHtml(d);
}

async function loadAgentData() {
  try {
    const d = await API.get('/api/bank-agent/overview');
    if (d && d.devices) _refreshAgentCards(d);
  } catch (e) {}
}

function _agentModal(innerHtml) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `<div class="modal-content" style="max-width:560px;">${innerHtml}</div>`;
  document.body.appendChild(overlay);
  if (typeof bindClickHandlers === 'function') bindClickHandlers(overlay);
  return overlay;
}

window.refreshAgentPayments = function() { loadAgentData(); };

window.linkAgentDevice = async function() {
  const input = document.getElementById('agent-link-code');
  const code = (input && input.value || '').trim().toUpperCase();
  if (!/^MF-[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(code)) { showToast('Enter the integration code from the PC app (MF-XXXX-XXXX)', 'error'); return; }
  try {
    const res = await API.post('/api/bank-agent/link', { code });
    showToast('PC linked: ' + ((res.device && res.device.name) || 'MoneyFest PC app'), 'success');
    loadAgentData();
  } catch (err) { showToast(err.message, 'error'); }
};

window.unlinkAgentDevice = async function(deviceId) {
  if (!confirm('Unlink this PC? Automatic payments will stop until you link a PC again.')) return;
  try {
    await API.del('/api/bank-agent/devices/' + encodeURIComponent(deviceId));
    showToast('PC unlinked', 'info');
    loadAgentData();
  } catch (err) { showToast(err.message, 'error'); }
};

window.toggleAutoPay = async function(checked) {
  try {
    await API.post('/api/automation/v2/config/engine', { autoPay: !!checked });
    // The actual payment loop lives in the v1 automation engine.
    if (checked) { try { await API.post('/api/automation/start'); } catch (e) {} }
    else { try { await API.post('/api/automation/stop'); } catch (e) {} }
    showToast('Auto-pay ' + (checked ? 'enabled' : 'disabled'), checked ? 'success' : 'info');
  } catch (err) { showToast(err.message, 'error'); }
  loadAgentData();
};

window.toggleAgentRail = async function(rail, checked) {
  try {
    const payload = rail === 'payMobile'
      ? { payMpesa: !!checked, payAirtel: !!checked }
      : { payBank: !!checked };
    await API.post('/api/automation/v2/config/engine', payload);
    showToast((rail === 'payMobile' ? 'Mobile money' : 'Bank transfer') + (checked ? ' enabled' : ' disabled'), 'info');
  } catch (err) { showToast(err.message, 'error'); }
  loadAgentData();
};

window.sendAgentPayment = async function() {
  const kind = document.getElementById('agent-pay-kind').value;
  const target = (document.getElementById('agent-pay-target').value || '').trim();
  const amount = parseInt(document.getElementById('agent-pay-amount').value, 10);
  if (!target) { showToast('Enter the phone number or account number', 'error'); return; }
  if (!amount || amount <= 0) { showToast('Enter a valid amount', 'error'); return; }
  showToast('Payment sent to your PC — waiting for result…', 'info');
  try {
    const res = await API.post('/api/bank-agent/pay', { kind, target, amount, manual: true }, 330000);
    if (res.duplicate) showToast(res.error || 'Duplicate payment blocked', 'error', 6000);
    else if (res.ok) showToast('Payment done — ref ' + (res.reference || '') + (res.proofFile ? ' (proof saved)' : ''), 'success', 6000);
    else showToast('Payment failed: ' + (res.error || 'unknown error'), 'error', 8000);
  } catch (err) { showToast(err.message, 'error', 6000); }
  loadAgentData();
};

window.openAgentProof = async function(paymentId) {
  const overlay = _agentModal('<div style="text-align:center;padding:20px;"><div class="spinner"></div><p style="color:var(--text-tertiary);font-size:13px;">Loading receipt…</p></div>');
  try {
    const res = await fetch('/api/bank-agent/payments/' + encodeURIComponent(paymentId) + '/proof', {
      headers: { Authorization: 'Bearer ' + API.getToken() }
    });
    if (!res.ok) throw new Error('Receipt not available');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    overlay.querySelector('.modal-content').innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
        <b>Screenshot receipt</b>
        <button class="btn btn-sm btn-outline" data-on-click="this.closest('.modal-overlay').remove()">Close</button>
      </div>
      <img src="${url}" style="width:100%;border-radius:8px;border:1px solid var(--border-primary);" alt="Payment receipt" />`;
  } catch (err) {
    overlay.querySelector('.modal-content').innerHTML = `<p style="color:var(--accent-red);font-size:13px;">${esc(err.message)}</p>
      <button class="btn btn-sm btn-outline" data-on-click="this.closest('.modal-overlay').remove()">Close</button>`;
  }
};

window.showSmsProof = function(paymentId) {
  const d = window._agentData || {};
  const p = (d.payments || []).find(x => x.id === paymentId);
  if (!p) { showToast('SMS proof not found — refresh the page', 'error'); return; }
  _agentModal(`
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
      <b>Bank SMS confirmation</b>
      <button class="btn btn-sm btn-outline" data-on-click="this.closest('.modal-overlay').remove()">Close</button>
    </div>
    <div style="font-size:12px;color:var(--text-tertiary);margin-bottom:8px;">Ref: <span style="font-family:monospace;user-select:all;">${esc(p.smsRef || '—')}</span>${p.smsAt ? ' · ' + new Date(p.smsAt).toLocaleString() : ''}</div>
    <pre style="white-space:pre-wrap;font-size:12px;background:var(--bg-primary);border:1px solid var(--border-primary);border-radius:8px;padding:12px;margin:0;color:var(--text-primary);">${esc(p.smsText || 'No SMS text stored')}</pre>`);
};

async function refreshP2pBalance() {
  try {
    const res = await API.get('/api/okx/p2p/user/balance?currencySymbol=USDT');
    showToast('P2P balance refreshed', 'success');
    navigate('bank');
  } catch (err) { showToast(err.message, 'error'); }
}

async function setBankBalanceManually() {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal-content" style="max-width:400px;">
      <h3>Set Bank Balance</h3>
      <p style="color:var(--text-tertiary);font-size:13px;margin-bottom:16px;">Enter bank balance manually (KES, whole number)</p>
      <form data-on-submit="submitBankBalance(event)">
        <div class="form-group"><input id="manual-balance" type="number" step="1" placeholder="e.g. 500000" required style="width:100%;padding:12px;font-size:18px;font-weight:700;text-align:center;border-radius:10px;border:1px solid var(--border-primary);background:var(--bg-primary);color:var(--accent-blue);"></div>
        <div class="form-actions">
          <button type="button" class="btn btn-secondary" data-on-click="this.closest('.modal-overlay').remove()">Cancel</button>
          <button type="submit" class="btn btn-primary">Save</button>
        </div>
      </form>
    </div>`;
  document.body.appendChild(overlay);
  if (typeof bindClickHandlers === 'function') bindClickHandlers(overlay);
  if (typeof bindFormHandlers === 'function') bindFormHandlers(overlay);
}

async function submitBankBalance(e) {
  e.preventDefault();
  try {
    const val = document.getElementById('manual-balance').value;
    await API.put('/api/bank/balance', { balance: parseInt(val) });
    e.target.closest('.modal-overlay').remove();
    showToast('Bank balance updated', 'success');
    navigate('bank');
  } catch (err) { showToast(err.message, 'error'); }
}

async function releaseSellOrder(orderId) {
  try {
    const order = window._ordersMap && window._ordersMap[orderId];
    if (order && window.isAppealedOrder && window.isAppealedOrder(order)) {
      showToast('⚠️ Order is under appeal — release blocked', 'error', 5000);
      return;
    }
    const amount = order ? String(order.fiatAmount || '') : '';
    const res = await API.post('/api/okx/p2p/orders/' + orderId + '/release', { amount });
    if (order && order.fiatAmount) {
      API.post('/api/bank/auto-sync', { orders: [{ orderId, side: 'sell', fiatAmount: order.fiatAmount, paymentStatus: 'paid', state: 'completed' }] }).catch(() => {});
    }
    showToast('Crypto released successfully!', 'success');
  } catch (err) { console.error(err); showToast(err.message, 'error'); }
}

window.payNowOrder = async function(orderId) {
  if (!orderId) { showToast('Order id missing — refresh and try again', 'error'); return; }
  showToast('Running autopay for this order…', 'info');
  try {
    let res = await API.post('/api/automation/process-now', { manualRetry: true, orderId });
    if (res && res.needsConfirm) {
      if (!confirm((res.error || 'Previous attempt timed out.') + '\n\nOnly retry if your bank/M-Pesa shows NO payment for this order.')) return;
      res = await API.post('/api/automation/process-now', { manualRetry: true, orderId, force: true });
    }
    if (res && res.blocked) {
      showToast(res.error || 'Payment for this order is already running', 'error', 7000);
      return;
    }
    showToast('Autopay triggered — check Automation logs / bank payment log', 'success', 5000);
  } catch (err) {
    showToast(err.message || 'Autopay failed', 'error', 5000);
  }
  const el = document.getElementById('page-content');
  if (el) renderBank(el);
};

async function markBuyOrderPaid(orderId, proofUrl) {
  try {
    // Preferences cached for 60s — no blocking roundtrip on every mark-paid tap.
    var prefs = {};
    if (window._userPrefsCache && Date.now() - window._userPrefsCache.ts < 60000) prefs = window._userPrefsCache.data;
    else {
      try { prefs = (await API.get('/api/auth/preferences')).data || {}; window._userPrefsCache = { data: prefs, ts: Date.now() }; } catch {}
    }
    if (prefs.requirePaymentProof && !proofUrl) {
      if (prefs.paymentProofImage && prefs.paymentProofUseForAll) {
        proofUrl = prefs.paymentProofImage;
      } else {
        showFileUploader(function(url) {
          markBuyOrderPaid(orderId, url);
        });
        return;
      }
    }
    var payload = {};
    if (proofUrl) payload.paymentProofFileUrls = proofUrl;
    // Instant visual feedback — OKX ack can take 5-20s.
    optimisticMarkPaid(orderId);
    try {
      // Long timeout (90s): mark-paid runs on the OKX foreground queue, which can
      // be paused up to 60s by a throttle; a 30s default would abort the call and
      // the order would expire un-paid even though payment was made.
      await API.post('/api/okx/p2p/orders/' + orderId + '/mark-paid', payload, 90000);
    } catch (postErr) {
      revertOptimisticPaid(orderId);
      throw postErr;
    }
    showToast('Marked as paid — waiting for seller to release', 'success');
    var o = window._ordersMap?.[orderId];
    if (o && o.fiatAmount) {
      API.post('/api/bank/auto-sync', { orders: [{ orderId, side: 'buy', fiatAmount: o.fiatAmount, paymentStatus: 'paid', state: 'pending' }] }).catch(function() {});
    }
  } catch (err) { showToast(err.message, 'error'); }
}
function _incrementalBankUpdate(container, state) {
  var lu = document.getElementById('page-last-updated');
  if (lu) lu.innerHTML = 'Updated ' + state._lu;
  // Update stats
  var setStat = function(name, text) {
    var el = container.querySelector('[data-stat="' + name + '"]');
    if (el) el.innerHTML = text;
  };
  setStat('buy-unpaid', state.buyOrders.length);
  setStat('buy-paid', state.buyPaidOrders.length);
  setStat('sell-unpaid', state.sellUnpaidOrders.length);
  setStat('sell-release', state.sellReleaseOrders.length);
  setStat('p2p-balance', parseFloat(state.balance).toFixed(2) + ' USDT');
  if (state.agentData) _refreshAgentCards(state.agentData);
  // Update section counters
  var setCounter = function(name, text) {
    var el = container.querySelector('[data-counter="' + name + '"]');
    if (el) el.innerHTML = text;
  };
  setCounter('buy-unpaid-count', state.buyOrders.length + ' unpaid');
  setCounter('buy-paid-count', state.buyPaidOrders.length + ' paid');
  setCounter('sell-unpaid-count', state.sellUnpaidOrders.length + ' unpaid');
  setCounter('sell-release-count', state.sellReleaseOrders.length + ' paid');
  // Build and set section content
  var setSection = function(section, html) {
    var el = container.querySelector('[data-section="' + section + '"]');
    if (!el) return;
    // Preserve the card header area (first child div with display:flex)
    var header = el.querySelector('div[style*="justify-content"]');
    var nextSibling = header ? header.nextElementSibling : null;
    // Remove everything after header
    while (nextSibling) {
      var rm = nextSibling;
      nextSibling = nextSibling.nextElementSibling;
      rm.remove();
    }
    // Insert new content after header
    if (header && header.parentNode) {
      header.insertAdjacentHTML('afterend', html);
    }
  };
  // Build section content HTML (order cards or empty state)
  var cardCss = 'style="background:var(--bg-primary);border:1px solid var(--border-primary);border-radius:12px;padding:16px;"';
  var emptyCss = 'style="text-align:center;padding:30px;background:var(--bg-primary);border-radius:10px;border:1px dashed #1e1e2e;"';
  // Buy unpaid
  var buyH = '';
  if (state.buyOrders.length === 0) {
    buyH = '<div ' + emptyCss + '><div style="font-size:32px;margin-bottom:8px;opacity:0.4;">✅</div><p style="color:var(--text-quaternary);font-size:13px;margin:0;">No buy orders awaiting payment</p></div>';
  } else {
    buyH = '<div style="display:flex;flex-direction:column;gap:10px;">';
    state.buyOrders.slice(0, 10).forEach(function(o) {
      var short = (o.orderId || '').slice(0, 14);
      var fiat = parseFloat(o.fiatAmount || '0').toFixed(2);
      var crypto = parseFloat(o.cryptoAmount || '0').toFixed(4);
      var price = o.unitPrice || '-';
      var cp = o.counterpartyDetail?.takerPaymentMethod || o.takerPaymentMethod || {};
      var bankName = cp.bankName || cp.type || 'Bank Transfer';
      var accountNo = cp.accountNo || cp.accountNumber || '';
      var ref = o.orderId?.slice(-8) || '';
      var created = o.createdTimestamp ? new Date(parseInt(o.createdTimestamp)).toLocaleString() : '';
      var customerRef = ref;
      var tl = bankTimeLeft(o);
      buyH += '<div ' + cardCss + '><div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;flex-wrap:wrap;gap:8px;"><div style="display:flex;align-items:center;gap:8px;"><span style="font-family:monospace;font-size:11px;color:var(--text-quaternary);user-select:all;cursor:pointer;" title="Click to copy" data-on-click="copyText(\'' + esc(o.orderId || '') + '\')">' + short + '</span><span class="badge badge-buy" style="font-size:10px;">BUY</span><span style="font-size:11px;background:rgba(255,122,0,0.1);padding:2px 8px;border-radius:4px;color:var(--accent-orange);">' + tl + '</span></div><div style="font-size:18px;font-weight:700;color:var(--accent-orange);">' + formatKES(fiat) + '</div></div><div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-bottom:12px;font-size:12px;"><div><span style="color:var(--text-quaternary);">Crypto:</span> <span style="color:var(--text-primary);">' + crypto + ' USDT</span></div><div><span style="color:var(--text-quaternary);">Price:</span> <span style="color:var(--text-primary);">' + price + '/USDT</span></div><div><span style="color:var(--text-quaternary);">Created:</span> <span style="color:var(--text-primary);">' + created + '</span></div></div><div style="background:rgba(91,124,250,0.06);border:1px solid rgba(68,138,255,0.1);border-radius:8px;padding:12px;margin-bottom:12px;"><div style="font-size:11px;color:#8888a0;font-weight:600;margin-bottom:6px;">Payment Details</div><div style="display:grid;grid-template-columns:1fr 1fr;gap:4px;font-size:12px;"><div><span style="color:var(--text-quaternary);">Bank:</span> <span style="color:var(--text-primary);">' + bankName + '</span></div><div><span style="color:var(--text-quaternary);">Account:</span> <span style="color:var(--text-primary);font-family:monospace;">' + (accountNo || '—') + '</span></div><div><span style="color:var(--text-quaternary);">Amount:</span> <span style="color:var(--accent-orange);font-weight:700;">' + formatKES(fiat) + '</span></div><div><span style="color:var(--text-quaternary);">Ref:</span> <span style="color:var(--text-primary);font-family:monospace;">' + ref + '</span></div></div></div><div style="display:flex;gap:8px;flex-wrap:wrap;"><button class="btn btn-sm btn-primary" data-on-click="markBuyOrderPaid(\'' + esc(o.orderId) + '\')" style="background:linear-gradient(135deg,var(--accent-green),var(--accent-green-bright));color:var(--text-on-accent);font-weight:600;">✓ I\'ve Paid — Release Crypto</button><button class="btn btn-sm btn-outline" data-on-click="payNowOrder(\'\' + esc(o.orderId) + \'\')" style="font-size:11px;border-color:var(--accent-blue);color:var(--accent-blue);">Pay now</button><button class="btn btn-sm btn-outline" data-on-click="navigate(\'orders\')" style="font-size:11px;">View Orders</button></div></div>';
    });
    buyH += '</div>';
  }
  setSection('buy-unpaid', buyH);
  // Buy paid
  var buyPaidH = '';
  if (state.buyPaidOrders.length === 0) {
    buyPaidH = '<div ' + emptyCss + '><div style="font-size:32px;margin-bottom:8px;opacity:0.4;">🔵</div><p style="color:var(--text-quaternary);font-size:13px;margin:0;">No paid buy orders awaiting seller release</p></div>';
  } else {
    buyPaidH = '<div style="display:flex;flex-direction:column;gap:10px;">';
    state.buyPaidOrders.slice(0, 10).forEach(function(o) {
      var short = (o.orderId || '').slice(0, 14);
      var fiat = parseFloat(o.fiatAmount || '0').toFixed(2);
      var crypto = parseFloat(o.cryptoAmount || '0').toFixed(4);
      var cp = o.counterpartyDetail || {};
      var sellerName = cp.userName || cp.sellerName || cp.nickName || 'Seller';
      var paidTime = o.paymentTimestamp ? new Date(parseInt(o.paymentTimestamp)).toLocaleString() : '';
      buyPaidH += '<div style="background:var(--bg-primary);border:1px solid rgba(68,138,255,0.15);border-radius:12px;padding:16px;"><div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;flex-wrap:wrap;gap:8px;"><div style="display:flex;align-items:center;gap:8px;"><span style="font-family:monospace;font-size:11px;color:var(--text-quaternary);user-select:all;cursor:pointer;" title="Click to copy" data-on-click="copyText(\'' + esc(o.orderId || '') + '\')">' + short + '</span><span class="badge badge-buy" style="font-size:10px;">BUY</span><span style="font-size:10px;padding:2px 6px;border-radius:4px;background:rgba(91,124,250,0.1);color:var(--accent-blue);">PAID</span><span style="font-size:11px;color:var(--accent-blue);">Awaiting release</span></div><div style="font-size:18px;font-weight:700;color:var(--accent-blue);">' + crypto + ' USDT</div></div><div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:12px;font-size:12px;"><div><span style="color:var(--text-quaternary);">Amount:</span> <span style="color:var(--text-primary);">' + formatKES(fiat) + '</span></div><div><span style="color:var(--text-quaternary);">Seller:</span> <span style="color:var(--text-primary);">' + sellerName + '</span></div><div><span style="color:var(--text-quaternary);">Paid at:</span> <span style="color:var(--text-primary);">' + paidTime + '</span></div></div><div style="display:flex;gap:8px;flex-wrap:wrap;"><button class="btn btn-sm btn-outline" data-on-click="appealOrder(\'' + esc(o.orderId) + '\')" style="font-size:11px;color:var(--accent-orange);border-color:#ff980044;font-weight:600;">⚠️ Appeal</button><button class="btn btn-sm btn-outline" data-on-click="navigate(\'orders\')" style="font-size:11px;">View Orders</button></div></div>';
    });
    buyPaidH += '</div>';
  }
  setSection('buy-paid', buyPaidH);
  // Sell unpaid
  var sellUnpH = '';
  if (state.sellUnpaidOrders.length === 0) {
    sellUnpH = '<div ' + emptyCss + '><div style="font-size:32px;margin-bottom:8px;opacity:0.4;">⏳</div><p style="color:var(--text-quaternary);font-size:13px;margin:0;">No sell orders waiting for buyer payment</p></div>';
  } else {
    sellUnpH = '<div style="display:flex;flex-direction:column;gap:10px;">';
    state.sellUnpaidOrders.slice(0, 10).forEach(function(o) {
      var short = (o.orderId || '').slice(0, 14);
      var fiat = parseFloat(o.fiatAmount || '0').toFixed(2);
      var crypto = parseFloat(o.cryptoAmount || '0').toFixed(4);
      var price = o.unitPrice || '-';
      var cp = o.counterpartyDetail || {};
      var buyerName = cp.userName || cp.takerName || cp.nickName || 'Buyer';
      var created = o.createdTimestamp ? new Date(parseInt(o.createdTimestamp)).toLocaleString() : '';
      var tl = bankTimeLeft(o);
      sellUnpH += '<div style="background:var(--bg-primary);border:1px solid var(--accent-amber-glow);border-radius:12px;padding:16px;"><div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;flex-wrap:wrap;gap:8px;"><div style="display:flex;align-items:center;gap:8px;"><span style="font-family:monospace;font-size:11px;color:var(--text-quaternary);user-select:all;cursor:pointer;" title="Click to copy" data-on-click="copyText(\'' + esc(o.orderId || '') + '\')">' + short + '</span><span class="badge badge-sell" style="font-size:10px;">SELL</span><span style="font-size:10px;padding:2px 6px;border-radius:4px;background:var(--accent-amber-glow);color:var(--accent-amber);">UNPAID</span><span style="font-size:11px;background:var(--accent-amber-glow);padding:2px 8px;border-radius:4px;color:var(--accent-amber);">' + tl + '</span></div><div style="font-size:18px;font-weight:700;color:var(--accent-amber);">' + crypto + ' USDT</div></div><div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-bottom:12px;font-size:12px;"><div><span style="color:var(--text-quaternary);">Amount:</span> <span style="color:var(--text-primary);">' + formatKES(fiat) + '</span></div><div><span style="color:var(--text-quaternary);">Price:</span> <span style="color:var(--text-primary);">' + price + '/USDT</span></div><div><span style="color:var(--text-quaternary);">Buyer:</span> <span style="color:var(--text-primary);">' + buyerName + '</span></div><div><span style="color:var(--text-quaternary);">Created:</span> <span style="color:var(--text-primary);">' + created + '</span></div></div><div style="display:flex;gap:8px;flex-wrap:wrap;"><button class="btn btn-sm btn-outline" data-on-click="navigate(\'orders\')" style="font-size:11px;">View Orders</button></div></div>';
    });
    sellUnpH += '</div>';
  }
  setSection('sell-unpaid', sellUnpH);
  // Sell release
  var sellRelH = '';
  if (state.sellReleaseOrders.length === 0) {
    sellRelH = '<div ' + emptyCss + '><div style="font-size:32px;margin-bottom:8px;opacity:0.4;">🔒</div><p style="color:var(--text-quaternary);font-size:13px;margin:0;">No sell orders awaiting release</p></div>';
  } else {
    sellRelH = '<div style="display:flex;flex-direction:column;gap:10px;">';
    state.sellReleaseOrders.slice(0, 10).forEach(function(o) {
      var short = (o.orderId || '').slice(0, 14);
      var fiat = parseFloat(o.fiatAmount || '0').toFixed(2);
      var crypto = parseFloat(o.cryptoAmount || '0').toFixed(4);
      var price = o.unitPrice || '-';
      var cp = o.counterpartyDetail || {};
      var buyerName = cp.userName || cp.takerName || cp.nickName || 'Buyer';
      var created = o.createdTimestamp ? new Date(parseInt(o.createdTimestamp)).toLocaleString() : '';
      var paidTime = o.paymentTimestamp ? new Date(parseInt(o.paymentTimestamp)).toLocaleString() : created;
      var tl = bankTimeLeft(o);
      var relBtn = (window.isAppealedOrder && window.isAppealedOrder(o))
        ? '<span style="font-size:11px;padding:6px 12px;border-radius:6px;background:rgba(244,67,54,0.12);border:1px solid rgba(244,67,54,0.35);color:var(--accent-red);font-weight:700;white-space:nowrap;">⚖️ Under Appeal — Release Disabled</span>'
        : '<button class="btn btn-sm btn-primary" data-on-click="releaseSellOrder(\'' + esc(o.orderId) + '\')" style="background:linear-gradient(135deg,#ff9100,#ffab00);color:var(--text-on-accent);font-weight:700;">🚀 Confirm & Release Crypto</button>';
      sellRelH += '<div style="background:var(--bg-primary);border:1px solid rgba(255,145,0,0.2);border-radius:12px;padding:16px;"><div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;flex-wrap:wrap;gap:8px;"><div style="display:flex;align-items:center;gap:8px;"><span style="font-family:monospace;font-size:11px;color:var(--text-quaternary);user-select:all;cursor:pointer;" title="Click to copy" data-on-click="copyText(\'' + esc(o.orderId || '') + '\')">' + short + '</span><span class="badge badge-sell" style="font-size:10px;">SELL</span><span style="font-size:10px;padding:2px 6px;border-radius:4px;background:rgba(0,210,104,0.1);color:var(--accent-green);">PAID</span><span style="font-size:11px;background:rgba(255,122,0,0.1);padding:2px 8px;border-radius:4px;color:var(--accent-orange);">' + tl + '</span></div><div style="font-size:18px;font-weight:700;color:var(--accent-green);">' + crypto + ' USDT</div></div><div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-bottom:12px;font-size:12px;"><div><span style="color:var(--text-quaternary);">Amount:</span> <span style="color:var(--text-primary);">' + formatKES(fiat) + '</span></div><div><span style="color:var(--text-quaternary);">Price:</span> <span style="color:var(--text-primary);">' + price + '/USDT</span></div><div><span style="color:var(--text-quaternary);">Buyer:</span> <span style="color:var(--text-primary);">' + buyerName + '</span></div><div><span style="color:var(--text-quaternary);">Paid:</span> <span style="color:var(--text-primary);">' + paidTime + '</span></div><div style="grid-column:span 2;"><span style="color:var(--text-quaternary);">Created:</span> <span style="color:var(--text-primary);">' + created + '</span></div></div><div style="display:flex;gap:8px;flex-wrap:wrap;">' + relBtn + '<button class="btn btn-sm btn-outline" data-on-click="navigate(\'orders\')" style="font-size:11px;">View Orders</button></div></div>';
    });
    sellRelH += '</div>';
  }
  setSection('sell-release', sellRelH);
}

window.grantSmsListenerAccess = function() {
  try { if (typeof AndroidOtp !== 'undefined' && AndroidOtp.requestAccess) AndroidOtp.requestAccess(); } catch (e) {}
};

window.openSmsPermissionSettings = function() {
  try { if (typeof AndroidOtp !== 'undefined' && AndroidOtp.openAppSettings) AndroidOtp.openAppSettings(); } catch (e) {}
};

window.__apkSmsPermission = function(granted) {
  try { console.log('[sms] RECEIVE_SMS ' + (granted ? 'granted' : 'denied')); } catch (e) {}
};

window.toggleSmsDebug = function() {
  var el = document.getElementById('sms-debug-panel');
  if (!el) return;
  if (el.style.display !== 'none') { el.style.display = 'none'; return; }
  var rows = [];
  try {
    var arr = JSON.parse(AndroidOtp.recentNotifications() || '[]');
    for (var i = arr.length - 1; i >= 0; i--) {
      var d = arr[i];
      if (typeof d === 'string') { try { d = JSON.parse(d); } catch (e) { continue; } }
      var t = new Date(d.ts).toLocaleTimeString();
      var app = String(d.pkg || '').replace(/^com\./, '').slice(0, 18);
      rows.push('<tr>' +
        '<td style="white-space:nowrap;color:var(--text-tertiary);">' + esc(t) + '</td>' +
        '<td style="max-width:90px;overflow:hidden;text-overflow:ellipsis;">' + esc(app) + '</td>' +
        '<td style="max-width:110px;overflow:hidden;text-overflow:ellipsis;font-weight:600;">' + esc(d.title || '') + '</td>' +
        '<td style="max-width:260px;overflow:hidden;text-overflow:ellipsis;" title="' + esc(d.text || '') + '">' + esc((d.text || '').slice(0, 70)) + '</td>' +
        '<td style="text-align:center;">' + (d.bank ? '✅' : '—') + '</td>' +
        '</tr>');
    }
  } catch (e) { rows.push('<tr><td colspan="5" style="color:var(--accent-red);">Diagnostics unavailable: ' + esc(e.message) + '</td></tr>'); }
  var payLines = '';
  try { if (AndroidOtp.paymentLog) payLines = String(AndroidOtp.paymentLog() || '').trim(); } catch (e) {}
  var smsPerm = null;
  try { if (AndroidOtp.hasSmsPermission) smsPerm = AndroidOtp.hasSmsPermission(); } catch (e) {}
  var permWarn = '';
  if (smsPerm === false) {
    permWarn = '<div style="background:rgba(255,82,82,0.12);border:1px solid rgba(255,82,82,0.4);border-radius:10px;padding:10px;margin-bottom:10px;font-size:12px;">' +
      '<b style="color:var(--accent-red,#ff5252);">SMS permission is OFF.</b> Bank SMS cannot be captured, so payment proof falls back to the reference-only message. ' +
      '<button class="btn btn-sm btn-primary" data-on-click="openSmsPermissionSettings()" style="margin-left:8px;font-size:11px;">Enable SMS permission</button></div>';
  }
  el.innerHTML = '<div class="card" style="margin-top:8px;padding:12px;">' + permWarn +
    '<div style="font-size:12px;font-weight:700;margin-bottom:6px;">SMS Listener — last notifications seen (✅ = matched as bank payment)</div>' +
    '<div style="max-height:260px;overflow:auto;"><table style="width:100%;font-size:11px;border-collapse:collapse;"><thead><tr style="color:var(--text-tertiary);text-align:left;"><th>Time</th><th>App</th><th>Title</th><th>Text</th><th>Bank</th></tr></thead><tbody>' +
    (rows.length ? rows.join('') : '<tr><td colspan="5" style="color:var(--text-tertiary);">No notifications captured yet. Send a test SMS and reopen this panel.</td></tr>') +
    '</tbody></table></div>' +
    '<div style="font-size:12px;font-weight:700;margin:10px 0 4px;">Delivery log (parse → app → server)</div>' +
    '<pre style="font-size:10px;white-space:pre-wrap;color:#9a9ab0;margin:0;max-height:140px;overflow:auto;">' + esc(payLines || 'No payment activity yet.') + '</pre></div>';
  el.style.display = 'block';
};

/* ==== js/modules/support.js ==== */
let _activeTicketId = null;
let _chatbotMessages = [];
let _escalating = false;
var _activeCallId = null;
var _callRingingTimer = null;
var _localStream = null;
var _peerConn = null;
var _callStartTime = null;

function scrollChatbot() {
  const el = document.getElementById('chatbot-messages');
  if (el) el.scrollTop = el.scrollHeight;
}

function addChatbotMsg(role, text) {
  const el = document.getElementById('chatbot-messages');
  if (!el) return;
  _chatbotMessages.push({ role, text });
  const cls = role === 'user' ? 'user' : (role === 'admin' ? 'admin' : 'bot');
  const label = role === 'admin' ? 'Admin' : (role === 'user' ? 'You' : 'Bot');
  el.insertAdjacentHTML('beforeend', `<div class="label">${label}</div><div class="msg ${cls}">${esc(text)}</div>`);
  scrollChatbot();
}

function removeTyping() { const e = document.getElementById('chatbot-messages'); if (e) { const t = e.querySelector('.typing'); if (t) t.remove(); } }

function showTyping() {
  const el = document.getElementById('chatbot-messages');
  if (!el) return;
  removeTyping();
  el.insertAdjacentHTML('beforeend', '<div class="typing"><span></span><span></span><span></span></div>');
  scrollChatbot();
}

function showEscalate(msg) {
  const area = document.getElementById('chatbot-escalate-area');
  if (!area) return;
  _escalating = true;
  const btnId = 'escalate-btn-' + Date.now();
  area.innerHTML = `<div class="chatbot-escalate"><p style="font-size:13px;color:var(--text-tertiary);margin-bottom:12px;">${esc(msg||'')}</p><button id="${btnId}" onclick="createSupportTicket()">Contact Support</button></div>`;
}

function hideEscalate() {
  const area = document.getElementById('chatbot-escalate-area');
  if (area) area.innerHTML = '';
  _escalating = false;
}

function showTicketBar(ticketId) {
  _activeTicketId = ticketId;
  const area = document.getElementById('chatbot-escalate-area');
  if (!area) return;
  area.innerHTML = `<div class="chatbot-ticket-bar"><span class="dot"></span> Support ticket #${ticketId.slice(0,8)} is open — admin will respond here</div>`;
}

async function sendChatbotMsg() {
  const input = document.getElementById('chatbot-input');
  const btn = document.getElementById('chatbot-send-btn');
  if (!input || !btn) return;
  const msg = input.value.trim();
  if (!msg) return;
  input.value = '';
  btn.disabled = true;
  addChatbotMsg('user', msg);
  hideEscalate();
  if (_activeTicketId) {
    showTyping();
    try {
      const res = await API.sendTicketMessage(_activeTicketId, msg);
      removeTyping();
      if (res.data && res.data.closed) {
        addChatbotMsg('bot', 'This ticket is closed. Create a new one if you need further help.');
        _activeTicketId = null;
      } else {
        addChatbotMsg('bot', 'Message sent to support. An admin will respond here shortly.');
      }
    } catch {
      removeTyping();
      addChatbotMsg('bot', 'Failed to send message. Please try again.');
    }
    btn.disabled = false;
    input.focus();
    return;
  }
  showTyping();
  try {
    const res = await API.post('/api/ai/ask', { question: msg });
    removeTyping();
    const reply = res?.data?.reply || 'No response';
    addChatbotMsg('bot', reply);
  } catch {
    removeTyping();
    addChatbotMsg('bot', 'Sorry, I encountered an error. Please try again or contact support.');
  }
  btn.disabled = false;
  input.focus();
}

async function createSupportTicket() {
  const btn = document.querySelector('#chatbot-escalate-area button');
  if (btn) { btn.disabled = true; btn.textContent = 'Creating...'; }
  try {
    const last = _chatbotMessages.filter(m => m.role !== 'admin').pop();
    const res = await API.createSupportTicket(last ? last.text : '');
    const ticketId = res.data && res.data.ticketId;
    if (ticketId) {
      showTicketBar(ticketId);
      addChatbotMsg('bot', 'Ticket created! An admin will respond here shortly. You can continue chatting — your messages will be sent to the support team.');
    } else {
      const existing = res.data && res.data.ticketId;
      showTicketBar(existing);
      addChatbotMsg('bot', 'You already have an open ticket. An admin will respond shortly.');
    }
    _escalating = false;
  } catch {
    if (btn) { btn.disabled = false; btn.textContent = 'Contact Support'; }
    addChatbotMsg('bot', 'Failed to create ticket. Please try again or use WhatsApp/Email.');
  }
}

function handleSupportReply(data) {
  if (!data || !data.message) return;
  addChatbotMsg('admin', data.message);
  hideEscalate();
}

function handleSupportClosed(data) {
  if (!data) return;
  addChatbotMsg('bot', 'This support ticket has been closed. If you need further assistance, feel free to ask again.');
  const area = document.getElementById('chatbot-escalate-area');
  if (area) area.innerHTML = '';
  _activeTicketId = null;
}

function openWhatsApp() {
  const phone = '254723472206';
  const text = encodeURIComponent('Hi MONEYFESTLTD, I need help with P2P trading.');
  if (/Android/i.test(navigator.userAgent)) {
    window.location.href = 'intent://send?phone=' + phone + '&text=' + text + '#Intent;scheme=whatsapp;package=com.whatsapp;end';
    setTimeout(function() { window.open('https://wa.me/' + phone + '?text=' + text, '_blank'); }, 500);
  } else if (/iPad|iPhone|iPod/i.test(navigator.userAgent)) {
    window.location.href = 'whatsapp://send?phone=' + phone + '&text=' + text;
    setTimeout(function() { window.open('https://wa.me/' + phone + '?text=' + text, '_blank'); }, 500);
  } else {
    window.open('https://wa.me/' + phone + '?text=' + text, '_blank');
  }
}

// ── CLIENT CALL FUNCTIONS ──
var _callTimerInterval = null;
var _callRingingOverlay = null;

window.clientStartCall = async function() {
  if (_activeCallId) return showToast('You are already in a call', 'info');
  var btn = document.getElementById('support-call-btn');
  if (btn) { btn.disabled = true; btn.textContent = '⏳ Connecting...'; }
  try {
    // Check if admins online
    var availRes = await API.get('/api/support/call/admins-online');
    if (!availRes.data || !availRes.data.online) {
      _showCallTimeoutUI(true);
      if (btn) { btn.disabled = false; btn.textContent = '🎧 Call Support'; }
      return;
    }
    // Start the call
    var startRes = await API.post('/api/support/call/start', {});
    if (startRes.data && startRes.data.callId) {
      _activeCallId = startRes.data.callId;
      _showCallRinging();
    }
  } catch (err) {
    showToast('Failed to start call: ' + err.message, 'error');
    if (btn) { btn.disabled = false; btn.textContent = '🎧 Call Support'; }
  }
};

function _showCallRinging() {
  var overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'call-ringing-overlay';
  _callRingingOverlay = overlay;
  var sec = 0;
  overlay.innerHTML = '<div class="modal-content" style="max-width:380px;text-align:center;"><div style="font-size:64px;margin-bottom:16px;animation:pulse 1s infinite;">🔔</div><h3>Calling Support</h3><p style="color:var(--text-tertiary);margin:12px 0;">Ringing admin...</p><div style="font-size:32px;font-weight:700;color:#00e6c8;margin:12px 0;" id="call-ringing-timer">00</div><p style="color:var(--text-quaternary);font-size:12px;margin-bottom:16px;">If no admin answers within 1 minute, the call will end.</p><button class="btn btn-secondary" data-on-click="clientCancelCall()">Cancel Call</button></div>';
  document.body.appendChild(overlay);
  _callRingingTimer = setInterval(function() {
    sec++;
    var el = document.getElementById('call-ringing-timer');
    if (el) el.textContent = String(sec).padStart(2, '0');
  }, 1000);
  window._callRingingTimer = _callRingingTimer;
}

window.clientCancelCall = function() {
  if (_callRingingTimer) { clearInterval(_callRingingTimer); _callRingingTimer = null; }
  if (_callRingingOverlay) { _callRingingOverlay.remove(); _callRingingOverlay = null; }
  if (_activeCallId) {
    API.post('/api/support/call/end', { callId: _activeCallId }).catch(function(){});
    _activeCallId = null;
  }
  var btn = document.getElementById('support-call-btn');
  if (btn) { btn.disabled = false; btn.textContent = '🎧 Call Support'; }
};

function _showCallTimeoutUI(noAdmins) {
  if (_callRingingOverlay) { _callRingingOverlay.remove(); _callRingingOverlay = null; }
  if (_callRingingTimer) { clearInterval(_callRingingTimer); _callRingingTimer = null; }
  var statusEl = document.getElementById('support-call-status');
  if (statusEl) statusEl.textContent = noAdmins ? 'No admin available right now' : 'No admin answered';
  var btn = document.getElementById('support-call-btn');
  if (btn) { btn.disabled = true; btn.textContent = '⏳ Wait...'; }
  // Show options
  var overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = '<div class="modal-content" style="max-width:400px;text-align:center;"><div style="font-size:48px;margin-bottom:12px;">😔</div><h3>' + (noAdmins ? 'No Support Available' : 'Call Unanswered') + '</h3><p style="color:var(--text-tertiary);margin:12px 0;">' + (noAdmins ? 'No support agents are online right now.' : 'No admin answered your call.') + '</p><div style="display:flex;flex-direction:column;gap:8px;margin-top:16px;"><button class="btn btn-primary" data-on-click="clientJoinQueue()">⏳ Wait for Next Available Admin</button><button class="btn btn-secondary" data-on-click="createSupportTicket()">📝 Submit a Ticket</button><button class="btn btn-outline" data-on-click="closeModal(); resetCallUI()">Close</button></div></div>';
  document.body.appendChild(overlay);
}

window.clientJoinQueue = async function() {
  closeModal();
  try {
    var r = await API.post('/api/support/call/queue/join', {});
    var pos = r.data && r.data.position ? r.data.position : '?';
    showToast('You are #' + pos + ' in line. You will be connected when an admin is free.', 'info', 8000);
    var statusEl = document.getElementById('support-call-status');
    if (statusEl) statusEl.textContent = 'In queue (#' + pos + ')';
    var btn = document.getElementById('support-call-btn');
    if (btn) { btn.textContent = '⏳ In Queue'; btn.disabled = true; }
  } catch (err) {
    showToast('Failed to join queue', 'error');
    resetCallUI();
  }
};

function resetCallUI() {
  _activeCallId = null;
  var btn = document.getElementById('support-call-btn');
  if (btn) { btn.disabled = false; btn.textContent = '🎧 Call Support'; }
  var statusEl = document.getElementById('support-call-status');
  if (statusEl) statusEl.textContent = 'Talk to support via audio call';
  var ctrlEl = document.getElementById('support-call-controls');
  if (ctrlEl) ctrlEl.style.display = 'none';
  if (_callTimerInterval) { clearInterval(_callTimerInterval); _callTimerInterval = null; }
  if (_localStream) { _localStream.getTracks().forEach(function(t) { t.stop(); }); _localStream = null; }
  if (_peerConn) { _peerConn.close(); _peerConn = null; }
  _callStartTime = null;
}

window.clientCallAnswered = async function(data) {
  if (_callRingingOverlay) { _callRingingOverlay.remove(); _callRingingOverlay = null; }
  if (_callRingingTimer) { clearInterval(_callRingingTimer); _callRingingTimer = null; }
  showToast('Connected to ' + (data.adminName || 'Support'), 'success');
  var statusEl = document.getElementById('support-call-status');
  if (statusEl) statusEl.textContent = 'Connected to ' + (data.adminName || 'Support');
  var ctrlEl = document.getElementById('support-call-controls');
  if (ctrlEl) ctrlEl.style.display = 'block';
  var btn = document.getElementById('support-call-btn');
  if (btn) { btn.style.display = 'none'; }
  _callStartTime = Date.now();
  _callTimerInterval = setInterval(function() {
    var el = document.getElementById('support-call-timer');
    if (el && _callStartTime) {
      var diff = Math.floor((Date.now() - _callStartTime) / 1000);
      var m = String(Math.floor(diff / 60)).padStart(2, '0');
      var s = String(diff % 60).padStart(2, '0');
      el.textContent = m + ':' + s;
    }
  }, 1000);
  window._callTimerInterval = _callTimerInterval;
  // Start WebRTC
  await _startWebRTC('caller');
};

window.clientCallEnded = function() {
  showToast('Call ended', 'info');
  resetCallUI();
};

window.clientCallSignal = async function(data) {
  if (!_peerConn) return;
  try {
    if (data.signal && data.signal.type === 'offer') {
      await _peerConn.setRemoteDescription(new RTCSessionDescription(data.signal));
      var answer = await _peerConn.createAnswer();
      await _peerConn.setLocalDescription(answer);
      API.post('/api/support/call/signal', { callId: _activeCallId, signal: { type: 'answer', sdp: answer.sdp } }).catch(function(){});
    } else if (data.signal && data.signal.type === 'answer') {
      await _peerConn.setRemoteDescription(new RTCSessionDescription(data.signal));
    } else if (data.signal && data.signal.candidate) {
      await _peerConn.addIceCandidate(new RTCIceCandidate(data.signal));
    }
  } catch (err) {
    console.warn('WebRTC signal error:', err.message);
  }
};

async function _startWebRTC(role) {
  try {
    _localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    var stun = { urls: 'stun:stun.l.google.com:19302' };
    _peerConn = new RTCPeerConnection({ iceServers: [stun] });
    _localStream.getTracks().forEach(function(track) {
      _peerConn.addTrack(track, _localStream);
    });
    _peerConn.onicecandidate = function(e) {
      if (e.candidate && _activeCallId) {
        API.post('/api/support/call/signal', { callId: _activeCallId, signal: { candidate: e.candidate.candidate, sdpMid: e.candidate.sdpMid, sdpMLineIndex: e.candidate.sdpMLineIndex } }).catch(function(){});
      }
    };
    _peerConn.ontrack = function(e) {
      var audioEl = document.getElementById('support-call-audio');
      if (!audioEl) {
        audioEl = document.createElement('audio');
        audioEl.id = 'support-call-audio';
        audioEl.autoplay = true;
        document.body.appendChild(audioEl);
      }
      audioEl.srcObject = e.streams[0];
    };
    if (role === 'caller') {
      var offer = await _peerConn.createOffer();
      await _peerConn.setLocalDescription(offer);
      API.post('/api/support/call/signal', { callId: _activeCallId, signal: { type: 'offer', sdp: offer.sdp } }).catch(function(){});
    }
  } catch (err) {
    showToast('Could not access microphone: ' + err.message, 'error');
    clientEndCall();
  }
}

window.clientToggleMute = function() {
  if (_localStream) {
    var audioTrack = _localStream.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      var btn = document.getElementById('support-call-mute-btn');
      if (btn) btn.textContent = audioTrack.enabled ? '🔇 Mute' : '🔊 Unmute';
    }
  }
};

window.clientEndCall = function() {
  if (_activeCallId) {
    API.post('/api/support/call/end', { callId: _activeCallId }).catch(function(){});
  }
  _activeCallId = null;
  resetCallUI();
  var btn = document.getElementById('support-call-btn');
  if (btn) { btn.style.display = ''; }
};

/* ==== js/modules/settings.js ==== */
async function renderSettings(container, defaultTab) {
  const initials = currentUser.name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase();
  const colors = ['#448aff','#00c853','#ff9100','#f44336','#9c27b0','#00bcd4','#ff5722','#607d8b'];
  const color = colors[currentUser.name.length % colors.length];
  const roleBadge = currentUser.role === 'admin' ? '👑 Admin' : '👤 User';
  const avatarSrc = currentUser.avatar || '';
  container.innerHTML = `
    <div class="page-header"><div><h2>Settings</h2><div class="subtitle">Manage your profile, notifications and API config</div></div></div>
    <div class="settings-groups">
      <div class="settings-group">
        <div class="settings-group-label">🔐 Security</div>
        <div class="settings-tabs">
          <button class="settings-tab active" data-tab="passkey" data-on-click="switchSettingsTab('passkey')">🔑 Passkey</button>
          <button class="settings-tab" data-tab="account" data-on-click="switchSettingsTab('account')">🔐 Account</button>
        </div>
      </div>
      <div class="settings-group">
        <div class="settings-group-label">📈 Trading</div>
        <div class="settings-tabs">
          <button class="settings-tab" data-tab="api" data-on-click="switchSettingsTab('api')">🔑 API</button>
          <button class="settings-tab" data-tab="price-alerts" data-on-click="switchSettingsTab('price-alerts')">🔔 Price Alerts</button>
          <button class="settings-tab" data-tab="webhooks" data-on-click="switchSettingsTab('webhooks')">🔗 Webhooks</button>
          <button class="settings-tab" data-tab="telegram" data-on-click="switchSettingsTab('telegram')">✈️ Telegram</button>
        </div>
      </div>
      <div class="settings-group">
        <div class="settings-group-label">⚙️ App &amp; Notifications</div>
        <div class="settings-tabs">
          <button class="settings-tab" data-tab="payment-reminder" data-on-click="switchSettingsTab('payment-reminder')">🔔 Reminder</button>
          ${currentUser.role === 'admin' ? '<button class="settings-tab" data-tab="notifications" data-on-click="switchSettingsTab(\'notifications\')">📢 Notifications</button>' : ''}
          <button class="settings-tab" data-tab="advanced" data-on-click="switchSettingsTab('advanced')">⚡ Advanced</button>
          <button class="settings-tab" data-tab="about" data-on-click="switchSettingsTab('about')">ℹ️ About</button>
        </div>
      </div>
    </div>
    <div id="notif-channel-banner"></div>
    <div id="settings-content"></div>`;
  checkNotifChannels();
  switchSettingsTab(defaultTab || 'passkey');
}

window.checkNotifChannels = function() {
  try {
    if (typeof AndroidNotification === 'undefined' || !AndroidNotification.channelStatus) return;
    var chans = JSON.parse(AndroidNotification.channelStatus() || '[]');
    var blocked = chans.filter(function(c) { return c.blocked; });
    var el = document.getElementById('notif-channel-banner');
    if (!el) return;
    if (!blocked.length) {
      el.innerHTML = '<div style="margin:8px 0;padding:8px 14px;border-radius:8px;background:rgba(0,200,83,0.12);border:1px solid rgba(0,200,83,0.35);color:var(--accent-green);font-size:12px;display:flex;align-items:center;gap:10px;flex-wrap:wrap;">🔔 Notifications fully enabled (' + chans.length + ' channel' + (chans.length === 1 ? '' : 's') + ' active) <button class="btn btn-sm btn-outline" data-on-click="showFcmTrace()" style="font-size:10px;">Push log</button></div><div id="fcm-trace-panel" style="display:none;"></div>';
      return;
    }
    el.innerHTML = '<div style="margin:8px 0;padding:10px 14px;border-radius:8px;background:rgba(255,82,82,0.12);border:1px solid rgba(255,82,82,0.4);color:#ff5252;font-size:12px;display:flex;align-items:center;gap:10px;flex-wrap:wrap;">⛔ ' + blocked.length + ' notification channel(s) muted — pushes will NOT appear even though the app shows "Allowed" <button class="btn btn-sm btn-outline" data-on-click="openNotifSettings()" style="font-size:11px;">Fix now</button></div>';
  } catch (e) {}
};

window.openNotifSettings = function() {
  try { if (typeof AndroidNotification !== 'undefined' && AndroidNotification.openSettings) AndroidNotification.openSettings(); } catch (e) {}
};

window.showFcmTrace = function() {
  var el = document.getElementById('fcm-trace-panel');
  if (!el) return;
  if (el.style.display !== 'none') { el.style.display = 'none'; return; }
  var lines = [];
  try { if (typeof AndroidPush !== 'undefined' && AndroidPush.fcmTrace) lines = String(AndroidPush.fcmTrace() || '').split('\n').filter(Boolean); } catch (e) {}
  el.innerHTML = '<div class="card" style="margin-top:6px;padding:10px;"><div style="font-size:12px;font-weight:700;margin-bottom:4px;">FCM service log (latest first)</div><pre style="font-size:10px;white-space:pre-wrap;color:#9a9ab0;margin:0;max-height:200px;overflow:auto;">' + (lines.length ? esc(lines.join('\n')) : 'No FCM activity recorded yet — send a test push, then reopen this.') + '</pre></div>';
  el.style.display = 'block';
};

window.switchSettingsTab = async function(tab) {
  if (tab === 'profile') { navigate('profile'); return; }
  document.querySelectorAll('.settings-tab').forEach(t => t.classList.toggle('active', t.dataset.tab === tab));
  const content = document.getElementById('settings-content');
  if (tab === 'profile') renderProfileTab(content);
  else if (tab === 'account') await renderAccountTab(content);
  else if (tab === 'payment-reminder') await renderPaymentReminderTab(content);
  else if (tab === 'notifications') await renderNotificationsTab(content);
  else if (tab === 'advanced') await renderAdvancedTab(content);
  else if (tab === 'api') await renderApiTab(content);
  else if (tab === 'price-alerts') await renderPriceAlertsTab(content);
  else if (tab === 'webhooks') await renderWebhooksTab(content);
  else if (tab === 'telegram') await renderTelegramTab(content);
  else if (tab === 'passkey') await renderPasskeyTab(content);
  else if (tab === 'about') renderAboutTab(content);
  // Update URL
  var path = '/settings/' + tab;
  if (typeof updateUrl === 'function') {
    updateUrl('settings-' + tab);
  } else {
    history.pushState({ page: 'settings-' + tab }, '', path);
  }
};

function renderProfileTab(content) {
  const initials = currentUser.name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase();
  const colors = ['#448aff','#00c853','#ff9100','#f44336','#9c27b0','#00bcd4','#ff5722','#607d8b'];
  const color = colors[currentUser.name.length % colors.length];
  const avatarSrc = currentUser.avatar || '';
  const roleBadge = currentUser.role === 'admin' ? '👑 Admin' : '👤 User';
  content.innerHTML = `
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;">
      <div class="card" style="text-align:center;padding:32px;">
        ${avatarSrc ? `<img src="${avatarSrc}" style="width:80px;height:80px;border-radius:50%;object-fit:cover;margin:0 auto 16px;border:2px solid rgba(0,200,83,0.3);display:block;">` : `<div style="width:80px;height:80px;border-radius:50%;background:${color}20;color:${color};display:flex;align-items:center;justify-content:center;font-weight:800;font-size:32px;margin:0 auto 16px;">${initials}</div>`}
        <h3 style="text-transform:none;letter-spacing:0;font-size:20px;color:#fff;margin-bottom:4px;">${currentUser.name}</h3>
        <div style="color:var(--text-tertiary);font-size:13px;margin-bottom:4px;">@${currentUser.username || currentUser.name}</div>
        <div style="color:var(--text-tertiary);font-size:13px;margin-bottom:8px;">${currentUser.email}</div>
        <div><span class="badge badge-${currentUser.role === 'admin' ? 'completed' : 'pending'}">${roleBadge}</span></div>
      </div>
      <div class="card">
        <h3>Edit Profile</h3>
        <form data-on-submit="updateProfile(event)">
          <div class="form-group"><label>Username</label><input id="settings-username" value="${currentUser.username || currentUser.name}" required></div>
          <div class="form-group"><label>Full Name</label><input id="settings-name" value="${currentUser.name}" required></div>
          <div class="form-group"><label>Email</label><input id="settings-email" type="email" value="${currentUser.email}" required></div>
          <div class="form-group">
            <label>Profile Picture</label>
            <div style="display:flex;align-items:center;gap:12px;">
              <input type="file" id="settings-avatar-input" accept="image/*" style="display:none;" onchange="previewAvatar(event)">
              <button type="button" class="btn btn-sm btn-outline" data-on-click="document.getElementById('settings-avatar-input').click()">Upload Image</button>
              <span id="settings-avatar-name" style="font-size:12px;color:var(--text-quaternary);"></span>
            </div>
          </div>
          <div style="border-top:1px solid #1e1e2e;padding-top:16px;margin-top:4px;">
            <h3 style="text-transform:none;letter-spacing:0;font-size:15px;margin-bottom:12px;">Change Password</h3>
            <div class="form-group"><label>Current Password</label><input id="settings-cur-pw" type="password" placeholder="Enter current password"></div>
            <div class="form-group"><label>New Password</label><div style="position:relative;"><input id="settings-new-pw" type="password" placeholder="Enter new password" style="width:100%;padding-right:36px;"><span data-on-click="togglePassword('settings-new-pw',this)" style="position:absolute;right:10px;top:50%;transform:translateY(-50%);cursor:pointer;color:var(--text-quaternary);font-size:14px;user-select:none;">👁</span></div></div>
            <div class="form-group"><label>Confirm New Password</label><div style="position:relative;"><input id="settings-conf-pw" type="password" placeholder="Confirm new password" style="width:100%;padding-right:36px;"><span data-on-click="togglePassword('settings-conf-pw',this)" style="position:absolute;right:10px;top:50%;transform:translateY(-50%);cursor:pointer;color:var(--text-quaternary);font-size:14px;user-select:none;">👁</span></div></div>
          </div>
          <button type="submit" class="btn btn-primary" style="width:100%;">Save Changes</button>
        </form>
      </div>
    </div>`;
}

async function renderAccountTab(content) {
  const contacts = await API.getAltContacts().then(r => r.data || []).catch(() => []);
  const hasEmail = !!currentUser.email;
  const hasPhone = !!currentUser.phone;
  let prefs = {};
  try {
    const r = await API.get('/api/auth/preferences');
    prefs = r.data || {};
  } catch {}
  const notifyOnLogin = prefs.notifyOnLogin !== false;
  content.innerHTML = `
    <div class="card" style="margin-bottom:16px;">
      <h3>Primary Contacts</h3>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:8px;">
        <div class="form-group">
          <label>Email</label>
          <div style="display:flex;align-items:center;gap:8px;">
            <input type="email" id="account-primary-email" value="${currentUser.email || ''}" placeholder="Not set" style="flex:1;" readonly>
            ${hasEmail ? '<span class="badge badge-completed">Verified</span>' : '<span class="badge badge-pending">Not set</span>'}
          </div>
        </div>
        <div class="form-group">
          <label>Phone</label>
          <div style="display:flex;align-items:center;gap:8px;">
            <input type="text" id="account-primary-phone" value="${currentUser.phone || ''}" placeholder="Not set" style="flex:1;" readonly>
            ${hasPhone ? '<span class="badge badge-completed">Verified</span>' : '<span class="badge badge-pending">Not set</span>'}
          </div>
        </div>
      </div>
      ${!hasPhone ? '<button class="btn btn-sm btn-outline" data-on-click="showLinkPhoneModal()">+ Add Phone Number</button>' : ''}
      ${!hasEmail ? '<button class="btn btn-sm btn-outline" data-on-click="showLinkEmailModal()">+ Add Email Address</button>' : ''}
    </div>
    <div class="card">
      <h3>Alternative Contacts <span style="font-size:12px;color:var(--text-quaternary);font-weight:400;">(can be used for login)</span></h3>
      <div id="alt-contacts-list">
        ${contacts.length === 0 ? '<div style="color:var(--text-quaternary);padding:12px 0;">No alternative contacts added yet.</div>' : ''}
        ${contacts.map(c => {
          const badge = c.verified ? '<span class="badge badge-completed">Verified</span>' : '<span class="badge badge-pending">Unverified</span>';
          return `<div style="display:flex;align-items:center;justify-content:space-between;padding:10px 0;border-bottom:1px solid #1e1e2e;">
            <div><strong>${c.contact}</strong><div style="font-size:12px;color:var(--text-quaternary);">${c.type}</div></div>
            <div style="display:flex;align-items:center;gap:8px;">${badge}<button class="btn btn-sm btn-danger" data-on-click="removeAltContact('${esc(c.id)}')">✕</button></div>
          </div>`;
        }).join('')}
      </div>
      <div style="margin-top:12px;padding-top:12px;border-top:1px solid #1e1e2e;">
        <h4 style="font-size:14px;margin-bottom:8px;">Add Alternative Contact</h4>
        <div style="display:flex;gap:8px;">
          <select id="alt-contact-type" class="form-input" style="width:120px;flex-shrink:0;">
            <option value="email">Email</option>
            <option value="phone">Phone</option>
          </select>
          <input type="text" id="alt-contact-value" class="form-input" placeholder="Enter email or phone number" style="flex:1;">
          <button class="btn btn-primary btn-sm" data-on-click="addAltContact()">Verify & Add</button>
        </div>
      </div>
    </div>
    <div class="card" style="border-color:rgba(255,107,53,0.15);">
      <h3 style="display:flex;align-items:center;gap:8px;">🔐 Security Notifications</h3>
      <label class="toggle-row" style="display:flex;align-items:center;justify-content:space-between;padding:12px 0;">
        <div>
          <div style="font-size:14px;color:var(--text-primary);">Email me on new login</div>
          <div style="font-size:11px;color:var(--text-tertiary);margin-top:2px;">Get an email with IP, device, location and map when someone logs into your account</div>
        </div>
        <label class="switch" style="position:relative;display:inline-block;width:44px;height:24px;flex-shrink:0;">
          <input type="checkbox" ${notifyOnLogin ? 'checked' : ''} onchange="toggleLoginNotify(this.checked)" style="opacity:0;width:0;height:0;">
          <span class="slider" style="position:absolute;cursor:pointer;top:0;left:0;right:0;bottom:0;background:#2a2a3e;border-radius:24px;transition:0.3s;"></span>
        </label>
      </label>
    </div>
    <div id="online-status-section" style="margin-top:16px;"></div>`;
  var osEl = document.getElementById('online-status-section');
  if (osEl && typeof showOnlineStatusToggle === 'function') showOnlineStatusToggle(osEl);
}

window.showLinkPhoneModal = function() {
  const email = currentUser.email;
  if (!email) { showToast('No email on this account', 'error'); return; }
  showModal(`
    <h3>Link Phone Number</h3>
    <p style="color:var(--text-tertiary);font-size:14px;margin-bottom:16px;">Add a phone number to your account (${email}).</p>
    <div class="form-group"><label>Phone Number (with country code)</label><input id="link-phone-input" class="form-input" placeholder="+2547XXXXXXXX" value="+254"></div>
    <button class="btn btn-primary" style="width:100%;margin-top:8px;" data-on-click="confirmLinkPhone()">Send OTP</button>
  `);
};

window.confirmLinkPhone = async function() {
  const phone = document.getElementById('link-phone-input').value.trim();
  if (!phone) { showToast('Enter a phone number', 'error'); return; }
  try {
    const res = await API.linkPhone(currentUser.email, phone, '');
    if (res.data.step === 'verify-otp') {
      showOtpModal(res.data.tempToken, 'phone');
    }
    closeModal();
  } catch (err) { showToast(err.message, 'error'); }
};

window.showLinkEmailModal = function() {
  const phone = currentUser.phone;
  if (!phone) { showToast('No phone on this account', 'error'); return; }
  showModal(`
    <h3>Link Email Address</h3>
    <p style="color:var(--text-tertiary);font-size:14px;margin-bottom:16px;">Add an email address to your account (${phone}).</p>
    <div class="form-group"><label>Email Address</label><input id="link-email-input" class="form-input" type="email" placeholder="you@example.com"></div>
    <button class="btn btn-primary" style="width:100%;margin-top:8px;" data-on-click="confirmLinkEmail()">Send OTP</button>
  `);
};

window.confirmLinkEmail = async function() {
  const email = document.getElementById('link-email-input').value.trim();
  if (!email) { showToast('Enter an email address', 'error'); return; }
  try {
    const res = await API.linkEmail(email, currentUser.phone, '');
    if (res.data.step === 'verify-otp') {
      showOtpModal(res.data.tempToken, 'email');
    }
    closeModal();
  } catch (err) { showToast(err.message, 'error'); }
};

window.showOtpModal = function(tempToken, type) {
  showModal(`
    <h3>Verify ${type === 'email' ? 'Email' : 'Phone'}</h3>
    <p style="color:var(--text-tertiary);font-size:14px;margin-bottom:16px;">Enter the OTP sent to your ${type}.</p>
    <div class="form-group"><label>OTP Code</label><input id="otp-code-input" class="form-input" placeholder="6-digit code" maxlength="6" style="text-align:center;font-size:24px;letter-spacing:8px;"></div>
    <button class="btn btn-primary" style="width:100%;margin-top:8px;" data-on-click="confirmOtp('${esc(tempToken)}')">Verify</button>
  `);
  window._otpTempToken = tempToken;
  if (window._applyPendingApkOtp) window._applyPendingApkOtp();
};

window.confirmOtp = async function(tempToken) {
  const otp = document.getElementById('otp-code-input').value.trim();
  if (!otp) { showToast('Enter OTP code', 'error'); return; }
  try {
    const res = await API.post('/api/auth/verify-otp', { tempToken, otp });
    if (res.code === 0 || res.data) {
      showToast('Contact verified!', 'success');
      closeModal();
      const content = document.getElementById('settings-content');
      if (content) renderAccountTab(content);
    }
  } catch (err) { showToast(err.message, 'error'); }
};

window.addAltContact = async function() {
  const type = document.getElementById('alt-contact-type').value;
  const contact = document.getElementById('alt-contact-value').value.trim();
  if (!contact) { showToast('Enter a contact value', 'error'); return; }
  try {
    const res = await API.addAltContact(contact, type);
    if (res.data.tempToken) {
      showModal(`
        <h3>Verify ${type === 'email' ? 'Email' : 'Phone'}</h3>
        <p style="color:var(--text-tertiary);font-size:14px;margin-bottom:16px;">Enter the OTP sent to ${contact}.</p>
        <div class="form-group"><label>OTP Code</label><input id="alt-otp-input" class="form-input" placeholder="6-digit code" maxlength="6" style="text-align:center;font-size:24px;letter-spacing:8px;"></div>
        <button class="btn btn-primary" style="width:100%;margin-top:8px;" data-on-click="confirmAltOtp('${esc(res.data.tempToken)}')">Verify</button>
      `);
      if (window._applyPendingApkOtp) window._applyPendingApkOtp();
    }
  } catch (err) { showToast(err.message, 'error'); }
};

window.confirmAltOtp = async function(tempToken) {
  const otp = document.getElementById('alt-otp-input').value.trim();
  if (!otp) { showToast('Enter OTP code', 'error'); return; }
  try {
    const res = await API.verifyAltContact(tempToken, otp);
    if (res.code === 0 || res.data) {
      showToast('Alternative contact verified and added!', 'success');
      closeModal();
      document.getElementById('alt-contact-value').value = '';
      const content = document.getElementById('settings-content');
      if (content) renderAccountTab(content);
    }
  } catch (err) { showToast(err.message, 'error'); }
};

window.removeAltContact = async function(id) {
  if (!confirm('Remove this alternative contact?')) return;
  try {
    await API.deleteAltContact(id);
    showToast('Contact removed', 'success');
    const content = document.getElementById('settings-content');
    if (content) renderAccountTab(content);
  } catch (err) { showToast(err.message, 'error'); }
};

const SOUND_LIBRARY = [
  { id: 'classic', name: 'Classic Ring', desc: 'Nokia-style ascending' },
  { id: 'marimba', name: 'Marimba Ring', desc: 'Cheerful bright melody' },
  { id: 'xylophone', name: 'Xylophone', desc: 'Rising wooden hits' },
  { id: 'alarm', name: 'Alarm', desc: 'Insistent repeating beep' },
  { id: 'ascending', name: 'Ascending', desc: 'Five rising notes' },
  { id: 'descending', name: 'Descending', desc: 'Five falling notes' },
  { id: 'sms', name: 'SMS Tone', desc: 'Classic message alert' },
  { id: 'digital', name: 'Digital', desc: 'Electronic sequence' },
  { id: 'phonering', name: 'Phone Ring', desc: 'Traditional ring' },
  { id: 'chime', name: 'Chime Ring', desc: 'Bell-like peal' },
  { id: 'wakeup', name: 'Wake Up', desc: 'Slow bright build' },
  { id: 'crystal', name: 'Crystal', desc: 'Clear high chimes' },
  { id: 'echoring', name: 'Echo Ring', desc: 'Fading ring pattern' },
  { id: 'vibrato', name: 'Vibrato Ring', desc: 'Wavering tone' },
  { id: 'siren', name: 'Siren Ring', desc: 'Wobbling alert' },
  { id: 'jingle', name: 'Jingle', desc: 'Happy ascending tune' }
];

async function loadRingtones() {
  try {
    const res = await API.get('/api/ringtones');
    window._ringtones = res.data || [];
  } catch { window._ringtones = window._ringtones || []; }
}

window.selectPresetSound = function(id) {
  localStorage.setItem('notificationSoundPreset', id);
  localStorage.removeItem('notificationSoundFile');
  localStorage.removeItem('notificationSoundId');
  const tab = document.querySelector('.settings-tab.active');
  if (tab && tab.dataset.tab === 'notifications') switchSettingsTab('notifications');
  showToast('Ringtone: ' + (SOUND_LIBRARY.find(s => s.id === id)?.name || id), 'success');
};

window.testNotificationSound = function() {
  playNotification();
};

window.importSound = async function() {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'audio/*';
  input.onchange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 200 * 1024) { showToast('Max 200KB — base64 would blow the local cache', 'error'); return; }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const d = ev.target.result;
      if (!d.startsWith('data:audio/')) { showToast('Invalid audio', 'error'); return; }
      try {
        const res = await API.post('/api/ringtones', { name: file.name, data: d });
        localStorage.setItem('notificationSoundId', res.data.id);
        localStorage.setItem('notificationSoundFile', d);
        await loadRingtones();
        showToast('Sound imported!', 'success');
        const el = document.getElementById('page-content');
        if (el) renderNotificationsTab(el);
      } catch (err) { showToast(err.message, 'error'); }
    };
    reader.readAsDataURL(file);
  };
  input.click();
};

window.clearSound = async function() {
  const id = localStorage.getItem('notificationSoundId');
  if (id) { try { await API.delete('/api/ringtones/' + id); } catch {} }
  localStorage.removeItem('notificationSoundFile');
  localStorage.removeItem('notificationSoundId');
  await loadRingtones();
  showToast('Custom sound cleared', 'info');
  const el = document.getElementById('page-content');
  if (el) renderNotificationsTab(el);
};

async function renderNotificationsTab(content) {
  await loadRingtones();
  const soundEnabled = localStorage.getItem('notificationSoundEnabled') !== 'false';
  const nativeEnabled = localStorage.getItem('nativeNotifications') !== 'false';
  const volume = parseFloat(localStorage.getItem('notificationVolume') || '0.6');
  const currentFile = localStorage.getItem('notificationSoundFile') || '';
  const currentPreset = localStorage.getItem('notificationSoundPreset') || 'classic';
  content.innerHTML = `
    <div class="card">
      <h3>Notifications &amp; Sounds</h3>
      <div style="margin-top:16px;">
        <label class="toggle-row" style="display:flex;align-items:center;justify-content:space-between;padding:12px 0;border-bottom:1px solid #1e1e2e;">
          <span style="font-size:14px;color:var(--text-primary);">Ringtone Sound</span>
          <label class="switch" style="position:relative;display:inline-block;width:44px;height:24px;flex-shrink:0;">
            <input type="checkbox" ${soundEnabled ? 'checked' : ''} onchange="localStorage.setItem('notificationSoundEnabled', this.checked);showToast('Sound ' + (this.checked ? 'enabled' : 'disabled'), 'success');" style="opacity:0;width:0;height:0;">
            <span class="slider" style="position:absolute;cursor:pointer;top:0;left:0;right:0;bottom:0;background:#2a2a3e;border-radius:24px;transition:0.3s;"></span>
          </label>
        </label>
        <label class="toggle-row" style="display:flex;align-items:center;justify-content:space-between;padding:12px 0;border-bottom:1px solid #1e1e2e;">
          <span style="font-size:14px;color:var(--text-primary);">Windows Notification Toast</span>
          <label class="switch" style="position:relative;display:inline-block;width:44px;height:24px;flex-shrink:0;">
            <input type="checkbox" ${nativeEnabled ? 'checked' : ''} onchange="localStorage.setItem('nativeNotifications', this.checked);showToast('Windows notifications ' + (this.checked ? 'enabled' : 'disabled'), 'success');if(this.checked&&'Notification'in window&&Notification.permission==='default')Notification.requestPermission();" style="opacity:0;width:0;height:0;">
            <span class="slider" style="position:absolute;cursor:pointer;top:0;left:0;right:0;bottom:0;background:#2a2a3e;border-radius:24px;transition:0.3s;"></span>
          </label>
        </label>
        <div style="padding:12px 0;border-bottom:1px solid #1e1e2e;">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">
            <span style="font-size:14px;color:var(--text-primary);">Volume</span>
            <span style="font-size:12px;color:var(--text-quaternary);" id="volume-label">${Math.round(volume * 100)}%</span>
          </div>
          <input type="range" min="0" max="1" step="0.05" value="${volume}" oninput="localStorage.setItem('notificationVolume', this.value);document.getElementById('volume-label').textContent=Math.round(this.value*100)+'%'" style="width:100%;accent-color:#00c853;">
        </div>
        <div style="padding:12px 0;border-bottom:1px solid #1e1e2e;">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">
            <span style="font-size:14px;color:var(--text-primary);">Ringtones</span>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;">
            ${SOUND_LIBRARY.map(s => `
              <div data-on-click="selectPresetSound('${esc(s.id)}')" style="padding:10px 14px;border-radius:10px;border:1px solid ${currentPreset === s.id && !currentFile ? '#00c853' : '#1e1e2e'};background:${currentPreset === s.id && !currentFile ? 'rgba(0,200,83,0.08)' : 'transparent'};cursor:pointer;transition:0.2s;" id="preset-${s.id}">
                <div style="font-size:13px;font-weight:600;color:${currentPreset === s.id && !currentFile ? '#00c853' : '#e4e4ec'};">${s.name}</div>
                <div style="font-size:11px;color:var(--text-quaternary);margin-top:2px;">${s.desc}</div>
              </div>
            `).join('')}
          </div>
        </div>
        <div style="padding:12px 0;">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">
            <span style="font-size:14px;color:var(--text-primary);">Custom Upload</span>
            ${currentFile ? `<audio id="preview-audio" src="${currentFile}" style="display:none;"></audio>` : ''}
          </div>
          <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;">
            <button class="btn btn-sm btn-outline" data-on-click="importSound()">Choose File</button>
            <span style="font-size:12px;color:var(--text-quaternary);" id="sound-file-name">${currentFile ? 'Custom sound loaded' : 'None selected'}</span>
          </div>
          <div style="margin-top:12px;display:flex;gap:8px;">
            <button class="btn btn-sm btn-primary" data-on-click="testNotificationSound()">Test Sound</button>
            ${currentFile ? '<button class="btn btn-sm btn-outline" data-on-click="clearSound()">Remove</button>' : ''}
          </div>
        </div>
        ${window.AndroidOverlay ? `
        <label class="toggle-row" style="display:flex;align-items:center;justify-content:space-between;padding:12px 0;border-top:1px solid #1e1e2e;">
          <span style="font-size:14px;color:var(--text-primary);">Floating Orders Widget <span style="font-size:11px;color:var(--text-quaternary);">(draw over other apps)</span></span>
          <label class="switch" style="position:relative;display:inline-block;width:44px;height:24px;flex-shrink:0;">
            <input type="checkbox" id="overlay-widget-toggle" ${window._overlayWidgetEnabled ? 'checked' : ''} onchange="toggleOrdersOverlay(this.checked)" style="opacity:0;width:0;height:0;">
            <span class="slider" style="position:absolute;cursor:pointer;top:0;left:0;right:0;bottom:0;background:#2a2a3e;border-radius:24px;transition:0.3s;"></span>
          </label>
        </label>` : ''}
      </div>
    </div>`;
}

window.toggleOrdersOverlay = function(checked) {
  if (!window.AndroidOverlay) return;
  if (checked) {
    if (AndroidOverlay.isEnabled()) {
      window._overlayWidgetEnabled = true;
      showToast('Orders overlay enabled', 'success');
      syncOrdersOverlay(window._sseOrdersCache && window._sseOrdersCache.orders);
    } else {
      AndroidOverlay.requestPermission();
      showToast('Grant "Display over other apps" and come back', 'info');
      var tries = 0;
      var iv = setInterval(function() {
        tries++;
        if (AndroidOverlay.isEnabled()) {
          clearInterval(iv);
          window._overlayWidgetEnabled = true;
          document.getElementById('overlay-widget-toggle').checked = true;
          showToast('Orders overlay enabled', 'success');
          syncOrdersOverlay(window._sseOrdersCache && window._sseOrdersCache.orders);
        } else if (tries > 30) {
          clearInterval(iv);
          document.getElementById('overlay-widget-toggle').checked = false;
          showToast('Permission not granted — overlay off', 'error');
        }
      }, 2000);
    }
  } else {
    window._overlayWidgetEnabled = false;
    AndroidOverlay.hide();
    showToast('Orders overlay disabled', 'success');
  }
};

async function renderAdvancedTab(content) {
  let prefs = {};
  try {
    const res = await API.get('/api/auth/preferences');
    prefs = res.data || {};
  } catch {}
  content.innerHTML = `
    <div class="card" style="border-color:rgba(255,87,34,0.15);">
      <h3 style="display:flex;align-items:center;gap:8px;">⚡ System Advanced</h3>
      <p style="color:var(--text-tertiary);font-size:13px;margin:8px 0 16px;">Ads-related advanced features have moved to <strong>Ads → Advanced</strong>.</p>
      <div style="margin-top:12px;">
        <label class="toggle-row" style="display:flex;align-items:center;justify-content:space-between;padding:12px 0;border-bottom:1px solid #1e1e2e;">
          <div>
            <div style="font-size:14px;color:var(--text-primary);">📱 System Notifications</div>
            <div style="font-size:11px;color:var(--text-tertiary);margin-top:2px;">Use phone or PC native notifications &amp; ringtones instead of in-browser sounds</div>
          </div>
          <label class="switch" style="position:relative;display:inline-block;width:44px;height:24px;flex-shrink:0;">
            <input type="checkbox" ${prefs.systemNotifications ? 'checked' : ''} onchange="toggleSystemNotifications(this.checked)" style="opacity:0;width:0;height:0;">
            <span class="slider" style="position:absolute;cursor:pointer;top:0;left:0;right:0;bottom:0;background:#2a2a3e;border-radius:24px;transition:0.3s;"></span>
          </label>
        </label>
        ${prefs.systemNotifications ? `<div style="padding:12px 0;border-bottom:1px solid #1e1e2e;">
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
            <button class="btn btn-sm btn-outline" data-on-click="openSystemNotificationSettings()" style="font-size:11px;padding:6px 14px;">⚙️ Open System Notification Settings</button>
            <span style="font-size:11px;color:var(--text-quaternary);">Redirects to your device notification settings</span>
          </div>
        </div>` : ''}
        <div style="padding:12px 0;border-bottom:1px solid #1e1e2e;">
          <label style="display:block;font-size:13px;color:var(--text-primary);margin-bottom:8px;">Theme 🌙☀️</label>
          <div style="display:flex;gap:8px;">
            <button class="btn ${currentUser?.theme === 'dark' ? 'btn-primary' : 'btn-outline'}" data-on-click="toggleTheme(); renderAdvancedTab(document.getElementById('settings-content'));">🌙 Dark</button>
            <button class="btn ${currentUser?.theme === 'light' ? 'btn-primary' : 'btn-outline'}" data-on-click="toggleTheme(); renderAdvancedTab(document.getElementById('settings-content'));">☀️ Light</button>
          </div>
          <div style="font-size:11px;color:var(--text-tertiary);margin-top:6px;">Current: ${currentUser?.theme === 'light' ? 'Light Mode' : 'Dark Mode'}</div>
        </div>
      </div>
    </div>`;
}

window.pauseOkxTab = async function() {
  if (!confirm('Go unavailable?\n\nYour merchant presence will be dropped — you will show OFFLINE in OKX and your ads will leave the marketplace. Click "Come Back Online" here later to resume.')) return;
  try {
    await API.post('/api/okx/tab/pause');
    await API.put('/api/auth/preferences', { keepOnlineEnabled: false });
    showToast('Paused — going unavailable (ads off market)', 'success');
    var el = document.getElementById('settings-content');
    if (el) renderAdvancedTab(el);
  } catch (err) { showToast(err.message || 'Pause failed', 'error'); }
};

window.resumeOkxTab = async function() {
  try {
    await API.post('/api/okx/tab/resume');
    await API.put('/api/auth/preferences', { keepOnlineEnabled: true });
    showToast('Resumed — coming back online in ~1 minute', 'success');
    var el = document.getElementById('settings-content');
    if (el) setTimeout(function(){ if (el) renderAdvancedTab(el); }, 4000);
  } catch (err) { showToast(err.message || 'Resume failed', 'error'); }
};

window.toggleAdvancedSetting = async function(key, value) {
  try {
    await API.put('/api/auth/preferences', { [key]: !!value });
    showToast('Setting ' + (value ? 'enabled' : 'disabled'), 'success');
    var el = document.getElementById('settings-content');
    if (el) renderAdvancedTab(el);
    if (typeof renderAdAdvanced === 'function') renderAdAdvanced(document.getElementById('ads-tab-content'));
  } catch (err) { showToast(err.message, 'error'); }
};

window.toggleAutoReactivateAds = async function(enabled) {
  try {
    await API.put('/api/auth/preferences', { autoReactivateAds: !!enabled });
    showToast('Auto-reactivate ads ' + (enabled ? 'enabled' : 'disabled'), 'success');
    var el = document.getElementById('settings-content');
    if (el) renderAdvancedTab(el);
    if (typeof renderAdAdvanced === 'function') renderAdAdvanced(document.getElementById('ads-tab-content'));
  } catch (err) { showToast(err.message, 'error'); }
};

window.toggleRequirePaymentProof = async function(enabled) {
  try {
    await API.put('/api/auth/preferences', { requirePaymentProof: !!enabled });
    showToast('Payment proof ' + (enabled ? 'required' : 'optional'), 'success');
    var el = document.getElementById('settings-content');
    if (el) renderAdvancedTab(el);
    if (typeof renderAdAdvanced === 'function') renderAdAdvanced(document.getElementById('ads-tab-content'));
  } catch (err) { showToast(err.message, 'error'); }
};

window.toggleSystemNotifications = async function(enabled) {
  localStorage.setItem('nativeNotifications', enabled ? 'true' : 'false');
  try {
    await API.put('/api/auth/preferences', { systemNotifications: !!enabled });
    if (enabled && 'Notification' in window && Notification.permission !== 'granted') {
      Notification.requestPermission().catch(function() {});
    }
    showToast('System notifications ' + (enabled ? 'enabled' : 'disabled'), 'success');
    renderAdvancedTab(document.getElementById('settings-content'));
  } catch (err) { showToast(err.message, 'error'); }
};

window.openSystemNotificationSettings = function() {
  var ual = (navigator.userAgent || '').toLowerCase();
  if (ual.indexOf('android') !== -1) {
    showToast('Open your device Settings > Apps > OKX > Notifications to customize', 'info', 8000);
  } else if (ual.indexOf('windows') !== -1) {
    window.open('ms-settings:notifications', '_blank');
  } else if (ual.indexOf('mac') !== -1) {
    window.open('x-apple.systempreferences:com.apple.preference.notifications', '_blank');
  } else {
    window.open('chrome://settings/content/notifications', '_blank');
  }
};

window.toggleLoginNotify = async function(enabled) {
  try {
    await API.put('/api/auth/preferences', { notifyOnLogin: !!enabled });
    showToast('Login notifications ' + (enabled ? 'enabled' : 'disabled'), 'success');
  } catch (err) { showToast(err.message, 'error'); }
};

window.toggleCompetitiveAlert = async function(enabled) {
  try {
    await API.put('/api/auth/profile', { competitiveAlertEnabled: !!enabled });
    currentUser.competitiveAlertEnabled = !!enabled;
    showToast('Competitive alert ' + (enabled ? 'enabled' : 'disabled'), 'success');
    var el = document.getElementById('settings-content');
    if (el) renderAdvancedTab(el);
    if (typeof renderAdAdvanced === 'function') renderAdAdvanced(document.getElementById('ads-tab-content'));
  } catch (err) { showToast(err.message, 'error'); }
};

window.saveCompetitiveAlertThreshold = async function(value) {
  const threshold = parseInt(value) || 100;
  try {
    await API.put('/api/auth/profile', { competitiveAlertLimitThreshold: threshold });
    currentUser.competitiveAlertLimitThreshold = threshold;
    showToast('Alert threshold updated to ' + threshold + ' KES', 'success');
    var el = document.getElementById('settings-content');
    if (el) renderAdvancedTab(el);
    if (typeof renderAdAdvanced === 'function') renderAdAdvanced(document.getElementById('ads-tab-content'));
  } catch (err) { showToast(err.message, 'error'); }
};

window.saveCompetitiveAlertPriceThreshold = async function(value) {
  const threshold = parseInt(value) || 0;
  try {
    await API.put('/api/auth/profile', { competitiveAlertPriceThreshold: threshold });
    currentUser.competitiveAlertPriceThreshold = threshold;
    showToast('Price threshold updated to ' + (threshold === 0 ? 'any price above your SELL ad' : threshold + ' KES'), 'success');
    var el = document.getElementById('settings-content');
    if (el) renderAdvancedTab(el);
    if (typeof renderAdAdvanced === 'function') renderAdAdvanced(document.getElementById('ads-tab-content'));
  } catch (err) { showToast(err.message, 'error'); }
};

async function renderPaymentReminderTab(content) {
  await loadRingtones();
  const list = window._ringtones || [];
  const custom = list.filter(r => !r.isPreset);
  const volume = parseFloat(localStorage.getItem('paymentReminderVolume') || '0.7');
  content.innerHTML = `
    <div class="card" style="border-color:rgba(255,152,0,0.15);">
      <div style="display:flex;align-items:center;gap:12px;margin-bottom:6px;">
        <span style="font-size:22px;">🔔</span>
        <h3 style="margin:0;">Payment Reminder</h3>
      </div>
      <p style="font-size:13px;color:var(--text-quaternary);margin:0 0 18px 0;">Plays a random song from your playlist when a buy order is unpaid for 45 sec, or a sell order is paid but not released for 45 sec.</p>
      <div style="background:rgba(255,152,0,0.04);border:1px solid rgba(255,152,0,0.1);border-radius:12px;padding:16px;margin-bottom:16px;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">
          <span style="font-size:13px;color:var(--text-primary);font-weight:600;">Volume &amp; Boost</span>
          <div style="display:flex;align-items:center;gap:8px;">
            <span style="font-size:12px;color:var(--text-quaternary);" id="reminder-vol-label">${Math.round(volume * 100)}% (${Math.round(Math.min(volume * 2.5, 1) * 100)}% boosted)</span>
            <button class="btn btn-sm btn-outline" data-on-click="playPaymentReminder()" style="font-size:11px;padding:3px 10px;">Test</button>
          </div>
        </div>
        <input type="range" min="0" max="1" step="0.05" value="${volume}" oninput="localStorage.setItem('paymentReminderVolume', this.value);document.getElementById('reminder-vol-label').textContent=Math.round(this.value*100)+'% ('+Math.round(Math.min(this.value*2.5,1)*100)+'% boosted)'" style="width:100%;accent-color:#ff9800;">
        <div style="display:flex;justify-content:space-between;font-size:11px;color:var(--text-quaternary);margin-top:2px;"><span>Quiet</span><span>Loud</span></div>
      </div>
      <div style="margin-bottom:14px;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;">
          <span style="font-size:13px;color:var(--text-primary);font-weight:600;">Playlist (${custom.length} songs)</span>
          <button class="btn btn-sm btn-primary" data-on-click="importReminderSound()" style="background:linear-gradient(135deg,#ff9800,#ffa726);color:var(--text-on-accent);">+ Add Song</button>
        </div>
        ${custom.length === 0 ? `<div style="text-align:center;padding:28px 16px;background:rgba(255,152,0,0.03);border-radius:10px;border:1px dashed rgba(255,152,0,0.15);">
          <div style="font-size:32px;margin-bottom:8px;opacity:0.4;">🎵</div>
          <p style="font-size:13px;color:var(--text-quaternary);margin:0;">No songs yet — add one to get reminded with style</p>
        </div>` : `<div id="reminder-playlist" style="display:flex;flex-direction:column;gap:6px;">
          ${custom.map((s, i) => `<div style="display:flex;align-items:center;gap:10px;padding:10px 14px;background:rgba(255,152,0,0.04);border:1px solid rgba(255,152,0,0.08);border-radius:10px;transition:0.2s;">
            <span style="font-size:12px;color:var(--text-quaternary);min-width:20px;font-weight:700;">${i + 1}</span>
            <span style="font-size:11px;color:#ff9800;cursor:pointer;" data-on-click="previewReminderSound('${esc(s.id)}')">▶</span>
            <span style="flex:1;font-size:13px;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${s.name}</span>
            <button class="btn btn-sm" style="padding:4px 8px;font-size:11px;background:rgba(255,152,0,0.08);color:#ff9800;border:none;border-radius:6px;cursor:pointer;${i === 0 ? 'opacity:0.3;pointer-events:none;' : ''}" data-on-click="moveReminderSound('${esc(s.id)}', -1)" title="Move up">▲</button>
            <button class="btn btn-sm" style="padding:4px 8px;font-size:11px;background:rgba(255,152,0,0.08);color:#ff9800;border:none;border-radius:6px;cursor:pointer;${i === custom.length - 1 ? 'opacity:0.3;pointer-events:none;' : ''}" data-on-click="moveReminderSound('${esc(s.id)}', 1)" title="Move down">▼</button>
            <button class="btn btn-sm" style="padding:4px 8px;font-size:11px;background:rgba(244,67,54,0.1);color:#f44336;border:none;border-radius:6px;cursor:pointer;" data-on-click="deleteReminderSound('${esc(s.id)}')" title="Delete">✕</button>
          </div>`).join('')}
        </div>`}
      </div>
      <div style="display:flex;gap:8px;padding-top:12px;border-top:1px solid rgba(255,152,0,0.08);">
        <button class="btn btn-sm btn-primary" data-on-click="testPaymentReminder()" style="background:linear-gradient(135deg,#ff9800,#ffa726);color:var(--text-on-accent);">▶ Test Random Song</button>
        ${custom.length > 0 ? '<button class="btn btn-sm btn-outline" data-on-click="clearReminderPlaylist()" style="border-color:rgba(244,67,54,0.3);color:#f44336;">Clear All Songs</button>' : ''}
      </div>
    </div>`;
}

async function importReminderSound() {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'audio/*';
  input.onchange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { showToast('File too large (max 4MB)', 'error'); return; }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target.result;
      if (!dataUrl.startsWith('data:audio/')) { showToast('Invalid audio file', 'error'); return; }
      try {
        await API.post('/api/ringtones', { name: file.name, data: dataUrl });
        showToast('Ringtone saved!', 'success');
        await loadRingtones();
        const el = document.getElementById('page-content');
        if (el) renderPaymentReminderTab(el);
      } catch (err) { showToast(err.message, 'error'); }
    };
    reader.readAsDataURL(file);
  };
  input.click();
}

async function deleteReminderSound(id) {
  if (!confirm('Delete this ringtone?')) return;
  try {
    await API.delete('/api/ringtones/' + id);
    await loadRingtones();
    const el = document.getElementById('page-content');
    if (el) renderPaymentReminderTab(el);
    showToast('Ringtone deleted', 'info');
  } catch (err) { showToast(err.message, 'error'); }
}

function moveReminderSound(id, dir) {
  const list = window._ringtones || [];
  const custom = list.filter(r => !r.isPreset);
  const idx = custom.findIndex(s => s.id === id);
  if (idx === -1) return;
  const to = idx + dir;
  if (to < 0 || to >= custom.length) return;
  [custom[idx], custom[to]] = [custom[to], custom[idx]];
  const el = document.getElementById('page-content');
  if (el) renderPaymentReminderTab(el);
}

async function clearReminderPlaylist() {
  if (!confirm('Delete all custom ringtones?')) return;
  try {
    const list = window._ringtones || [];
    for (const r of list) {
      if (!r.isPreset) await API.delete('/api/ringtones/' + r.id).catch(() => {});
    }
    await loadRingtones();
    const el = document.getElementById('page-content');
    if (el) renderPaymentReminderTab(el);
    showToast('Playlist cleared', 'info');
  } catch (err) { showToast(err.message, 'error'); }
}

async function previewReminderSound(id) {
  try {
    const res = await API.get('/api/ringtones/' + id + '/data');
    const d = res.data;
    if (!d || !d.data || !d.data.startsWith('data:audio/')) { showToast('Invalid ringtone data', 'error'); return; }
    const vol = Math.min(parseFloat(localStorage.getItem('notificationVolume') || '0.5') * 2.5, 1);
    const a = new Audio(d.data);
    a.volume = vol;
    a.play().catch(() => {});
  } catch (err) { showToast(err.message, 'error'); }
}

function testPaymentReminder() {
  playPaymentReminder();
  showToast('Playing random song from playlist', 'info');
}

// ── Telegram Tab ──
async function renderTelegramTab(content) {
  content.innerHTML = '<div style="text-align:center;padding:40px;color:var(--text-quaternary);">Loading...</div>';
  try {
    var status = (await API.getTelegramStatus()).data || {};
    var linked = status.linked;
    var botUsername = 'moneyfestltdbot';
    if (linked) {
      content.innerHTML = '<div class="card" style="text-align:center;padding:32px;border-color:rgba(0,200,83,0.2);">' +
        '<div style="font-size:48px;margin-bottom:12px;">✅</div>' +
        '<h3 style="margin-bottom:4px;">Telegram Connected</h3>' +
        '<p style="font-size:13px;color:var(--text-quaternary);margin-bottom:20px;">You\'ll receive order notifications here.</p>' +
        '<button class="btn btn-danger" data-on-click="unlinkTelegram()">Disconnect Telegram</button>' +
        '</div>';
    } else {
      content.innerHTML = '<div class="card" style="text-align:center;padding:32px;">' +
        '<div style="font-size:48px;margin-bottom:12px;">✈️</div>' +
        '<h3 style="margin-bottom:4px;">Connect Telegram</h3>' +
        '<p style="font-size:13px;color:var(--text-quaternary);margin-bottom:16px;max-width:400px;margin-left:auto;margin-right:auto;">Get real-time order notifications and control your P2P trading via Telegram.</p>' +
        '<div id="telegram-link-section">' +
        '<button class="btn btn-primary" data-on-click="generateTelegramLink()" id="telegram-connect-btn" style="background:linear-gradient(135deg,#0088cc,#00a0e6);color:#fff;padding:12px 28px;font-size:15px;">Connect Telegram</button>' +
        '</div>' +
        '<div id="telegram-instructions" style="display:none;margin-top:20px;background:var(--bg-tertiary);border-radius:12px;padding:20px;max-width:400px;margin-left:auto;margin-right:auto;text-align:left;">' +
        '<div style="font-size:13px;font-weight:600;margin-bottom:10px;color:var(--text-primary);">Step-by-step:</div>' +
        '<div style="font-size:12px;color:#8888a0;line-height:1.8;">' +
        '1. Open Telegram and search for <strong>@' + botUsername + '</strong><br>' +
        '2. Start a chat and send:<br>' +
        '<div style="background:#1a1a2e;border-radius:8px;padding:10px 14px;margin:8px 0;font-family:monospace;font-size:14px;text-align:center;color:#00c853;" id="telegram-link-code-display">Loading...</div>' +
        '3. The bot will confirm your account is linked<br>' +
        '4. Try <strong>/orders</strong> to see your open orders' +
        '</div></div></div>';
    }
  } catch (err) {
    content.innerHTML = '<div class="card" style="text-align:center;padding:40px;"><p style="color:var(--accent-red);">' + esc(err.message) + '</p></div>';
  }
}

window.generateTelegramLink = async function() {
  var btn = document.getElementById('telegram-connect-btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Generating...'; }
  try {
    var res = await API.generateTelegramLink();
    var data = res.data || {};
    if (data.msg) {
      showToast(data.msg, 'info');
      if (btn) { btn.disabled = false; btn.textContent = 'Connect Telegram'; }
      return;
    }
    var code = data.code;
    var display = document.getElementById('telegram-link-code-display');
    if (display) display.textContent = '/link ' + code;
    var inst = document.getElementById('telegram-instructions');
    if (inst) inst.style.display = 'block';
    var section = document.getElementById('telegram-link-section');
    if (section) section.innerHTML = '<div style="font-size:12px;color:var(--text-quaternary);">Code valid for 15 minutes. Send it to @MoneyfestLtdApiBot</div>';
    showToast('Link code generated! Send /link ' + code + ' to @MoneyfestLtdApiBot', 'success');
  } catch (err) {
    showToast(err.message, 'error');
    if (btn) { btn.disabled = false; btn.textContent = 'Connect Telegram'; }
  }
};

window.unlinkTelegram = async function() {
  if (!confirm('Disconnect Telegram? You will stop receiving notifications.')) return;
  try {
    await API.unlinkTelegram();
    showToast('Telegram disconnected', 'success');
    switchSettingsTab('telegram');
  } catch (err) { showToast(err.message, 'error'); }
};

// ── Multi-OKX-Account UI (up to 2 accounts) ──

async function renderApiTab(content) {
  content.innerHTML = '<div style="text-align:center;padding:40px;color:var(--text-quaternary);">Loading API config...</div>';
  try {
    var [okxRes, bybitRes, binanceRes] = await Promise.all([
      API.get('/api/auth/okx-accounts'),
      API.get('/api/bybit/keys'),
      API.get('/api/binance/keys')
    ]);
    var okxAcct = (okxRes.data || [])[0] || {};
    var bybitConfigured = bybitRes?.data?.configured;
    var binanceConfigured = binanceRes?.data?.configured;

    var html = '<div style="display:grid;gap:24px;">';

    // OKX API
    var okxHasKeys = okxAcct.apiKey && okxAcct.apiKey !== '';
    var ipNotice = await window.okxIpNoticeHtml();
    html += '<div class="card" style="border-color:' + (okxHasKeys ? 'rgba(0,200,83,0.2)' : 'rgba(255,152,0,0.15)') + ';">' +
      '<div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;">' +
      '<span style="font-size:20px;">\uD83D\uDD11</span>' +
      '<h3 style="margin:0;flex:1;">OKX API</h3>' +
      (okxHasKeys ? '<span style="font-size:12px;padding:2px 10px;border-radius:6px;background:rgba(0,200,83,0.1);color:#00c853;">\u2705 Configured</span>' : '<span style="font-size:12px;padding:2px 10px;border-radius:6px;background:rgba(255,152,0,0.1);color:#ff9800;">\u274C Not set</span>') +
      '</div>' +
      ipNotice +
      '<div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">' +
      '<div class="form-group"><label>API Key</label><input id="okx-api-key" value="" placeholder="' + (okxHasKeys ? 'Current: ' + okxAcct.apiKey : 'Enter OKX API Key') + '" style="font-family:monospace;"></div>' +
      '<div class="form-group"><label>Secret Key</label><input id="okx-secret-key" type="password" value="" placeholder="' + (okxHasKeys ? 'Current: ' + okxAcct.secretKey : 'Enter Secret Key') + '" style="font-family:monospace;"></div>' +
      '<div class="form-group"><label>Passphrase</label><input id="okx-passphrase" type="password" value="" placeholder="' + (okxHasKeys ? 'Current: ' + okxAcct.passphrase : 'Enter Passphrase') + '" style="font-family:monospace;"></div>' +
      '<div class="form-group"><label>API URL</label><input id="okx-api-url" value="' + (okxAcct.apiUrl || 'https://www.okx.com') + '" placeholder="https://www.okx.com" style="font-family:monospace;"></div>' +
      '</div>' +
      '<label style="display:flex;align-items:flex-start;gap:8px;font-size:12px;color:#c9c9d6;margin-top:10px;cursor:pointer;"><input type="checkbox" id="okx-ip-confirmed" ' + (okxHasKeys ? 'checked' : '') + ' onchange="window.okxIpGate(this)" style="transform:scale(1.2);margin-top:2px;"> I added the IP whitelist above to my OKX API key settings</label>' +
      '<button class="btn btn-primary" id="okx-save-btn" data-on-click="saveOkxKeys()" style="margin-top:12px;' + (okxHasKeys ? '' : 'opacity:0.5;cursor:not-allowed;') + '"' + (okxHasKeys ? '' : ' disabled') + '>Save OKX Keys</button>' +
      (okxHasKeys ? '<button class="btn btn-secondary" data-on-click="clearOkxKeys()" style="margin-top:12px;margin-left:8px;">Clear</button>' : '') +
      '<div id="okx-key-status" style="margin-top:8px;font-size:12px;"></div>' +
      '</div>';

    // Bybit API
    html += '<div class="card" style="border-color:' + (bybitConfigured ? 'rgba(0,200,83,0.2)' : 'rgba(255,152,0,0.15)') + ';">' +
      '<div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;">' +
      '<span style="font-size:20px;">\uD83D\uDD11</span>' +
      '<h3 style="margin:0;flex:1;">Bybit API</h3>' +
      (bybitConfigured ? '<span style="font-size:12px;padding:2px 10px;border-radius:6px;background:rgba(0,200,83,0.1);color:#00c853;">\u2705 Configured</span>' : '<span style="font-size:12px;padding:2px 10px;border-radius:6px;background:rgba(255,152,0,0.1);color:#ff9800;">\u274C Not set</span>') +
      '</div>' +
      '<p style="color:var(--text-tertiary);font-size:12px;margin:0 0 10px;">Enter your Bybit API key and secret for P2P features.</p>' +
      '<div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">' +
      '<div class="form-group"><label>API Key</label><input id="bybit-api-key" placeholder="Enter Bybit API key" style="font-family:monospace;"></div>' +
      '<div class="form-group"><label>Secret Key</label><input id="bybit-secret-key" type="password" placeholder="Enter Bybit API secret" style="font-family:monospace;"></div>' +
      '</div>' +
      '<button class="btn btn-primary" data-on-click="saveBybitKeys()" style="margin-top:12px;">Save Bybit Keys</button>' +
      (bybitConfigured ? '<button class="btn btn-secondary" data-on-click="clearBybitKeys()" style="margin-top:12px;margin-left:8px;">Clear</button>' : '') +
      '<div id="bybit-key-status" style="margin-top:8px;font-size:12px;"></div>' +
      '</div>';

    // Binance API
    html += '<div class="card" style="border-color:' + (binanceConfigured ? 'rgba(0,200,83,0.2)' : 'rgba(255,152,0,0.15)') + ';">' +
      '<div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;">' +
      '<span style="font-size:20px;">\uD83D\uDD11</span>' +
      '<h3 style="margin:0;flex:1;">Binance API</h3>' +
      (binanceConfigured ? '<span style="font-size:12px;padding:2px 10px;border-radius:6px;background:rgba(0,200,83,0.1);color:#00c853;">\u2705 Configured</span>' : '<span style="font-size:12px;padding:2px 10px;border-radius:6px;background:rgba(255,152,0,0.1);color:#ff9800;">\u274C Not set</span>') +
      '</div>' +
      '<p style="color:var(--text-tertiary);font-size:12px;margin:0 0 10px;">Enter your Binance API key and secret for P2P features.</p>' +
      '<div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">' +
      '<div class="form-group"><label>API Key</label><input id="binance-api-key" placeholder="Enter Binance API key" style="font-family:monospace;"></div>' +
      '<div class="form-group"><label>Secret Key</label><input id="binance-secret-key" type="password" placeholder="Enter Binance API secret" style="font-family:monospace;"></div>' +
      '</div>' +
      '<button class="btn btn-primary" data-on-click="saveBinanceKeys()" style="margin-top:12px;">Save Binance Keys</button>' +
      (binanceConfigured ? '<button class="btn btn-secondary" data-on-click="clearBinanceKeys()" style="margin-top:12px;margin-left:8px;">Clear</button>' : '') +
      '<div id="binance-key-status" style="margin-top:8px;font-size:12px;"></div>' +
      '</div>';

    html += '</div>';
    content.innerHTML = html;
  } catch (err) { content.innerHTML = '<div class="card"><p style="color:#f44336;">Failed to load: ' + err.message + '</p></div>'; }
}

window.okxIpGate = function(cb) {
  var btn = document.getElementById('okx-save-btn');
  if (!btn) return;
  btn.disabled = !cb.checked;
  btn.style.opacity = cb.checked ? '' : '0.5';
  btn.style.cursor = cb.checked ? '' : 'not-allowed';
};

window.saveOkxKeys = async function() {
  var ipCb = document.getElementById('okx-ip-confirmed');
  var hadKeys = !!document.getElementById('okx-api-key').placeholder?.startsWith('Current:');
  if (!hadKeys && ipCb && !ipCb.checked) {
    showToast('Confirm you added the IP whitelist to your OKX API key first', 'error');
    return;
  }
  var apiKey = document.getElementById('okx-api-key').value.trim();
  var secretKey = document.getElementById('okx-secret-key').value.trim();
  var passphrase = document.getElementById('okx-passphrase').value.trim();
  var apiUrl = document.getElementById('okx-api-url').value.trim() || 'https://www.okx.com';
  if (!apiKey && !secretKey && !passphrase) { showToast('Fill at least one field', 'error'); return; }
  var accounts = [{ id: 'acc_1', label: 'Account 1', apiKey: apiKey, secretKey: secretKey, passphrase: passphrase, apiUrl: apiUrl, active: true }];
  try {
    await API.put('/api/auth/okx-accounts', { accounts: accounts });
    showToast('OKX keys saved!', 'success');
    renderApiTab(document.getElementById('settings-content'));
  } catch (err) { showToast(err.message, 'error'); }
};

window.clearOkxKeys = async function() {
  if (!confirm('Remove OKX API keys?')) return;
  try {
    await API.put('/api/auth/okx-accounts', { accounts: [] });
    showToast('OKX keys cleared', 'info');
    renderApiTab(document.getElementById('settings-content'));
  } catch (err) { showToast(err.message, 'error'); }
};

window.saveBybitKeys = async function() {
  var apiKey = document.getElementById('bybit-api-key').value.trim();
  var secretKey = document.getElementById('bybit-secret-key').value.trim();
  if (!apiKey || !secretKey) { showToast('Both fields are required', 'error'); return; }
  try {
    var r = await API.post('/api/bybit/keys', { apiKey, secretKey });
    if (r.code === 0) { showToast('Bybit keys saved!', 'success'); renderApiTab(document.getElementById('settings-content')); }
    else { showToast(r.msg || 'Error saving keys', 'error'); }
  } catch (err) { showToast(err.message, 'error'); }
};

window.clearBybitKeys = async function() {
  if (!confirm('Remove Bybit API keys?')) return;
  try {
    await API.post('/api/bybit/keys', { apiKey: '', secretKey: '' });
    showToast('Bybit keys cleared', 'info');
    renderApiTab(document.getElementById('settings-content'));
  } catch (err) { showToast(err.message, 'error'); }
};

window.saveBinanceKeys = async function() {
  var apiKey = document.getElementById('binance-api-key').value.trim();
  var secretKey = document.getElementById('binance-secret-key').value.trim();
  if (!apiKey || !secretKey) { showToast('Both fields are required', 'error'); return; }
  try {
    var r = await API.post('/api/binance/keys', { apiKey, secretKey });
    if (r.code === 0) { showToast('Binance keys saved!', 'success'); renderApiTab(document.getElementById('settings-content')); }
    else { showToast(r.msg || 'Error saving keys', 'error'); }
  } catch (err) { showToast(err.message, 'error'); }
};

window.clearBinanceKeys = async function() {
  if (!confirm('Remove Binance API keys?')) return;
  try {
    await API.post('/api/binance/keys', { apiKey: '', secretKey: '' });
    showToast('Binance keys cleared', 'info');
    renderApiTab(document.getElementById('settings-content'));
  } catch (err) { showToast(err.message, 'error'); }
};

// ===== 2FA / TOTP =====
window.setup2FA = async function() {
  const area = document.getElementById('2fa-setup-area');
  if (!area) return;
  try {
    const res = await API.post('/api/auth/2fa/setup', {});
    const data = res.data;
    area.innerHTML = [
      '<div style="margin-top:16px;padding:16px;background:var(--bg-primary);border-radius:10px;border:1px solid var(--border-primary);">',
      '<p style="color:var(--text-tertiary);font-size:13px;margin-bottom:12px;">Scan this QR code with your authenticator app, or enter the secret key manually:</p>',
      data.qrCode ? '<div style="text-align:center;margin-bottom:12px;"><img src="' + data.qrCode + '" style="width:180px;height:180px;border-radius:8px;background:#fff;padding:8px;"></div>' : '',
      '<div style="background:#07070d;padding:10px;border-radius:6px;text-align:center;font-family:monospace;font-size:13px;color:var(--text-primary);margin-bottom:12px;word-break:break-all;">' + data.secret + '</div>',
      '<div class="form-group">',
      '<label>Enter the 6-digit code from your authenticator app:</label>',
      '<div style="display:flex;gap:8px;">',
      '<input type="text" id="2fa-verify-token" class="form-input" placeholder="000000" maxlength="6" style="width:140px;text-align:center;font-size:18px;letter-spacing:4px;font-family:monospace;">',
      '<button class="btn btn-primary" data-on-click="verify2FASetup()">Verify & Enable</button>',
      '<button class="btn btn-secondary" data-on-click="document.getElementById(\'2fa-setup-area\').innerHTML=\'\'">Cancel</button>',
      '</div></div></div>'
    ].join('');
  } catch (err) { showToast(err.message, 'error'); }
};

window.verify2FASetup = async function() {
  const token = document.getElementById('2fa-verify-token').value.trim();
  if (!token || token.length < 6) { showToast('Enter a valid 6-digit code', 'error'); return; }
  try {
    await API.verify2fa(token);
    showToast('2FA enabled successfully!', 'success');
    if (typeof renderPasskeyTab === 'function') {
      const c = document.getElementById('settings-content');
      if (c) renderPasskeyTab(c);
    }
  } catch (err) { showToast(err.message, 'error'); }
};

window.disable2FA = async function() {
  var pwd = prompt('Enter your password to disable two-factor authentication:');
  if (!pwd) return;
  try {
    await API.disable2fa(pwd, null);
    showToast('2FA disabled', 'success');
    if (typeof renderPasskeyTab === 'function') {
      const c = document.getElementById('settings-content');
      if (c) renderPasskeyTab(c);
    }
  } catch (err) { showToast(err.message, 'error'); }
};

window.show2faQR = async function() {
  const area = document.getElementById('2fa-qr-area');
  if (!area) return;
  if (area.style.display !== 'none') { area.style.display = 'none'; return; }
  try {
    const res = await API.get2faQR();
    const data = res.data;
    area.style.display = 'block';
    area.innerHTML = [
      '<div style="padding:16px;background:var(--bg-primary);border-radius:10px;border:1px solid var(--border-primary);">',
      '<p style="color:var(--text-tertiary);font-size:13px;margin-bottom:12px;">Scan this QR code with your authenticator app:</p>',
      '<div style="text-align:center;margin-bottom:12px;"><img src="' + data.qrCode + '" style="width:180px;height:180px;border-radius:8px;background:#fff;padding:8px;"></div>',
      '<div style="background:#07070d;padding:10px;border-radius:6px;text-align:center;font-family:monospace;font-size:13px;color:var(--text-primary);word-break:break-all;">' + data.secret + '</div>',
      '<div style="text-align:center;margin-top:8px;"><button class="btn btn-sm btn-ghost" data-on-click="document.getElementById(\'2fa-qr-area\').style.display=\'none\'">Hide</button></div>',
      '</div>'
    ].join('');
  } catch (err) { showToast(err.message, 'error'); }
};

// ===== Session Management =====
window.revokeSession = async function(sessionId) {
  if (!confirm('Revoke this session?')) return;
  try {
    await API.revokeSession(sessionId);
    showToast('Session revoked', 'success');
    if (typeof renderPasskeyTab === 'function') {
      const c = document.getElementById('settings-content');
      if (c) renderPasskeyTab(c);
    }
  } catch (err) { showToast(err.message, 'error'); }
};

window.revokeAllSessions = async function() {
  const pwd = prompt('Enter your password to revoke all other sessions:');
  if (!pwd) return;
  try {
    await API.revokeAllSessions(pwd);
    showToast('All other sessions revoked', 'success');
    if (typeof renderPasskeyTab === 'function') {
      const c = document.getElementById('settings-content');
      if (c) renderPasskeyTab(c);
    }
  } catch (err) { showToast(err.message, 'error'); }
};

function previewAvatar(e) {
  const file = e.target.files[0];
  if (!file) return;
  document.getElementById('settings-avatar-name').textContent = file.name;
  const reader = new FileReader();
  reader.onload = function(ev) { window._pendingAvatar = ev.target.result; };
  reader.readAsDataURL(file);
}

function updateUserUI() {
  if (currentUser) {
    const nameEl = document.getElementById('userName');
    const avatarEl = document.getElementById('userAvatar');
    const initialEl = document.getElementById('userInitial');
    if (nameEl) nameEl.textContent = (currentUser.username || currentUser.name);
    // Avatar: image when set, colored initial otherwise (chip + dropdown header)
    var initial = (currentUser.name || '?').trim().charAt(0).toUpperCase();
    if (initialEl) initialEl.textContent = initial;
    const pmInitial = document.getElementById('pmInitial');
    if (pmInitial) pmInitial.textContent = initial;
    const hasAvatar = !!currentUser.avatar;
    if (avatarEl) {
      if (hasAvatar) { avatarEl.src = currentUser.avatar; avatarEl.style.display = 'inline-block'; }
      else avatarEl.style.display = 'none';
    }
    const pmAvatar = document.getElementById('pmAvatar');
    if (pmAvatar) {
      if (hasAvatar) { pmAvatar.src = currentUser.avatar; pmAvatar.style.display = 'inline-block'; }
      else pmAvatar.style.display = 'none';
    }
    if (initialEl) initialEl.style.display = hasAvatar ? 'none' : 'inline-flex';
    const pmName = document.getElementById('pmName');
    if (pmName) pmName.textContent = currentUser.name || '';
    const pmSub = document.getElementById('pmSub');
    if (pmSub) {
      const uname = currentUser.username ? '@' + currentUser.username : '';
      pmSub.textContent = [uname, currentUser.email].filter(Boolean).join(' · ');
    }
    const banner = document.getElementById('pending-banner');
    if (banner) {
      const isPending = currentUser.approved === 0 && currentUser.hasApiKeys;
      banner.classList.toggle('hidden', !isPending);
    }
  }
}

async function updateProfile(e) {
  e.preventDefault();
  const name = document.getElementById('settings-name').value;
  const email = document.getElementById('settings-email').value;
  const username = document.getElementById('settings-username').value;
  const curPw = document.getElementById('settings-cur-pw').value;
  const newPw = document.getElementById('settings-new-pw').value;
  const confPw = document.getElementById('settings-conf-pw').value;
  if (newPw && newPw !== confPw) { showToast('Passwords do not match', 'error'); return; }
  const body = { name, email, username, currentPassword: curPw, newPassword: newPw || undefined };
  if (window._pendingAvatar) { body.avatar = window._pendingAvatar; delete window._pendingAvatar; }
  try {
    await API.put('/api/auth/profile', body);
    const profileRes = await API.getProfile().catch(() => null);
    if (profileRes) currentUser = profileRes.data;
    updateUserUI();
    showToast('Profile updated!', 'success');
    navigate('settings');
  } catch (err) { showToast(err.message, 'error'); }
}

// Standalone "Change Password" card (settings.js changePassword form submit).
async function changePassword(e) {
  e.preventDefault();
  const cur = document.getElementById('settings-cur-pw').value;
  const next = document.getElementById('settings-new-pw').value;
  const conf = document.getElementById('settings-conf-pw').value;
  if (!cur) { showToast('Enter your current password', 'error'); return; }
  if (!next) { showToast('Enter a new password', 'error'); return; }
  if (next !== conf) { showToast('New passwords do not match', 'error'); return; }
  try {
    await API.put('/api/auth/profile', { currentPassword: cur, newPassword: next });
    document.getElementById('settings-cur-pw').value = '';
    document.getElementById('settings-new-pw').value = '';
    document.getElementById('settings-conf-pw').value = '';
    showToast('Password updated', 'success');
  } catch (err) { showToast(err.message, 'error'); }
}

function formatDeviceName(ua) {
  if (!ua) return 'Unknown device';
  var name = 'Unknown';
  var os = '';
  var browser = '';
  if (/MONEYFESTLTD-APK/i.test(ua)) name = 'MONEYFESTLTD APK';
  else if (/Android/i.test(ua)) name = 'Android';
  else if (/iPhone|iPad|iPod/i.test(ua)) name = 'iOS';
  else if (/Windows/i.test(ua)) name = 'Windows';
  else if (/Mac OS/i.test(ua)) name = 'Mac';
  else if (/Linux/i.test(ua)) name = 'Linux';
  var m = ua.match(/Android [\d.]+/); if (m) os = ' ' + m[0];
  m = ua.match(/Windows NT [\d.]+/); if (m) os = ' ' + m[0];
  m = ua.match(/Chrome\/[\d.]+/); if (m) browser = ' ' + m[0].replace('Chrome/', 'Chrome ');
  m = ua.match(/Firefox\/[\d.]+/); if (m) browser = ' ' + m[0].replace('Firefox/', 'Firefox ');
  m = ua.match(/Safari\/[\d.]+/); if (!browser && m) browser = ' Safari';
  m = ua.match(/Edg\/[\d.]+/); if (m) browser = ' ' + m[0].replace('Edg/', 'Edge ');
  m = ua.match(/Mobile/); if (m && !name.includes('APK')) name = name + ' Phone';
  m = ua.match(/SM-[\w]+/); if (m) name = 'Samsung ' + m[0];
  m = ua.match(/Pixel [\w]+/); if (m) name = 'Google ' + m[0];
  return name + os + browser;
}

async function renderPasskeyTab(content) {
  try {
    var passkeys = [];
    try { var r = await API.passkeyList(); if (r.code === 0) passkeys = r.data; } catch {}
    var twofaStatus = { enabled: false };
    try { twofaStatus = (await API.get('/api/auth/2fa/status')).data || { enabled: false }; } catch {}
    var sessions = [];
    try { sessions = (await API.get('/api/auth/sessions')).data || []; } catch {}
    var html = '';

    // Passkeys section
    html += '<div class="card"><h3>🔑 Passkeys</h3><p style="color:var(--text-tertiary);font-size:13px;margin:8px 0 16px;">Use your device fingerprint, face, or PIN to sign in instantly.</p>';
    html += '<button class="btn btn-primary" id="register-passkey-btn" data-on-click="registerPasskey()">➕ Register New Passkey</button>';
    if (passkeys.length > 0) {
      html += '<div style="margin-top:20px;"><h4 style="color:var(--text-primary);margin-bottom:12px;">Your Passkeys</h4>';
      passkeys.forEach(function(pk) {
        var name = esc(pk.deviceName || 'Passkey');
        html += '<div style="display:flex;align-items:center;justify-content:space-between;padding:12px;background:var(--bg-primary);border-radius:8px;border:1px solid var(--border-primary);margin-bottom:8px;" id="pk-row-' + esc(pk.id) + '">';
        html += '<div style="flex:1;min-width:0;"><span id="pk-name-' + esc(pk.id) + '" style="color:var(--text-primary);cursor:pointer;" onclick="renamePasskeyPrompt(\'' + esc(pk.id) + '\')" title="Click to rename">' + name + ' ✏️</span><br><span style="color:var(--text-quaternary);font-size:11px;">' + fmtDate(pk.createdAt || pk.created_at, 'date') + '</span></div>';
        html += '<button class="btn btn-danger btn-sm" data-on-click="deletePasskey(\'' + esc(pk.id) + '\')">Remove</button>';
        html += '</div>';
      });
      html += '</div>';
    }
    html += '</div>';

    // 2FA section
    html += '<div class="card" style="margin-top:16px;">';
    html += '<h3>🔐 Two-Factor Authentication (2FA)</h3>';
    html += '<div style="margin-top:12px;">';
    if (twofaStatus.enabled) {
      html += '<div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;">';
      html += '<span class="badge badge-completed" style="font-size:14px;padding:4px 12px;">✅ Enabled</span>';
      html += '<button class="btn btn-sm btn-outline" data-on-click="show2faQR()">📱 Show QR Code</button>';
      html += '<button class="btn btn-sm btn-danger" data-on-click="disable2FA()">Disable 2FA</button>';
      html += '</div>';
      html += '<div id="2fa-qr-area" style="margin-top:12px;display:none;"></div>';
    } else {
      html += '<p style="color:var(--text-tertiary);font-size:13px;margin-bottom:12px;">Add an extra layer of security by enabling two-factor authentication via authenticator app (Google Authenticator, Authy, etc.)</p>';
      html += '<button class="btn btn-primary" data-on-click="setup2FA()">🔐 Enable 2FA</button>';
      html += '<div id="2fa-setup-area"></div>';
    }
    html += '</div></div>';

    // Sessions section
    html += '<div class="card" style="margin-top:16px;">';
    html += '<h3>📱 Active Sessions</h3>';
    html += '<div style="margin-top:8px;">';
    if (sessions.length === 0) {
      html += '<div style="color:var(--text-quaternary);padding:12px 0;">No session data available.</div>';
    } else {
      sessions.forEach(function(s) {
        var isActive = s.active ? '<span class="badge badge-completed">Active</span>' : '<span class="badge badge-pending">Inactive</span>';
        var device = formatDeviceName(s.userAgent || '');
        var ip = s.ip || 'N/A';
        var lastActive = s.lastActive || 'N/A';
        var created = s.createdAt || 'N/A';
        html += '<div style="display:flex;align-items:center;justify-content:space-between;padding:10px 0;border-bottom:1px solid #1e1e2e;font-size:13px;">';
        html += '<div><div style="color:var(--text-primary);font-weight:500;">' + esc(device) + '</div>';
        html += '<div style="font-size:11px;color:var(--text-tertiary);">IP: ' + esc(ip) + '</div>';
        html += '<div style="font-size:11px;color:var(--text-quaternary);">Last active: ' + lastActive + ' &middot; Created: ' + created + '</div></div>';
        html += '<div style="display:flex;align-items:center;gap:8px;flex-shrink:0;">' + isActive;
        if (s.active) html += '<button class="btn btn-sm btn-danger" data-on-click="revokeSession(\'' + esc(s.id) + '\')">Revoke</button>';
        html += '</div></div>';
      });
    }
    html += '</div>';
    if (sessions.filter(function(s) { return s.active; }).length > 1) {
      html += '<div style="margin-top:12px;"><button class="btn btn-sm btn-outline" data-on-click="revokeAllSessions()">Revoke All Other Sessions</button></div>';
    }
    html += '</div>';

    content.innerHTML = html;
  } catch (err) { content.innerHTML = '<div class="card"><p style="color:#f44336;">Error loading passkey page</p></div>'; }
}

window.registerPasskey = async function() {
  try {
    if (!window._apkGetPublicKeyCredential()) { showToast('Passkey not supported on this browser', 'error'); return; }
    var btn = document.getElementById('register-passkey-btn');
    if (btn) { btn.disabled = true; btn.textContent = '⏳ Registering...'; }
    var deviceName = detectDeviceName();
    if (isApk() && window._apkHasCredMan && window._apkHasCredMan()) {
      // Google Password Manager passkey via the native Credential Manager
      // bridge — synced across every device signed into this Google account.
      try {
        var cmBegin = await API.passkeyRegisterBegin();
        if (!cmBegin || cmBegin.code !== 0) { showToast(cmBegin?.msg || 'Failed to start passkey registration', 'error'); return; }
        var credJson = await _apkCredManCreate(JSON.stringify(cmBegin.data));
        var regResp = typeof credJson === 'string' ? JSON.parse(credJson) : credJson;
        if (regResp.error) throw new Error(regResp.error);
        regResp.deviceName = deviceName + ' · Google synced';
        var cmComplete = await API.passkeyRegisterComplete(regResp);
        if (cmComplete.code === 0) { showToast('✅ Passkey saved to Google — works on all your signed-in devices!', 'success'); switchSettingsTab('passkey'); }
        else showToast(cmComplete.msg || 'Passkey registration failed', 'error');
      } catch (err2) {
        showToast(err2.message || 'Passkey registration failed', 'error');
      }
      return;
    }
    if (isApk() && !(window._apkHasNativeWebAuthn && window._apkHasNativeWebAuthn())) {
      // Standalone passkey — Android Keystore + BiometricPrompt (no Credential Manager)
      if (typeof _apkStandaloneRegister !== 'function') {
        showToast('Standalone passkey not available — update APK from Downloads page', 'error');
        if (btn) { btn.disabled = false; btn.textContent = '🔑 Register Passkey'; }
        return;
      }
      var beginRes = await API.passkeyStandaloneRegisterBegin();
      if (beginRes.code !== 0) { showToast(beginRes.msg || 'Failed', 'error'); return; }
      var userId = currentUser?.id || '';
      var userName = currentUser?.email || currentUser?.name || '';
      var apkData = await _apkStandaloneRegister(userId, userName, beginRes.data.challenge, beginRes.data.tempToken);
      var completeRes = await API.passkeyStandaloneRegisterComplete({ tempToken: apkData.tempToken, credentialId: apkData.credentialId, publicKeySpki: apkData.publicKeySpki, clientSignature: apkData.clientSignature });
      if (completeRes.code === 0) { showToast('✅ Passkey registered on this device!', 'success'); switchSettingsTab('passkey'); }
      else showToast('Passkey registration failed', 'error');
      return;
    }
    var beginRes = await API.passkeyRegisterBegin();
    var opts = beginRes.data;
    opts.challenge = base64urlToBuffer(opts.challenge);
    opts.user.id = base64urlToBuffer(opts.user.id);
    opts.excludeCredentials = (opts.excludeCredentials || []).map(function(c) { c.id = base64urlToBuffer(c.id); return c; });
    var cred = await navigator.credentials.create({ publicKey: opts });
    var response = {
      id: cred.id, rawId: bufferToBase64url(cred.rawId), type: cred.type, deviceName: deviceName,
      response: {
        clientDataJSON: bufferToBase64url(cred.response.clientDataJSON),
        attestationObject: bufferToBase64url(cred.response.attestationObject),
        transports: cred.response.getTransports ? cred.response.getTransports() : []
      }
    };
    var completeRes = await API.passkeyRegisterComplete(response);
    if (completeRes.code === 0) { showToast('✅ Passkey registered', 'success'); switchSettingsTab('passkey'); }
    else showToast('Passkey registration failed', 'error');
  } catch (err) { showToast(err.message || 'Passkey registration failed', 'error'); }
  finally { if (btn) { btn.disabled = false; btn.textContent = '➕ Register New Passkey'; } }
};

window.deletePasskey = async function(id) {
  if (!confirm('Remove this passkey?')) return;
  try {
    var r = await API.passkeyDelete(id);
    if (r.code === 0) { showToast('Passkey removed', 'info'); switchSettingsTab('passkey'); }
  } catch (err) { showToast(err.message, 'error'); }
};

window.renamePasskeyPrompt = function(id) {
  var nameEl = document.getElementById('pk-name-' + id);
  if (!nameEl) return;
  var currentName = nameEl.textContent.replace(/\s*✏️$/, '');
  var newName = prompt('Rename this passkey:', currentName);
  if (!newName || newName.trim() === '' || newName.trim() === currentName) return;
  if (newName.length > 50) { showToast('Name too long (max 50 chars)', 'error'); return; }
  API.passkeyRename(id, newName.trim()).then(function(r) {
    if (r.code === 0) {
      nameEl.innerHTML = esc(newName.trim()) + ' ✏️';
      showToast('Passkey renamed', 'success');
    } else showToast(r.msg || 'Failed to rename', 'error');
  }).catch(function(err) { showToast(err.message, 'error'); });
};

function renderAboutTab(content) {
  var isApk = navigator.userAgent.indexOf('MONEYFESTLTD-APK') !== -1;
  var apkVersion = (typeof AndroidUpdate !== 'undefined') ? AndroidUpdate.getCurrentVersion() : '';
  var apkBuildDate = (typeof AndroidUpdate !== 'undefined' && AndroidUpdate.getCurrentBuildDate) ? ' built ' + esc(AndroidUpdate.getCurrentBuildDate()) : '';
  content.innerHTML = '<div class="card"><div style="text-align:center;padding:20px;"><div style="font-size:48px;margin-bottom:8px;">ℹ️</div><h3 style="text-transform:none;letter-spacing:0;">MONEYFESTLTD P2P Console</h3><div id="about-version-info"><p style="color:var(--text-tertiary);font-size:13px;">Loading version...</p></div>' + (isApk ? '<p style="color:var(--text-quaternary);font-size:12px;margin-top:8px;">APK version: <strong style="color:var(--text-primary);">' + esc(apkVersion || 'unknown') + '</strong>' + apkBuildDate + '</p>' : '') + '<p style="color:var(--text-quaternary);font-size:12px;max-width:400px;margin:12px auto;">Real-time P2P trading console connected to OKX API and the MoneyFest I&M Payment Agent.</p><div style="display:flex;gap:12px;justify-content:center;flex-wrap:wrap;margin-top:16px;">' + (isApk ? '<button data-on-click="checkApkUpdate()" style="padding:10px 24px;border-radius:10px;border:none;background:#00c853;color:var(--text-on-accent);font-weight:600;cursor:pointer;">⬇ Check for Updates</button>' : '') + '<button data-on-click="navigate(\'generalsupport\')" style="padding:10px 24px;border-radius:10px;border:none;background:#448aff;color:#fff;font-weight:600;cursor:pointer;">Customer Care</button></div></div></div>';
  fetch('/api/system/app/version').then(function(r) { return r.json(); }).then(function(d) {
    var el = document.getElementById('about-version-info');
    if (!el) return;
    if (d && d.code === 0 && d.data) {
      var v = d.data;
      el.innerHTML = '<p style="color:var(--text-tertiary);font-size:13px;">Version <strong style="color:var(--text-primary);">v' + esc((v.version||'').replace(/^v\.?/i,'')) + '</strong>' + (v.buildDate ? ' <span style="color:var(--text-quaternary);font-size:11px;">built ' + esc(v.buildDate) + '</span>' : '') + (v.commit ? ' <span style="color:#33334a;font-size:10px;">(' + esc(v.commit) + ')</span>' : '') + '</p>';
    } else {
      el.innerHTML = '<p style="color:var(--text-tertiary);font-size:13px;">Version info unavailable</p>';
    }
  }).catch(function() {
    var el = document.getElementById('about-version-info');
    if (el) el.innerHTML = '<p style="color:var(--text-tertiary);font-size:13px;">Version info unavailable</p>';
  });
}
function _cmpVersions(a, b) {
  // Numeric-aware compare across BOTH version styles:
  //   legacy: "v.2.000.5535"        -> [2, 0, 5535]
  //   semver: "2.4.0 (build 5536)"  -> [2, 4, 0, 5536]
  // Non-digit characters (v, spaces, parens, the word "build") become
  // separators so every number group is compared in order. This keeps the
  // legacy→semver migration monotonic and handles 2.10.0 > 2.9.0 correctly.
  function parts(s) {
    return String(s || '').toLowerCase()
      .replace(/^v[.\s]*/, '')
      .replace(/build/g, '.')
      .replace(/[^0-9.]+/g, '.')
      .split('.').map(function(x) { return parseInt(x, 10) || 0; });
  }
  var pa = parts(a), pb = parts(b);
  for (var i = 0; i < Math.max(pa.length, pb.length); i++) {
    var x = pa[i] || 0, y = pb[i] || 0;
    if (x !== y) return x > y ? 1 : -1;
  }
  return 0;
}
function _installApkUpdate() {
  _fetchLatestVersion().then(function(latest) {
    var q = latest ? '?v=' + encodeURIComponent(String(latest).replace(/^v[.]?/i, '')) : '';
    var apkUrl = '/downloads/MONEYFESTLTD-release.apk' + q;
    window.__apkUrl = apkUrl;
    fetch(apkUrl, { method: 'HEAD' }).then(function(r) {
      if (r.ok) {
        showApkDownloadModal();
        AndroidUpdate.downloadAndInstall(window.location.protocol + '//' + window.location.host + apkUrl);
      } else {
        showToast('Update file not found on server', 'error');
      }
    }).catch(function() { showToast('Update check failed', 'error'); });
  }).catch(function() { showToast('Update check failed', 'error'); });
}

function showApkDownloadModal() {
  var overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'apk-download-overlay';
  overlay.innerHTML = `
    <div class="modal-content" style="max-width:360px;text-align:center;">
      <h3>⬇ Downloading Update</h3>
      <div style="margin:16px 0 8px;height:8px;border-radius:4px;background:#1e1e2e;overflow:hidden;">
        <div id="apk-download-bar" style="width:0%;height:100%;background:linear-gradient(90deg,#00c853,#448aff);border-radius:4px;transition:width 0.3s;"></div>
      </div>
      <p id="apk-download-status" style="color:var(--text-tertiary);font-size:12px;">Preparing...</p>
      <div id="apk-download-actions" style="margin-top:12px;display:none;">
        <button class="btn btn-primary" data-on-click="document.getElementById('apk-download-overlay')?.remove()">Close</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  if (typeof bindClickHandlers === 'function') bindClickHandlers(overlay);
}

window.__apkDownloadProgress = function(pct) {
  var bar = document.getElementById('apk-download-bar');
  var status = document.getElementById('apk-download-status');
  if (bar) bar.style.width = pct + '%';
  if (status) status.textContent = 'Downloading... ' + pct + '% (retrying automatically if the network stalls)';
};

window.__apkDownloadFinished = function(ok, message) {
  var status = document.getElementById('apk-download-status');
  var actions = document.getElementById('apk-download-actions');
  if (ok) {
    if (status) status.textContent = 'Download complete — installing...';
    var overlay = document.getElementById('apk-download-overlay');
    if (overlay) setTimeout(function() { overlay.remove(); }, 1500);
  } else {
    if (status) status.textContent = message || 'Download failed';
    if (actions) actions.style.display = 'block';
    var existing = document.getElementById('apk-retry-btn');
    if (!existing && window.__apkUrl) {
      var retry = document.createElement('button');
      retry.className = 'btn btn-primary';
      retry.id = 'apk-retry-btn';
      retry.style.marginRight = '8px';
      retry.textContent = 'Retry';
      retry.addEventListener('click', function() {
        var ov = document.getElementById('apk-download-overlay');
        if (ov) ov.remove();
        window._installApkUpdate && window._installApkUpdate();
      });
      actions.insertBefore(retry, actions.firstChild);
    }
  }
};
function _fetchLatestVersion() {
  return fetch('/api/system/app/version').then(function(r) { return r.json(); }).then(function(d) {
    return (d && d.code === 0 && d.data && d.data.version) ? d.data.version : null;
  }).catch(function() { return null; });
}
function _compareAndPromptInstall(btn) {
  var installed = AndroidUpdate.getCurrentVersion ? AndroidUpdate.getCurrentVersion() : '';
  _fetchLatestVersion().then(function(latest) {
    if (btn) btn.disabled = false;
    if (!latest) { showToast('Could not fetch latest version', 'error'); return; }
    if (_cmpVersions(installed, latest) >= 0) {
      showToast('You are up to date (v' + String(installed).replace(/^v[.]?/i, '') + ')', 'success', 5000);
      return;
    }
    if (confirm('A new version is available:\n\nInstalled: v' + String(installed).replace(/^v[.]?/i, '') + '\nLatest:    v' + String(latest).replace(/^v[.]?/i, '') + '\n\nDownload and install now?')) {
      _installApkUpdate();
    }
  }).catch(function() {
    if (btn) btn.disabled = false;
    showToast('Update check failed', 'error');
  });
}
function checkApkUpdate() {
  if (typeof AndroidUpdate === 'undefined') { showToast('Not running in APK', 'error'); return; }
  var btn = document.activeElement;
  if (btn) btn.disabled = true;
  showToast('Checking for updates...', 'info');
  _compareAndPromptInstall(btn);
}

// Auto-prompt on app start: a newer APK version triggers an in-app download +
// install dialog (resumable). Sideloading stays available as a fallback via the
// settings check button. Dismissing the prompt silences it for 24h.
window._autoCheckApkUpdate = function() {
  try {
    if (typeof AndroidUpdate === 'undefined' || !AndroidUpdate.getCurrentVersion) return;
    if (document.getElementById('apk-update-overlay')) return;
    var dismissedAt = parseInt(localStorage.getItem('apkUpdateDismissedAt') || '0', 10);
    if (dismissedAt && Date.now() - dismissedAt < 86400000) return;
    var installed = AndroidUpdate.getCurrentVersion();
    _fetchLatestVersion().then(function(latest) {
      if (!latest || _cmpVersions(installed, latest) >= 0) return;
      var ov = document.createElement('div');
      ov.className = 'modal-overlay';
      ov.id = 'apk-update-overlay';
      ov.innerHTML = '<div class="modal-content" style="max-width:420px;text-align:center;">' +
        '<div style="font-size:40px;margin-bottom:8px;">📲</div>' +
        '<h3 style="margin-bottom:6px;">Update available</h3>' +
        '<p style="font-size:13px;color:var(--text-tertiary);margin-bottom:4px;">Installed: v<span class="apk-up-installed"></span></p>' +
        '<p style="font-size:13px;color:var(--text-tertiary);margin-bottom:14px;">Latest: v<span class="apk-up-latest"></span></p>' +
        '<p style="font-size:12px;color:var(--text-quaternary);margin-bottom:16px;">Downloads and installs inside the app (resumable). Sideload the APK only if this fails.</p>' +
        '<div style="display:flex;gap:8px;justify-content:center;flex-wrap:wrap;">' +
        '<button class="btn btn-primary apk-up-now">⬇️ Update Now</button>' +
        '<button class="btn btn-secondary btn-sm apk-up-later">Later</button>' +
        '</div></div>';
      document.body.appendChild(ov);
      ov.querySelector('.apk-up-installed').textContent = String(installed).replace(/^v[.]?/i, '');
      ov.querySelector('.apk-up-latest').textContent = String(latest).replace(/^v[.]?/i, '');
      ov.querySelector('.apk-up-now').addEventListener('click', function() { ov.remove(); _installApkUpdate(); });
      ov.querySelector('.apk-up-later').addEventListener('click', function() {
        localStorage.setItem('apkUpdateDismissedAt', String(Date.now()));
        ov.remove();
      });
    });
  } catch (e) {}
};

// ── Price Alerts Tab ──
async function renderPriceAlertsTab(content) {
  try {
    var alerts = (await API.getPriceAlerts()).data || [];
    var html = '<div class="card"><h3 style="margin-bottom:12px;">🔔 Price Alerts</h3><p style="font-size:13px;color:var(--text-quaternary);margin-bottom:16px;">Get notified when USDT price crosses a threshold on P2P.</p>';
    html += '<div id="price-alerts-list">';
    if (alerts.length === 0) {
      html += '<div style="padding:16px;background:var(--bg-tertiary);border-radius:8px;text-align:center;color:var(--text-quaternary);font-size:13px;">No price alerts configured.</div>';
    } else {
      alerts.forEach(function(a) {
        var dir = a.direction === 'above' ? '📈 rises above' : '📉 drops below';
        html += '<div style="display:flex;align-items:center;gap:12px;padding:12px;background:var(--bg-tertiary);border-radius:8px;margin-bottom:8px;border:1px solid var(--glass-border);">';
        html += '<div style="flex:1;"><strong style="color:var(--text-primary);">' + esc(a.instId) + '</strong> ' + dir + ' <strong style="color:var(--accent-amber);">KES ' + parseFloat(a.threshold).toFixed(2) + '</strong>';
        if (a.note) html += '<div style="font-size:11px;color:var(--text-quaternary);margin-top:2px;">' + esc(a.note) + '</div>';
        html += '</div>';
        html += '<button class="btn btn-sm btn-danger" data-on-click="deletePriceAlert(\'' + esc(a.id) + '\')" style="font-size:11px;">Delete</button>';
        html += '</div>';
      });
    }
    html += '</div><div style="margin-top:16px;padding-top:16px;border-top:1px solid var(--glass-border);"><h4 style="font-size:13px;margin-bottom:12px;">➕ New Alert</h4>';
    html += '<div style="display:flex;gap:8px;flex-wrap:wrap;align-items:end;"><div class="form-group" style="flex:1;min-width:100px;"><label>Pair</label><select id="new-alert-instId" style="width:100%;"><option value="USDT-KES">USDT-KES</option><option value="BTC-KES">BTC-KES</option><option value="ETH-KES">ETH-KES</option></select></div>';
    html += '<div class="form-group" style="flex:1;min-width:100px;"><label>Direction</label><select id="new-alert-direction" style="width:100%;"><option value="above">Above</option><option value="below">Below</option></select></div>';
    html += '<div class="form-group" style="flex:1;min-width:100px;"><label>Threshold (KES)</label><input type="number" id="new-alert-threshold" step="0.01" min="0" placeholder="e.g. 140" style="width:100%;"></div>';
    html += '<div class="form-group" style="flex:1;min-width:100px;"><label>Note (optional)</label><input type="text" id="new-alert-note" placeholder="e.g. Buy zone" style="width:100%;"></div>';
    html += '<button class="btn btn-primary" data-on-click="createPriceAlert()" style="margin-bottom:4px;">➕ Add Alert</button></div></div></div>';
    content.innerHTML = html;
  } catch (err) {
    content.innerHTML = '<div class="card" style="text-align:center;padding:40px;"><p style="color:var(--accent-red);">' + esc(err.message) + '</p></div>';
  }
}

window.createPriceAlert = async function() {
  var instId = document.getElementById('new-alert-instId')?.value;
  var direction = document.getElementById('new-alert-direction')?.value;
  var threshold = document.getElementById('new-alert-threshold')?.value;
  var note = document.getElementById('new-alert-note')?.value;
  if (!instId || !direction || !threshold) { showToast('Fill in all required fields', 'error'); return; }
  try {
    await API.createPriceAlert({ instId: instId, direction: direction, threshold: parseFloat(threshold), note: note || '' });
    showToast('Price alert created', 'success');
    switchSettingsTab('price-alerts');
  } catch (err) { showToast(err.message, 'error'); }
};

window.deletePriceAlert = async function(id) {
  if (!confirm('Delete this price alert?')) return;
  try {
    await API.deletePriceAlert(id);
    showToast('Price alert deleted', 'success');
    switchSettingsTab('price-alerts');
  } catch (err) { showToast(err.message, 'error'); }
};

// ── Webhooks Tab ──
async function renderWebhooksTab(content) {
  var EVENTS = [{ id: 'order.placed', label: 'Order Placed' }, { id: 'order.paid', label: 'Order Paid' }, { id: 'order.released', label: 'Order Released' }, { id: 'order.cancelled', label: 'Order Cancelled' }, { id: 'order.disputed', label: 'Order Disputed' }];
  try {
    var hooks = (await API.getWebhooks()).data || [];
    var html = '<div class="card"><h3 style="margin-bottom:12px;">🔗 Webhooks</h3><p style="font-size:13px;color:var(--text-quaternary);margin-bottom:16px;">Forward order events to your own server via HTTPS POST requests.</p>';
    html += '<div id="webhooks-list">';
    if (hooks.length === 0) {
      html += '<div style="padding:16px;background:var(--bg-tertiary);border-radius:8px;text-align:center;color:var(--text-quaternary);font-size:13px;">No webhooks configured.</div>';
    } else {
      hooks.forEach(function(h) {
        var evts = (h.events || []).map(function(e) { var found = EVENTS.find(function(ev) { return ev.id === e; }); return found ? found.label : e; }).join(', ');
        html += '<div style="display:flex;align-items:center;gap:12px;padding:12px;background:var(--bg-tertiary);border-radius:8px;margin-bottom:8px;border:1px solid var(--glass-border);">';
        html += '<div style="flex:1;min-width:0;"><div style="font-size:13px;color:var(--text-primary);word-break:break-all;">' + esc(h.url) + '</div><div style="font-size:11px;color:var(--text-quaternary);margin-top:2px;">Events: ' + esc(evts) + (h.secret ? ' | 🔐 Signed' : '') + '</div></div>';
        html += '<label class="switch" style="position:relative;display:inline-block;width:36px;height:20px;flex-shrink:0;"><input type="checkbox" ' + (h.active !== false ? 'checked' : '') + ' onchange="toggleWebhook(\'' + esc(h.id) + '\', this.checked)" style="opacity:0;width:0;height:0;"><span class="slider" style="position:absolute;cursor:pointer;top:0;left:0;right:0;bottom:0;background:#2a2a3e;border-radius:20px;transition:0.3s;"></span></label>';
        html += '<button class="btn btn-sm btn-danger" data-on-click="deleteWebhook(\'' + esc(h.id) + '\')" style="font-size:11px;">Delete</button>';
        html += '</div>';
      });
    }
    html += '</div></div>';
    html += '<div class="card" style="margin-top:16px;"><h4 style="font-size:13px;margin-bottom:12px;">➕ New Webhook</h4>';
    html += '<div class="form-group"><label>HTTPS URL</label><input type="url" id="new-webhook-url" placeholder="https://your-server.com/webhook" style="width:100%;"></div>';
    html += '<div class="form-group"><label>Events</label><div id="new-webhook-events" style="display:flex;flex-wrap:wrap;gap:6px;">';
    EVENTS.forEach(function(e) {
      html += '<label style="display:flex;align-items:center;gap:4px;font-size:12px;color:#8888a0;cursor:pointer;"><input type="checkbox" value="' + e.id + '" checked> ' + e.label + '</label>';
    });
    html += '</div></div>';
    html += '<div class="form-group"><label>Secret (optional — for HMAC signing)</label><input type="text" id="new-webhook-secret" placeholder="leave blank for no signing" style="width:100%;"></div>';
    html += '<button class="btn btn-primary" data-on-click="createWebhook()">➕ Add Webhook</button></div>';
    content.innerHTML = html;
  } catch (err) {
    content.innerHTML = '<div class="card" style="text-align:center;padding:40px;"><p style="color:var(--accent-red);">' + esc(err.message) + '</p></div>';
  }
}

window.createWebhook = async function() {
  var url = document.getElementById('new-webhook-url')?.value?.trim();
  var secret = document.getElementById('new-webhook-secret')?.value?.trim() || '';
  var eventEls = document.querySelectorAll('#new-webhook-events input[type="checkbox"]:checked');
  var events = Array.from(eventEls).map(function(el) { return el.value; });
  if (!url) { showToast('Enter a webhook URL', 'error'); return; }
  if (!url.startsWith('https://')) { showToast('URL must start with https://', 'error'); return; }
  if (events.length === 0) { showToast('Select at least one event', 'error'); return; }
  try {
    await API.createWebhook({ url: url, events: events, secret: secret || undefined });
    showToast('Webhook created', 'success');
    switchSettingsTab('webhooks');
  } catch (err) { showToast(err.message, 'error'); }
};

window.toggleWebhook = async function(id, active) {
  try {
    await API.updateWebhook(id, { active: active });
    showToast('Webhook ' + (active ? 'enabled' : 'disabled'), 'success');
  } catch (err) { showToast(err.message, 'error'); switchSettingsTab('webhooks'); }
};

window.deleteWebhook = async function(id) {
  if (!confirm('Delete this webhook?')) return;
  try {
    await API.deleteWebhook(id);
    showToast('Webhook deleted', 'success');
    switchSettingsTab('webhooks');
  } catch (err) { showToast(err.message, 'error'); }
};

window.renderProfilePage = function(container) {
  if (!currentUser) { renderLogin(container); return; }
  const initials = currentUser.name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase();
  const colors = ['#448aff','#00c853','#ff9100','#f44336','#9c27b0','#00bcd4','#ff5722','#607d8b'];
  const color = colors[currentUser.name.length % colors.length];
  const avatarSrc = currentUser.avatar || '';
  const roleBadge = currentUser.role === 'admin' ? '👑 Admin' : '👤 User';
  container.innerHTML = `
    <div class="page-header"><div><h2>My Profile</h2><div class="subtitle">View and edit your profile details</div></div></div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-top:16px;">
      <div class="card" style="text-align:center;padding:32px;">
        ${avatarSrc ? '<img src="' + avatarSrc + '" style="width:80px;height:80px;border-radius:50%;object-fit:cover;margin:0 auto 16px;border:2px solid rgba(0,200,83,0.3);display:block;">' : '<div style="width:80px;height:80px;border-radius:50%;background:' + color + '20;color:' + color + ';display:flex;align-items:center;justify-content:center;font-weight:800;font-size:32px;margin:0 auto 16px;">' + initials + '</div>'}
        <h3 style="text-transform:none;letter-spacing:0;font-size:20px;color:#fff;margin-bottom:4px;">' + currentUser.name + '</h3>
        <div style="color:var(--text-tertiary);font-size:13px;margin-bottom:4px;">@' + (currentUser.username || currentUser.name) + '</div>
        <div style="color:var(--text-tertiary);font-size:13px;margin-bottom:8px;">' + currentUser.email + '</div>
        <div><span class="badge badge-' + (currentUser.role === 'admin' ? 'completed' : 'pending') + '">' + roleBadge + '</span></div>
      </div>
      <div class="card">
        <h3>Edit Profile</h3>
        <form data-on-submit="updateProfile(event)">
          <div class="form-group"><label>Username</label><input id="settings-username" value="' + (currentUser.username || currentUser.name) + '" required></div>
          <div class="form-group"><label>Full Name</label><input id="settings-name" value="' + currentUser.name + '" required></div>
          <div class="form-group"><label>Email</label><input id="settings-email" type="email" value="' + currentUser.email + '" required></div>
          <div class="form-group">
            <label>Profile Picture</label>
            <div style="display:flex;align-items:center;gap:12px;">
              <input type="file" id="settings-avatar-input" accept="image/*" style="display:none;" onchange="previewAvatar(event)">
              <button type="button" class="btn btn-sm btn-outline" data-on-click="document.getElementById(\'settings-avatar-input\').click()">Upload Image</button>
              <span id="settings-avatar-name" style="font-size:12px;color:var(--text-quaternary);"></span>
            </div>
          </div>
          <button type="submit" class="btn btn-primary" style="width:100%;">Save Changes</button>
        </form>
      </div>
    </div>
    <div class="card" style="margin-top:20px;">
      <h3 style="text-transform:none;letter-spacing:0;font-size:15px;margin-bottom:12px;">Change Password</h3>
      <form data-on-submit="changePassword(event)">
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px;">
          <div class="form-group"><label>Current Password</label><input id="settings-cur-pw" type="password" placeholder="Enter current password"></div>
          <div class="form-group"><label>New Password</label><div style="position:relative;"><input id="settings-new-pw" type="password" placeholder="Enter new password" style="width:100%;padding-right:36px;"><span data-on-click="togglePassword(\'settings-new-pw\',this)" style="position:absolute;right:10px;top:50%;transform:translateY(-50%);cursor:pointer;color:var(--text-quaternary);font-size:14px;user-select:none;">👁</span></div></div>
          <div class="form-group"><label>Confirm New Password</label><div style="position:relative;"><input id="settings-conf-pw" type="password" placeholder="Confirm new password" style="width:100%;padding-right:36px;"><span data-on-click="togglePassword(\'settings-conf-pw\',this)" style="position:absolute;right:10px;top:50%;transform:translateY(-50%);cursor:pointer;color:var(--text-quaternary);font-size:14px;user-select:none;">👁</span></div></div>
        </div>
        <button type="submit" class="btn btn-primary" style="margin-top:8px;">Update Password</button>
      </form>
    </div>`;
};

/* ==== js/modules/pricing.js ==== */
var _paymentInfo = null;

async function renderPricing(content) {
  if (!currentUser) { renderLogin(content); return; }
  var subRes, payInfoRes;
  try { subRes = await API.get('/api/subscription/status'); } catch {}
  try { payInfoRes = await API.get('/api/subscription/payment-info'); } catch {}
  var sub = subRes && subRes.code === 0 ? subRes.data : null;
  _paymentInfo = payInfoRes && payInfoRes.code === 0 ? payInfoRes.data : null;
  var isActive = sub && sub.status === 'active' && sub.currentPeriodEnd > Math.floor(Date.now() / 1000);
  var periodEnd = sub && sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd * 1000).toLocaleDateString() : '';

  content.innerHTML = '<div class="page-header"><div><h2>Subscription</h2><div class="subtitle">Full API Access — one plan, all features</div></div></div>';

  if (!_paymentInfo) {
    content.innerHTML += '<div class="card" style="margin-top:16px;text-align:center;padding:40px;"><p style="color:var(--text-quaternary);">Loading payment info...</p></div>';
    return;
  }

  var isAdmin = currentUser && currentUser.role === 'admin';
  if (isActive) {
    content.innerHTML += '<div class="card" style="border-color:rgba(0,200,83,0.3);margin-bottom:20px;background:linear-gradient(135deg,#0c1a14,#0e0e18);"><div style="display:flex;align-items:center;gap:12px;"><span style="font-size:28px;">✅</span><div><h3 style="margin:0;color:var(--accent-green);">Active — Full API Access</h3><p style="margin:4px 0 0;color:var(--text-tertiary);font-size:13px;">Your subscription is active until ' + periodEnd + '</p></div></div></div>';
  } else if (sub && sub.status === 'active' && !isAdmin) {
    content.innerHTML += '<div class="card" style="border-color:rgba(244,67,54,0.3);margin-bottom:20px;background:var(--bg-secondary);"><div style="display:flex;align-items:center;gap:12px;"><span style="font-size:28px;">🔴</span><div><h3 style="margin:0;color:var(--accent-red);">Inactive — Subscription Expired</h3><p style="margin:4px 0 0;color:var(--text-quaternary);font-size:13px;">Your subscription ended ' + periodEnd + '. Subscribe again to reactivate.</p></div></div></div>';
  }

  var html = '<div style="margin-top:16px;"><div class="card" style="text-align:center;border-color:rgba(68,138,255,0.3);background:linear-gradient(135deg,#0c1220,#0e0e18);padding:32px 20px;"><h2 style="margin:0 0 4px;font-size:24px;">🚀 Full API Access</h2><div style="font-size:42px;font-weight:900;background:linear-gradient(135deg,var(--accent-green),var(--accent-blue));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;margin:12px 0 4px;">' + _paymentInfo.kes.amount.toLocaleString() + ' KES</div><div style="font-size:16px;color:var(--text-tertiary);margin-bottom:16px;"><span style="color:var(--text-secondary);">' + _paymentInfo.usdt.amount + ' USDT</span> equivalent</div><p style="font-size:13px;color:var(--text-tertiary);max-width:400px;margin:0 auto 20px;">Unrestricted access to all P2P, Bank, and OKX API features. No limits.</p></div></div>';

  // Payment methods
  html += '<h3 style="margin:24px 0 12px;font-size:15px;">Choose Payment Method</h3>';
  html += '<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:16px;">';

  // USDT
  html += '<div class="card" style="display:flex;flex-direction:column;border-color:rgba(0,200,83,0.25);' + (isActive ? 'opacity:0.5;' : '') + '">';
  html += '<div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;"><span style="font-size:20px;">💎</span><h3 style="margin:0;font-size:15px;">USDT (TRC-20)</h3></div>';
  html += '<div style="font-size:24px;font-weight:700;color:var(--accent-green);margin-bottom:4px;">' + _paymentInfo.usdt.amount + ' USDT</div>';
  html += '<p style="font-size:12px;color:var(--text-tertiary);margin-bottom:12px;">Send to the email below on any supported exchange</p>';
  html += '<div style="background:var(--bg-primary);border:1px solid var(--border-primary);border-radius:8px;padding:10px 12px;margin-bottom:8px;display:flex;align-items:center;justify-content:space-between;"><span style="font-size:13px;color:var(--text-secondary);user-select:all;" id="usdt-email">' + esc(_paymentInfo.usdt.email) + '</span><button class="btn btn-sm btn-primary" onclick="copyUsdtEmail()" style="white-space:nowrap;font-size:11px;">📋 Copy</button></div>';
  html += '<p style="font-size:11px;color:var(--text-quaternary);margin-bottom:8px;">Supported exchanges: <strong>' + _paymentInfo.usdt.exchanges.join(', ') + '</strong></p>';
  html += '<div style="flex:1;"></div>';
  html += '<button class="btn btn-primary" onclick="notifyPayment(\'usdt\')" style="width:100%;' + (isActive ? 'opacity:0.5;pointer-events:none;' : '') + '">✅ I\'ve Paid — Notify Admin</button>';
  html += '</div>';

  // KES (Paystack)
  html += '<div class="card" style="display:flex;flex-direction:column;border-color:rgba(68,138,255,0.25);' + (isActive ? 'opacity:0.5;' : '') + '">';
  html += '<div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;"><span style="font-size:20px;">🇰🇪</span><h3 style="margin:0;font-size:15px;">KES — M-Pesa (Paystack)</h3></div>';
  html += '<div style="font-size:24px;font-weight:700;color:var(--accent-blue);margin-bottom:4px;">KES ' + _paymentInfo.kes.amount.toLocaleString() + '</div>';
  html += '<p style="font-size:12px;color:var(--text-tertiary);margin-bottom:12px;flex:1;">Pay via M-Pesa through our secure Paystack checkout</p>';
  html += '<button class="btn btn-primary" onclick="initiatePayment(\'full\',this)" style="width:100%;background:linear-gradient(135deg,var(--accent-blue),#3a5de7);' + (isActive ? 'opacity:0.5;pointer-events:none;' : '') + '">💳 Pay with M-Pesa</button>';
  html += '</div>';

  html += '</div>'; // end grid

  // WhatsApp verification step (shown after USDT payment)
  html += '<div class="card" style="margin-top:16px;border-color:rgba(37,211,102,0.25);background:linear-gradient(135deg,#0c1a14,#0e0e18);">';
  html += '<div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;"><span style="font-size:32px;">📱</span><div style="flex:1;"><h3 style="margin:0 0 4px;font-size:15px;">After Payment — Text Me on WhatsApp for Approval</h3><p style="margin:0;font-size:12px;color:var(--text-tertiary);">Once you\'ve sent <strong>' + _paymentInfo.usdt.amount + ' USDT</strong> to the email above, text me on WhatsApp with your payment proof and I\'ll activate your subscription within minutes.</p></div><a href="https://wa.me/' + _paymentInfo.crypto.whatsapp + '" target="_blank" class="btn btn-primary" style="white-space:nowrap;background:linear-gradient(135deg,#25D366,#128C7E);text-decoration:none;display:inline-flex;align-items:center;gap:6px;padding:10px 24px;"><span>📲</span> Text for Approval</a></div>';
  html += '</div>';

  if (isActive) {
    html += '<div style="margin-top:20px;"><button class="btn btn-outline btn-sm" onclick="cancelSubscription()" style="color:var(--accent-red);border-color:var(--accent-red);">Cancel Subscription</button></div>';
  }

  content.innerHTML += html;
}

window.initiatePayment = async function(planId, btn) {
  try {
    if (btn) { btn.disabled = true; btn.textContent = '⏳ Redirecting...'; }
    var r = await API.post('/api/subscription/initialize', { plan: planId });
    if (r.code === 0 && r.data && r.data.authorizationUrl) {
      window.open(r.data.authorizationUrl, '_blank', 'width=800,height=700');
      var _paymentCheckStart = Date.now();
      var checkInterval = setInterval(function() {
        if (window.__paymentSuccess) { clearInterval(checkInterval); renderPricing(document.getElementById('page-content')); }
        if (Date.now() - _paymentCheckStart > 120000) clearInterval(checkInterval);
      }, 1000);
    } else { showToast(r?.msg || r?.message || 'Payment initialization failed', 'error'); }
  } catch (err) { showToast(err.message || 'Payment failed', 'error'); }
  finally { if (btn) { btn.disabled = false; btn.textContent = '💳 Pay with M-Pesa'; } }
};

window.addEventListener('message', function(e) {
  if (e.data === 'payment_success') { window.__paymentSuccess = true; showToast('✅ Subscription activated! All features unlocked.', 'success'); }
  if (e.data === 'payment_failed') { showToast('Payment failed or cancelled', 'error'); }
});

window.cancelSubscription = async function() {
  if (!confirm('Cancel your subscription? You will lose access at the end of the billing period.')) return;
  try { await API.post('/api/subscription/cancel'); showToast('Subscription cancelled', 'info'); renderPricing(document.getElementById('page-content')); }
  catch (err) { showToast(err.message, 'error'); }
};

window.copyUsdtEmail = function() {
  var el = document.getElementById('usdt-email');
  if (!el) return;
  var text = el.textContent;
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(function() { showToast('📋 Email copied to clipboard', 'success'); }).catch(function() { fallbackCopy(text); });
  } else { fallbackCopy(text); }
};

function fallbackCopy(text) {
  var ta = document.createElement('textarea');
  ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
  document.body.appendChild(ta); ta.select();
  try { document.execCommand('copy'); showToast('📋 Email copied to clipboard', 'success'); } catch {}
  document.body.removeChild(ta);
}

window.notifyPayment = async function(type) {
  try {
    var btn = event && event.target ? event.target : document.querySelector('[onclick*="notifyPayment"]');
    if (btn) { btn.disabled = true; btn.textContent = '⏳ Sending...'; }
    var r = await API.post('/api/subscription/notify-payment', { type: type });
    if (r && r.code === 0) {
      showToast(r.msg || '✅ Notification sent! Admin will verify and activate your subscription.', 'success');
    } else {
      showToast(r?.msg || 'Failed to send notification', 'error');
    }
  } catch (err) { showToast(err.message || 'Error', 'error'); }
  finally { if (btn) { btn.disabled = false; btn.textContent = "✅ I've Paid — Notify Admin"; } }
};

async function loadPublicConfig() {
  try {
    var r = await fetch('/api/config/public');
    var data = await r.json();
    window._paystackPublicKey = data.paystackPublicKey || '';
  } catch {}
}
loadPublicConfig();

/* ==== js/modules/ai-assistant.js ==== */
function toggleAIChat() {
  document.getElementById('ai-chat-panel').classList.toggle('hidden');
}

async function sendAIChat() {
  const input = document.getElementById('ai-chat-input');
  const msg = input.value.trim();
  if (!msg) return;
  input.value = '';

  const msgs = document.getElementById('ai-chat-messages');
  msgs.innerHTML += '<div class="ai-msg ai-msg-user">' + escapeHtml(msg) + '</div>';
  msgs.scrollTop = msgs.scrollHeight;

  const thinking = document.createElement('div');
  thinking.className = 'ai-msg ai-msg-bot';
  thinking.textContent = 'Thinking...';
  msgs.appendChild(thinking);

  try {
    const res = await API.post('/api/ai/ask', { question: msg });
    thinking.textContent = res?.data?.reply || 'No response';
  } catch (e) {
    thinking.textContent = 'AI service unavailable. Try again later. (' + e.message + ')';
  }
  msgs.scrollTop = msgs.scrollHeight;
}

function escapeHtml(s) {
  var d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

/* ==== js/modules/onboarding.js ==== */
var ONBOARDING_KEY = 'onboarding_completed_v1';

function needsOnboarding() {
  return !localStorage.getItem(ONBOARDING_KEY);
}

function completeOnboarding() {
  localStorage.setItem(ONBOARDING_KEY, 'true');
  document.getElementById('onboarding-overlay')?.remove();
}

async function showOnboarding() {
  if (!needsOnboarding()) return;
  var ipNotice = '';
  try { if (typeof window.okxIpNoticeHtml === 'function') ipNotice = await window.okxIpNoticeHtml(); } catch (e) {}
  var steps = [
    { icon: '🔑', title: '1. Add API Keys',
      desc: 'Go to Settings → API Keys to connect OKX, Bybit, or Binance. Your dashboard and trading features will activate once connected.',
      extra: ipNotice },
    { icon: '📊', title: '2. Your Dashboard',
      desc: 'The General Dashboard shows an overview of all connected exchanges. Click any card to open that exchange\'s full dashboard.' },
    { icon: '📢', title: '3. Ads & Orders',
      desc: 'Create and manage P2P ads under each exchange\'s Ads page. Track your orders, release crypto, and handle disputes from the Orders page.' },
    { icon: '🤖', title: '4. Automation',
      desc: 'Set up Auto Fund to automatically top up your sell ads, or configure Trailing to adjust prices based on market movements.' },
    { icon: '💬', title: '5. AI Assistant',
      desc: 'Click the 🤖 bubble at the bottom-right to ask the AI assistant anything about P2P trading, API setup, or platform features.' }
  ];
  var overlay = document.createElement('div');
  overlay.id = 'onboarding-overlay';
  overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,.7);z-index:10000;display:flex;align-items:center;justify-content:center;';
  var stepIdx = 0;
  function renderStep() {
    var s = steps[stepIdx];
    overlay.innerHTML = '<div style="background:var(--card-bg,#1a1a2e);border:1px solid #2a2a3e;border-radius:20px;padding:32px;max-width:440px;width:90%;text-align:center;box-shadow:0 16px 64px rgba(0,0,0,.6);">' +
      '<div style="font-size:56px;margin-bottom:12px;">' + s.icon + '</div>' +
      '<h2 style="margin:0 0 8px;">' + s.title + '</h2>' +
      '<p style="color:var(--text-tertiary,#8888a0);font-size:14px;line-height:1.6;margin:0 0 24px;">' + s.desc + '</p>' +
      (s.extra || '') +
      '<div style="display:flex;justify-content:center;gap:6px;margin-bottom:20px;">' +
      steps.map(function(_, i) { return '<span style="width:8px;height:8px;border-radius:50%;background:' + (i === stepIdx ? '#6c63ff' : '#2a2a3e') + ';display:inline-block;"></span>'; }).join('') +
      '</div>' +
      '<div style="display:flex;gap:8px;justify-content:center;">' +
      (stepIdx > 0 ? '<button class="btn btn-outline" onclick="prevOnboardingStep()" style="padding:8px 20px;">← Back</button>' : '') +
      (stepIdx < steps.length - 1 ? '<button class="btn btn-primary" onclick="nextOnboardingStep()" style="padding:8px 24px;">Next →</button>' : '') +
      (stepIdx === steps.length - 1 ? '<button class="btn btn-primary" onclick="completeOnboarding()" style="padding:8px 24px;">✓ Get Started</button>' : '') +
      '</div>' +
      '<button onclick="completeOnboarding()" style="background:none;border:none;color:var(--text-quaternary);cursor:pointer;font-size:12px;margin-top:12px;">Skip tour</button>' +
      '</div>';
  }
  window.nextOnboardingStep = function() {
    if (stepIdx < steps.length - 1) { stepIdx++; renderStep(); }
  };
  window.prevOnboardingStep = function() {
    if (stepIdx > 0) { stepIdx--; renderStep(); }
  };
  renderStep();
  document.body.appendChild(overlay);
}

/* ==== js/modules/exchangeNav.js ==== */
var _activeExchange = 'okx';

function getExchangeFromPage(page) {
  if (!page) return 'general';
  if (page === 'general-dashboard' || page === 'login') return 'general';
  if (page.startsWith('bybit-')) return 'bybit';
  if (page.startsWith('binance-')) return 'binance';
  if (page.startsWith('settings') || page === 'pricing' || page === 'reviews' || page === 'admin' || page === 'support-admin' || page === 'admin-suggestions') return 'general';
  return 'okx';
}

function syncExchangeUI(exchange) {
  _activeExchange = exchange;
  document.querySelectorAll('.exchange-tab').forEach(function(t) {
    var isActive = t.getAttribute('data-exchange') === exchange;
    t.classList.toggle('active', isActive);
    t.setAttribute('aria-selected', isActive ? 'true' : 'false');
    t.setAttribute('tabindex', isActive ? '0' : '-1');
  });
  var sidebarExchange = exchange;
  document.querySelectorAll('.platform-group').forEach(function(g) {
    g.style.display = g.classList.contains('platform-' + sidebarExchange) ? '' : 'none';
  });
  document.body.dataset.exchange = exchange;
}

function switchExchange(exchange) {
  syncExchangeUI(exchange);
  var page = exchange === 'okx' ? 'dashboard' : exchange === 'general' ? 'general-dashboard' : exchange + '-dashboard';
  if (typeof navigate === 'function') navigate(page);
}

var _origNav = window.navigate;
if (typeof _origNav === 'function') {
  window.navigate = function(page, skipHistory) {
    var result = _origNav.call(this, page, skipHistory);
    syncExchangeUI(getExchangeFromPage(typeof page === 'string' ? page : currentPage));
    return result;
  };
}

(function() {
  var path = window.location.pathname;
  var page = null;
  if (typeof getPageFromPath === 'function') {
    page = getPageFromPath(path);
  } else {
    if (path === '/general') page = 'general-dashboard';
    else if (path.startsWith('/bybit/')) page = 'bybit-' + path.split('/')[2];
    else if (path.startsWith('/binance/')) page = 'binance-' + path.split('/')[2];
    else if (path.startsWith('/okx/')) page = path.split('/')[2] || 'dashboard';
    else page = 'general-dashboard';
  }
  syncExchangeUI(getExchangeFromPage(page));
})();
